/* ============================================================================
   CSL CASHLESS V8 — logique applicative
   ----------------------------------------------------------------------------
   Externalisé depuis templates/page.csl-cashless.liquid : le template dépassait
   la limite Shopify de 256 Ko.

   Ce fichier attend que le template ait déjà défini, AVANT de le charger :
     SUPABASE_URL, SUPABASE_ANON_KEY, SHOPIFY_RECHARGE_URL,
     SHOPIFY_HOME_URL, CSL_LOGO
   ========================================================================== */

var supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: window.localStorage,
    storageKey: "csl-auth-v6"
  }
});

// Palette bleu Lons-le-Saunier
var C = {
  blue: "#d02128",        // bleu Lons-le-Saunier
  deep: "#1a0e0f",        // navy CSL
  light: "#4f9cd6",       // bleu clair
  pale: "#e8f1f9",        // bleu très pâle
  gold: "#FFB627",        // jaune Lédoniens
  bg: "#f4f7fb",          // fond app
  card: "#ffffff",        // fond cards
  border: "#dde5ef",      // bordures
  text: "#1a0e0f",        // texte principal
  muted: "#6b7a90",       // texte secondaire
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b"
};

var useState = React.useState;
var useEffect = React.useEffect;
var useRef = React.useRef;
var h = React.createElement;

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════
function formatEur(cents) { return ((cents || 0) / 100).toFixed(2) + " €"; }
function toCents(eur) { return Math.round(parseFloat(eur) * 100); }

function secureId(length) {
  length = length || 12;
  var chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  var arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  var out = "";
  for (var i = 0; i < length; i++) out += chars[arr[i] % chars.length];
  return out;
}

function makeQR(id) {
  var ck = 0;
  for (var i = 0; i < id.length; i++) ck += id.charCodeAt(i);
  ck = ck % 97;
  return "CSL-" + id + "-" + ck;
}

function parseQR(d) {
  if (!d || d.indexOf("CSL-") !== 0) return null;
  var p = d.split("-");
  if (p.length < 3) return null;
  var id = p.slice(1, -1).join("-");
  var ck = parseInt(p[p.length - 1]);
  var ex = 0;
  for (var i = 0; i < id.length; i++) ex += id.charCodeAt(i);
  ex = ex % 97;
  return ck === ex ? id : null;
}

// ═══════════════════════════════════════════════════════════════════
// DB LAYER
// ═══════════════════════════════════════════════════════════════════
var db = {
  createAccount: async function(params) {
    var id = secureId(12).toLowerCase();
    var qr = makeQR(id);
    return await supabase.from("accounts").insert({
      id: id, name: params.name, email: params.email.toLowerCase(),
      phone: params.phone, qr: qr, auth_user_id: params.authUserId || null
    }).select().single();
  },
  getAccountByAuthUser: async function(authUserId) {
    return await supabase.from("accounts").select("*").eq("auth_user_id", authUserId).maybeSingle();
  },
  getAccountByEmail: async function(email) {
    return await supabase.from("accounts").select("*").eq("email", email.toLowerCase()).maybeSingle();
  },
  linkAccountToAuth: async function(accountId, authUserId) {
    return await supabase.from("accounts").update({auth_user_id: authUserId}).eq("id", accountId);
  },
  getAccount: async function(id) {
    return await supabase.from("accounts").select("*").eq("id", id).maybeSingle();
  },
  findAccount: async function(query) {
    var q = query.trim();
    if (!q) return {data: null};
    var qrId = parseQR(q);
    if (qrId) return this.getAccount(qrId);
    var r1 = await supabase.from("accounts").select("*").eq("email", q.toLowerCase()).maybeSingle();
    if (r1.data) return r1;
    var r2 = await supabase.from("accounts").select("*").eq("qr", q).maybeSingle();
    if (r2.data) return r2;
    return await supabase.from("accounts").select("*").ilike("name", "%" + q + "%").limit(1).maybeSingle();
  },
  listAccountsPage: async function(page, perPage, search) {
    page = page || 0;
    perPage = perPage || 20;
    var from = page * perPage;
    var to = from + perPage - 1;
    var q = supabase.from("accounts").select("*", {count: "exact"});
    if (search && search.trim()) {
      var s = search.trim();
      q = q.or("name.ilike.%" + s + "%,email.ilike.%" + s + "%");
    }
    q = q.order("created_at", {ascending: false}).range(from, to);
    var r = await q;
    return {data: r.data || [], count: r.count || 0, error: r.error};
  },
  countAccountsToday: async function() {
    // Date du jour locale -> début et fin journée en ISO
    var now = new Date();
    var startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
    var endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
    var r = await supabase.from("accounts")
      .select("id", {count: "exact", head: true})
      .gte("created_at", startOfDay)
      .lte("created_at", endOfDay);
    return {count: r.count || 0, error: r.error};
  },
  listProducts: async function(posId) {
    var q = supabase.from("products").select("*").eq("active", true);
    if (posId) q = q.eq("pos_id", posId);
    var r = await q.order("sort_order");
    return {data: r.data || [], error: r.error};
  },
  listAllProducts: async function(posId) {
    var q = supabase.from("products").select("*");
    if (posId) q = q.eq("pos_id", posId);
    var r = await q.order("sort_order");
    return {data: r.data || [], error: r.error};
  },
  addProduct: async function(p) {
    var id = secureId(10).toLowerCase();
    return await supabase.from("products").insert({
      id: id, name: p.name, price_cents: p.priceCents,
      emoji: p.emoji || "", sort_order: p.sortOrder || 99, active: true,
      pos_id: p.posId || null
    }).select().single();
  },
  updateProduct: async function(id, updates) {
    return await supabase.from("products").update(updates).eq("id", id);
  },
  deleteProduct: async function(id) {
    // .select() force PostgREST a renvoyer les lignes reellement supprimees :
    // sans ca, une suppression refusee par RLS renvoie 204 sans erreur.
    var r = await supabase.from("products").delete().eq("id", id).select();
    if (!r.error && (!r.data || r.data.length === 0)) {
      return {data: null, error: {message: "Suppression refusee (droits insuffisants)"}};
    }
    return r;
  },

  // ─────────────────────────────── POINTS DE VENTE
  // ─────────────────────────────── INSCRIPTION ET CARTE
  signupMemberTypes: async function() {
    return await supabase.rpc("signup_member_types");
  },
  completeMyProfile: async function(p) {
    return await supabase.rpc("complete_my_profile", {
      p_member_type_id: p.typeId,
      p_licence_number: p.licence || null,
      p_company_name: p.company || null,
      p_phone: p.phone || null,
      p_birth_date: p.birthDate || null,
      p_photo_path: p.photoPath || null
    });
  },
  myCard: async function() {
    return await supabase.rpc("my_card");
  },
  memberCard: async function(accountId) {
    return await supabase.rpc("member_card", {p_account_id: accountId});
  },
  markCardPrinted: async function(accountId) {
    return await supabase.rpc("mark_card_printed", {p_account_id: accountId});
  },
  // Le bucket est privé : on passe par une URL signée, valable 1 heure.
  photoUrl: async function(path) {
    if (!path) return null;
    var r = await supabase.storage.from("photos-membres")
      .createSignedUrl(path, 3600);
    return (r.data && r.data.signedUrl) || null;
  },
  uploadPhoto: async function(blob, authUserId) {
    // Rangée sous <auth_user_id>/photo.jpg : les règles du bucket
    // n'autorisent chacun que dans son propre dossier.
    var chemin = authUserId + "/photo.jpg";
    var r = await supabase.storage.from("photos-membres")
      .upload(chemin, blob, {contentType: "image/jpeg", upsert: true});
    if (r.error) return {error: r.error};
    return {data: chemin};
  },

  // ─────────────────────────────── STATUTS ET AVANTAGES
  memberTypes: async function() {
    return await supabase.rpc("member_types_list");
  },
  memberTypeSave: async function(x) {
    return await supabase.rpc("member_type_save", {
      p_id: x.id || null, p_name: x.name, p_slug: x.slug || null,
      p_emoji: x.emoji || null, p_color: x.color || null,
      p_free_entry: x.freeEntry, p_entry_discount_pct: x.entryPct,
      p_shop_discount_pct: x.shopPct, p_bar_discount_pct: x.barPct,
      p_meal_discount_pct: x.mealPct, p_notes: x.notes || null,
      p_active: x.active, p_sort: x.sort
    });
  },
  memberTypeDelete: async function(id) {
    return await supabase.rpc("member_type_delete", {p_id: id});
  },
  setAccountMemberType: async function(accountId, typeId) {
    return await supabase.rpc("set_account_member_type", {
      p_account_id: accountId, p_type_id: typeId
    });
  },
  accountBenefits: async function(qr) {
    return await supabase.rpc("account_benefits", {p_qr: qr});
  },
  entryScan: async function(qr, gate) {
    return await supabase.rpc("entry_scan", {p_qr: qr, p_gate: gate || null});
  },

  // ─────────────────────────────── BILLETS DU SUPPORTER
  myTickets: async function() {
    return await supabase.rpc("my_tickets");
  },
  ticketScan: async function(code, gate) {
    return await supabase.rpc("ticket_scan", {p_code: code, p_gate: gate || null});
  },
  ticketToday: async function() {
    return await supabase.rpc("ticket_today");
  },
  ticketGateStats: async function(eventId) {
    return await supabase.rpc("ticket_gate_stats", {p_event: eventId});
  },

  // ─────────────────────────────── BILLETTERIE
  ticketEvents: async function() {
    return await supabase.rpc("ticket_admin_events");
  },
  ticketOrders: async function(eventId) {
    return await supabase.rpc("ticket_orders_of", {p_event: eventId});
  },
  ticketEventSave: async function(e) {
    return await supabase.rpc("ticket_event_save", {
      p_id: e.id || null, p_title: e.title, p_slug: e.slug || null,
      p_competition: e.competition || null, p_starts_at: e.starts_at,
      p_gates_open_at: e.gates_open_at || null, p_venue: e.venue || null,
      p_poster_url: e.poster_url || null, p_info: e.info || null,
      p_capacity: e.capacity, p_sales_open: e.sales_open,
      p_closes_at: e.closes_at || null, p_published: e.published
    });
  },
  ticketEventDelete: async function(id) {
    return await supabase.rpc("ticket_event_delete", {p_id: id});
  },
  ticketTypeSave: async function(ty) {
    return await supabase.rpc("ticket_type_save", {
      p_id: ty.id || null, p_event_id: ty.eventId, p_name: ty.name,
      p_price_cents: ty.priceCents, p_description: ty.description || null,
      p_capacity: ty.capacity, p_max_per_order: ty.maxPerOrder,
      p_active: ty.active, p_sort: ty.sort
    });
  },
  ticketTypeDelete: async function(id) {
    return await supabase.rpc("ticket_type_delete", {p_id: id});
  },
  ticketCopyTypes: async function(from, to) {
    return await supabase.rpc("ticket_copy_types", {p_from: from, p_to: to});
  },
  ticketOrderStatus: async function(id, status) {
    return await supabase.rpc("ticket_order_status", {p_order: id, p_status: status});
  },
  ticketCancelOne: async function(code) {
    return await supabase.rpc("ticket_cancel_one", {p_code: code});
  },

  // ─────────────────────────────── STAGES
  stageBookings: async function(slug) {
    return await supabase.rpc("stage_bookings_list", {p_slug: slug || null});
  },
  stageDayCounts: async function(slug) {
    return await supabase.rpc("stage_day_counts", {p_slug: slug});
  },
  stageAdminList: async function() {
    return await supabase.rpc("stage_admin_list");
  },
  stageSave: async function(s) {
    return await supabase.rpc("stage_save", {
      p_id: s.id || null, p_name: s.name, p_slug: s.slug || null,
      p_starts_on: s.starts_on, p_ends_on: s.ends_on, p_subtitle: s.subtitle || null,
      p_week_price_cents: s.weekPrice, p_week_teams: s.weekTeams || [],
      p_place: s.place || null, p_schedule: s.schedule || null,
      p_info: s.info || null, p_open: s.open, p_closes_at: s.closes_at || null
    });
  },
  stageDelete: async function(id) {
    return await supabase.rpc("stage_delete", {p_id: id});
  },
  stageDaySave: async function(d) {
    return await supabase.rpc("stage_day_save", {
      p_id: d.id || null, p_stage_id: d.stageId, p_day_date: d.date,
      p_label: d.label, p_price_cents: d.priceCents, p_activity: d.activity || null,
      p_teams: d.teams || [], p_capacity: d.capacity, p_sort: d.sort
    });
  },
  stageDayDelete: async function(id) {
    return await supabase.rpc("stage_day_delete", {p_id: id});
  },
  stageDuplicate: async function(from, name, slug, startsOn) {
    return await supabase.rpc("stage_duplicate", {
      p_from: from, p_name: name, p_slug: slug || null, p_starts_on: startsOn
    });
  },
  stageSetStatus: async function(id, status) {
    return await supabase.rpc("stage_set_status", {p_id: id, p_status: status});
  },

  // ─────────────────────────────── REPAS
  mealEvents: async function() {
    return await supabase.rpc("meal_admin_events");
  },
  mealBookings: async function(eventId) {
    return await supabase.rpc("meal_bookings_of", {p_event: eventId});
  },
  mealSheet: async function(eventId) {
    return await supabase.rpc("meal_sheet", {p_event: eventId});
  },
  mealEventSave: async function(e) {
    return await supabase.rpc("meal_event_save", {
      p_id: e.id || null, p_title: e.title, p_subtitle: e.subtitle || null,
      p_served_at: e.served_at, p_location: e.location || null,
      p_deadline: e.deadline || null, p_capacity: e.capacity,
      p_intro: e.intro || null, p_published: e.published
    });
  },
  mealItemSave: async function(i) {
    return await supabase.rpc("meal_item_save", {
      p_id: i.id || null, p_event_id: i.eventId, p_name: i.name,
      p_price_cents: i.priceCents, p_description: i.description || null,
      p_kind: i.kind || "plat", p_counts_seat: i.countsSeat,
      p_active: i.active, p_sort: i.sort
    });
  },
  mealItemDelete: async function(id) {
    return await supabase.rpc("meal_item_delete", {p_id: id});
  },
  mealCopyMenu: async function(from, to) {
    return await supabase.rpc("meal_copy_menu", {p_from: from, p_to: to});
  },
  mealSetStatus: async function(bookingId, status) {
    return await supabase.rpc("meal_set_status", {p_booking: bookingId, p_status: status});
  },

  // ─────────────────────────────── CONSIGNES
  listDeposits: async function(activeOnly) {
    var q = supabase.from("deposits").select("*");
    if (activeOnly) q = q.eq("active", true);
    var r = await q.order("sort_order");
    return {data: r.data || [], error: r.error};
  },
  addDeposit: async function(d) {
    var id = secureId(8).toLowerCase();
    return await supabase.from("deposits").insert({
      id: id, name: d.name, emoji: d.emoji || "🥤",
      price_cents: d.priceCents, sort_order: d.sortOrder || 99, active: true
    }).select().single();
  },
  updateDeposit: async function(id, updates) {
    return await supabase.from("deposits").update(updates).eq("id", id);
  },
  deleteDeposit: async function(id) {
    var r = await supabase.from("deposits").delete().eq("id", id).select();
    if (!r.error && (!r.data || r.data.length === 0)) {
      return {data: null, error: {message: "Suppression refusee (droits insuffisants)"}};
    }
    return r;
  },
  // ─────────────────────────────── SUPPRESSION DE COMPTE
  accountSummary: async function() {
    return await supabase.rpc("my_account_summary");
  },
  deleteMyAccount: async function() {
    // Passe par l'Edge Function : elle seule peut supprimer le compte
    // d'authentification, avec la clé de service.
    var s = await supabase.auth.getSession();
    var token = s && s.data && s.data.session ? s.data.session.access_token : null;
    if (!token) return {error: {message: "Session expirée, reconnecte-toi."}};
    var r = await fetch(SUPABASE_URL + "/functions/v1/delete-account", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: "Bearer " + token
      }
    });
    var d = null;
    try { d = await r.json(); } catch (e) {}
    if (!r.ok || !d || !d.success) {
      return {error: {message: (d && d.error) || "Suppression impossible"}};
    }
    return {data: d};
  },

  refundDeposit: async function(pp) {
    return await supabase.rpc("refund_deposit", {
      p_account_id: pp.accountId, p_deposit_id: pp.depositId, p_qty: pp.qty,
      p_cashier_name: pp.cashierName, p_pos_id: pp.posId || null
    });
  },

  listPos: async function(activeOnly) {
    var q = supabase.from("pos").select("*");
    if (activeOnly) q = q.eq("active", true);
    var r = await q.order("sort_order");
    return {data: r.data || [], error: r.error};
  },
  resolvePos: async function(token) {
    return await supabase.rpc("resolve_pos", {p_token: token});
  },
  addPos: async function(p) {
    var id = secureId(8).toLowerCase();
    var token = "CSLPOS-" + secureId(10).toUpperCase();
    return await supabase.from("pos").insert({
      id: id, name: p.name, emoji: p.emoji || "🏉",
      qr_token: token, sort_order: p.sortOrder || 99, active: true
    }).select().single();
  },
  updatePos: async function(id, updates) {
    return await supabase.from("pos").update(updates).eq("id", id);
  },
  deletePos: async function(id) {
    var r = await supabase.from("pos").delete().eq("id", id).select();
    if (!r.error && (!r.data || r.data.length === 0)) {
      return {data: null, error: {message: "Suppression refusee (droits insuffisants)"}};
    }
    return r;
  },
  listTransactions: async function(limit) {
    limit = limit || 100;
    var r = await supabase.from("transactions").select("*, account:accounts(name)").order("created_at", {ascending: false}).limit(limit);
    return {data: r.data || [], error: r.error};
  },
  listTransactionsFor: async function(accountId, limit) {
    limit = limit || 20;
    var r = await supabase.from("transactions").select("*").eq("account_id", accountId).order("created_at", {ascending: false}).limit(limit);
    return {data: r.data || [], error: r.error};
  },
  redeemCode: async function(code, accountId) {
    return await supabase.rpc("redeem_code", {p_code: code, p_account_id: accountId});
  },
  payCart: async function(p) {
    return await supabase.rpc("pay_cart", {
      p_account_id: p.accountId, p_total_cents: p.totalCents,
      p_label: p.label, p_cashier_name: p.cashierName, p_items: p.items,
      p_pos_id: p.posId || null,
      p_deposit_cents: p.depositCents || 0,
      // Le serveur revérifie ce pourcentage contre le droit réel du
      // compte : on ne peut jamais s'accorder plus que son statut.
      p_discount_pct: p.discountPct || 0,
      p_discount_kind: p.discountKind || "bar"
    });
  },
  rechargeCounter: async function(p) {
    return await supabase.rpc("recharge_counter", {
      p_account_id: p.accountId, p_amount_cents: p.amountCents, p_cashier_name: p.cashierName
    });
  },
  listAuditDay: async function(dateStr) {
    var start = dateStr + "T00:00:00";
    var end = dateStr + "T23:59:59";
    var r = await supabase.from("audit_log")
      .select("*")
      .gte("created_at", start)
      .lte("created_at", end)
      .order("created_at", {ascending: false});
    return {data: r.data || [], error: r.error};
  },
  listTransactionsRange: async function(startDate, endDate) {
    // startDate / endDate au format YYYY-MM-DD inclus
    var start = startDate + "T00:00:00";
    var end = endDate + "T23:59:59";
    var r = await supabase.from("transactions")
      .select("*, account:accounts(name), pos:pos(id,name,emoji)")
      .gte("created_at", start)
      .lte("created_at", end)
      .order("created_at", {ascending: true});
    return {data: r.data || [], error: r.error};
  },
  listAuditRange: async function(startDate, endDate) {
    var start = startDate + "T00:00:00";
    var end = endDate + "T23:59:59";
    var r = await supabase.from("audit_log")
      .select("*")
      .gte("created_at", start)
      .lte("created_at", end)
      .order("created_at", {ascending: true});
    return {data: r.data || [], error: r.error};
  },
  listAudit: async function(limit) {
    limit = limit || 200;
    var r = await supabase.from("audit_log").select("*").order("created_at", {ascending: false}).limit(limit);
    return {data: r.data || [], error: r.error};
  },
  setAccountRole: async function(accountId, newRole) {
    return await supabase.rpc("set_account_role", {p_target_account_id: accountId, p_new_role: newRole});
  },
  setAccountRoles: async function(accountId, roles) {
    return await supabase.rpc("set_account_roles", {p_target_account_id: accountId, p_roles: roles});
  },
  adjustBalance: async function(accountId, amountCents, reason, adminName) {
    return await supabase.rpc("adjust_balance", {
      p_account_id: accountId,
      p_amount_cents: amountCents,
      p_reason: reason,
      p_admin_name: adminName
    });
  },
  submitSupportMessage: async function(firstName, lastName, email, message) {
    // 1. Insert via RPC (stocke en DB)
    var r = await supabase.rpc("submit_support_message", {
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
      p_message: message
    });
    if (r.error || !r.data || !r.data.success) {
      return {data: r.data, error: r.error || {message: (r.data && r.data.error) || "unknown_error"}};
    }
    // 2. Déclencher Edge Function pour envoyer le mail
    try {
      var emailRes = await supabase.functions.invoke("send-support-email", {
        body: {
          message_id: r.data.message_id,
          first_name: firstName,
          last_name: lastName,
          email: email,
          message: message
        }
      });
      // On retourne success même si le mail a échoué — le message est stocké et visible en admin
      return {data: {success: true, message_id: r.data.message_id, email_sent: !emailRes.error}, error: null};
    } catch (e) {
      // Email a échoué mais le message est stocké, donc on considère succès
      return {data: {success: true, message_id: r.data.message_id, email_sent: false}, error: null};
    }
  },
  validateTicket: async function(qrCode, validatorAccountId) {
    return await supabase.rpc("validate_ticket", {p_qr_code: qrCode, p_validator_account_id: validatorAccountId});
  },
  sellEntryWithBalance: async function(buyerId, sellerId, ticketType, priceCents) {
    return await supabase.rpc("sell_entry_with_balance", {
      p_buyer_account_id: buyerId, p_seller_account_id: sellerId,
      p_ticket_type: ticketType, p_price_cents: priceCents
    });
  },
  boutiqueSale: async function(buyerId, sellerId, article, size, color, priceCents) {
    return await supabase.rpc("boutique_sale", {
      p_buyer_account_id: buyerId, p_seller_account_id: sellerId,
      p_article: article, p_size: size, p_color: color, p_price_cents: priceCents
    });
  },
  getTicketsStats: async function() {
    var r = await supabase.from("tickets_today_stats").select("*").limit(1).maybeSingle();
    return {data: r.data, error: r.error};
  },
  subscribeToAccounts: function(callback) {
    return supabase.channel("accounts-changes")
      .on("postgres_changes", {event: "*", schema: "public", table: "accounts"}, callback)
      .subscribe();
  },
  subscribeToTransactions: function(callback) {
    return supabase.channel("tx-changes")
      .on("postgres_changes", {event: "INSERT", schema: "public", table: "transactions"}, callback)
      .subscribe();
  },
  signUp: async function(email, password) {
    return await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password: password,
      options: { emailRedirectTo: undefined }
    });
  },
  signIn: async function(email, password) {
    return await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password: password
    });
  },
  resetPassword: async function(email) {
    return await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
      redirectTo: window.location.origin + window.location.pathname + "?reset=1"
    });
  },
  updatePassword: async function(newPassword) {
    return await supabase.auth.updateUser({ password: newPassword });
  },
  signOut: async function() {
    return await supabase.auth.signOut();
  },
  getSession: async function() {
    return await supabase.auth.getSession();
  }
};

// ═══════════════════════════════════════════════════════════════════
// COMPOSANTS UI DE BASE
// ═══════════════════════════════════════════════════════════════════

function QRCodeView(props) {
  var data = props.data, size = props.size || 200;
  var containerRef = useRef(null);
  useEffect(function() {
    if (!containerRef.current || !window.QRCode) return;
    containerRef.current.innerHTML = "";
    new window.QRCode(containerRef.current, {
      text: data, width: size, height: size,
      colorDark: "#000000", colorLight: "#ffffff",
      correctLevel: window.QRCode.CorrectLevel.H
    });
  }, [data, size]);
  return h("div", {
    ref: containerRef,
    className: "csl-qr-safe",
    style: {
      width: size, height: size, background: "#ffffff",
      borderRadius: 12, padding: 10,
      display: "inline-block", boxSizing: "content-box",
      boxShadow: "0 4px 16px rgba(208,33,40,.08)",
      // Protection contre mode sombre forcé (Dark Reader, force-dark Chrome, etc.)
      colorScheme: "light",
      filter: "none"
    }
  });
}

var IS = {
  width: "100%", padding: "12px 14px", borderRadius: 10,
  border: "1px solid " + C.border, background: "#fff",
  color: C.text, fontFamily: "Barlow Condensed,sans-serif",
  fontSize: 15, outline: "none", marginBottom: 8, display: "block", boxSizing: "border-box"
};
var IS_FOCUS = {borderColor: C.blue, boxShadow: "0 0 0 3px rgba(208,33,40,.1)"};
var AB = {
  padding: "12px 24px", borderRadius: 10, border: "none", color: "#fff",
  fontFamily: "Oswald,sans-serif", fontSize: 14, fontWeight: 700,
  letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer",
  transition: "all .2s"
};
var MODAL_BG = {
  position: "fixed", inset: 0, background: "rgba(26,14,15,.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 16, zIndex: 500, overflowY: "auto"
};
var MODAL_BOX = {
  background: "#fff", borderRadius: 16, padding: 22,
  width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,.3)"
};
var LABEL = {
  fontSize: 11, color: C.muted, letterSpacing: 1,
  textTransform: "uppercase", marginBottom: 4,
  fontFamily: "Oswald", fontWeight: 600
};

function FormField(props) {
  return h("div", {style: {marginBottom: 12}},
    h("div", {style: LABEL}, props.label),
    h("input", Object.assign({}, props.inputProps, {style: Object.assign({}, IS, props.inputProps.style || {}, {marginBottom: 0})}))
  );
}

// ═══════════════════════════════════════════════════════════════════
// LAYOUT AVEC SIDEBAR
// ═══════════════════════════════════════════════════════════════════

function Layout(props) {
  var s1 = useState(false); var menuOpen = s1[0], setMenuOpen = s1[1];
  var role = props.account ? props.account.role : "supporter";
  var view = props.view;
  var setView = props.setView;

  // Un compte peut cumuler plusieurs rôles : le menu montre tout ce à
  // quoi la personne a droit, pas seulement son rôle principal.
  var myRoles = (function() {
    var a = props.account || {};
    var r = (a.roles && a.roles.length) ? a.roles.slice() : [];
    if (a.role && r.indexOf(a.role) === -1) r.push(a.role);
    if (!r.length) r = ["supporter"];
    return r;
  })();
  var TOUS = ["supporter", "encaissement", "recharge", "boutique", "billetterie", "admin"];

  var menuItems = [
    {key: "account", icon: "👤", label: "Mon compte", roles: TOUS},
    {key: "tickets", icon: "🎟️", label: "Mes billets", roles: TOUS},
    {key: "encaissement", icon: "🏉", label: "Encaissement", roles: ["encaissement", "admin"]},
    {key: "recharge", icon: "💰", label: "Recharge", roles: ["recharge", "admin"]},
    {key: "boutique", icon: "🛍", label: "Boutique", roles: ["boutique", "admin"]},
    {key: "scan", icon: "🎫", label: "Scan billets", roles: ["billetterie", "admin"]},
    {key: "admin", icon: "⚙️", label: "Administration", roles: ["admin"]},
    {key: "support", icon: "💬", label: "Besoin d'aide", roles: TOUS}
  ];
  var visibleItems = menuItems.filter(function(m) {
    return m.roles.some(function(r) { return myRoles.indexOf(r) >= 0; });
  });

  var titleByView = {
    account: "Mon compte",
    tickets: "Mes billets",
    encaissement: "Encaissement",
    recharge: "Recharge supporters",
    boutique: "Boutique du club",
    scan: "Contrôle des billets",
    admin: "Administration",
    support: "Besoin d'aide"
  };

  // Sidebar (rendu commun desktop/mobile, juste comportement différent)
  var sidebar = h("aside", {className: "csl-sidebar", style: {
    position: "fixed", top: 0, left: 0, bottom: 0, width: 240,
    background: "linear-gradient(180deg, " + C.deep + " 0%, " + C.blue + " 100%)",
    display: "flex", flexDirection: "column",
    zIndex: 100,
    transform: menuOpen ? "translateX(0)" : null,
    transition: "transform .3s ease"
  }},
    // Logo + nom
    h("div", {style: {padding: "24px 20px", borderBottom: "1px solid rgba(255,255,255,.1)", display: "flex", alignItems: "center", gap: 12}},
      h("img", {src: CSL_LOGO, alt: "CSL", style: {width: 44, height: 44, objectFit: "contain"}}),
      h("div", null,
        h("div", {style: {color: "#fff", fontFamily: "Oswald", fontWeight: 800, fontSize: 16, letterSpacing: 1}}, "CSL CASHLESS"),
        h("div", {style: {color: C.gold, fontSize: 10, fontFamily: "Oswald", letterSpacing: 2, fontWeight: 700}}, "SANG ET OR 2026")
      )
    ),
    // Menu nav
    h("nav", {style: {flex: 1, padding: "20px 12px", overflowY: "auto"}},
      visibleItems.map(function(item) {
        var active = view === item.key;
        return h("button", {
          key: item.key,
          onClick: function() { setView(item.key); setMenuOpen(false); },
          style: {
            display: "flex", alignItems: "center", gap: 14,
            width: "100%", padding: "12px 16px", marginBottom: 4,
            background: active ? "rgba(255,255,255,.15)" : "transparent",
            border: active ? "1px solid rgba(255,255,255,.2)" : "1px solid transparent",
            borderRadius: 10, cursor: "pointer", color: "#fff",
            fontFamily: "Oswald", fontWeight: active ? 700 : 500, fontSize: 14,
            letterSpacing: 1, textTransform: "uppercase",
            transition: "all .2s",
            textAlign: "left"
          }
        },
          h("span", {style: {fontSize: 18}}, item.icon),
          h("span", null, item.label),
          active && h("span", {style: {marginLeft: "auto", color: C.gold, fontSize: 18}}, "›")
        );
      })
    ),
    // Profil + déconnexion
    props.account && h("div", {style: {padding: 16, borderTop: "1px solid rgba(255,255,255,.1)"}},
      h("div", {style: {display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "rgba(255,255,255,.05)", borderRadius: 10, marginBottom: 8}},
        h("div", {style: {width: 32, height: 32, borderRadius: "50%", background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", color: C.deep, fontFamily: "Oswald", fontWeight: 800, fontSize: 13}},
          (props.account.name || "?").charAt(0).toUpperCase()
        ),
        h("div", {style: {flex: 1, minWidth: 0}},
          h("div", {style: {color: "#fff", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}, (props.account.name || "").split(" ")[0]),
          h("div", {style: {color: C.gold, fontSize: 9, fontFamily: "Oswald", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700}}, role)
        )
      ),
      h("button", {
        onClick: props.onLogout,
        style: {
          width: "100%", padding: "8px 12px",
          background: "rgba(239,68,68,.15)", border: "1px solid rgba(239,68,68,.3)",
          borderRadius: 8, color: "#fff",
          fontFamily: "Oswald", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase",
          cursor: "pointer", fontWeight: 700
        }
      }, "Déconnexion")
    ),
    h("a", {href: SHOPIFY_HOME_URL, style: {display: "block", padding: "10px 16px 16px", textAlign: "center", color: "rgba(255,255,255,.4)", fontSize: 10, fontFamily: "Oswald", letterSpacing: 2, textDecoration: "none"}}, "← Site CSL")
  );

  // Overlay pour fermer menu sur mobile
  var overlay = menuOpen && h("div", {
    onClick: function() { setMenuOpen(false); },
    style: {position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 99}
  });

  return h("div", {style: {minHeight: "100vh", background: C.bg}},
    h("style", null,
      "@media (max-width: 860px) {" +
        ".csl-sidebar { transform: translateX(-100%); }" +
        ".csl-main { margin-left: 0 !important; }" +
        ".csl-mobile-header { display: flex !important; }" +
      "}" +
      "@media (min-width: 861px) {" +
        ".csl-sidebar { transform: translateX(0) !important; }" +
      "}"
    ),
    sidebar,
    overlay,
    h("div", {className: "csl-main", style: {marginLeft: 240, minHeight: "100vh"}},
      // Mobile header
      h("div", {className: "csl-mobile-header", style: {
        display: "none", alignItems: "center", justifyContent: "space-between",
        padding: "14px 16px", background: "#fff", borderBottom: "1px solid " + C.border,
        position: "sticky", top: 0, zIndex: 50
      }},
        h("button", {
          onClick: function() { setMenuOpen(true); },
          style: {background: "none", border: "none", fontSize: 24, cursor: "pointer", color: C.deep, padding: 0}
        }, "☰"),
        h("img", {src: CSL_LOGO, alt: "CSL", style: {width: 32, height: 32, objectFit: "contain"}}),
        h("div", {style: {width: 24}})
      ),
      // Desktop header
      h("div", {style: {padding: "24px 32px", background: "#fff", borderBottom: "1px solid " + C.border}},
        h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Oswald", fontWeight: 600, marginBottom: 4}}, "CSL CASHLESS"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800, color: C.deep, textTransform: "uppercase", letterSpacing: 1}}, titleByView[view] || view)
      ),
      // Content
      h("div", {style: {padding: 24, maxWidth: 1200, margin: "0 auto"}}, props.children)
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// AUTH (login/register/forgot) — sans sidebar
// ═══════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════
// LA CARTE DE MEMBRE
// Format carte bancaire : 86 x 55 mm. À 300 points par pouce, cela
// fait 1016 x 650 pixels — la taille qu'attend un imprimeur.
// On ajoute 3 mm de fond perdu sur chaque bord, soit 1087 x 721.
// ══════════════════════════════════════════════════════════════════
var CARTE = {
  larg: 1016, haut: 650,      // 86 x 55 mm à 300 ppp
  fond: 35                    // 3 mm de fond perdu
};

function chargerImage(src) {
  return new Promise(function(ok, ko) {
    var i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = function() { ok(i); };
    i.onerror = ko;
    i.src = src;
  });
}

// Dessine la carte sur un canvas et renvoie son URL de données.
async function dessinerCarte(d, opts) {
  opts = opts || {};
  var fp = opts.fondPerdu ? CARTE.fond : 0;
  var W = CARTE.larg + fp * 2, H = CARTE.haut + fp * 2;

  var cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  var g = cv.getContext("2d");

  var NOIR = "#1F2430", BLANC = "#FFFFFF";
  var coul = d.color || "#F4E834";

  // Fond
  g.fillStyle = BLANC; g.fillRect(0, 0, W, H);

  // Bandeau supérieur
  var hb = Math.round(H * 0.255);
  g.fillStyle = NOIR; g.fillRect(0, 0, W, hb);

  // Le filet rouge et jaune sous le bandeau
  g.fillStyle = "#D02128"; g.fillRect(0, hb, W / 2, 10);
  g.fillStyle = "#F4E834"; g.fillRect(W / 2, hb, W / 2, 10);

  // Bandeau inférieur : la devise du club
  var hbas = Math.round(H * 0.135);
  g.fillStyle = NOIR; g.fillRect(0, H - hbas, W, hbas);
  g.fillStyle = BLANC;
  g.font = "700 30px Oswald, Arial";
  g.textAlign = "center";
  ["RESPECT", "HONNEUR", "PASSION"].forEach(function(m, i) {
    g.fillText(m, W * (0.2 + i * 0.3), H - hbas / 2 + 11);
  });
  g.fillStyle = "#29A9E0";
  [0.35, 0.65].forEach(function(x) { g.fillRect(W * x - 3, H - hbas + 16, 6, hbas - 32); });

  // Le logo
  try {
    var logo = await chargerImage(CSL_LOGO);
    var tl = hb - 22;
    g.drawImage(logo, fp + 24, 11, tl, tl);
  } catch (e) {}

  // Le titre
  g.textAlign = "left";
  g.fillStyle = BLANC;
  g.font = "800 58px Oswald, Arial";
  g.fillText("CARTE CSL", fp + 190, fp + 76);
  g.fillStyle = "#29A9E0";
  g.font = "700 27px Oswald, Arial";
  g.fillText("CERCLE SPORTIF LÉDONIEN", fp + 192, fp + 112);

  // ── Le rectangle du statut, en haut à droite ──────────────────
  // C'est l'encart jaune de la maquette : il porte le rôle au club.
  var lb = 300, hbd = 54, xb = W - fp - 24 - lb, yb = fp + 32;
  g.fillStyle = coul;
  if (g.roundRect) { g.beginPath(); g.roundRect(xb, yb, lb, hbd, 9); g.fill(); }
  else g.fillRect(xb, yb, lb, hbd);

  // Texte sombre sur couleur claire, clair sur couleur sombre
  var rgb = (coul.match(/^#(..)(..)(..)$/) || []).slice(1)
            .map(function(x) { return parseInt(x, 16); });
  var lum = rgb.length === 3 ? (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 : 255;
  g.fillStyle = lum > 150 ? "#1F2430" : "#FFFFFF";
  g.font = "800 30px Oswald, Arial";
  g.textAlign = "center";
  g.fillText(String(d.type || "Supporter").toUpperCase(), xb + lb / 2, yb + 38);

  // ── La photo ──────────────────────────────────────────────────
  var px = fp + 26, py = hb + 36, pw = 232, ph = 310;
  g.fillStyle = "#F2F0ED"; g.fillRect(px, py, pw, ph);
  if (opts.photoUrl) {
    try {
      var ph2 = await chargerImage(opts.photoUrl);
      // Recadrage centré : une photo de téléphone n'a pas le bon rapport
      var r = Math.max(pw / ph2.width, ph / ph2.height);
      var lw = ph2.width * r, lh = ph2.height * r;
      g.save();
      g.beginPath(); g.rect(px, py, pw, ph); g.clip();
      g.drawImage(ph2, px + (pw - lw) / 2, py + (ph - lh) / 2, lw, lh);
      g.restore();
    } catch (e) {}
  } else {
    g.fillStyle = "#C9C3BE";
    g.font = "400 78px Arial";
    g.textAlign = "center";
    g.fillText("🙂", px + pw / 2, py + ph / 2 + 26);
  }
  g.strokeStyle = "#9A9490"; g.lineWidth = 2; g.strokeRect(px, py, pw, ph);

  // ── L'identité ────────────────────────────────────────────────
  var ix = px + pw + 44, iy = py + 14;
  var parts = String(d.name || "").trim().split(/\s+/);
  var prenom = parts.shift() || "";
  var nom = parts.join(" ");

  var ligne = function(lab, val, y) {
    g.textAlign = "left";
    g.fillStyle = "#6B6460";
    g.font = "700 23px Oswald, Arial";
    g.fillText(lab, ix, y);
    g.fillStyle = "#1F2430";
    g.font = "700 35px Oswald, Arial";
    g.fillText(val || "—", ix, y + 42);
  };
  ligne("PRÉNOM", prenom, iy + 18);
  ligne("NOM", nom, iy + 108);

  if (d.birth_date) {
    var dt = new Date(d.birth_date);
    ligne("NÉ(E) LE", dt.toLocaleDateString("fr-FR"), iy + 198);
  } else if (d.company_name) {
    ligne("SOCIÉTÉ", d.company_name, iy + 198);
  }

  if (d.licence_number) {
    ligne("N° DE LICENCE", d.licence_number, iy + 198 + (d.birth_date || d.company_name ? 90 : 0));
  }

  // ── Le QR, le même que le cashless ────────────────────────────
  var qs = 200, qx = W - fp - 26 - qs, qy = py + 34;
  g.fillStyle = "#6B6460";
  g.font = "700 22px Oswald, Arial";
  g.textAlign = "center";
  g.fillText("QR CODE", qx + qs / 2, qy - 14);

  if (opts.qrCanvas) {
    g.fillStyle = BLANC; g.fillRect(qx - 8, qy - 8, qs + 16, qs + 16);
    g.drawImage(opts.qrCanvas, qx, qy, qs, qs);
  }
  g.strokeStyle = "#9A9490"; g.lineWidth = 2;
  g.strokeRect(qx - 8, qy - 8, qs + 16, qs + 16);

  // Le code en clair sous le QR : lisible au téléphone si le QR
  // ne se laisse pas scanner.
  g.fillStyle = "#6B6460";
  g.font = "400 19px monospace";
  g.fillText(String(d.qr || ""), qx + qs / 2, qy + qs + 32);

  // Repères de coupe quand il y a du fond perdu
  if (fp) {
    g.strokeStyle = "rgba(0,0,0,.35)"; g.lineWidth = 1;
    [[fp, 0, fp, 18], [fp, H, fp, H - 18], [W - fp, 0, W - fp, 18], [W - fp, H, W - fp, H - 18],
     [0, fp, 18, fp], [W, fp, W - 18, fp], [0, H - fp, 18, H - fp], [W, H - fp, W - 18, H - fp]]
    .forEach(function(l) {
      g.beginPath(); g.moveTo(l[0], l[1]); g.lineTo(l[2], l[3]); g.stroke();
    });
  }

  return cv.toDataURL("image/png");
}

// Un QR fabriqué hors écran, pour être dessiné dans la carte.
function qrHorsEcran(texte, taille) {
  return new Promise(function(ok) {
    if (!window.QRCode) { ok(null); return; }
    var box = document.createElement("div");
    box.style.cssText = "position:fixed;left:-9999px;top:0";
    document.body.appendChild(box);
    new window.QRCode(box, {
      text: texte, width: taille, height: taille,
      colorDark: "#000000", colorLight: "#ffffff",
      correctLevel: window.QRCode.CorrectLevel.H
    });
    // La bibliothèque dessine de façon asynchrone sur certains navigateurs
    setTimeout(function() {
      var cv = box.querySelector("canvas");
      if (!cv) {
        var img = box.querySelector("img");
        if (img && img.complete) {
          cv = document.createElement("canvas");
          cv.width = taille; cv.height = taille;
          cv.getContext("2d").drawImage(img, 0, 0, taille, taille);
        }
      }
      // On détache le canvas avant de retirer la boîte
      var copie = null;
      if (cv) {
        copie = document.createElement("canvas");
        copie.width = cv.width; copie.height = cv.height;
        copie.getContext("2d").drawImage(cv, 0, 0);
      }
      document.body.removeChild(box);
      ok(copie);
    }, 160);
  });
}

function AuthScreen(props) {
  var step = props.step, setStep = props.setStep;
  var emailInput = props.emailInput, setEmailInput = props.setEmailInput;
  var passwordInput = props.passwordInput, setPasswordInput = props.setPasswordInput;
  var nameInput = props.nameInput, setNameInput = props.setNameInput;
  var phoneInput = props.phoneInput, setPhoneInput = props.setPhoneInput;
  var error = props.error, info = props.info;
  var loading = props.loading;
  var s = useState(false); var showPwd = s[0], setShowPwd = s[1];

  var card = function(content) {
    return h("div", {style: {minHeight: "100vh", background: "linear-gradient(135deg, " + C.deep + " 0%, " + C.blue + " 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20}},
      h("div", {style: {maxWidth: 420, width: "100%"}},
        h("div", {style: {textAlign: "center", marginBottom: 24}},
          h("img", {src: CSL_LOGO, alt: "CSL", style: {width: 80, height: 80, objectFit: "contain", filter: "drop-shadow(0 8px 20px rgba(0,0,0,.3))"}}),
          h("div", {style: {color: "#fff", fontFamily: "Oswald", fontWeight: 800, fontSize: 22, letterSpacing: 2, marginTop: 12, textTransform: "uppercase"}}, "CSL Cashless"),
          h("div", {style: {color: C.gold, fontSize: 11, fontFamily: "Oswald", letterSpacing: 3, fontWeight: 700, marginTop: 4}}, "🏉 SAISON 2026 🏉")
        ),
        h("div", {style: {background: "#fff", borderRadius: 18, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,.2)"}},
          content
        )
      )
    );
  };

  if (step === "login") return card(
    h("div", null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 700, color: C.deep, marginBottom: 18}}, "Connexion"),
      h(FormField, {
        label: "Email",
        inputProps: {
          value: emailInput, onChange: function(e) { setEmailInput(e.target.value); },
          placeholder: "exemple@email.com", type: "email", autoComplete: "email"
        }
      }),
      h("div", {style: LABEL}, "Mot de passe"),
      h("div", {style: {position: "relative", marginBottom: 12}},
        h("input", {
          value: passwordInput, onChange: function(e) { setPasswordInput(e.target.value); },
          placeholder: "Ton mot de passe", type: showPwd ? "text" : "password",
          autoComplete: "current-password",
          onKeyDown: function(e) { if (e.key === "Enter") props.onLogin(); },
          style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 50})
        }),
        h("button", {onClick: function() { setShowPwd(!showPwd); }, type: "button", style: {position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "4px 8px"}}, showPwd ? "🙈" : "👁")
      ),
      h("button", {onClick: props.onLogin, disabled: loading, style: Object.assign({}, AB, {background: loading ? C.muted : C.blue, width: "100%", marginTop: 6, padding: "14px 24px", fontSize: 15, cursor: loading ? "wait" : "pointer"})},
        loading ? "Connexion..." : "Se connecter"
      ),
      error && h("div", {style: {color: C.red, fontSize: 13, marginTop: 10, textAlign: "center"}}, error),
      info && h("div", {style: {color: C.green, fontSize: 13, marginTop: 10, textAlign: "center"}}, info),
      h("div", {style: {textAlign: "center", marginTop: 14}},
        h("button", {onClick: function() { setStep("forgot"); }, style: {background: "none", border: "none", color: C.blue, fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit"}}, "Mot de passe oublié ?")
      ),
      h("div", {style: {textAlign: "center", margin: "20px 0", color: C.muted, fontSize: 12}}, "— ou —"),
      h("button", {onClick: function() { setStep("register"); setPasswordInput(""); }, style: Object.assign({}, AB, {background: "transparent", border: "2px solid " + C.blue, color: C.blue, width: "100%", padding: "12px 24px", fontSize: 13})}, "Créer mon compte")
    )
  );

  if (step === "register") return card(
    h("div", null,
      h("button", {onClick: function() { setStep("login"); }, style: {background: "none", border: "none", color: C.blue, fontSize: 13, cursor: "pointer", marginBottom: 12, padding: 0, fontFamily: "inherit"}}, "← Retour"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 700, color: C.deep, marginBottom: 6}}, "Créer mon compte"),
      h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16}}, "30 secondes, et tu pourras aller à la buvette."),
      h(FormField, {label: "Prénom et nom", inputProps: {value: nameInput, onChange: function(e) { setNameInput(e.target.value); }, placeholder: "Ex: Jean Dupont", autoFocus: true, autoComplete: "name"}}),
      h(FormField, {label: "Email", inputProps: {value: emailInput, onChange: function(e) { setEmailInput(e.target.value); }, placeholder: "exemple@email.com", type: "email", autoComplete: "email"}}),
      h("div", {style: LABEL}, "Mot de passe"),
      h("div", {style: {position: "relative", marginBottom: 12}},
        h("input", {value: passwordInput, onChange: function(e) { setPasswordInput(e.target.value); }, placeholder: "Choisis un mot de passe", type: showPwd ? "text" : "password", autoComplete: "new-password", style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 50})}),
        h("button", {onClick: function() { setShowPwd(!showPwd); }, type: "button", style: {position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "4px 8px"}}, showPwd ? "🙈" : "👁")
      ),
      h(FormField, {label: "Téléphone", inputProps: {value: phoneInput, onChange: function(e) { setPhoneInput(e.target.value); }, placeholder: "06 12 34 56 78", type: "tel", autoComplete: "tel"}}),

      // ── Le rôle au club : il décide des avantages et de la carte
      h("div", {style: LABEL}, "Votre rôle au club"),
      h("div", {style: {display: "grid", gap: 6, gridTemplateColumns: "1fr 1fr", marginBottom: 12}},
        (props.memberTypes || []).map(function(ty) {
          var on = props.typeId === ty.id;
          return h("button", {key: ty.id, type: "button",
            onClick: function() { props.setTypeId(ty.id); },
            style: {textAlign: "left", border: "1px solid " + (on ? ty.color : C.border),
                    background: on ? ty.color : "#fff", color: on ? "#fff" : C.deep,
                    borderRadius: 10, padding: "11px 12px", cursor: "pointer",
                    fontSize: 13, fontWeight: 600, lineHeight: 1.25}},
            h("span", {style: {marginRight: 5}}, ty.emoji), ty.name);
        })
      ),

      // ── Ce que le rôle choisi exige en plus
      props.selectedType && props.selectedType.requires_licence && h(FormField, {
        label: "Numéro de licence",
        inputProps: {value: props.licence, onChange: function(e) { props.setLicence(e.target.value); },
                     placeholder: "Sur votre licence FFR", autoComplete: "off"}
      }),
      props.selectedType && props.selectedType.requires_company && h(FormField, {
        label: "Nom de la société",
        inputProps: {value: props.company, onChange: function(e) { props.setCompany(e.target.value); },
                     placeholder: "Raison sociale", autoComplete: "organization"}
      }),

      // ── La photo de la carte
      h("div", {style: LABEL}, "Votre photo"),
      h("div", {style: {display: "flex", gap: 12, alignItems: "center", marginBottom: 6}},
        h("div", {style: {width: 72, height: 90, borderRadius: 10, overflow: "hidden",
                          background: C.pale, border: "1px solid " + C.border,
                          display: "grid", placeItems: "center", flexShrink: 0}},
          props.photoPreview
            ? h("img", {src: props.photoPreview, alt: "", style: {width: "100%", height: "100%", objectFit: "cover"}})
            : h("span", {style: {fontSize: 26, opacity: .4}}, "🙂")
        ),
        h("div", {style: {flex: 1, minWidth: 0}},
          h("label", {style: Object.assign({}, AB, {background: C.pale, color: C.deep, display: "block", textAlign: "center", padding: "12px 14px", fontSize: 12, cursor: "pointer"})},
            props.photoPreview ? "Changer la photo" : "📷 Prendre une photo",
            h("input", {type: "file", accept: "image/*", capture: "user",
                        onChange: function(e) { props.onPhoto(e.target.files && e.target.files[0]); },
                        style: {display: "none"}})
          ),
          h("div", {style: {fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4}},
            "Elle figurera sur votre carte de membre. Visage de face, comme une photo d'identité.")
        )
      ),
      h("div", {style: {fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.45, background: C.pale, padding: "10px 12px", borderRadius: 8}},
        "Pour un enfant, la photo est téléversée par le parent, qui autorise ainsi le club à l'utiliser sur sa carte de membre. Elle n'est visible que par l'administration du club."),

      h("button", {onClick: props.onRegister, disabled: loading, style: Object.assign({}, AB, {background: loading ? C.muted : C.green, width: "100%", marginTop: 6, padding: "14px 24px", fontSize: 15, cursor: loading ? "wait" : "pointer"})},
        loading ? "Création..." : "Créer mon compte"
      ),
      error && h("div", {style: {color: C.red, fontSize: 13, marginTop: 10, textAlign: "center"}}, error)
    )
  );

  if (step === "forgot") return card(
    h("div", null,
      h("button", {onClick: function() { setStep("login"); }, style: {background: "none", border: "none", color: C.blue, fontSize: 13, cursor: "pointer", marginBottom: 12, padding: 0, fontFamily: "inherit"}}, "← Retour"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 700, color: C.deep, marginBottom: 6}}, "Mot de passe oublié"),
      h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16}}, "Entre ton email, on t'envoie un lien pour réinitialiser."),
      h(FormField, {label: "Ton email", inputProps: {value: emailInput, onChange: function(e) { setEmailInput(e.target.value); }, placeholder: "exemple@email.com", type: "email", autoFocus: true}}),
      h("button", {onClick: props.onSendReset, disabled: loading, style: Object.assign({}, AB, {background: loading ? C.muted : C.blue, width: "100%", marginTop: 6, padding: "14px 24px", fontSize: 15})},
        loading ? "Envoi..." : "Envoyer le lien"
      ),
      error && h("div", {style: {color: C.red, fontSize: 13, marginTop: 10, textAlign: "center"}}, error),
      info && h("div", {style: {color: C.green, fontSize: 13, marginTop: 10, textAlign: "center"}}, info)
    )
  );

  if (step === "reset-password") return card(
    h("div", null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 700, color: C.deep, marginBottom: 6}}, "Nouveau mot de passe"),
      h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16}}, "Choisis un nouveau mot de passe."),
      h("div", {style: LABEL}, "Nouveau mot de passe"),
      h("div", {style: {position: "relative", marginBottom: 12}},
        h("input", {value: passwordInput, onChange: function(e) { setPasswordInput(e.target.value); }, placeholder: "Ton nouveau mot de passe", type: showPwd ? "text" : "password", autoFocus: true, style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 50})}),
        h("button", {onClick: function() { setShowPwd(!showPwd); }, type: "button", style: {position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "4px 8px"}}, showPwd ? "🙈" : "👁")
      ),
      h("button", {onClick: props.onResetPassword, disabled: loading, style: Object.assign({}, AB, {background: loading ? C.muted : C.green, width: "100%", marginTop: 6, padding: "14px 24px", fontSize: 15})},
        loading ? "..." : "Enregistrer"
      ),
      error && h("div", {style: {color: C.red, fontSize: 13, marginTop: 10, textAlign: "center"}}, error)
    )
  );

  if (step === "reset-expired") return card(
    h("div", {style: {textAlign: "center"}},
      h("div", {style: {fontSize: 48, marginBottom: 12}}, "⏰"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 700, color: C.deep, marginBottom: 10}}, "Lien expiré"),
      h("div", {style: {fontSize: 14, color: C.muted, marginBottom: 8, lineHeight: 1.6}}, "Ce lien de réinitialisation a expiré ou a déjà été utilisé."),
      h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6}}, "Les liens sont valables 1 heure. Demande un nouveau lien pour réinitialiser ton mot de passe."),
      h("button", {
        onClick: function() {
          // Nettoyer l'URL (enlever le hash d'erreur + le param reset) puis aller sur "Mot de passe oublié"
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, "", window.location.pathname);
          }
          setStep("forgot");
        },
        style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: "14px 24px", fontSize: 15})
      }, "Demander un nouveau lien"),
      h("button", {
        onClick: function() {
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, "", window.location.pathname);
          }
          setStep("login");
        },
        style: {background: "none", border: "none", color: C.blue, fontSize: 13, cursor: "pointer", marginTop: 14, fontFamily: "inherit", textDecoration: "underline"}
      }, "← Retour à la connexion")
    )
  );

  return null;
}

// ═══════════════════════════════════════════════════════════════════
// CARD (composant générique de card)
// ═══════════════════════════════════════════════════════════════════
function Card(props) {
  return h("div", {style: Object.assign({
    background: C.card, borderRadius: 16, padding: 24,
    boxShadow: "0 2px 8px rgba(26,14,15,.04)", border: "1px solid " + C.border
  }, props.style || {})}, props.children);
}

// ═══════════════════════════════════════════════════════════════════
// VUE : MON COMPTE
// ═══════════════════════════════════════════════════════════════════
function AccountView(props) {
  var account = props.account;
  var s1 = useState([]); var myTx = s1[0], setMyTx = s1[1];
  var s2 = useState(""); var rechargeCode = s2[0], setRechargeCode = s2[1];
  var s3 = useState(""); var rechargeMsg = s3[0], setRechargeMsg = s3[1];
  var s4 = useState(false); var redeeming = s4[0], setRedeeming = s4[1];
  var s5 = useState(1); var txPage = s5[0], setTxPage = s5[1];
  var s6 = useState(false); var qrExpanded = s6[0], setQrExpanded = s6[1];
  var s7 = useState(false); var codeFormOpen = s7[0], setCodeFormOpen = s7[1];
  var s8 = useState(0); var displayedBalance = s8[0], setDisplayedBalance = s8[1];
  var sd1 = useState(null); var delStep = sd1[0], setDelStep = sd1[1];
  var sd2 = useState(null); var delSummary = sd2[0], setDelSummary = sd2[1];
  var sd3 = useState(""); var delConfirm = sd3[0], setDelConfirm = sd3[1];
  var sd4 = useState(false); var deleting = sd4[0], setDeleting = sd4[1];
  var sd5 = useState(""); var delError = sd5[0], setDelError = sd5[1];
  // Les comptes créés avant la carte n'ont ni statut ni photo : on le
  // leur signale sur leur page d'accueil plutôt que d'attendre.
  var sp1 = useState(null); var myProfile = sp1[0], setMyProfile = sp1[1];
  var sp2 = useState(false); var profOpen = sp2[0], setProfOpen = sp2[1];
  var sp3 = useState([]); var profTypes = sp3[0], setProfTypes = sp3[1];
  var sp4 = useState(null); var profType = sp4[0], setProfType = sp4[1];
  var sp5 = useState(""); var profLicence = sp5[0], setProfLicence = sp5[1];
  var sp6 = useState(""); var profCompany = sp6[0], setProfCompany = sp6[1];
  var sp7 = useState(null); var profPhoto = sp7[0], setProfPhoto = sp7[1];
  var sp8 = useState(""); var profPreview = sp8[0], setProfPreview = sp8[1];
  var sp9 = useState(""); var profErr = sp9[0], setProfErr = sp9[1];
  var sp10 = useState(false); var profBusy = sp10[0], setProfBusy = sp10[1];
  var TX_PER_PAGE = 5;

  useEffect(function() {
    (async function() {
      var tx = await db.listTransactionsFor(account.id, 100);
      setMyTx(tx.data);
    })();
    var channel = db.subscribeToAccounts(async function(payload) {
      if (payload.new && payload.new.id === account.id) {
        var tx = await db.listTransactionsFor(account.id, 100);
        setMyTx(tx.data);
        if (props.refreshAccount) props.refreshAccount();
      }
    });
    return function() { supabase.removeChannel(channel); };
  }, []);

  useEffect(function() {
    db.myCard().then(function(r) { setMyProfile(r.data || null); }).catch(function() {});
  }, []);

  var ouvrirProfil = async function() {
    setProfErr(""); setProfOpen(true);
    if (!profTypes.length) {
      var r = await db.signupMemberTypes();
      setProfTypes(r.data || []);
    }
    if (myProfile) {
      setProfType(myProfile.member_type_id || null);
      setProfLicence(myProfile.licence_number || "");
      setProfCompany(myProfile.company_name || "");
    }
  };

  var onProfPhoto = function(file) {
    if (!file) return;
    var img = new Image();
    var url = URL.createObjectURL(file);
    img.onload = function() {
      var ratio = Math.min(1, 600 / Math.max(img.width, img.height));
      var cv = document.createElement("canvas");
      cv.width = Math.round(img.width * ratio);
      cv.height = Math.round(img.height * ratio);
      cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
      cv.toBlob(function(blob) {
        URL.revokeObjectURL(url);
        setProfPhoto(blob);
        setProfPreview(cv.toDataURL("image/jpeg", 0.85));
      }, "image/jpeg", 0.85);
    };
    img.onerror = function() { URL.revokeObjectURL(url); setProfErr("Photo illisible"); };
    img.src = url;
  };

  var enregistrerProfil = async function() {
    if (!profType) { setProfErr("Choisis ton rôle au club."); return; }
    var ty = profTypes.filter(function(x) { return x.id === profType; })[0];
    if (ty && ty.requires_licence && !profLicence.trim()) { setProfErr("Le numéro de licence est requis."); return; }
    if (ty && ty.requires_company && !profCompany.trim()) { setProfErr("Le nom de la société est requis."); return; }

    setProfBusy(true); setProfErr("");
    var chemin = null;
    if (profPhoto) {
      var s = await supabase.auth.getUser();
      var uid = s && s.data && s.data.user ? s.data.user.id : null;
      if (uid) {
        var up = await db.uploadPhoto(profPhoto, uid);
        if (!up.error) chemin = up.data;
      }
    }
    var r = await db.completeMyProfile({
      typeId: profType, licence: profLicence.trim(), company: profCompany.trim(),
      photoPath: chemin
    });
    setProfBusy(false);
    if (r.data && r.data.success === false) {
      setProfErr(r.data.error === "licence_required" ? "Le numéro de licence est requis."
               : r.data.error === "company_required" ? "Le nom de la société est requis."
               : r.data.error);
      return;
    }
    var maj = await db.myCard();
    setMyProfile(maj.data || null);
    setProfOpen(false);
    if (props.refreshAccount) props.refreshAccount();
  };

  // ─── Suppression de compte (exigence Apple 5.1.1(v)) ───
  var openDelete = async function() {
    setDelError(""); setDelConfirm("");
    var r = await db.accountSummary();
    setDelSummary((r && r.data) || {balance_cents: account.balance_cents || 0});
    setDelStep("confirm");
  };

  var doDelete = async function() {
    if (deleting) return;
    if (delConfirm.trim().toUpperCase() !== "SUPPRIMER") {
      setDelError("Saisis SUPPRIMER pour confirmer.");
      return;
    }
    setDeleting(true); setDelError("");
    var r = await db.deleteMyAccount();
    if (r.error) { setDeleting(false); setDelError(r.error.message); return; }
    setDelStep("done");
    // On laisse le message s'afficher avant de fermer la session.
    setTimeout(function() {
      supabase.auth.signOut().finally(function() { window.location.reload(); });
    }, 3500);
  };

  // Animation compteur solde : 0 -> account.balance_cents en 800ms
  useEffect(function() {
    var target = account.balance_cents || 0;
    var start = displayedBalance;
    var duration = 800;
    var startTime = null;
    var frameId;
    function tick(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      // Easing "easeOutCubic"
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(start + (target - start) * eased);
      setDisplayedBalance(current);
      if (progress < 1) frameId = requestAnimationFrame(tick);
    }
    frameId = requestAnimationFrame(tick);
    return function() { if (frameId) cancelAnimationFrame(frameId); };
  }, [account.balance_cents]);

  var useCode = async function() {
    if (redeeming) return;
    var code = rechargeCode.trim().toUpperCase();
    if (!code) { setRechargeMsg("❌ Entre un code"); return; }
    setRedeeming(true); setRechargeMsg("");
    var r = await db.redeemCode(code, account.id);
    if (r.error) setRechargeMsg("❌ " + r.error.message);
    else if (!r.data.success) {
      var msgs = {code_invalid: "❌ Code invalide", code_already_used: "❌ Code déjà utilisé", account_not_found: "❌ Compte introuvable"};
      setRechargeMsg(msgs[r.data.error] || "❌ " + r.data.error);
    } else {
      setRechargeMsg("✅ +" + formatEur(r.data.amount_cents) + " ajoutés !");
      setRechargeCode("");
    }
    setRedeeming(false);
  };

  // Détermine catégorie + icône + couleur d'une transaction (pour timeline)
  var txCategory = function(t) {
    var label = (t.label || "").toLowerCase();
    var cashier = (t.cashier_name || "");
    if (t.type === "adjustment") return {icon: "⚖️", bg: "rgba(255,182,39,.12)", accent: C.gold, label: "Ajustement"};
    if (t.amount_cents > 0) {
      // Recharge
      if (cashier.indexOf("AUTO") === 0 || cashier.indexOf("Shopify") >= 0) return {icon: "💳", bg: "rgba(34,197,94,.1)", accent: C.green, label: "Recharge en ligne"};
      if (t.type === "recharge_counter") return {icon: "💰", bg: "rgba(34,197,94,.1)", accent: C.green, label: "Recharge guichet"};
      return {icon: "🎁", bg: "rgba(34,197,94,.1)", accent: C.green, label: "Recharge"};
    }
    // Vente
    if (label.indexOf("boutique") >= 0 || label.indexOf("article") >= 0 || label.indexOf("maillot") >= 0) return {icon: "🛍", bg: "rgba(208,33,40,.08)", accent: C.blue, label: "Boutique"};
    if (label.indexOf("billet") >= 0 || label.indexOf("tarif") >= 0 || label.indexOf("entrée") >= 0) return {icon: "🎫", bg: "rgba(208,33,40,.08)", accent: C.blue, label: "Billetterie"};
    if (label.indexOf("jeton") >= 0) return {icon: "🎰", bg: "rgba(255,182,39,.12)", accent: C.gold, label: "Retrait jetons"};
    return {icon: "🍺", bg: "rgba(208,33,40,.08)", accent: C.blue, label: "Buvette"};
  };

  return h("div", null,
    // ─── Styles globaux animations ───
    h("style", null,
      "@keyframes cslCardIn { from { transform: translateY(120px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }" +
      "@keyframes cslFadeIn { from { opacity: 0; } to { opacity: 1; } }" +
      "@keyframes cslScaleIn { from { transform: scale(.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }" +
      "@keyframes cslSlideRight { from { transform: translateX(-16px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }" +
      ".csl-card-anim { animation: cslCardIn .55s cubic-bezier(.22,.61,.36,1) both; }" +
      ".csl-fade-anim { animation: cslFadeIn .5s ease-out both; }" +
      ".csl-scale-anim { animation: cslScaleIn .35s cubic-bezier(.22,.61,.36,1) both; }" +
      ".csl-tx-anim { animation: cslSlideRight .35s ease-out both; }" +
      ".csl-quick-action { transition: transform .18s, box-shadow .18s; cursor: pointer; }" +
      ".csl-quick-action:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(26,14,15,.10); }" +
      ".csl-quick-action:active { transform: translateY(0); }" +
      ".csl-wallet-card { transition: transform .25s cubic-bezier(.22,.61,.36,1), box-shadow .25s; }" +
      ".csl-wallet-card:hover { transform: translateY(-4px); }" +
      "@media (max-width: 860px) { .csl-wallet-grid { grid-template-columns: 1fr !important; } }" +
      "@media (max-width: 640px) { .csl-quick-actions-grid { grid-template-columns: repeat(2, 1fr) !important; } }"
    ),

    // ─── HEADER : bonjour + avatar ───
    h("div", {className: "csl-fade-anim", style: {display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, animationDelay: "0s"}},
      h("div", null,
        h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600}}, "Bienvenue"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 700, color: C.deep, marginTop: 2, letterSpacing: .5}}, account.name || "Supporter")
      ),
      h("div", {style: {width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, " + C.deep + ", " + C.blue + ")", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, fontFamily: "Oswald", boxShadow: "0 4px 12px rgba(26,14,15,.2)"}},
        (account.name || "?").charAt(0).toUpperCase()
      )
    ),

    // ─── Profil incomplet : rôle au club ou photo manquante ───
    // Les comptes créés avant la carte de membre n'ont ni l'un ni
    // l'autre. On le leur dit ici plutôt que d'attendre qu'ils
    // tombent dessus.
    (function() {
      if (!myProfile) return null;
      var sansProfil = !myProfile.member_type_id || !myProfile.photo_path;
      var sansRecharge = !myProfile.card_activated;
      if (!sansProfil && !sansRecharge) return null;

      // La première recharge prime : sans elle, la carte n'est pas éditée,
      // même si le profil est complet.
      var manque = Math.max((myProfile.activation_cents || 1000)
                            - (myProfile.recharges_cents || 0), 0);

      return h("div", {className: "csl-fade-anim", style: {
        background: "linear-gradient(135deg, rgba(244,232,52,.22), rgba(244,232,52,.10))",
        border: "1px solid rgba(200,160,0,.35)", borderRadius: 16,
        padding: "16px 18px", marginBottom: 20, display: "flex",
        gap: 14, alignItems: "flex-start"
      }},
        h("div", {style: {fontSize: 30, flexShrink: 0, lineHeight: 1}}, "🪪"),
        h("div", {style: {flex: 1, minWidth: 0}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 15, fontWeight: 700, color: C.deep, textTransform: "uppercase", letterSpacing: .8}},
            sansRecharge ? "Activez votre carte" : "Complétez votre profil"),

          sansRecharge && h("div", {style: {fontSize: 14, color: "#6b5d00", lineHeight: 1.5, marginTop: 4}},
            "Une première recharge de ", h("strong", null, formatEur(myProfile.activation_cents || 1000)),
            " active votre carte de membre. ",
            h("strong", null, "Cette somme est intégralement créditée sur votre compte"),
            " : elle n'est pas un frais, vous la dépensez à la buvette.",
            manque < (myProfile.activation_cents || 1000) && manque > 0
              ? h("div", {style: {marginTop: 6}}, "Il vous manque " + formatEur(manque) + ".")
              : null),

          !sansRecharge && sansProfil && h("div", {style: {fontSize: 14, color: "#6b5d00", lineHeight: 1.5, marginTop: 4}},
            !myProfile.member_type_id && !myProfile.photo_path
              ? "Il manque votre rôle au club et votre photo pour éditer votre carte de membre."
              : !myProfile.member_type_id
                ? "Il manque votre rôle au club : c'est lui qui détermine vos avantages."
                : "Il manque votre photo pour éditer votre carte de membre."),

          sansRecharge && sansProfil && h("div", {style: {fontSize: 13, color: "#6b5d00", lineHeight: 1.5, marginTop: 8, opacity: .9}},
            "Il manque aussi votre rôle au club et votre photo."),

          h("div", {style: {display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12}},
            sansRecharge && h("a", {href: SHOPIFY_RECHARGE_URL,
              style: Object.assign({}, AB, {background: C.green, padding: "11px 20px", fontSize: 12, textDecoration: "none", display: "inline-block"})},
              "Recharger " + formatEur(manque || (myProfile.activation_cents || 1000))),
            sansProfil && h("button", {onClick: ouvrirProfil, style: Object.assign({}, AB, {
              background: sansRecharge ? C.pale : C.red,
              color: sansRecharge ? C.deep : "#fff",
              padding: "11px 20px", fontSize: 12
            })}, "Compléter mon profil")
          )
        )
      );
    })(),

    // ─── Le formulaire de complétion ───
    profOpen && h("div", {style: MODAL_BG, onClick: function() { if (!profBusy) setProfOpen(false); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6}},
          "Votre profil"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5}},
          "Votre rôle au club détermine vos avantages, et votre photo figure sur votre carte de membre."),

        h("div", {style: LABEL}, "Votre rôle au club"),
        h("div", {style: {display: "grid", gap: 6, gridTemplateColumns: "1fr 1fr", marginBottom: 12}},
          profTypes.map(function(ty) {
            var on = profType === ty.id;
            return h("button", {key: ty.id, type: "button",
              onClick: function() { setProfType(ty.id); },
              style: {textAlign: "left", border: "1px solid " + (on ? ty.color : C.border),
                      background: on ? ty.color : "#fff", color: on ? "#fff" : C.deep,
                      borderRadius: 10, padding: "11px 12px", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, lineHeight: 1.25}},
              h("span", {style: {marginRight: 5}}, ty.emoji), ty.name);
          })
        ),

        (function() {
          var ty = profTypes.filter(function(x) { return x.id === profType; })[0];
          if (!ty) return null;
          return h("div", null,
            ty.requires_licence && h("div", null,
              h("div", {style: LABEL}, "Numéro de licence"),
              h("input", {value: profLicence, onChange: function(e) { setProfLicence(e.target.value); },
                          placeholder: "Sur votre licence FFR", style: IS})),
            ty.requires_company && h("div", null,
              h("div", {style: LABEL}, "Nom de la société"),
              h("input", {value: profCompany, onChange: function(e) { setProfCompany(e.target.value); },
                          placeholder: "Raison sociale", style: IS}))
          );
        })(),

        h("div", {style: LABEL}, "Votre photo"),
        h("div", {style: {display: "flex", gap: 12, alignItems: "center", marginBottom: 8}},
          h("div", {style: {width: 72, height: 90, borderRadius: 10, overflow: "hidden",
                            background: C.pale, border: "1px solid " + C.border,
                            display: "grid", placeItems: "center", flexShrink: 0}},
            profPreview
              ? h("img", {src: profPreview, alt: "", style: {width: "100%", height: "100%", objectFit: "cover"}})
              : h("span", {style: {fontSize: 26, opacity: .4}}, "🙂")
          ),
          h("div", {style: {flex: 1, minWidth: 0}},
            h("label", {style: Object.assign({}, AB, {background: C.pale, color: C.deep, display: "block", textAlign: "center", padding: "12px 14px", fontSize: 12, cursor: "pointer"})},
              profPreview ? "Changer la photo" : "📷 Prendre une photo",
              h("input", {type: "file", accept: "image/*", capture: "user",
                          onChange: function(e) { onProfPhoto(e.target.files && e.target.files[0]); },
                          style: {display: "none"}})
            ),
            h("div", {style: {fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4}},
              "Visage de face, comme une photo d'identité.")
          )
        ),
        h("div", {style: {fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.45, background: C.pale, padding: "10px 12px", borderRadius: 8}},
          "Pour un enfant, la photo est téléversée par le parent, qui autorise ainsi le club à l'utiliser sur sa carte. Elle n'est visible que par l'administration."),

        profErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginBottom: 12}}, profErr),

        h("div", {style: {display: "flex", gap: 8}},
          h("button", {onClick: function() { setProfOpen(false); }, disabled: profBusy,
            style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Plus tard"),
          h("button", {onClick: enregistrerProfil, disabled: profBusy,
            style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})},
            profBusy ? "..." : "Enregistrer")
        )
      )
    ),

    // ─── CARTES EMPILÉES (Apple Wallet style) ───
    h("div", {className: "csl-wallet-grid", style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20}},

      // ═══ CARTE 1 : SOLDE (grand format, navy dégradé) ═══
      h("div", {
        className: "csl-card-anim csl-wallet-card",
        style: {
          animationDelay: ".05s",
          background: "linear-gradient(135deg, " + C.blue + " 0%, " + C.deep + " 100%)",
          borderRadius: 20,
          padding: "28px 26px",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(26,14,15,.28), 0 8px 16px rgba(26,14,15,.14)",
          minHeight: 240
        }
      },
        // Effet lumière subtle en fond
        h("div", {style: {position: "absolute", top: -80, right: -80, width: 260, height: 260, background: "radial-gradient(circle, rgba(255,182,39,.14), transparent 70%)", borderRadius: "50%", pointerEvents: "none"}}),
        h("div", {style: {position: "absolute", bottom: -60, left: -60, width: 200, height: 200, background: "radial-gradient(circle, rgba(255,255,255,.06), transparent 70%)", borderRadius: "50%", pointerEvents: "none"}}),

        // Header
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "start", position: "relative"}},
          h("div", null,
            h("div", {style: {fontSize: 10, letterSpacing: 3, textTransform: "uppercase", opacity: .7, fontWeight: 600}}, "Solde cashless"),
            h("div", {style: {fontSize: 11, color: C.gold, letterSpacing: 1.5, marginTop: 5, fontWeight: 700}}, "CSL RUGBY · SAISON 2026-2027")
          ),
          h("div", {style: {width: 36, height: 36, background: "rgba(255,255,255,.14)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18}}, "🛡")
        ),

        // Solde énorme (animé)
        h("div", {style: {fontFamily: "Oswald", fontSize: 54, fontWeight: 800, lineHeight: 1, marginTop: 32, letterSpacing: -1, position: "relative"}}, formatEur(displayedBalance)),

        // Footer titulaire
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "end", marginTop: 30, position: "relative"}},
          h("div", null,
            h("div", {style: {fontSize: 9, opacity: .55, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600}}, "Titulaire"),
            h("div", {style: {fontSize: 13, fontWeight: 600, marginTop: 3, opacity: .95}}, account.name || "—")
          ),
          h("div", {style: {fontSize: 10, opacity: .55, letterSpacing: 3, fontFamily: "monospace"}}, "• • • •  " + ((account.id || "0000").slice(-4).toUpperCase()))
        )
      ),

      // ═══ CARTE 2 : QR CODE (or, cliquable pour agrandir) ═══
      h("div", {
        className: "csl-card-anim csl-wallet-card",
        onClick: function() { setQrExpanded(true); },
        style: {
          animationDelay: ".15s",
          background: "linear-gradient(135deg, " + C.gold + " 0%, #C99B00 100%)",
          borderRadius: 20,
          padding: "28px 26px",
          color: C.deep,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(255,182,39,.28), 0 8px 16px rgba(255,182,39,.14)",
          cursor: "pointer",
          minHeight: 240,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      },
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "start"}},
          h("div", null,
            h("div", {style: {fontSize: 10, letterSpacing: 3, textTransform: "uppercase", opacity: .65, fontWeight: 700}}, "Mon QR code"),
            h("div", {style: {fontSize: 17, fontWeight: 700, marginTop: 5, fontFamily: "Oswald", letterSpacing: .5}}, "À présenter à la buvette")
          ),
          h("div", {style: {width: 44, height: 44, background: C.deep, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: C.gold}}, "▦")
        ),
        // Aperçu QR intégré
        h("div", {style: {alignSelf: "center", marginTop: 10}},
          h("div", {className: "csl-qr-safe", style: {background: "#fff", borderRadius: 10, padding: 8, display: "inline-block", boxShadow: "0 4px 12px rgba(0,0,0,.12)", colorScheme: "light"}},
            h(QRCodeView, {data: account.qr, size: 90})
          )
        ),
        h("div", {style: {fontSize: 11, opacity: .7, textAlign: "center", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase"}}, "Cliquer pour agrandir →")
      )
    ),

    // ─── ACTIONS RAPIDES (3 pastilles) ───
    h("div", {className: "csl-quick-actions-grid csl-fade-anim", style: {display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20, animationDelay: ".3s"}},

      // Recharger
      h("a", {href: SHOPIFY_RECHARGE_URL, className: "csl-quick-action", style: {display: "flex", alignItems: "center", gap: 12, background: "#fff", borderRadius: 14, padding: "16px 18px", textDecoration: "none", border: "1px solid " + C.border, boxShadow: "0 2px 6px rgba(0,0,0,.03)"}},
        h("div", {style: {width: 40, height: 40, background: "rgba(208,33,40,.1)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: C.blue, flexShrink: 0}}, "＋"),
        h("div", {style: {minWidth: 0}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, color: C.deep, letterSpacing: .5}}, "Recharger"),
          h("div", {style: {fontSize: 11, color: C.muted, marginTop: 2}}, "+ bonus jusqu'à 23€")
        )
      ),

      // Code de recharge
      h("div", {className: "csl-quick-action", onClick: function() { setCodeFormOpen(!codeFormOpen); }, style: {display: "flex", alignItems: "center", gap: 12, background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid " + C.border, boxShadow: "0 2px 6px rgba(0,0,0,.03)"}},
        h("div", {style: {width: 40, height: 40, background: "rgba(34,197,94,.1)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: C.green, flexShrink: 0}}, "🎁"),
        h("div", {style: {minWidth: 0}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, color: C.deep, letterSpacing: .5}}, "J'ai un code"),
          h("div", {style: {fontSize: 11, color: C.muted, marginTop: 2}}, "Saisir un code cadeau")
        )
      ),

      // Site club
      h("a", {href: "https://csl-rugby.fr/", target: "_blank", rel: "noopener", className: "csl-quick-action", style: {display: "flex", alignItems: "center", gap: 12, background: "#fff", borderRadius: 14, padding: "16px 18px", textDecoration: "none", border: "1px solid " + C.border, boxShadow: "0 2px 6px rgba(0,0,0,.03)"}},
        h("div", {style: {width: 40, height: 40, background: "rgba(255,182,39,.15)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0}}, "🏉"),
        h("div", {style: {minWidth: 0}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, color: C.deep, letterSpacing: .5}}, "Site CSL"),
          h("div", {style: {fontSize: 11, color: C.muted, marginTop: 2}}, "Actus & calendrier")
        )
      )
    ),

    // ─── Zone déroulable : saisie code recharge ───
    codeFormOpen && h("div", {className: "csl-scale-anim", style: {background: "linear-gradient(135deg, rgba(34,197,94,.04), rgba(34,197,94,.08))", border: "1px solid rgba(34,197,94,.2)", borderRadius: 14, padding: 18, marginBottom: 20}},
      h("div", {style: {fontFamily: "Oswald", fontSize: 12, fontWeight: 700, color: C.green, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase"}}, "🎁 Utiliser un code de recharge"),
      h("div", {style: {fontSize: 12, color: C.muted, marginBottom: 12}}, "Si tu as reçu un code par email après un achat."),
      h("div", {style: {display: "flex", gap: 8}},
        h("input", {value: rechargeCode, onChange: function(e) { setRechargeCode(e.target.value); }, placeholder: "RECH-XXXX-XXXX", disabled: redeeming, autoFocus: true, style: Object.assign({}, IS, {flex: 1, marginBottom: 0, textTransform: "uppercase", letterSpacing: 2, textAlign: "center", fontFamily: "monospace"}), onKeyDown: function(e) { if (e.key === "Enter") useCode(); }}),
        h("button", {onClick: useCode, disabled: redeeming, style: Object.assign({}, AB, {background: redeeming ? C.muted : C.green, padding: "10px 20px"})}, redeeming ? "..." : "Valider")
      ),
      rechargeMsg && h("div", {style: {fontSize: 13, marginTop: 10, textAlign: "center", color: rechargeMsg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, rechargeMsg)
    ),

    // ─── HISTORIQUE (Timeline verticale style iMessage) ───
    h("div", {className: "csl-fade-anim", style: {background: "#fff", borderRadius: 18, padding: "24px 22px", border: "1px solid " + C.border, boxShadow: "0 2px 8px rgba(0,0,0,.03)", animationDelay: ".4s", marginBottom: 20}},
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4}},
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, color: C.deep, letterSpacing: 1, textTransform: "uppercase"}}, "Activité récente"),
        myTx.length > 0 && h("div", {style: {fontSize: 11, color: C.muted, fontWeight: 600}}, myTx.length + " mouvement" + (myTx.length > 1 ? "s" : ""))
      ),
      h("div", {style: {height: 1, background: C.border, marginTop: 14, marginBottom: 6}}),

      myTx.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 30}},
        h("div", {style: {fontSize: 32, opacity: .3, marginBottom: 8}}, "💤"),
        h("div", null, "Aucun mouvement pour le moment"),
        h("div", {style: {fontSize: 11, marginTop: 4}}, "Ta première recharge apparaîtra ici")
      ),

      // Timeline verticale
      h("div", {style: {position: "relative"}},
        // Ligne verticale continue
        myTx.length > 0 && h("div", {style: {position: "absolute", left: 19, top: 26, bottom: 20, width: 2, background: C.pale, borderRadius: 1}}),

        myTx.slice((txPage - 1) * TX_PER_PAGE, txPage * TX_PER_PAGE).map(function(t, idx) {
          var cat = txCategory(t);
          var isPositive = t.amount_cents > 0;
          var d = new Date(t.created_at);
          // Format date : "Aujourd'hui", "Hier", ou "jj/mm"
          var now = new Date();
          var isToday = d.toDateString() === now.toDateString();
          var yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
          var isYesterday = d.toDateString() === yesterday.toDateString();
          var timeStr = d.toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"});
          var dateStr = isToday ? "Aujourd'hui · " + timeStr : (isYesterday ? "Hier · " + timeStr : d.toLocaleDateString("fr-FR") + " · " + timeStr);
          return h("div", {
            key: t.id,
            className: "csl-tx-anim",
            style: {
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              padding: "14px 0",
              position: "relative",
              animationDelay: (idx * 60) + "ms"
            }
          },
            // Bulle icône (sur la ligne verticale)
            h("div", {style: {width: 40, height: 40, borderRadius: "50%", background: cat.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, position: "relative", zIndex: 1, border: "3px solid #fff"}}, cat.icon),

            // Contenu
            h("div", {style: {flex: 1, minWidth: 0}},
              // Ligne 1 : label + montant
              h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10}},
                h("div", {style: {color: C.deep, fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1}}, t.label || "—"),
                h("div", {style: {fontFamily: "Oswald", fontSize: 16, fontWeight: 800, color: isPositive ? C.green : C.deep, whiteSpace: "nowrap"}},
                  (isPositive ? "+" : "") + formatEur(t.amount_cents)
                )
              ),
              // Ligne 2 : type + date + bénévole
              h("div", {style: {display: "flex", alignItems: "center", gap: 10, marginTop: 4, fontSize: 11, color: C.muted, flexWrap: "wrap"}},
                h("span", {style: {color: cat.accent, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", fontSize: 10}}, cat.label),
                h("span", {style: {width: 3, height: 3, background: C.muted, borderRadius: "50%", opacity: .5}}),
                h("span", null, dateStr),
                t.cashier_name && t.cashier_name.indexOf("AUTO") !== 0 && h("span", {style: {color: C.blue, fontWeight: 600}}, "· " + t.cashier_name)
              )
            )
          );
        }),

        // Pagination (design plus doux qu'avant)
        myTx.length > TX_PER_PAGE && (function() {
          var totalPages = Math.ceil(myTx.length / TX_PER_PAGE);
          return h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, paddingTop: 16, borderTop: "1px solid " + C.border}},
            h("button", {
              onClick: function() { if (txPage > 1) setTxPage(txPage - 1); },
              disabled: txPage <= 1,
              style: {background: "transparent", color: txPage <= 1 ? C.muted : C.blue, border: "1px solid " + (txPage <= 1 ? C.border : C.blue), borderRadius: 10, padding: "8px 16px", fontFamily: "Oswald", fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: txPage <= 1 ? "not-allowed" : "pointer", textTransform: "uppercase", transition: "all .2s"}
            }, "← Précédent"),
            h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.muted, letterSpacing: 1, fontWeight: 700}},
              "Page " + txPage + " / " + totalPages
            ),
            h("button", {
              onClick: function() { if (txPage < totalPages) setTxPage(txPage + 1); },
              disabled: txPage >= totalPages,
              style: {background: "transparent", color: txPage >= totalPages ? C.muted : C.blue, border: "1px solid " + (txPage >= totalPages ? C.border : C.blue), borderRadius: 10, padding: "8px 16px", fontFamily: "Oswald", fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: txPage >= totalPages ? "not-allowed" : "pointer", textTransform: "uppercase", transition: "all .2s"}
            }, "Suivant →")
          );
        })()
      )
    ),

    // ─── QR AGRANDI (overlay plein écran) ───
    qrExpanded && h("div", {
      className: "csl-fade-anim",
      onClick: function() { setQrExpanded(false); },
      style: {position: "fixed", inset: 0, background: "rgba(26,14,15,.85)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, cursor: "pointer", animationDuration: ".25s"}
    },
      h("div", {
        className: "csl-scale-anim",
        onClick: function(e) { e.stopPropagation(); },
        style: {background: "#fff", borderRadius: 24, padding: 32, maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 24px 60px rgba(0,0,0,.4)", cursor: "default"}
      },
        h("div", {style: {fontFamily: "Oswald", fontSize: 11, color: C.gold, letterSpacing: 4, textTransform: "uppercase", fontWeight: 700, marginBottom: 6}}, "CSL Rugby"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.deep, letterSpacing: .5, marginBottom: 22, textTransform: "uppercase"}}, "Mon QR code"),
        h("div", {className: "csl-qr-safe", style: {background: "#fff", borderRadius: 14, padding: 16, display: "inline-block", boxShadow: "0 8px 24px rgba(0,0,0,.08)", border: "1px solid " + C.border, colorScheme: "light"}},
          h(QRCodeView, {data: account.qr, size: 260})
        ),
        h("div", {style: {marginTop: 20, fontSize: 13, color: C.muted, fontWeight: 500}}, "Présente ce code au bénévole"),
        h("div", {style: {marginTop: 6, fontFamily: "monospace", fontSize: 13, color: C.blue, letterSpacing: 2, background: C.pale, padding: "8px 14px", borderRadius: 10, display: "inline-block", fontWeight: 600, marginTop: 10}}, account.qr),
        h("button", {onClick: function() { setQrExpanded(false); }, style: {marginTop: 24, background: C.deep, color: "#fff", border: "none", borderRadius: 12, padding: "14px 32px", fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", width: "100%"}}, "Fermer")
      )
    ),

    // ═══════════════════════════════════════════════════════════════
    // TUTORIEL CASHLESS — comment ça marche (côté supporter)
    // ═══════════════════════════════════════════════════════════════
    h("div", {style: {marginTop: 40}},
      h("div", {style: {textAlign: "center", marginBottom: 24}},
        h("div", {style: {fontFamily: "Oswald", fontSize: 11, color: C.gold, letterSpacing: 4, fontWeight: 700, textTransform: "uppercase", marginBottom: 8}}, "🏉 Mode d'emploi"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, color: C.deep, letterSpacing: 1, textTransform: "uppercase"}}, "Comment ça marche ?"),
        h("div", {style: {fontSize: 13, color: C.muted, marginTop: 6}}, "3 étapes pour profiter de la buvette sans monnaie")
      ),
      h("div", {className: "csl-tuto-grid", style: {display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16}},
        h("style", null, "@media (max-width: 760px) { .csl-tuto-grid { grid-template-columns: 1fr !important; } }"),

        // ÉTAPE 1
        h("div", {style: {background: "#fff", borderRadius: 16, padding: 24, border: "2px solid " + C.border, position: "relative", textAlign: "center"}},
          h("div", {style: {position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)", background: C.gold, color: C.deep, width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Oswald", fontSize: 18, fontWeight: 800, boxShadow: "0 4px 12px rgba(255,182,39,.4)"}}, "1"),
          h("div", {style: {fontSize: 48, marginTop: 8, marginBottom: 12}}, "💳"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 16, fontWeight: 800, color: C.deep, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8}}, "Je recharge"),
          h("div", {style: {fontSize: 13, color: C.muted, lineHeight: 1.6}},
            "J'achète une recharge ",
            h("strong", {style: {color: C.deep}}, "en ligne"),
            " sur le site du club, ou directement ",
            h("strong", {style: {color: C.deep}}, "au stade"),
            " auprès d'un bénévole de la table recharge."
          )
        ),

        // ÉTAPE 2
        h("div", {style: {background: "#fff", borderRadius: 16, padding: 24, border: "2px solid " + C.border, position: "relative", textAlign: "center"}},
          h("div", {style: {position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)", background: C.gold, color: C.deep, width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Oswald", fontSize: 18, fontWeight: 800, boxShadow: "0 4px 12px rgba(255,182,39,.4)"}}, "2"),
          h("div", {style: {fontSize: 48, marginTop: 8, marginBottom: 12}}, "📱"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 16, fontWeight: 800, color: C.deep, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8}}, "Je montre mon QR"),
          h("div", {style: {fontSize: 13, color: C.muted, lineHeight: 1.6}},
            "À la buvette, je présente mon ",
            h("strong", {style: {color: C.deep}}, "QR code personnel"),
            " (visible plus haut sur cette page) au bénévole qui le scanne avec sa tablette."
          )
        ),

        // ÉTAPE 3
        h("div", {style: {background: "#fff", borderRadius: 16, padding: 24, border: "2px solid " + C.border, position: "relative", textAlign: "center"}},
          h("div", {style: {position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)", background: C.gold, color: C.deep, width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Oswald", fontSize: 18, fontWeight: 800, boxShadow: "0 4px 12px rgba(255,182,39,.4)"}}, "3"),
          h("div", {style: {fontSize: 48, marginTop: 8, marginBottom: 12}}, "🍻"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 16, fontWeight: 800, color: C.deep, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8}}, "Je profite"),
          h("div", {style: {fontSize: 13, color: C.muted, lineHeight: 1.6}},
            "Le montant est ",
            h("strong", {style: {color: C.deep}}, "débité directement"),
            " de mon solde. Plus besoin de monnaie ni de carte bancaire. Je peux suivre mon solde ici à tout moment."
          )
        )
      ),

      // FAQ rapide
      h("div", {style: {marginTop: 32, background: "linear-gradient(135deg, " + C.pale + ", #fff)", borderRadius: 16, padding: 24, border: "1px solid " + C.border}},
        h("div", {style: {fontFamily: "Oswald", fontSize: 13, fontWeight: 700, color: C.deep, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase"}}, "❓ Questions fréquentes"),
        h("div", {style: {display: "flex", flexDirection: "column", gap: 14}},
          h("div", null,
            h("div", {style: {fontWeight: 700, color: C.deep, fontSize: 14, marginBottom: 4}}, "💰 Comment recharger mon compte ?"),
            h("div", {style: {color: C.muted, fontSize: 13, lineHeight: 1.5}}, "Clique sur \"🎟 Acheter une recharge\" en haut de cette page. Le crédit est ajouté automatiquement à ton compte. Tu peux aussi recharger sur place au stade.")
          ),
          h("div", null,
            h("div", {style: {fontWeight: 700, color: C.deep, fontSize: 14, marginBottom: 4}}, "📅 Mon solde est-il valable longtemps ?"),
            h("div", {style: {color: C.muted, fontSize: 13, lineHeight: 1.5}}, "Le solde est valable pour toute la saison sportive 2026 — 2027. Tu peux l'utiliser à tous les matchs à domicile.")
          ),
          h("div", null,
            h("div", {style: {fontWeight: 700, color: C.deep, fontSize: 14, marginBottom: 4}}, "🔒 C'est sécurisé ?"),
            h("div", {style: {color: C.muted, fontSize: 13, lineHeight: 1.5}}, "Ton QR code est unique et lié à ton compte. Seuls les bénévoles autorisés peuvent débiter ton solde, et tu vois chaque transaction dans ton historique.")
          ),
          h("div", null,
            h("div", {style: {fontWeight: 700, color: C.deep, fontSize: 14, marginBottom: 4}}, "🤔 Un problème, une question ?"),
            h("div", {style: {color: C.muted, fontSize: 13, lineHeight: 1.5}}, "Demande à un bénévole au stade ou contacte le club via ",
              h("a", {href: "https://csl-rugby.fr/", target: "_blank", rel: "noopener", style: {color: C.blue, fontWeight: 600}}, "csl-rugby.fr"),
              "."
            )
          )
        )
      ),

      // ─── Supprimer mon compte ───
      h("div", {style: {marginTop: 26}},
        h("button", {
          onClick: openDelete,
          style: {width: "100%", background: "transparent", border: "1px solid " + C.border,
                  color: C.muted, borderRadius: 10, padding: "13px 16px", cursor: "pointer",
                  fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                  letterSpacing: 1, textTransform: "uppercase"}
        }, "Supprimer mon compte")
      ),

      // Popup de confirmation
      delStep === "confirm" && h("div", {style: MODAL_BG, onClick: function() { if (!deleting) setDelStep(null); }},
        h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
          h("div", {style: {fontSize: 44, textAlign: "center", marginBottom: 8}}, "⚠️"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 20, color: C.deep, fontWeight: 800,
                            letterSpacing: 1, textTransform: "uppercase", textAlign: "center", marginBottom: 10}},
            "Supprimer mon compte"),
          h("div", {style: {fontSize: 14, color: C.muted, lineHeight: 1.55, marginBottom: 16}},
            "Cette action est définitive. Ton compte, tes informations personnelles et ton accès à CSL Connect seront supprimés."),

          // Le solde est le point qui compte : il est affiché en grand.
          delSummary && (delSummary.balance_cents || 0) > 0 && h("div", {
            style: {background: "rgba(208,33,40,.07)", border: "1px solid rgba(208,33,40,.28)",
                    borderRadius: 12, padding: 16, marginBottom: 16, textAlign: "center"}
          },
            h("div", {style: {fontSize: 11, letterSpacing: 2, fontFamily: "Oswald", fontWeight: 700,
                              textTransform: "uppercase", color: C.red}}, "Solde qui sera perdu"),
            h("div", {style: {fontFamily: "Oswald", fontSize: 34, fontWeight: 800, color: C.red, lineHeight: 1.1}},
              formatEur(delSummary.balance_cents)),
            h("div", {style: {fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.5}},
              "Dépense-le à la buvette avant de supprimer ton compte, il ne sera pas remboursé.")
          ),

          h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5}},
            "Tes paiements passés restent enregistrés de façon anonyme dans la comptabilité du club, comme la loi l'exige."),

          h("div", {style: LABEL}, "Saisis SUPPRIMER pour confirmer"),
          h("input", {value: delConfirm, onChange: function(e) { setDelConfirm(e.target.value); },
                      placeholder: "SUPPRIMER", style: IS}),
          delError && h("div", {style: {color: C.red, fontSize: 13, marginBottom: 10}}, delError),

          h("div", {style: {display: "flex", gap: 8, marginTop: 6}},
            h("button", {onClick: function() { setDelStep(null); }, disabled: deleting,
                         style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 14, fontSize: 13})},
              "Annuler"),
            h("button", {onClick: doDelete, disabled: deleting,
                         style: Object.assign({}, AB, {background: C.red, flex: 1, padding: 14, fontSize: 13})},
              deleting ? "..." : "Supprimer")
          )
        )
      ),

      // Confirmation finale
      delStep === "done" && h("div", {style: MODAL_BG},
        h("div", {style: MODAL_BOX},
          h("div", {style: {fontSize: 44, textAlign: "center", marginBottom: 10}}, "✅"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800,
                            letterSpacing: 1, textTransform: "uppercase", textAlign: "center", marginBottom: 8}},
            "Compte supprimé"),
          h("div", {style: {fontSize: 14, color: C.muted, textAlign: "center", lineHeight: 1.55}},
            "Tes données ont été supprimées. Merci d'avoir soutenu le club, et à bientôt au Stade Dumas.")
        )
      ),

      // Footer encouragement
      h("div", {style: {textAlign: "center", marginTop: 30, padding: 20}},
        h("div", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.deep, letterSpacing: 2, textTransform: "uppercase"}}, "Allez les Lédoniens ! 🏉"),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 6}}, "Cercle Sportif Lédonien · Fédérale 2 · Saison 2026 — 2027")
      )
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// VUE : BUVETTE (scan + encaisser)
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// ECRAN : LIAISON A UN POINT DE VENTE (scan du QR du poste)
// Le benevole doit scanner le QR colle sur son poste avant d'encaisser.
// La liaison est stockee en local et vaut 24 h.
// ═══════════════════════════════════════════════════════════════════
var POS_STORAGE_KEY = "csl_pos_binding";
var POS_BINDING_MS = 24 * 60 * 60 * 1000;

function readPosBinding() {
  try {
    var raw = window.localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return null;
    var b = JSON.parse(raw);
    if (!b || !b.id || !b.ts) return null;
    if (Date.now() - b.ts > POS_BINDING_MS) {
      window.localStorage.removeItem(POS_STORAGE_KEY);
      return null;
    }
    return b;
  } catch (e) { return null; }
}

function writePosBinding(pos) {
  try {
    window.localStorage.setItem(POS_STORAGE_KEY, JSON.stringify({
      id: pos.id, name: pos.name, emoji: pos.emoji || "🏉", ts: Date.now()
    }));
  } catch (e) {}
}

function clearPosBinding() {
  try { window.localStorage.removeItem(POS_STORAGE_KEY); } catch (e) {}
}

function PosBindScreen(props) {
  var s1 = useState("scan"); var mode = s1[0], setMode = s1[1];
  var s2 = useState(""); var manual = s2[0], setManual = s2[1];
  var s3 = useState(""); var error = s3[0], setError = s3[1];
  var s4 = useState(false); var busy = s4[0], setBusy = s4[1];
  var videoRef = useRef(null), canvasRef = useRef(null), streamRef = useRef(null),
      scanRef = useRef(null), busyRef = useRef(false);

  var stopCam = function() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(x) { x.stop(); }); streamRef.current = null; }
    if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
    busyRef.current = false;
  };

  var bindToken = async function(token) {
    if (busy) return;
    setBusy(true); setError("");
    var r = await db.resolvePos((token || "").trim());
    setBusy(false);
    if (r.error) { setError("Erreur : " + r.error.message); return false; }
    if (!r.data || !r.data.success) { setError("Ce QR ne correspond à aucun poste actif."); return false; }
    stopCam();
    writePosBinding(r.data);
    if (navigator.vibrate) navigator.vibrate(80);
    props.onBound({id: r.data.id, name: r.data.name, emoji: r.data.emoji, ts: Date.now()});
    return true;
  };

  var scanFrame = async function() {
    if (busyRef.current) return;
    var v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
    busyRef.current = true;
    try {
      var w = v.videoWidth, hh = v.videoHeight;
      if (!w || !hh) { busyRef.current = false; return; }
      cv.width = w; cv.height = hh;
      var ctx = cv.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(v, 0, 0, w, hh);
      var img = ctx.getImageData(0, 0, w, hh);
      var code = window.jsQR(img.data, w, hh, {inversionAttempts: "dontInvert"});
      if (code && code.data && code.data.indexOf("CSLPOS-") === 0) {
        await bindToken(code.data);
      }
    } catch (e) {} finally { busyRef.current = false; }
  };

  var startCam = async function() {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment", width: {ideal: 1280}, height: {ideal: 720}}, audio: false});
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", true);
        await videoRef.current.play();
      }
      scanRef.current = setInterval(scanFrame, 200);
    } catch (e) {
      setError("Caméra non disponible. Utilise la saisie manuelle.");
      setMode("manual");
    }
  };

  useEffect(function() {
    if (mode === "scan") startCam(); else stopCam();
    return function() { stopCam(); };
  }, [mode]);

  return h("div", {style: {maxWidth: 560, margin: "0 auto"}},
    h(Card, null,
      h("div", {style: {textAlign: "center", marginBottom: 18}},
        h("div", {style: {fontSize: 44, marginBottom: 8}}, "📍"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 800, color: C.deep, letterSpacing: 1, textTransform: "uppercase"}}, "Où es-tu ?"),
        h("div", {style: {fontSize: 14, color: C.muted, marginTop: 6, lineHeight: 1.5}},
          "Scanne le QR code collé sur ton poste avant de commencer à encaisser. Tu resteras lié à ce poste pendant 24 h.")
      ),
      h("div", {style: {display: "flex", gap: 8, marginBottom: 18}},
        h("button", {onClick: function() { setMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "scan" ? C.blue : C.pale, color: mode === "scan" ? "#fff" : C.muted})}, "📷 Scanner"),
        h("button", {onClick: function() { stopCam(); setMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "manual" ? C.blue : C.pale, color: mode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      mode === "scan" && h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
        h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, muted: true, playsInline: true}),
        h("canvas", {ref: canvasRef, style: {display: "none"}}),
        h("div", {style: {position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none"}},
          h("div", {style: {width: "62%", aspectRatio: "1", border: "3px solid " + C.gold, borderRadius: 16, boxShadow: "0 0 0 9999px rgba(0,0,0,.35)"}})
        ),
        h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontFamily: "Oswald", fontSize: 13, letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "🏉 VISER LE QR DU POSTE")
      ),
      mode === "manual" && h("div", null,
        h("div", {style: LABEL}, "Code du poste"),
        h("input", {value: manual, onChange: function(e) { setManual(e.target.value); }, placeholder: "CSLPOS-…", style: IS}),
        h("button", {onClick: function() { bindToken(manual); }, disabled: busy, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 14})}, busy ? "..." : "Me lier à ce poste")
      ),
      error && h("div", {style: {color: C.red, marginTop: 12, textAlign: "center", fontSize: 13}}, error)
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// VUE : MES BILLETS
// Le supporter retrouve ses billets et leur QR code. Un billet par
// place : c'est ce QR qui est scanné à l'entrée.
// ══════════════════════════════════════════════════════════════════
function MyTicketsView(props) {
  var s1 = useState(null); var orders = s1[0], setOrders = s1[1];
  var s2 = useState(null); var zoom = s2[0], setZoom = s2[1];
  var s3 = useState(false); var past = s3[0], setPast = s3[1];

  useEffect(function() {
    db.myTickets().then(function(r) { setOrders(r.data || []); })
      .catch(function() { setOrders([]); });
  }, []);

  if (orders === null) {
    return h("div", {style: {textAlign: "center", padding: 40, color: C.muted, fontFamily: "Oswald"}}, "Chargement...");
  }

  var list = orders.filter(function(o) { return past ? o.past : !o.past; });

  return h("div", {style: {maxWidth: 620, margin: "0 auto"}},
    h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 16}},
      [[false, "À venir"], [true, "Passés"]].map(function(v) {
        return h("button", {key: String(v[0]), onClick: function() { setPast(v[0]); },
          style: {flex: 1, border: 0, background: past === v[0] ? "#fff" : "transparent",
                  color: past === v[0] ? C.deep : C.muted, padding: 10, borderRadius: 8,
                  cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                  letterSpacing: .5, textTransform: "uppercase"}}, v[1]);
      })
    ),

    list.length === 0 && h(Card, {style: {textAlign: "center", padding: 40}},
      h("div", {style: {fontSize: 46, marginBottom: 10}}, "🎟️"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 17, color: C.deep, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1}},
        past ? "Aucun billet passé" : "Aucun billet à venir"),
      !past && h("div", {style: {color: C.muted, fontSize: 14, marginTop: 8, lineHeight: 1.5}},
        "Vos billets achetés sur le site apparaîtront ici, avec leur QR code à présenter à l'entrée.")
    ),

    list.map(function(o) {
      var d = new Date(o.starts_at);
      return h(Card, {key: o.ref, style: {marginBottom: 14, opacity: o.past ? .65 : 1}},
        h("div", {style: {display: "flex", gap: 14, alignItems: "center", marginBottom: 12}},
          h("div", {style: {width: 58, flexShrink: 0, background: C.deep, color: "#fff", borderRadius: 11, padding: "9px 0", textAlign: "center", fontFamily: "Oswald"}},
            h("div", {style: {fontSize: 22, fontWeight: 800, lineHeight: 1}}, String(d.getDate()).padStart(2, "0")),
            h("div", {style: {fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: C.gold}},
              d.toLocaleDateString("fr-FR", {month: "short"}).replace(".", ""))
          ),
          h("div", {style: {flex: 1, minWidth: 0}},
            o.competition && h("div", {style: {fontFamily: "Oswald", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: C.red}}, o.competition),
            h("div", {style: {color: C.deep, fontSize: 17, fontWeight: 700}}, o.event),
            h("div", {style: {color: C.muted, fontSize: 13}},
              d.toLocaleDateString("fr-FR", {weekday: "long"}) + " · " +
              d.toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"})),
            o.venue && h("div", {style: {color: C.muted, fontSize: 12}}, "📍 " + o.venue)
          )
        ),

        o.status !== "payee" && h("div", {style: {background: "rgba(244,232,52,.2)", border: "1px solid rgba(200,160,0,.3)", borderRadius: 10, padding: "11px 13px", marginBottom: 12, fontSize: 13, color: "#7a5d00", lineHeight: 1.5}},
          h("strong", null, "Paiement en attente."),
          " Vos billets ne seront valables qu'une fois le règlement reçu. Référence " + o.ref + "."),

        (o.tickets || []).map(function(tk) {
          var ok = tk.status === "valide" && o.status === "payee" && !o.past;
          return h("div", {key: tk.code, style: {display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: "1px solid " + C.border}},
            h("div", {onClick: function() { if (ok) setZoom(tk); },
                      style: {width: 62, height: 62, background: "#fff", borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0, cursor: ok ? "pointer" : "default", opacity: ok ? 1 : .35, border: "1px solid " + C.border}},
              h(QRCodeView, {data: tk.code, size: 52})
            ),
            h("div", {style: {flex: 1, minWidth: 0}},
              h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, tk.type),
              h("div", {style: {color: C.muted, fontSize: 11, fontFamily: "monospace", marginTop: 2}}, tk.code),
              tk.status === "utilise" && h("div", {style: {color: C.green, fontSize: 12, marginTop: 3, fontWeight: 600}},
                "Entrée validée" + (tk.used_at ? " · " + new Date(tk.used_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}) : "")),
              tk.status === "annule" && h("div", {style: {color: C.red, fontSize: 12, marginTop: 3, fontWeight: 600}}, "Billet annulé")
            ),
            ok && h("button", {onClick: function() { setZoom(tk); },
              style: {background: C.pale, border: "none", color: C.blue, padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Agrandir")
          );
        }),

        h("div", {style: {marginTop: 12, paddingTop: 10, borderTop: "1px solid " + C.border, display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted}},
          h("span", null, "Commande " + o.ref),
          h("span", null, formatEur(o.total_cents))
        )
      );
    }),

    // Le QR en grand : c'est ce que le supporter présente à l'entrée
    zoom && h("div", {style: MODAL_BG, onClick: function() { setZoom(null); }},
      h("div", {style: Object.assign({}, MODAL_BOX, {textAlign: "center"}), onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 16, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4}}, zoom.type),
        h("div", {style: {color: C.muted, fontSize: 13, marginBottom: 16}}, "Présentez ce code à l'entrée"),
        h("div", {style: {background: "#fff", padding: 16, borderRadius: 14, display: "inline-block"}},
          h(QRCodeView, {data: zoom.code, size: 240})
        ),
        h("div", {style: {fontFamily: "monospace", fontSize: 14, color: C.deep, marginTop: 12, letterSpacing: 1}}, zoom.code),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.5}},
          "Montez la luminosité de votre écran pour faciliter la lecture."),
        h("button", {onClick: function() { setZoom(null); },
          style: Object.assign({}, AB, {background: C.pale, color: C.muted, width: "100%", padding: 14, fontSize: 13, marginTop: 16})}, "Fermer")
      )
    )
  );
}

// ══════════════════════════════════════════════════════════════════
// VUE : CONTRÔLE DES BILLETS
// Le bénévole scanne à l'entrée. Le verdict doit être lisible en une
// seconde, de loin, avec du monde qui attend derrière.
// ══════════════════════════════════════════════════════════════════
function ScanTicketsView(props) {
  var s1 = useState(null); var event = s1[0], setEvent = s1[1];
  var s2 = useState(null); var stats = s2[0], setStats = s2[1];
  var s3 = useState(null); var result = s3[0], setResult = s3[1];
  var s4 = useState(""); var manual = s4[0], setManual = s4[1];
  var s5 = useState(true); var camOn = s5[0], setCamOn = s5[1];
  var s6 = useState(false); var busy = s6[0], setBusy = s6[1];
  var s7 = useState(""); var gate = s7[0], setGate = s7[1];

  var videoRef = useRef(null);
  var canvasRef = useRef(null);
  var streamRef = useRef(null);
  var lastRef = useRef({code: "", at: 0});

  useEffect(function() {
    db.ticketToday().then(function(r) {
      setEvent(r.data || null);
      if (r.data && r.data.id) {
        db.ticketGateStats(r.data.id).then(function(s) { setStats(s.data || null); });
      }
    });
    try { setGate(window.localStorage.getItem("csl_gate") || ""); } catch (e) {}
  }, []);

  var refreshStats = function() {
    if (!event || !event.id) return;
    db.ticketGateStats(event.id).then(function(s) { setStats(s.data || null); });
  };

  // Le bénévole n'a aucun mode à choisir : une carte de supporter commence
  // par CSL- et un billet par CSL suivi de 12 caractères sans tiret.
  // On aiguille sur la forme du code, pas sur un réglage à ne pas oublier.
  var estCarte = function(c) { return /^CSL-/.test(c); };

  var doScan = async function(code) {
    if (busy) return;
    var c = String(code || "").trim().toUpperCase();
    if (!c) return;

    // Un QR reste devant la caméra plusieurs images d'affilée :
    // on ignore le même code pendant 3 secondes.
    var now = Date.now();
    if (lastRef.current.code === c && now - lastRef.current.at < 3000) return;
    lastRef.current = {code: c, at: now};

    setBusy(true);
    var r = estCarte(c) ? await db.entryScan(c, gate) : await db.ticketScan(c, gate);
    setBusy(false);
    if (r.error) { setResult({success: false, message: r.error.message}); return; }
    var d = r.data || {success: false, message: "Erreur"};
    d.kind = estCarte(c) ? "carte" : "billet";
    setResult(d);
    if (navigator.vibrate) {
      // Une entrée payante vibre différemment d'une entrée validée :
      // le bénévole sait sans regarder qu'il doit encaisser.
      navigator.vibrate(!d.success ? [80, 60, 80] : (d.kind === "carte" && !d.free) ? [50, 40, 50] : 60);
    }
    refreshStats();
  };

  // Caméra
  useEffect(function() {
    if (!camOn) return;
    var stop = false;
    navigator.mediaDevices.getUserMedia({video: {facingMode: "environment"}})
      .then(function(stream) {
        if (stop) { stream.getTracks().forEach(function(t) { t.stop(); }); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", true);
          videoRef.current.play();
        }
      })
      .catch(function() { setCamOn(false); });

    var timer = setInterval(function() {
      var v = videoRef.current, cv = canvasRef.current;
      if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
      var w = v.videoWidth, hh2 = v.videoHeight;
      if (!w || !hh2) return;
      cv.width = w; cv.height = hh2;
      var ctx = cv.getContext("2d");
      ctx.drawImage(v, 0, 0, w, hh2);
      try {
        var img = ctx.getImageData(0, 0, w, hh2);
        var found = window.jsQR(img.data, w, hh2, {inversionAttempts: "dontInvert"});
        if (found && found.data) doScan(found.data);
      } catch (e) {}
    }, 350);

    return function() {
      stop = true;
      clearInterval(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function(t) { t.stop(); });
        streamRef.current = null;
      }
    };
  }, [camOn, gate, busy]);

  var saveGate = function(v) {
    setGate(v);
    try { window.localStorage.setItem("csl_gate", v); } catch (e) {}
  };

  var VERDICTS = {
    unknown: {ic: "❓", bg: C.red, txt: "Billet inconnu"},
    cancelled: {ic: "🚫", bg: C.red, txt: "Billet annulé"},
    already_used: {ic: "⛔", bg: "#d97706", txt: "Déjà scanné"},
    unpaid: {ic: "💶", bg: "#d97706", txt: "Billet non réglé"},
    wrong_day: {ic: "📅", bg: "#d97706", txt: "Autre match"},
    forbidden: {ic: "🔒", bg: C.red, txt: "Accès refusé"}
  };

  return h("div", {style: {maxWidth: 620, margin: "0 auto"}},
    // Le match du jour et le compteur d'entrées
    h(Card, {style: {marginBottom: 14}},
      event
        ? h("div", null,
            h("div", {style: {fontFamily: "Oswald", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: C.red}}, "Match du jour"),
            h("div", {style: {color: C.deep, fontSize: 18, fontWeight: 700, marginTop: 2}}, event.title),
            h("div", {style: {color: C.muted, fontSize: 13}},
              new Date(event.starts_at).toLocaleString("fr-FR", {weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit"})),
            stats && h("div", {style: {display: "flex", gap: 8, marginTop: 12}},
              // Le chiffre que le club regarde : tout le monde dans l'enceinte.
              h("div", {style: {flex: 1, background: "rgba(34,197,94,.1)", borderRadius: 12, padding: 13, textAlign: "center"}},
                h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, color: C.green}}, stats.total_presents || 0),
                h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Présents")),
              h("div", {style: {flex: 1, background: C.pale, borderRadius: 12, padding: 13, textAlign: "center"}},
                h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, color: C.deep}}, stats.entres || 0),
                h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Billets")),
              h("div", {style: {flex: 1, background: C.pale, borderRadius: 12, padding: 13, textAlign: "center"}},
                h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, color: C.deep}}, stats.cartes || 0),
                h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Cartes"))
            )
          )
        : h("div", {style: {textAlign: "center", padding: 10}},
            h("div", {style: {fontSize: 34, marginBottom: 6}}, "📅"),
            h("div", {style: {color: C.muted, fontSize: 14, lineHeight: 1.5}},
              "Aucun match aujourd'hui. Le scan fonctionne quand même : un billet d'un autre jour sera refusé."))
    ),

    // Le verdict, en grand
    result && h("div", {style: {
      background: !result.success ? ((VERDICTS[result.error] || {}).bg || C.red)
                : (result.kind === "carte" && !result.free) ? "#d97706" : C.green,
      borderRadius: 16, padding: "26px 20px", textAlign: "center", marginBottom: 14, color: "#fff"
    }},
      h("div", {style: {fontSize: 56, marginBottom: 6}},
        !result.success ? ((VERDICTS[result.error] || {}).ic || "❌")
          : (result.kind === "carte" && !result.free) ? "💶" : "✅"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase"}},
        !result.success ? ((VERDICTS[result.error] || {}).txt || result.message || "Refusé")
          : (result.kind === "carte" ? result.message : "Entrée validée")),

      // Carte de supporter : le statut et ce qu'il donne
      result.success && result.kind === "carte" && h("div", null,
        h("div", {style: {fontSize: 20, marginTop: 8, fontWeight: 700}}, result.name),
        h("div", {style: {display: "inline-block", marginTop: 8, fontSize: 12, fontFamily: "Oswald", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", background: "rgba(255,255,255,.22)", padding: "6px 14px", borderRadius: 999}},
          (result.emoji || "") + " " + result.type),
        !result.free && result.discount_pct > 0 && h("div", {style: {fontSize: 16, marginTop: 10, fontWeight: 600}},
          "Tarif réduit : −" + result.discount_pct + " % sur l'entrée"),
        !result.free && !result.discount_pct && h("div", {style: {fontSize: 15, marginTop: 10, opacity: .9}},
          "Aucun avantage : entrée à encaisser"),
        result.balance_cents != null && h("div", {style: {fontSize: 14, marginTop: 8, opacity: .85}},
          "Solde cashless : " + formatEur(result.balance_cents)),
        result.already > 0 && h("div", {style: {fontSize: 14, marginTop: 10, background: "rgba(0,0,0,.18)", padding: "8px 12px", borderRadius: 8}},
          "⚠️ Déjà passé " + result.already + " fois aujourd'hui")
      ),
      result.type && h("div", {style: {fontSize: 17, marginTop: 6, fontWeight: 600}}, result.type),
      result.holder && h("div", {style: {fontSize: 15, opacity: .85}}, result.holder),
      result.event && h("div", {style: {fontSize: 13, opacity: .75, marginTop: 4}}, result.event),
      result.error === "already_used" && result.used_at && h("div", {style: {fontSize: 13, opacity: .85, marginTop: 6}},
        "Scanné à " + new Date(result.used_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}) +
        (result.used_by ? " · " + result.used_by : "")),
      result.error === "unpaid" && result.total_cents != null && h("div", {style: {fontSize: 15, marginTop: 8, fontWeight: 700}},
        "À encaisser : " + formatEur(result.total_cents) + " · réf. " + (result.ref || "")),
      h("button", {onClick: function() { setResult(null); lastRef.current = {code: "", at: 0}; },
        style: {marginTop: 16, background: "rgba(255,255,255,.2)", border: "1px solid rgba(255,255,255,.4)", color: "#fff", padding: "12px 24px", borderRadius: 10, cursor: "pointer", fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase"}},
        "Suivant")
    ),

    // La caméra
    h(Card, null,
      h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 14}},
        [[true, "📷 Caméra"], [false, "⌨️ Saisie"]].map(function(v) {
          return h("button", {key: String(v[0]), onClick: function() { setCamOn(v[0]); setResult(null); },
            style: {flex: 1, border: 0, background: camOn === v[0] ? "#fff" : "transparent",
                    color: camOn === v[0] ? C.deep : C.muted, padding: 10, borderRadius: 8,
                    cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                    letterSpacing: .5, textTransform: "uppercase"}}, v[1]);
        })
      ),

      camOn
        ? h("div", {style: {position: "relative", borderRadius: 14, overflow: "hidden", background: "#000"}},
            h("video", {ref: videoRef, style: {width: "100%", display: "block"}, muted: true, playsInline: true}),
            h("canvas", {ref: canvasRef, style: {display: "none"}}),
            h("div", {style: {position: "absolute", inset: 0, border: "3px solid rgba(244,232,52,.55)", borderRadius: 14, pointerEvents: "none"}}),
            busy && h("div", {style: {position: "absolute", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", color: "#fff", fontFamily: "Oswald", fontSize: 16, letterSpacing: 1}}, "Vérification…")
          )
        : h("div", null,
            h("div", {style: LABEL}, "Code du billet ou de la carte"),
            h("input", {value: manual, onChange: function(e) { setManual(e.target.value.toUpperCase()); },
                        onKeyDown: function(e) { if (e.key === "Enter") { doScan(manual); setManual(""); } },
                        placeholder: "CSL… (billet ou carte)", autoCapitalize: "characters",
                        style: Object.assign({}, IS, {fontFamily: "monospace", letterSpacing: 1})}),
            h("button", {onClick: function() { doScan(manual); setManual(""); },
              style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 14, fontSize: 13})}, "Vérifier")
          ),

      h("div", {style: {fontSize: 12, color: C.muted, marginTop: 10, textAlign: "center", lineHeight: 1.5}},
        "Billet acheté en ligne ou carte de supporter : scanne, l'écran s'adapte tout seul."),

      h("div", {style: {marginTop: 14}},
        h("div", {style: LABEL}, "Votre poste de contrôle (facultatif)"),
        h("input", {value: gate, onChange: function(e) { saveGate(e.target.value); },
                    placeholder: "Entrée principale", style: Object.assign({}, IS, {marginBottom: 0})}),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.45}},
          "Enregistré avec chaque entrée : utile pour savoir par où sont passés les spectateurs.")
      )
    ),

    stats && (stats.par_statut || []).length > 0 && h(Card, {style: {marginTop: 14}},
      h("div", {style: LABEL}, "Entrées par carte"),
      (stats.par_statut || []).map(function(x, i) {
        return h("div", {key: i, style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < stats.par_statut.length - 1 ? "1px solid " + C.border : "none"}},
          h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 600, textTransform: "capitalize"}}, x.name),
          h("div", {style: {textAlign: "right"}},
            h("span", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: C.deep}}, x.nb),
            x.gratuits > 0 && h("span", {style: {color: C.green, fontSize: 12, marginLeft: 8}}, x.gratuits + " gratuit" + (x.gratuits > 1 ? "s" : ""))
          )
        );
      })
    ),

    stats && (stats.par_type || []).length > 0 && h(Card, {style: {marginTop: 14}},
      h("div", {style: LABEL}, "Entrées par catégorie de billet"),
      (stats.par_type || []).map(function(x, i) {
        return h("div", {key: i, style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < stats.par_type.length - 1 ? "1px solid " + C.border : "none"}},
          h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 600}}, x.name),
          h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: C.deep}},
            x.entres, h("span", {style: {color: C.muted, fontWeight: 400, fontSize: 14}}, " / " + x.vendus))
        );
      })
    )
  );
}

// VUE : ENCAISSEMENT (poste + scan QR client + panier)
// ═══════════════════════════════════════════════════════════════════
function EncaissementView(props) {
  var account = props.account;
  var sp1 = useState(readPosBinding); var posBinding = sp1[0], setPosBinding = sp1[1];
  var sp2 = useState([]); var products = sp2[0], setProducts = sp2[1];
  var sp3 = useState(false); var loadingProducts = sp3[0], setLoadingProducts = sp3[1];
  var s1 = useState("scan"); var mode = s1[0], setMode = s1[1];
  var s2 = useState(""); var qrInput = s2[0], setQrInput = s2[1];
  var s3 = useState(null); var customer = s3[0], setCustomer = s3[1];
  var sv1 = useState(null); var benefits = sv1[0], setBenefits = sv1[1];
  var sv2 = useState(true); var useDiscount = sv2[0], setUseDiscount = sv2[1];
  var s4 = useState({}); var cart = s4[0], setCart = s4[1];
  var s5 = useState(false); var paid = s5[0], setPaid = s5[1];
  var s6 = useState(""); var error = s6[0], setError = s6[1];
  var s7 = useState(false); var paying = s7[0], setPaying = s7[1];
  var s8 = useState(""); var tokenAmount = s8[0], setTokenAmount = s8[1];
  var SHOW_TOKEN = false; // bloc « Jeton / Retrait » masque tant que le club ne le demande pas
  var sd1 = useState([]); var deposits = sd1[0], setDeposits = sd1[1];
  var sd2 = useState(null); var depositPrompt = sd2[0], setDepositPrompt = sd2[1];
  var sd3 = useState(null); var refundPrompt = sd3[0], setRefundPrompt = sd3[1];
  var sd4 = useState(false); var refunding = sd4[0], setRefunding = sd4[1];
  var sd5 = useState(null); var refundDone = sd5[0], setRefundDone = sd5[1];
  var videoRef = useRef(null), canvasRef = useRef(null), streamRef = useRef(null), scanRef = useRef(null), scanBusyRef = useRef(false);

  var startCam = async function() {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment", width: {ideal: 1280}, height: {ideal: 720}}, audio: false});
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", true);
        await videoRef.current.play();
      }
      scanRef.current = setInterval(scanFrame, 200);
    } catch (e) {
      setError("Caméra non disponible. Utilise la saisie manuelle.");
      setMode("manual");
    }
  };

  var stopCam = function() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(t) { t.stop(); }); streamRef.current = null; }
    if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
    scanBusyRef.current = false;
  };

  var scanFrame = async function() {
    if (scanBusyRef.current) return;
    var v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
    scanBusyRef.current = true;
    try {
      var w = v.videoWidth, hh = v.videoHeight;
      if (!w || !hh) { scanBusyRef.current = false; return; }
      cv.width = w; cv.height = hh;
      var ctx = cv.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(v, 0, 0, w, hh);
      var imageData = ctx.getImageData(0, 0, w, hh);
      var code = window.jsQR(imageData.data, w, hh, {inversionAttempts: "dontInvert"});
      if (code && code.data && code.data.indexOf("CSL-") === 0) {
        stopCam();
        var r = await db.findAccount(code.data);
        if (r.data) {
          setCustomer(r.data); setCart({}); setPaid(false);
          loadBenefits(code.data);
          if (navigator.vibrate) navigator.vibrate(80);
        } else {
          setError("QR valide mais compte introuvable");
          setMode("scan");
        }
      }
    } catch (e) {} finally { scanBusyRef.current = false; }
  };

  useEffect(function() {
    // Tant qu'aucun poste n'est lie, c'est PosBindScreen qui tient la camera.
    // Sans ce garde-fou, les deux composants la demandent en meme temps et le
    // flux est attache a une balise video qui n'existe pas encore rendue.
    if (!posBinding) { stopCam(); return; }
    if (mode === "scan" && !customer) {
      startCam();
    } else {
      stopCam();
    }
    return function() { stopCam(); };
  }, [mode, customer, posBinding]);

  // Les avantages du compte : statut, remise applicable à ce poste.
  var loadBenefits = async function(qr) {
    setBenefits(null); setUseDiscount(true);
    try {
      var b = await db.accountBenefits(qr);
      if (b.data && b.data.success) setBenefits(b.data);
    } catch (e) {}
  };

  var lookup = async function() {
    setError("");
    var r = await db.findAccount(qrInput);
    if (!r.data) { setError("Compte non trouvé"); return; }
    setCustomer(r.data); setCart({}); setPaid(false);
    loadBenefits(qrInput);
  };

  // Un poste « boutique » applique la remise boutique, les autres celle
  // de la buvette : c'est le poste qui décide, pas le bénévole.
  var discountKind = (posBinding && /boutique|shop/i.test(posBinding.name || "")) ? "shop" : "bar";
  var discountMax = benefits
    ? (discountKind === "shop" ? (benefits.shop_discount_pct || 0) : (benefits.bar_discount_pct || 0))
    : 0;
  var discountPct = useDiscount ? discountMax : 0;

  var addCart = function(pid) { setCart(function(c) { var n = Object.assign({}, c); n[pid] = (n[pid] || 0) + 1; return n; }); };
  var rmCart = function(pid) { setCart(function(c) { var n = Object.assign({}, c); if (n[pid] > 1) n[pid]--; else delete n[pid]; return n; }); };
  var tokenCents = (function() { var n = parseFloat((tokenAmount || "").replace(",", ".")); return (n && n > 0) ? Math.round(n * 100) : 0; })();
  var totalCents = Object.keys(cart).reduce(function(s, pid) {
    var p = products.find(function(x) { return x.id === pid; });
    return s + (p ? p.price_cents * cart[pid] : 0);
  }, 0) + tokenCents;
  // Le serveur refait le même calcul : cet affichage ne fait pas foi.
  var remiseCents = Math.floor((totalCents * discountPct) / 100);
  var aPayerCents = totalCents - remiseCents;

  // Consignes impliquees par le panier : {depositId: {deposit, qty}}
  var cartDeposits = (function() {
    var map = {};
    Object.keys(cart).forEach(function(pid) {
      var pr = products.find(function(x) { return x.id === pid; });
      if (!pr || !pr.deposit_id) return;
      var dep = deposits.find(function(d) { return d.id === pr.deposit_id; });
      if (!dep) return;
      if (!map[dep.id]) map[dep.id] = {deposit: dep, qty: 0};
      map[dep.id].qty += cart[pid];
    });
    return Object.keys(map).map(function(k) { return map[k]; });
  })();

  // Ouvre la popup de consigne, ou paie directement si le panier n'en porte pas
  var askDeposits = function() {
    if (paying || !customer) return;
    if (cartDeposits.length === 0) { pay(0); return; }
    var init = {};
    // Par defaut on facture toutes les consignes : le client est cense ne pas
    // avoir de contenant. Le benevole retire celles qu'il a rendues.
    cartDeposits.forEach(function(cd) { init[cd.deposit.id] = cd.qty; });
    setDepositPrompt(init);
  };

  var depositPromptTotal = depositPrompt
    ? cartDeposits.reduce(function(s, cd) {
        return s + (depositPrompt[cd.deposit.id] || 0) * cd.deposit.price_cents;
      }, 0)
    : 0;

  var pay = async function(depositCents) {
    if (paying || !customer || totalCents < 0) return;
    setPaying(true);
    var items = Object.keys(cart).map(function(pid) {
      var p = products.find(function(x) { return x.id === pid; });
      return {id: pid, name: p.name, qty: cart[pid], price_cents: p.price_cents};
    });
    // Ajout du jeton / retrait cashless si montant saisi
    if (tokenCents > 0) {
      items.push({id: "token", name: "Jeton / Retrait", qty: 1, price_cents: tokenCents});
    }
    // Les consignes entrent dans le panier comme des lignes distinctes
    if (depositCents > 0 && depositPrompt) {
      cartDeposits.forEach(function(cd) {
        var q = depositPrompt[cd.deposit.id] || 0;
        if (q > 0) {
          items.push({id: "deposit:" + cd.deposit.id, name: "Consigne " + cd.deposit.name,
                      qty: q, price_cents: cd.deposit.price_cents});
        }
      });
    }
    var label = items.map(function(i) { return i.qty + "x " + i.name; }).join(", ");
    var r = await db.payCart({
      accountId: customer.id, totalCents: totalCents + (depositCents || 0), label: label,
      cashierName: account.name, items: items,
      posId: posBinding ? posBinding.id : null,
      depositCents: depositCents || 0,
      discountPct: discountPct, discountKind: discountKind
    });
    if (r.error) setError("Erreur : " + r.error.message);
    else if (!r.data.success) {
      if (r.data.error === "insufficient_balance") setError("Solde insuffisant ! (" + formatEur(r.data.balance_cents) + ")");
      else setError(r.data.error);
    } else {
      setCustomer(Object.assign({}, customer, {balance_cents: r.data.new_balance_cents}));
      setPaid(true); setError("");
    }
    setDepositPrompt(null);
    setPaying(false);
  };

  // ─── Remboursement de consignes rapportees par le client ───
  // refundPrompt est une table {depositId: quantite}. On appelle refund_deposit
  // une fois par type : chaque retour reste une ligne lisible dans l'export.
  var refundPromptTotal = refundPrompt
    ? deposits.reduce(function(s, d) { return s + (refundPrompt[d.id] || 0) * d.price_cents; }, 0)
    : 0;

  var doRefund = async function() {
    if (!refundPrompt || refunding) return;
    var lignes = deposits.filter(function(d) { return (refundPrompt[d.id] || 0) > 0; });
    if (lignes.length === 0) return;
    setRefunding(true);
    var lastBalance = customer.balance_cents;
    var echecs = [];
    for (var i = 0; i < lignes.length; i++) {
      var d = lignes[i];
      var r = await db.refundDeposit({
        accountId: customer.id, depositId: d.id, qty: refundPrompt[d.id],
        cashierName: account.name, posId: posBinding ? posBinding.id : null
      });
      if (r.error) { echecs.push(d.name + " : " + r.error.message); continue; }
      if (!r.data || !r.data.success) { echecs.push(d.name + " : " + (r.data ? r.data.error : "erreur")); continue; }
      lastBalance = r.data.new_balance_cents;
    }
    setRefunding(false);
    setCustomer(Object.assign({}, customer, {balance_cents: lastBalance}));
    if (echecs.length > 0) {
      // Les retours qui ont abouti sont conserves : on signale seulement le reste.
      setError("Retours partiels — " + echecs.join(" · "));
    } else {
      setError("");
      setRefundDone({
        total: refundPromptTotal,
        lignes: lignes.map(function(d) { return {name: d.name, emoji: d.emoji || "🥤", qty: refundPrompt[d.id]}; })
      });
      setRefundPrompt(null);
      setPaid(true);
      if (navigator.vibrate) navigator.vibrate(60);
    }
  };

  var reset = function() { stopCam(); setCustomer(null); setCart({}); setPaid(false); setQrInput(""); setError(""); setMode("scan"); setTokenAmount(""); setRefundDone(null); setDepositPrompt(null); setRefundPrompt(null); setBenefits(null); setUseDiscount(true); };

  // Types de consigne (verre, pichet, coupe...)
  useEffect(function() {
    db.listDeposits(true).then(function(r) { setDeposits(r.data || []); });
  }, []);

  // Charge la carte du poste auquel le benevole est lie
  useEffect(function() {
    if (!posBinding) { setProducts([]); return; }
    var cancelled = false;
    setLoadingProducts(true);
    db.listProducts(posBinding.id).then(function(r) {
      if (cancelled) return;
      setProducts(r.data || []);
      setLoadingProducts(false);
    });
    return function() { cancelled = true; };
  }, [posBinding]);

  var unbind = function() {
    if (!confirm("Te délier de « " + posBinding.name + " » ? Tu devras rescanner un poste pour encaisser.")) return;
    stopCam();
    clearPosBinding();
    setCustomer(null); setCart({}); setPaid(false); setTokenAmount(""); setError("");
    setPosBinding(null);
  };

  // Bandeau du poste, affiche en haut de chaque ecran d'encaissement
  var posBar = posBinding && h("div", {key: "posbar", style: {maxWidth: 700, margin: "0 0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: C.deep, borderRadius: 12, padding: "10px 14px"}},
    h("div", {style: {display: "flex", alignItems: "center", gap: 10, minWidth: 0}},
      h("span", {style: {fontSize: 22}}, posBinding.emoji || "🏉"),
      h("div", {style: {minWidth: 0}},
        h("div", {style: {color: "rgba(255,255,255,.6)", fontSize: 9, letterSpacing: 2, fontFamily: "Oswald", fontWeight: 700, textTransform: "uppercase"}}, "Poste"),
        h("div", {style: {color: "#fff", fontFamily: "Oswald", fontSize: 15, fontWeight: 700, letterSpacing: .5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}, posBinding.name)
      )
    ),
    h("button", {onClick: unbind, style: {background: "rgba(255,255,255,.12)", border: "none", color: "#fff", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap"}}, "Changer")
  );

  // Pas de poste scanne : on bloque avant toute vente
  if (!posBinding) return h(PosBindScreen, {onBound: function(b) { setPosBinding(b); }});

  // Si pas de client → mode scan ou manuel
  if (!customer) return h("div", {style: {maxWidth: 600, margin: "0 auto"}}, posBar,
    h(Card, null,
      h("div", {style: {display: "flex", gap: 8, marginBottom: 20}},
        h("button", {onClick: function() { setMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "scan" ? C.blue : C.pale, color: mode === "scan" ? "#fff" : C.muted})}, "📷 Scanner QR"),
        h("button", {onClick: function() { stopCam(); setMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "manual" ? C.blue : C.pale, color: mode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      mode === "scan" && h("div", null,
        h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
          h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, playsInline: true, muted: true}),
          h("canvas", {ref: canvasRef, style: {display: "none"}}),
          h("div", {style: {position: "absolute", inset: "15%", border: "3px solid " + C.gold, borderRadius: 14, pointerEvents: "none", boxShadow: "0 0 0 9999px rgba(0,0,0,.3)"}}),
          h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, fontFamily: "Oswald", letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "🏉 VISER LE QR CODE")
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      ),
      mode === "manual" && h("div", null,
        h("input", {value: qrInput, onChange: function(e) { setQrInput(e.target.value); }, placeholder: "Nom, email ou code QR du supporter", style: IS, onKeyDown: function(e) { if (e.key === "Enter") lookup(); }, autoFocus: true}),
        h("button", {onClick: lookup, style: Object.assign({}, AB, {background: C.blue, width: "100%", marginTop: 8})}, "Chercher"),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      )
    )
  );

  // Ecran de confirmation : vente encaissee OU consignes remboursees
  if (paid) return h("div", {style: {maxWidth: 500, margin: "40px auto"}}, posBar,
    h(Card, {style: {textAlign: "center", padding: 40}},
      h("div", {style: {fontSize: 72, marginBottom: 16}}, refundDone ? "↩️" : "✅"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 26, color: C.green, fontWeight: 800, marginBottom: 6, letterSpacing: 1, textTransform: "uppercase"}},
        refundDone ? "Consigne remboursée !" : "Paiement validé !"),
      refundDone && h("div", {style: {background: "rgba(34,197,94,.08)", borderRadius: 12, padding: 14, marginBottom: 18}},
        refundDone.lignes.map(function(l, idx) {
          return h("div", {key: idx, style: {color: C.deep, fontSize: 15, fontWeight: 600, padding: "3px 0"}},
            l.emoji + " " + l.qty + "x " + l.name);
        }),
        h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800, color: C.green, marginTop: 8}}, "+" + formatEur(refundDone.total))
      ),
      h("div", {style: {color: C.muted, fontSize: 14, marginBottom: 4}}, "Nouveau solde de " + customer.name),
      h("div", {style: {fontFamily: "Oswald", fontSize: 40, color: C.deep, fontWeight: 800, marginBottom: 24}}, formatEur(customer.balance_cents)),
      h("button", {onClick: reset, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 16, fontSize: 16})}, "Client suivant")
    )
  );

  // Client trouvé → encaisser
  return h("div", {style: {maxWidth: 700, margin: "0 auto"}}, posBar,
    h(Card, {style: {background: "linear-gradient(135deg, " + C.pale + ", #fff)", marginBottom: 16, border: "2px solid " + C.blue}},
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
        h("div", {style: {flex: 1, minWidth: 0}},
          h("div", {style: {color: C.deep, fontSize: 18, fontWeight: 700, fontFamily: "Oswald", textTransform: "uppercase"}}, customer.name),
          h("div", {style: {color: C.muted, fontSize: 13, marginTop: 4}}, "Solde : ", h("strong", {style: {color: C.green}}, formatEur(customer.balance_cents))),
          // Le statut est affiché même sans avantage : le bénévole sait
          // tout de suite à qui il a affaire.
          benefits && h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, alignItems: "center"}},
            h("span", {style: {fontSize: 10, fontFamily: "Oswald", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#fff", background: benefits.color || C.muted, padding: "4px 10px", borderRadius: 999}},
              (benefits.emoji || "") + " " + benefits.type),
            benefits.free_entry && h("span", {style: {fontSize: 10, fontFamily: "Oswald", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: C.green, background: "rgba(34,197,94,.12)", padding: "4px 10px", borderRadius: 999}}, "Entrée gratuite"),
            discountMax > 0 && h("button", {
              onClick: function() { setUseDiscount(!useDiscount); },
              title: "Toucher pour appliquer ou retirer la remise",
              style: {fontSize: 10, fontFamily: "Oswald", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", border: "1px solid " + (useDiscount ? C.green : C.border), color: useDiscount ? C.green : C.muted, background: useDiscount ? "rgba(34,197,94,.12)" : "#fff", padding: "4px 10px", borderRadius: 999, cursor: "pointer"}
            }, (useDiscount ? "✓ " : "") + "Remise " + (discountKind === "shop" ? "boutique" : "buvette") + " −" + discountMax + " %")
          )
        ),
        h("button", {onClick: reset, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, fontSize: 22, cursor: "pointer", padding: "8px 14px", borderRadius: 10, fontWeight: 700, flexShrink: 0, alignSelf: "flex-start"}}, "×")
      )
    ),
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.muted, letterSpacing: 2, marginBottom: 12, textTransform: "uppercase"}}, "Produits"),
      h("div", {style: {display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10}},
        products.map(function(p) {
          return h("button", {key: p.id, onClick: function() { addCart(p.id); }, style: {background: cart[p.id] ? C.blue : C.pale, border: cart[p.id] ? "2px solid " + C.blue : "2px solid transparent", borderRadius: 12, padding: "14px 8px", cursor: "pointer", color: cart[p.id] ? "#fff" : C.deep, position: "relative", transition: "all .2s"}},
            h("div", {style: {fontSize: 32, marginBottom: 4}}, p.emoji),
            h("div", {style: {fontSize: 13, fontWeight: 700, fontFamily: "Oswald"}}, p.name),
            h("div", {style: {fontSize: 12, opacity: .8, marginTop: 2}}, formatEur(p.price_cents)),
            cart[p.id] && h("div", {style: {position: "absolute", top: 6, right: 6, background: C.gold, color: C.deep, borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Oswald", fontWeight: 800, fontSize: 13}}, cart[p.id])
          );
        })
      ),
      // ─── Remboursement de consigne ───
      deposits.length > 0 && h("div", {style: {marginTop: 16, paddingTop: 16, borderTop: "2px dashed " + C.border}},
        h("button", {
          onClick: function() { setRefundPrompt({}); },
          style: {width: "100%", background: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.35)", color: C.green, borderRadius: 10, padding: "13px 16px", cursor: "pointer", fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase"}
        }, "↩️ Rembourser une consigne"),
        h("div", {style: {fontSize: 11, color: C.muted, marginTop: 6, textAlign: "center"}}, "Le client rapporte un verre, un pichet…")
      ),

      // ─── Bloc Jeton / Retrait (montant libre) ───
      // DESACTIVE : fonctionnalite heritee de l'USV, pas demandee par le CSL.
      // Remettre SHOW_TOKEN a true pour la reactiver (le calcul du total et
      // l'ajout au panier sont restes en place).
      SHOW_TOKEN && h("div", {style: {marginTop: 16, paddingTop: 16, borderTop: "2px dashed " + C.border}},
        h("div", {style: {display: "flex", alignItems: "center", gap: 8, marginBottom: 10}},
          h("span", {style: {fontSize: 22}}, "🎰"),
          h("div", null,
            h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, color: C.deep, letterSpacing: 1, textTransform: "uppercase"}}, "Jeton / Retrait"),
            h("div", {style: {fontSize: 11, color: C.muted}}, "Échange du solde cashless contre des jetons")
          )
        ),
        h("div", {style: {position: "relative"}},
          h("input", {
            value: tokenAmount,
            onChange: function(e) {
              var v = e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".");
              setTokenAmount(v);
            },
            placeholder: "Montant libre en €",
            type: "text",
            inputMode: "decimal",
            style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 36, fontSize: 17, fontWeight: 700, textAlign: "right", padding: "14px 36px 14px 14px", border: tokenCents > 0 ? "2px solid " + C.gold : "2px solid " + C.border})
          }),
          h("span", {style: {position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.muted, fontWeight: 700, pointerEvents: "none", fontSize: 17}}, "€")
        ),
        // Boutons rapides jetons
        h("div", {style: {display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap"}},
          [1, 2, 5, 10].map(function(amt) {
            return h("button", {key: amt, onClick: function() { setTokenAmount(String(amt)); }, style: {flex: "1 1 60px", background: C.pale, border: "1px solid " + C.border, borderRadius: 8, padding: "8px 4px", cursor: "pointer", fontFamily: "Oswald", fontWeight: 700, fontSize: 13, color: C.deep}}, amt + "€");
          }),
          h("button", {onClick: function() { setTokenAmount(""); }, style: {flex: "1 1 60px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 8, padding: "8px 4px", cursor: "pointer", fontFamily: "Oswald", fontWeight: 700, fontSize: 13, color: C.red}}, "Effacer")
        )
      )
    ),
    (Object.keys(cart).length > 0 || tokenCents > 0) && h(Card, {style: {marginTop: 16}},
      h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.muted, letterSpacing: 2, marginBottom: 10, textTransform: "uppercase"}}, "Panier"),
      Object.keys(cart).map(function(pid) {
        var p = products.find(function(x) { return x.id === pid; });
        var q = cart[pid];
        return h("div", {key: pid, style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.border}},
          h("div", {style: {color: C.deep, fontSize: 14}}, q + "× " + p.emoji + " " + p.name),
          h("div", {style: {display: "flex", alignItems: "center", gap: 10}},
            h("div", {style: {color: C.blue, fontFamily: "Oswald", fontSize: 15, fontWeight: 700}}, formatEur(p.price_cents * q)),
            h("button", {onClick: function() { rmCart(pid); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 28, height: 28, borderRadius: 8, cursor: "pointer", fontSize: 16, fontWeight: 700}}, "−")
          )
        );
      }),
      // Ligne jeton dans le panier
      tokenCents > 0 && h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.border}},
        h("div", {style: {color: C.deep, fontSize: 14}}, "🎰 Jeton / Retrait"),
        h("div", {style: {display: "flex", alignItems: "center", gap: 10}},
          h("div", {style: {color: C.gold, fontFamily: "Oswald", fontSize: 15, fontWeight: 700}}, formatEur(tokenCents)),
          h("button", {onClick: function() { setTokenAmount(""); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 28, height: 28, borderRadius: 8, cursor: "pointer", fontSize: 16, fontWeight: 700}}, "−")
        )
      ),
      // La remise s'applique aux articles, jamais aux consignes : elles
      // sont ajoutées après, dans la popup.
      remiseCents > 0 && h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.border}},
        h("div", {style: {color: C.green, fontSize: 14, fontWeight: 600}},
          (benefits && benefits.emoji ? benefits.emoji + " " : "") + "Remise " + (benefits ? benefits.type : "") + " −" + discountPct + " %"),
        h("div", {style: {color: C.green, fontFamily: "Oswald", fontSize: 15, fontWeight: 700}}, "−" + formatEur(remiseCents))
      ),
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0 0", marginTop: 8, borderTop: "2px solid " + C.deep}},
        h("div", {style: {color: C.deep, fontFamily: "Oswald", fontSize: 18, fontWeight: 700, letterSpacing: 1}}, "TOTAL"),
        h("div", {style: {textAlign: "right"}},
          remiseCents > 0 && h("div", {style: {color: C.muted, fontSize: 14, textDecoration: "line-through"}}, formatEur(totalCents)),
          h("div", {style: {color: C.blue, fontFamily: "Oswald", fontSize: 28, fontWeight: 800}}, formatEur(aPayerCents))
        )
      ),
      h("button", {onClick: askDeposits, disabled: paying || totalCents === 0 || customer.balance_cents < aPayerCents, style: Object.assign({}, AB, {background: customer.balance_cents < aPayerCents ? C.red : C.green, width: "100%", padding: 18, fontSize: 17, marginTop: 16, cursor: paying ? "wait" : "pointer", opacity: totalCents === 0 ? .5 : 1})},
        paying ? "..." : customer.balance_cents < aPayerCents ? "Solde insuffisant" : "Encaisser " + formatEur(aPayerCents)
      ),
      cartDeposits.length > 0 && h("div", {style: {fontSize: 11, color: C.muted, marginTop: 8, textAlign: "center"}}, "Les consignes seront demandées à l'étape suivante"),
      error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
    ),

    // ─────────────────── POPUP : consignes du panier ───────────────────
    depositPrompt && h("div", {style: MODAL_BG, onClick: function() { if (!paying) setDepositPrompt(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6}}, "Consignes"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.5}},
          "Le client a-t-il déjà son contenant ? Retire les consignes qu'il n'a pas à payer."),
        cartDeposits.map(function(cd) {
          var q = depositPrompt[cd.deposit.id] || 0;
          return h("div", {key: cd.deposit.id, style: {background: C.pale, borderRadius: 12, padding: 14, marginBottom: 10}},
            h("div", {style: {display: "flex", alignItems: "center", gap: 10, marginBottom: 10}},
              h("span", {style: {fontSize: 26}}, cd.deposit.emoji),
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, cd.deposit.name),
                h("div", {style: {color: C.muted, fontSize: 12}}, formatEur(cd.deposit.price_cents) + " · " + cd.qty + " article" + (cd.qty > 1 ? "s" : "") + " concerné" + (cd.qty > 1 ? "s" : ""))
              ),
              h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: q > 0 ? C.blue : C.muted}}, formatEur(q * cd.deposit.price_cents))
            ),
            h("div", {style: {display: "flex", alignItems: "center", gap: 10}},
              h("button", {onClick: function() {
                var n = Object.assign({}, depositPrompt); n[cd.deposit.id] = Math.max(0, q - 1); setDepositPrompt(n);
              }, style: {width: 44, height: 44, borderRadius: 10, border: "none", background: "#fff", color: C.deep, fontSize: 22, fontWeight: 700, cursor: "pointer"}}, "−"),
              h("div", {style: {flex: 1, textAlign: "center", fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.deep}}, q),
              h("button", {onClick: function() {
                var n = Object.assign({}, depositPrompt); n[cd.deposit.id] = Math.min(cd.qty, q + 1); setDepositPrompt(n);
              }, style: {width: 44, height: 44, borderRadius: 10, border: "none", background: "#fff", color: C.deep, fontSize: 22, fontWeight: 700, cursor: "pointer"}}, "+")
            ),
            h("div", {style: {display: "flex", gap: 8, marginTop: 10}},
              h("button", {onClick: function() {
                var n = Object.assign({}, depositPrompt); n[cd.deposit.id] = 0; setDepositPrompt(n);
              }, style: {flex: 1, padding: "9px 8px", borderRadius: 8, border: "none", background: q === 0 ? C.green : "#fff", color: q === 0 ? "#fff" : C.muted, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Il a son contenant"),
              h("button", {onClick: function() {
                var n = Object.assign({}, depositPrompt); n[cd.deposit.id] = cd.qty; setDepositPrompt(n);
              }, style: {flex: 1, padding: "9px 8px", borderRadius: 8, border: "none", background: q === cd.qty ? C.blue : "#fff", color: q === cd.qty ? "#fff" : C.muted, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Tout consigner")
            )
          );
        }),
        h("div", {style: {borderTop: "2px solid " + C.deep, marginTop: 14, paddingTop: 12}},
          h("div", {style: {display: "flex", justifyContent: "space-between", fontSize: 13, color: C.muted, marginBottom: 4}},
            h("span", null, "Produits"), h("span", null, formatEur(totalCents))),
          h("div", {style: {display: "flex", justifyContent: "space-between", fontSize: 13, color: C.muted, marginBottom: 8}},
            h("span", null, "Consignes"), h("span", null, formatEur(depositPromptTotal))),
          h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
            h("div", {style: {color: C.deep, fontFamily: "Oswald", fontSize: 17, fontWeight: 700, letterSpacing: 1}}, "À PAYER"),
            h("div", {style: {color: C.blue, fontFamily: "Oswald", fontSize: 26, fontWeight: 800}}, formatEur(totalCents + depositPromptTotal)))
        ),
        customer.balance_cents < (totalCents + depositPromptTotal) && h("div", {style: {color: C.red, fontSize: 13, marginTop: 10, textAlign: "center", fontWeight: 600}}, "Solde insuffisant (" + formatEur(customer.balance_cents) + ")"),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setDepositPrompt(null); }, disabled: paying, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 14, fontSize: 13})}, "Annuler"),
          h("button", {onClick: function() { pay(depositPromptTotal); }, disabled: paying || customer.balance_cents < (totalCents + depositPromptTotal), style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 14, fontSize: 14, opacity: customer.balance_cents < (totalCents + depositPromptTotal) ? .5 : 1})},
            paying ? "..." : "Encaisser " + formatEur(totalCents + depositPromptTotal))
        )
      )
    ),

    // ─────────────────── POPUP : remboursement de consigne ───────────────────
    refundPrompt && h("div", {style: MODAL_BG, onClick: function() { if (!refunding) setRefundPrompt(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6}}, "Retour de consigne"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5}}, "Le montant est recrédité sur le compte de " + customer.name + "."),
        h("div", {style: LABEL}, "Ce que le client rapporte"),
        deposits.map(function(d) {
          var q = refundPrompt[d.id] || 0;
          return h("div", {key: d.id, style: {display: "flex", alignItems: "center", gap: 10, background: q > 0 ? "rgba(34,197,94,.08)" : C.pale, borderRadius: 12, padding: 12, marginBottom: 8, transition: "background .2s"}},
            h("span", {style: {fontSize: 26}}, d.emoji || "🥤"),
            h("div", {style: {flex: 1, minWidth: 0}},
              h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, d.name),
              h("div", {style: {color: C.muted, fontSize: 12}}, formatEur(d.price_cents) + " l'unité")
            ),
            h("button", {onClick: function() {
              var n = Object.assign({}, refundPrompt); n[d.id] = Math.max(0, q - 1); setRefundPrompt(n);
            }, style: {width: 40, height: 40, borderRadius: 10, border: "none", background: "#fff", color: C.deep, fontSize: 20, fontWeight: 700, cursor: "pointer"}}, "−"),
            h("div", {style: {minWidth: 34, textAlign: "center", fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: q > 0 ? C.green : C.muted}}, q),
            h("button", {onClick: function() {
              var n = Object.assign({}, refundPrompt); n[d.id] = Math.min(50, q + 1); setRefundPrompt(n);
            }, style: {width: 40, height: 40, borderRadius: 10, border: "none", background: "#fff", color: C.deep, fontSize: 20, fontWeight: 700, cursor: "pointer"}}, "+")
          );
        }),
        h("div", {style: {background: "rgba(34,197,94,.08)", borderRadius: 12, padding: 14, textAlign: "center", marginTop: 6}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, fontFamily: "Oswald", fontWeight: 700, textTransform: "uppercase"}}, "À recréditer"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 30, fontWeight: 800, color: refundPromptTotal > 0 ? C.green : C.muted}}, "+" + formatEur(refundPromptTotal))
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 12}}, error),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setRefundPrompt(null); }, disabled: refunding, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 14, fontSize: 13})}, "Annuler"),
          h("button", {onClick: doRefund, disabled: refunding || refundPromptTotal === 0, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 14, fontSize: 14, opacity: refundPromptTotal === 0 ? .5 : 1})}, refunding ? "..." : "Rembourser " + formatEur(refundPromptTotal))
        )
      )
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// VUE : RECHARGE (scan QR + montants fixes)
// ═══════════════════════════════════════════════════════════════════
function RechargeView(props) {
  var account = props.account;
  var s1 = useState("scan"); var mode = s1[0], setMode = s1[1];
  var s2 = useState(null); var customer = s2[0], setCustomer = s2[1];
  var s3 = useState(""); var qrInput = s3[0], setQrInput = s3[1];
  var s4 = useState(""); var msg = s4[0], setMsg = s4[1];
  var s5 = useState(false); var charging = s5[0], setCharging = s5[1];
  var s6 = useState(""); var error = s6[0], setError = s6[1];
  var s7 = useState(""); var customAmount = s7[0], setCustomAmount = s7[1];
  var s8 = useState(null); var selectedAmount = s8[0], setSelectedAmount = s8[1];
  var videoRef = useRef(null), canvasRef = useRef(null), streamRef = useRef(null), scanRef = useRef(null), scanBusyRef = useRef(false);

  var startCam = async function() {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment", width: {ideal: 1280}, height: {ideal: 720}}, audio: false});
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", true);
        await videoRef.current.play();
      }
      scanRef.current = setInterval(scanFrame, 200);
    } catch (e) {
      setError("Caméra non disponible.");
      setMode("manual");
    }
  };
  var stopCam = function() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(t) { t.stop(); }); streamRef.current = null; }
    if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
    scanBusyRef.current = false;
  };
  var scanFrame = async function() {
    if (scanBusyRef.current) return;
    var v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
    scanBusyRef.current = true;
    try {
      var w = v.videoWidth, hh = v.videoHeight;
      if (!w || !hh) { scanBusyRef.current = false; return; }
      cv.width = w; cv.height = hh;
      var ctx = cv.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(v, 0, 0, w, hh);
      var imageData = ctx.getImageData(0, 0, w, hh);
      var code = window.jsQR(imageData.data, w, hh, {inversionAttempts: "dontInvert"});
      if (code && code.data && code.data.indexOf("CSL-") === 0) {
        stopCam();
        var r = await db.findAccount(code.data);
        if (r.data) {
          setCustomer(r.data); setMsg(""); setError("");
          if (navigator.vibrate) navigator.vibrate(80);
        } else { setError("QR valide mais compte introuvable"); }
      }
    } catch (e) {} finally { scanBusyRef.current = false; }
  };

  useEffect(function() {
    if (mode === "scan" && !customer) {
      startCam();
    } else {
      stopCam();
    }
    return function() { stopCam(); };
  }, [mode, customer]);

  var lookup = async function() {
    setError("");
    var r = await db.findAccount(qrInput);
    if (!r.data) { setError("Compte non trouvé"); return; }
    setCustomer(r.data); setMsg(""); setError("");
  };

  var doRecharge = async function(amountEur) {
    if (charging || !customer) return;
    setCharging(true); setMsg("");
    var r = await db.rechargeCounter({accountId: customer.id, amountCents: amountEur * 100, cashierName: account.name});
    if (r.error || !r.data.success) {
      setMsg("❌ " + (r.error ? r.error.message : r.data.error));
    } else {
      setCustomer(Object.assign({}, customer, {balance_cents: r.data.new_balance_cents}));
      setMsg("✅ +" + amountEur + "€ rechargés ! Nouveau solde : " + formatEur(r.data.new_balance_cents));
    }
    setCharging(false);
  };

  var reset = function() { stopCam(); setCustomer(null); setQrInput(""); setMsg(""); setError(""); setMode("scan"); setSelectedAmount(null); setCustomAmount(""); };

  if (!customer) return h("div", {style: {maxWidth: 600, margin: "0 auto"}},
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase", textAlign: "center"}}, "Scanner pour recharger"),
      h("div", {style: {display: "flex", gap: 8, marginBottom: 20}},
        h("button", {onClick: function() { setMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "scan" ? C.blue : C.pale, color: mode === "scan" ? "#fff" : C.muted})}, "📷 QR Code"),
        h("button", {onClick: function() { stopCam(); setMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "manual" ? C.blue : C.pale, color: mode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      mode === "scan" && h("div", null,
        h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
          h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, playsInline: true, muted: true}),
          h("canvas", {ref: canvasRef, style: {display: "none"}}),
          h("div", {style: {position: "absolute", inset: "15%", border: "3px solid " + C.gold, borderRadius: 14, pointerEvents: "none", boxShadow: "0 0 0 9999px rgba(0,0,0,.3)"}}),
          h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, fontFamily: "Oswald", letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "💰 VISER LE QR")
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      ),
      mode === "manual" && h("div", null,
        h("input", {value: qrInput, onChange: function(e) { setQrInput(e.target.value); }, placeholder: "Nom, email ou QR code", style: IS, onKeyDown: function(e) { if (e.key === "Enter") lookup(); }, autoFocus: true}),
        h("button", {onClick: lookup, style: Object.assign({}, AB, {background: C.blue, width: "100%", marginTop: 8})}, "Chercher"),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      )
    )
  );

  return h("div", {style: {maxWidth: 600, margin: "0 auto"}},
    h(Card, {style: {background: "linear-gradient(135deg, " + C.pale + ", #fff)", marginBottom: 16, border: "2px solid " + C.blue}},
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
        h("div", null,
          h("div", {style: {color: C.deep, fontSize: 20, fontWeight: 800, fontFamily: "Oswald", textTransform: "uppercase"}}, customer.name),
          h("div", {style: {color: C.muted, fontSize: 13, marginTop: 4}}, "Solde actuel : ", h("strong", {style: {color: C.green}}, formatEur(customer.balance_cents)))
        ),
        h("button", {onClick: reset, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, fontSize: 22, cursor: "pointer", padding: "8px 14px", borderRadius: 10, fontWeight: 700}}, "×")
      )
    ),
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase", textAlign: "center"}}, "💰 Montant à recharger"),
      // Grille 7 boutons fixes — paliers avec bonus (paid = ce qu'on encaisse, credited = ce qu'on crédite)
      h("div", {className: "csl-recharge-grid", style: {display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 14}},
        h("style", null,
          "@media (max-width: 960px) { .csl-recharge-grid { grid-template-columns: repeat(4, 1fr) !important; } }" +
          "@media (max-width: 640px) { .csl-recharge-grid { grid-template-columns: repeat(3, 1fr) !important; } }" +
          "@media (max-width: 420px) { .csl-recharge-grid { grid-template-columns: repeat(2, 1fr) !important; } }"
        ),
        [
          {paid: 5,   credited: 5,   bonus: 0},
          {paid: 10,  credited: 10,  bonus: 0},
          {paid: 20,  credited: 23,  bonus: 3},
          {paid: 30,  credited: 36,  bonus: 6},
          {paid: 40,  credited: 48,  bonus: 8},
          {paid: 50,  credited: 60,  bonus: 10},
          {paid: 100, credited: 123, bonus: 23}
        ].map(function(tier) {
          var isSelected = selectedAmount === tier.credited;
          return h("button", {
            key: tier.paid,
            onClick: function() { setSelectedAmount(tier.credited); setCustomAmount(""); setMsg(""); },
            disabled: charging,
            style: {
              background: isSelected ? "linear-gradient(135deg, " + C.green + ", #16a34a)" : "#fff",
              border: isSelected ? "3px solid #16a34a" : "2px solid " + C.border,
              borderRadius: 12,
              padding: "18px 6px",
              cursor: charging ? "wait" : "pointer",
              color: isSelected ? "#fff" : C.deep,
              boxShadow: isSelected ? "0 6px 20px rgba(34,197,94,.35)" : "0 1px 3px rgba(0,0,0,.04)",
              transition: "all .2s",
              opacity: charging ? .5 : 1,
              transform: isSelected ? "scale(1.03)" : "scale(1)",
              position: "relative"
            }
          },
            // Badge bonus en haut à droite
            tier.bonus > 0 && h("div", {
              style: {
                position: "absolute", top: -8, right: -8,
                background: isSelected ? "#fff" : C.gold,
                color: isSelected ? C.green : C.deep,
                fontFamily: "Oswald", fontWeight: 800, fontSize: 11,
                padding: "3px 8px", borderRadius: 50,
                boxShadow: "0 2px 6px rgba(0,0,0,.15)",
                letterSpacing: .5
              }
            }, "+" + tier.bonus + "€"),
            // Montant payé en grand
            h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800}}, tier.paid + "€"),
            // Texte secondaire : ce qui sera crédité
            tier.bonus > 0
              ? h("div", {style: {fontSize: 10, opacity: .85, letterSpacing: .5, marginTop: 3, fontFamily: "Oswald", fontWeight: 600}}, "= " + tier.credited + "€ crédités")
              : h("div", {style: {fontSize: 10, opacity: .7, letterSpacing: 1, textTransform: "uppercase", marginTop: 3}}, isSelected ? "✓ Sélectionné" : "Sélectionner")
          );
        })
      ),
      // Champ montant libre (sélectionne aussi)
      h("div", {style: {position: "relative", marginBottom: 14}},
        h("input", {
          value: customAmount,
          onChange: function(e) {
            var v = e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".");
            setCustomAmount(v);
            var n = parseFloat(v);
            if (n && n > 0) { setSelectedAmount(n); }
            else if (!v) { setSelectedAmount(null); }
            setMsg("");
          },
          placeholder: "Ou montant libre",
          type: "text",
          inputMode: "decimal",
          disabled: charging,
          style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 36, fontSize: 17, fontWeight: 700, textAlign: "right", height: "auto", padding: "16px 36px 16px 16px", border: (customAmount && parseFloat(customAmount) > 0) ? "2px solid " + C.green : "2px solid " + C.border})
        }),
        h("span", {style: {position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.muted, fontWeight: 700, pointerEvents: "none", fontSize: 17}}, "€")
      ),
      // Récap pré-validation : montant à encaisser + montant à créditer + bonus si applicable
      selectedAmount && (function() {
        // Trouver si le montant sélectionné correspond à un palier connu
        var tier = [
          {paid: 5,   credited: 5,   bonus: 0},
          {paid: 10,  credited: 10,  bonus: 0},
          {paid: 20,  credited: 23,  bonus: 3},
          {paid: 30,  credited: 36,  bonus: 6},
          {paid: 40,  credited: 48,  bonus: 8},
          {paid: 50,  credited: 60,  bonus: 10},
          {paid: 100, credited: 123, bonus: 23}
        ].find(function(t) { return t.credited === selectedAmount; });
        return h("div", {style: {background: "linear-gradient(135deg, rgba(208,33,40,.05), rgba(208,33,40,.02))", border: "2px dashed " + C.blue, borderRadius: 12, padding: 14, marginBottom: 14}},
          h("div", {style: {fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: "uppercase", fontWeight: 700, textAlign: "center", marginBottom: 8}}, "Récapitulatif"),
          h("div", {style: {display: "flex", justifyContent: "space-around", alignItems: "center", gap: 8}},
            // Encaissé
            h("div", {style: {textAlign: "center", flex: 1}},
              h("div", {style: {fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2}}, "À encaisser"),
              h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800, color: C.deep}}, (tier ? tier.paid : selectedAmount) + "€")
            ),
            // Bonus si applicable
            tier && tier.bonus > 0 && h("div", {style: {textAlign: "center", flex: 1}},
              h("div", {style: {fontSize: 18, color: C.gold, marginBottom: 2}}, "+"),
              h("div", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.gold}}, tier.bonus + "€"),
              h("div", {style: {fontSize: 9, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginTop: 2, fontWeight: 700}}, "🎁 Bonus")
            ),
            // Flèche
            h("div", {style: {color: C.muted, fontSize: 18}}, "→"),
            // Crédité
            h("div", {style: {textAlign: "center", flex: 1}},
              h("div", {style: {fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2}}, "Crédité"),
              h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800, color: C.green}}, "+" + selectedAmount + "€")
            )
          )
        );
      })(),
      // BOUTON VALIDATION
      h("button", {
        onClick: function() {
          if (!selectedAmount || selectedAmount <= 0) return;
          doRecharge(selectedAmount);
          setSelectedAmount(null);
          setCustomAmount("");
        },
        disabled: charging || !selectedAmount || selectedAmount <= 0,
        style: {
          width: "100%",
          background: (charging || !selectedAmount) ? "#cbd5e1" : "linear-gradient(135deg, " + C.green + ", #16a34a)",
          border: "none",
          borderRadius: 14,
          padding: "20px 16px",
          color: "#fff",
          fontFamily: "Oswald",
          fontSize: 18,
          fontWeight: 800,
          letterSpacing: 2,
          textTransform: "uppercase",
          cursor: (charging || !selectedAmount) ? "not-allowed" : "pointer",
          boxShadow: (charging || !selectedAmount) ? "none" : "0 6px 20px rgba(34,197,94,.35)",
          transition: "all .2s"
        }
      },
        charging ? "⏳ Recharge en cours..." : (selectedAmount ? "✅ Valider la recharge de +" + selectedAmount + "€" : "Sélectionne un montant ci-dessus")
      ),
      msg && h("div", {style: {textAlign: "center", marginTop: 16, fontSize: 14, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600, padding: 12, background: msg.indexOf("✅") === 0 ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)", borderRadius: 10}}, msg),
      h("button", {onClick: reset, style: Object.assign({}, AB, {background: C.pale, color: C.deep, width: "100%", marginTop: 16, fontSize: 13})}, "Supporter suivant")
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// VUE : BOUTIQUE (scan QR + saisie article/taille/couleur + prix)
// ═══════════════════════════════════════════════════════════════════
function BoutiqueView(props) {
  var account = props.account;
  var s1 = useState("scan"); var mode = s1[0], setMode = s1[1];
  var s2 = useState(null); var customer = s2[0], setCustomer = s2[1];
  var s3 = useState(""); var qrInput = s3[0], setQrInput = s3[1];
  var s4 = useState(""); var article = s4[0], setArticle = s4[1];
  var s5 = useState(""); var size = s5[0], setSize = s5[1];
  var s6 = useState(""); var color = s6[0], setColor = s6[1];
  var s7 = useState(""); var price = s7[0], setPrice = s7[1];
  var s8 = useState(""); var msg = s8[0], setMsg = s8[1];
  var s9 = useState(false); var processing = s9[0], setProcessing = s9[1];
  var s10 = useState(""); var error = s10[0], setError = s10[1];
  var s11 = useState(false); var sold = s11[0], setSold = s11[1];
  var videoRef = useRef(null), canvasRef = useRef(null), streamRef = useRef(null), scanRef = useRef(null), scanBusyRef = useRef(false);

  var startCam = async function() {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment", width: {ideal: 1280}, height: {ideal: 720}}, audio: false});
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.setAttribute("playsinline", true); await videoRef.current.play(); }
      scanRef.current = setInterval(scanFrame, 200);
    } catch (e) { setError("Caméra non disponible."); setMode("manual"); }
  };
  var stopCam = function() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(t) { t.stop(); }); streamRef.current = null; }
    if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
    scanBusyRef.current = false;
  };
  var scanFrame = async function() {
    if (scanBusyRef.current) return;
    var v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
    scanBusyRef.current = true;
    try {
      var w = v.videoWidth, hh = v.videoHeight;
      if (!w || !hh) { scanBusyRef.current = false; return; }
      cv.width = w; cv.height = hh;
      var ctx = cv.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(v, 0, 0, w, hh);
      var imageData = ctx.getImageData(0, 0, w, hh);
      var code = window.jsQR(imageData.data, w, hh, {inversionAttempts: "dontInvert"});
      if (code && code.data && code.data.indexOf("CSL-") === 0 && code.data.indexOf("CSL-TICKET") !== 0) {
        stopCam();
        var r = await db.findAccount(code.data);
        if (r.data) { setCustomer(r.data); if (navigator.vibrate) navigator.vibrate(80); }
        else { setError("QR valide mais compte introuvable"); }
      }
    } catch (e) {} finally { scanBusyRef.current = false; }
  };
  useEffect(function() {
    if (mode === "scan" && !customer) { startCam(); } else { stopCam(); }
    return function() { stopCam(); };
  }, [mode, customer]);

  var lookup = async function() {
    setError("");
    var r = await db.findAccount(qrInput);
    if (!r.data) { setError("Compte non trouvé"); return; }
    setCustomer(r.data);
  };

  var doSale = async function() {
    if (processing || !customer) return;
    if (!article.trim()) { setMsg("❌ Article requis"); return; }
    var priceCents = toCents(price);
    if (!priceCents || priceCents <= 0) { setMsg("❌ Prix invalide"); return; }
    setProcessing(true); setMsg("");
    var r = await db.boutiqueSale(customer.id, account.id, article.trim(), size.trim(), color.trim(), priceCents);
    setProcessing(false);
    if (r.error || !r.data.success) {
      if (r.data && r.data.error === "insufficient_balance") {
        setMsg("❌ Solde insuffisant (" + formatEur(r.data.balance_cents) + " disponible)");
      } else {
        setMsg("❌ " + (r.error ? r.error.message : r.data.error));
      }
      return;
    }
    setCustomer(Object.assign({}, customer, {balance_cents: r.data.new_balance_cents}));
    setSold(true);
  };

  var reset = function() {
    stopCam(); setCustomer(null); setQrInput(""); setArticle(""); setSize(""); setColor(""); setPrice("");
    setMsg(""); setError(""); setSold(false); setMode("scan");
  };

  // Sans client → scan/manuel
  if (!customer) return h("div", {style: {maxWidth: 600, margin: "0 auto"}},
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase", textAlign: "center"}}, "Scanner un supporter"),
      h("div", {style: {display: "flex", gap: 8, marginBottom: 20}},
        h("button", {onClick: function() { setMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "scan" ? C.blue : C.pale, color: mode === "scan" ? "#fff" : C.muted})}, "📷 QR Code"),
        h("button", {onClick: function() { stopCam(); setMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: mode === "manual" ? C.blue : C.pale, color: mode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      mode === "scan" && h("div", null,
        h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
          h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, playsInline: true, muted: true}),
          h("canvas", {ref: canvasRef, style: {display: "none"}}),
          h("div", {style: {position: "absolute", inset: "15%", border: "3px solid " + C.gold, borderRadius: 14, pointerEvents: "none", boxShadow: "0 0 0 9999px rgba(0,0,0,.3)"}}),
          h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, fontFamily: "Oswald", letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "🛍 VISER LE QR")
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      ),
      mode === "manual" && h("div", null,
        h("input", {value: qrInput, onChange: function(e) { setQrInput(e.target.value); }, placeholder: "Nom, email ou QR du supporter", style: IS, onKeyDown: function(e) { if (e.key === "Enter") lookup(); }, autoFocus: true}),
        h("button", {onClick: lookup, style: Object.assign({}, AB, {background: C.blue, width: "100%", marginTop: 8})}, "Chercher"),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      )
    )
  );

  // Vente OK
  if (sold) return h("div", {style: {maxWidth: 500, margin: "40px auto"}},
    h(Card, {style: {textAlign: "center", padding: 40}},
      h("div", {style: {fontSize: 72, marginBottom: 16}}, "✅"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 26, color: C.green, fontWeight: 800, marginBottom: 6, letterSpacing: 1, textTransform: "uppercase"}}, "Vente validée"),
      h("div", {style: {color: C.muted, fontSize: 14, marginBottom: 4}}, "Nouveau solde de " + customer.name),
      h("div", {style: {fontFamily: "Oswald", fontSize: 40, color: C.deep, fontWeight: 800, marginBottom: 24}}, formatEur(customer.balance_cents)),
      h("button", {onClick: reset, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 16, fontSize: 16})}, "Client suivant")
    )
  );

  // Formulaire vente
  return h("div", {style: {maxWidth: 700, margin: "0 auto"}},
    h(Card, {style: {background: "linear-gradient(135deg, " + C.pale + ", #fff)", marginBottom: 16, border: "2px solid " + C.blue}},
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
        h("div", null,
          h("div", {style: {color: C.deep, fontSize: 18, fontWeight: 700, fontFamily: "Oswald", textTransform: "uppercase"}}, customer.name),
          h("div", {style: {color: C.muted, fontSize: 13, marginTop: 4}}, "Solde : ", h("strong", {style: {color: C.green}}, formatEur(customer.balance_cents)))
        ),
        h("button", {onClick: reset, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, fontSize: 22, cursor: "pointer", padding: "8px 14px", borderRadius: 10, fontWeight: 700}}, "×")
      )
    ),
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase"}}, "🛍 Détails de la vente"),
      h(FormField, {label: "Article *", inputProps: {value: article, onChange: function(e) { setArticle(e.target.value); }, placeholder: "Ex: T-shirt Lons-le-Saunier, Casquette, Écharpe", autoFocus: true}}),
      h("div", {style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10}},
        h(FormField, {label: "Taille (optionnel)", inputProps: {value: size, onChange: function(e) { setSize(e.target.value); }, placeholder: "S, M, L, XL..."}}),
        h(FormField, {label: "Couleur (optionnel)", inputProps: {value: color, onChange: function(e) { setColor(e.target.value); }, placeholder: "Bleu, Blanc..."}})
      ),
      h(FormField, {label: "Prix (€) *", inputProps: {value: price, onChange: function(e) { setPrice(e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".")); }, placeholder: "Ex: 15.00", type: "text", inputMode: "decimal", style: {fontSize: 20, fontWeight: 700, textAlign: "center"}}}),
      h("button", {onClick: doSale, disabled: processing || !article.trim() || !price, style: Object.assign({}, AB, {background: (processing || !article.trim() || !price) ? C.muted : C.green, width: "100%", padding: 18, fontSize: 17, marginTop: 6, cursor: processing ? "wait" : "pointer", opacity: (processing || !article.trim() || !price) ? .5 : 1})},
        processing ? "..." : "Vendre " + (price ? formatEur(toCents(price)) : "")
      ),
      msg && h("div", {style: {marginTop: 12, textAlign: "center", fontSize: 14, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600, padding: 10, background: msg.indexOf("✅") === 0 ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)", borderRadius: 10}}, msg)
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// VUE : BILLETTERIE (Mode A : valider billet match / Mode B : vendre entrée)
// ═══════════════════════════════════════════════════════════════════
function BilletterieView(props) {
  var account = props.account;
  // billetMode: "validate" (valider QR billet) | "sell" (vendre entrée avec solde cashless)
  var s1 = useState("validate"); var billetMode = s1[0], setBilletMode = s1[1];
  // scanMode: "scan" | "manual"
  var s2 = useState("scan"); var scanMode = s2[0], setScanMode = s2[1];
  var s3 = useState(""); var qrInput = s3[0], setQrInput = s3[1];
  var s4 = useState(null); var result = s4[0], setResult = s4[1]; // résultat validation
  var s5 = useState(null); var buyer = s5[0], setBuyer = s5[1]; // mode sell
  var s6 = useState(false); var processing = s6[0], setProcessing = s6[1];
  var s7 = useState(""); var error = s7[0], setError = s7[1];
  var s8 = useState(null); var sellResult = s8[0], setSellResult = s8[1];
  var s9 = useState(""); var customPlein = s9[0], setCustomPlein = s9[1];
  var s10 = useState(""); var customReduit = s10[0], setCustomReduit = s10[1];
  // Prix par défaut, modifiables par l'admin via les produits TICKET-PLEIN / TICKET-REDUIT
  var videoRef = useRef(null), canvasRef = useRef(null), streamRef = useRef(null), scanRef = useRef(null), scanBusyRef = useRef(false);

  // Récupérer les prix billets depuis la table products
  var s11 = useState(800); var priceFullCents = s11[0], setPriceFullCents = s11[1];
  var s12 = useState(500); var priceReducedCents = s12[0], setPriceReducedCents = s12[1];
  useEffect(function() {
    (async function() {
      var all = await db.listAllProducts();
      var pf = (all.data || []).find(function(p) { return (p.name || "").toUpperCase().indexOf("TICKET-PLEIN") >= 0 || (p.name || "").toUpperCase() === "ENTRÉE PLEIN"; });
      var pr = (all.data || []).find(function(p) { return (p.name || "").toUpperCase().indexOf("TICKET-REDUIT") >= 0 || (p.name || "").toUpperCase() === "ENTRÉE REDUIT"; });
      if (pf) setPriceFullCents(pf.price_cents);
      if (pr) setPriceReducedCents(pr.price_cents);
    })();
  }, []);

  var startCam = async function() {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment", width: {ideal: 1280}, height: {ideal: 720}}, audio: false});
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.setAttribute("playsinline", true); await videoRef.current.play(); }
      scanRef.current = setInterval(scanFrame, 200);
    } catch (e) { setError("Caméra non disponible."); setScanMode("manual"); }
  };
  var stopCam = function() {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(t) { t.stop(); }); streamRef.current = null; }
    if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
    scanBusyRef.current = false;
  };
  var scanFrame = async function() {
    if (scanBusyRef.current) return;
    var v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv || v.readyState !== 4 || !window.jsQR) return;
    scanBusyRef.current = true;
    try {
      var w = v.videoWidth, hh = v.videoHeight;
      if (!w || !hh) { scanBusyRef.current = false; return; }
      cv.width = w; cv.height = hh;
      var ctx = cv.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(v, 0, 0, w, hh);
      var imageData = ctx.getImageData(0, 0, w, hh);
      var code = window.jsQR(imageData.data, w, hh, {inversionAttempts: "dontInvert"});
      if (code && code.data) {
        if (billetMode === "validate" && code.data.indexOf("CSL-TICKET-") === 0) {
          stopCam();
          await processValidation(code.data);
        } else if (billetMode === "sell" && code.data.indexOf("CSL-") === 0 && code.data.indexOf("CSL-TICKET-") !== 0) {
          stopCam();
          var r = await db.findAccount(code.data);
          if (r.data) { setBuyer(r.data); if (navigator.vibrate) navigator.vibrate(80); }
          else { setError("Compte cashless introuvable"); }
        }
      }
    } catch (e) {} finally { scanBusyRef.current = false; }
  };
  useEffect(function() {
    if (scanMode === "scan" && !result && !buyer && !sellResult) {
      startCam();
    } else {
      // États "résultat" actifs → s'assurer que la caméra est bien stoppée
      stopCam();
    }
    return function() { stopCam(); };
  }, [scanMode, billetMode, result, buyer, sellResult]);

  // Mode A : validation d'un QR billet match
  var processValidation = async function(qrCode) {
    setProcessing(true); setError("");
    var r = await db.validateTicket(qrCode, account.id);
    setProcessing(false);
    if (r.error) { setError("Erreur : " + r.error.message); return; }
    setResult(r.data);
    if (r.data.success && navigator.vibrate) navigator.vibrate([100, 50, 100]);
  };

  var lookupValidate = async function() {
    if (!qrInput.trim()) return;
    await processValidation(qrInput.trim());
  };

  // Mode B : recherche manuelle supporter
  var lookupBuyer = async function() {
    setError("");
    var r = await db.findAccount(qrInput);
    if (!r.data) { setError("Compte non trouvé"); return; }
    setBuyer(r.data);
  };

  var doSellEntry = async function(ticketType, customAmt) {
    if (processing || !buyer) return;
    var priceCents = ticketType === "plein" ? priceFullCents : priceReducedCents;
    if (customAmt) {
      var c = toCents(customAmt);
      if (c > 0) priceCents = c;
    }
    setProcessing(true); setError("");
    var r = await db.sellEntryWithBalance(buyer.id, account.id, ticketType, priceCents);
    setProcessing(false);
    if (r.error || !r.data.success) {
      if (r.data && r.data.error === "insufficient_balance") {
        setError("❌ Solde insuffisant (" + formatEur(r.data.balance_cents) + " disponible)");
      } else {
        setError("❌ " + (r.error ? r.error.message : r.data.error));
      }
      return;
    }
    setSellResult({type: ticketType, priceCents: priceCents, newBalance: r.data.new_balance_cents, buyerName: buyer.name});
  };

  var reset = function() {
    stopCam();
    setResult(null); setBuyer(null); setSellResult(null);
    setQrInput(""); setError(""); setCustomPlein(""); setCustomReduit("");
    setScanMode("scan");
  };

  // ─── RENDER mode selector ───
  var modeSelector = h("div", {style: {display: "flex", gap: 8, marginBottom: 20, background: C.pale, padding: 6, borderRadius: 12}},
    h("button", {onClick: function() { reset(); setBilletMode("validate"); }, style: {flex: 1, padding: "12px", borderRadius: 8, border: "none", cursor: "pointer", background: billetMode === "validate" ? C.blue : "transparent", color: billetMode === "validate" ? "#fff" : C.muted, fontFamily: "Oswald", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase"}}, "🎫 Valider billet"),
    h("button", {onClick: function() { reset(); setBilletMode("sell"); }, style: {flex: 1, padding: "12px", borderRadius: 8, border: "none", cursor: "pointer", background: billetMode === "sell" ? C.blue : "transparent", color: billetMode === "sell" ? "#fff" : C.muted, fontFamily: "Oswald", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase"}}, "💳 Vendre entrée")
  );

  // ─── MODE A : Résultat de validation ───
  if (result) {
    if (result.success) {
      return h("div", {style: {maxWidth: 500, margin: "20px auto"}},
        modeSelector,
        h(Card, {style: {textAlign: "center", padding: 40, background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff", border: "none"}},
          h("div", {style: {fontSize: 90, marginBottom: 16}}, "✅"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 30, fontWeight: 800, marginBottom: 6, letterSpacing: 2, textTransform: "uppercase"}}, "Accès autorisé"),
          h("div", {style: {fontSize: 16, marginBottom: 4, opacity: .9}}, result.customer_name || "Supporter"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 700, marginTop: 10, padding: "8px 16px", background: "rgba(255,255,255,.2)", display: "inline-block", borderRadius: 50, textTransform: "uppercase", letterSpacing: 1}}, "Tarif " + result.ticket_type),
          h("button", {onClick: reset, style: Object.assign({}, AB, {background: "#fff", color: C.green, width: "100%", padding: 16, fontSize: 16, marginTop: 24})}, "Scanner suivant")
        )
      );
    } else {
      var errorMsg = "";
      if (result.error === "ticket_not_found") errorMsg = "Billet inconnu";
      else if (result.error === "wrong_date") errorMsg = "Billet pour le " + result.ticket_date + " — pas valable aujourd'hui";
      else if (result.error === "already_used") errorMsg = "Billet DÉJÀ UTILISÉ à " + new Date(result.used_at).toLocaleString("fr-FR") + (result.used_by_name ? " par " + result.used_by_name : "");
      else errorMsg = result.error;
      return h("div", {style: {maxWidth: 500, margin: "20px auto"}},
        modeSelector,
        h(Card, {style: {textAlign: "center", padding: 40, background: "linear-gradient(135deg, #ef4444, #b91c1c)", color: "#fff", border: "none"}},
          h("div", {style: {fontSize: 90, marginBottom: 16}}, "❌"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, marginBottom: 10, letterSpacing: 2, textTransform: "uppercase"}}, "Accès refusé"),
          result.customer_name && h("div", {style: {fontSize: 15, opacity: .9, marginBottom: 8}}, "Acheteur : " + result.customer_name),
          h("div", {style: {fontSize: 14, marginTop: 6, padding: "12px 16px", background: "rgba(0,0,0,.2)", borderRadius: 10}}, errorMsg),
          h("button", {onClick: reset, style: Object.assign({}, AB, {background: "#fff", color: C.red, width: "100%", padding: 16, fontSize: 16, marginTop: 24})}, "Scanner suivant")
        )
      );
    }
  }

  // ─── MODE B : Résultat vente entrée ───
  if (sellResult) return h("div", {style: {maxWidth: 500, margin: "20px auto"}},
    modeSelector,
    h(Card, {style: {textAlign: "center", padding: 40}},
      h("div", {style: {fontSize: 72, marginBottom: 16}}, "✅"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 26, color: C.green, fontWeight: 800, marginBottom: 6, letterSpacing: 1, textTransform: "uppercase"}}, "Entrée vendue"),
      h("div", {style: {color: C.muted, fontSize: 14, marginBottom: 16}}, sellResult.buyerName + " · Tarif " + sellResult.type + " (" + formatEur(sellResult.priceCents) + ")"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4}}, "Nouveau solde"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 36, color: C.deep, fontWeight: 800, marginBottom: 24}}, formatEur(sellResult.newBalance)),
      h("button", {onClick: reset, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 16, fontSize: 16})}, "Supporter suivant")
    )
  );

  // ─── MODE A : scan/manuel ───
  if (billetMode === "validate" && !result) return h("div", {style: {maxWidth: 600, margin: "0 auto"}},
    modeSelector,
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase", textAlign: "center"}}, "🎫 Scanner le QR du billet"),
      h("div", {style: {display: "flex", gap: 8, marginBottom: 20}},
        h("button", {onClick: function() { setScanMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: scanMode === "scan" ? C.blue : C.pale, color: scanMode === "scan" ? "#fff" : C.muted})}, "📷 Scanner"),
        h("button", {onClick: function() { stopCam(); setScanMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: scanMode === "manual" ? C.blue : C.pale, color: scanMode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      scanMode === "scan" && h("div", null,
        h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
          h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, playsInline: true, muted: true}),
          h("canvas", {ref: canvasRef, style: {display: "none"}}),
          h("div", {style: {position: "absolute", inset: "15%", border: "3px solid " + C.gold, borderRadius: 14, pointerEvents: "none", boxShadow: "0 0 0 9999px rgba(0,0,0,.3)"}}),
          h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, fontFamily: "Oswald", letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "🎫 VISER LE QR BILLET")
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      ),
      scanMode === "manual" && h("div", null,
        h("input", {value: qrInput, onChange: function(e) { setQrInput(e.target.value); }, placeholder: "Code QR du billet (CSL-TICKET-...)", style: IS, onKeyDown: function(e) { if (e.key === "Enter") lookupValidate(); }, autoFocus: true}),
        h("button", {onClick: lookupValidate, style: Object.assign({}, AB, {background: C.blue, width: "100%", marginTop: 8})}, "Valider")
      )
    )
  );

  // ─── MODE B : scan acheteur (pas encore choisi) ───
  if (billetMode === "sell" && !buyer) return h("div", {style: {maxWidth: 600, margin: "0 auto"}},
    modeSelector,
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 16, textTransform: "uppercase", textAlign: "center"}}, "💳 Scanner le QR cashless"),
      h("div", {style: {display: "flex", gap: 8, marginBottom: 20}},
        h("button", {onClick: function() { setScanMode("scan"); }, style: Object.assign({}, AB, {flex: 1, background: scanMode === "scan" ? C.blue : C.pale, color: scanMode === "scan" ? "#fff" : C.muted})}, "📷 Scanner"),
        h("button", {onClick: function() { stopCam(); setScanMode("manual"); }, style: Object.assign({}, AB, {flex: 1, background: scanMode === "manual" ? C.blue : C.pale, color: scanMode === "manual" ? "#fff" : C.muted})}, "⌨️ Manuel")
      ),
      scanMode === "scan" && h("div", null,
        h("div", {style: {position: "relative", background: "#000", borderRadius: 14, overflow: "hidden", aspectRatio: "4/3"}},
          h("video", {ref: videoRef, style: {width: "100%", height: "100%", objectFit: "cover"}, playsInline: true, muted: true}),
          h("canvas", {ref: canvasRef, style: {display: "none"}}),
          h("div", {style: {position: "absolute", inset: "15%", border: "3px solid " + C.gold, borderRadius: 14, pointerEvents: "none", boxShadow: "0 0 0 9999px rgba(0,0,0,.3)"}}),
          h("div", {style: {position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, fontFamily: "Oswald", letterSpacing: 2, textShadow: "0 1px 3px rgba(0,0,0,.8)"}}, "💳 VISER LE QR CASHLESS")
        ),
        error && h("div", {style: {color: C.red, marginTop: 10, textAlign: "center", fontSize: 13}}, error)
      ),
      scanMode === "manual" && h("div", null,
        h("input", {value: qrInput, onChange: function(e) { setQrInput(e.target.value); }, placeholder: "Nom, email ou QR cashless", style: IS, onKeyDown: function(e) { if (e.key === "Enter") lookupBuyer(); }, autoFocus: true}),
        h("button", {onClick: lookupBuyer, style: Object.assign({}, AB, {background: C.blue, width: "100%", marginTop: 8})}, "Chercher")
      )
    )
  );

  // ─── MODE B : Choix tarif après scan acheteur ───
  if (billetMode === "sell" && buyer) return h("div", {style: {maxWidth: 700, margin: "0 auto"}},
    modeSelector,
    h(Card, {style: {background: "linear-gradient(135deg, " + C.pale + ", #fff)", marginBottom: 16, border: "2px solid " + C.blue}},
      h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
        h("div", null,
          h("div", {style: {color: C.deep, fontSize: 18, fontWeight: 700, fontFamily: "Oswald", textTransform: "uppercase"}}, buyer.name),
          h("div", {style: {color: C.muted, fontSize: 13, marginTop: 4}}, "Solde : ", h("strong", {style: {color: C.green}}, formatEur(buyer.balance_cents)))
        ),
        h("button", {onClick: reset, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, fontSize: 22, cursor: "pointer", padding: "8px 14px", borderRadius: 10, fontWeight: 700}}, "×")
      )
    ),
    h(Card, null,
      h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.muted, letterSpacing: 2, marginBottom: 14, textTransform: "uppercase", textAlign: "center"}}, "Choisir le tarif"),
      h("div", {style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}},
        // Plein
        h("button", {onClick: function() { doSellEntry("plein", customPlein); }, disabled: processing || buyer.balance_cents < (toCents(customPlein) || priceFullCents), style: {background: buyer.balance_cents < (toCents(customPlein) || priceFullCents) ? C.muted : "linear-gradient(135deg, " + C.blue + ", " + C.deep + ")", border: "none", borderRadius: 14, padding: "24px 16px", cursor: processing ? "wait" : "pointer", color: "#fff", boxShadow: "0 4px 16px rgba(208,33,40,.25)", opacity: processing ? .5 : 1}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, opacity: .8}}, "Tarif plein"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 32, fontWeight: 800}}, formatEur(toCents(customPlein) || priceFullCents)),
          h("div", {style: {fontSize: 11, opacity: .7, marginTop: 4}}, "Cliquer pour vendre")
        ),
        // Réduit
        h("button", {onClick: function() { doSellEntry("reduit", customReduit); }, disabled: processing || buyer.balance_cents < (toCents(customReduit) || priceReducedCents), style: {background: buyer.balance_cents < (toCents(customReduit) || priceReducedCents) ? C.muted : "linear-gradient(135deg, " + C.gold + ", #ff9500)", border: "none", borderRadius: 14, padding: "24px 16px", cursor: processing ? "wait" : "pointer", color: C.deep, boxShadow: "0 4px 16px rgba(255,182,39,.3)", opacity: processing ? .5 : 1}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, opacity: .8}}, "Tarif réduit"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 32, fontWeight: 800}}, formatEur(toCents(customReduit) || priceReducedCents)),
          h("div", {style: {fontSize: 11, opacity: .7, marginTop: 4}}, "Cliquer pour vendre")
        )
      ),
      h("div", {style: {marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10}},
        h("div", null,
          h("div", {style: LABEL}, "Tarif plein perso"),
          h("input", {value: customPlein, onChange: function(e) { setCustomPlein(e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".")); }, placeholder: formatEur(priceFullCents), style: Object.assign({}, IS, {marginBottom: 0, textAlign: "center"})})
        ),
        h("div", null,
          h("div", {style: LABEL}, "Tarif réduit perso"),
          h("input", {value: customReduit, onChange: function(e) { setCustomReduit(e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".")); }, placeholder: formatEur(priceReducedCents), style: Object.assign({}, IS, {marginBottom: 0, textAlign: "center"})})
        )
      ),
      h("div", {style: {fontSize: 11, color: C.muted, textAlign: "center", marginTop: 10}}, "💡 Les prix par défaut viennent des produits \"TICKET-PLEIN\" et \"TICKET-REDUIT\" dans Admin → Produits"),
      error && h("div", {style: {color: C.red, marginTop: 12, textAlign: "center", fontSize: 14, fontWeight: 600, padding: 10, background: "rgba(239,68,68,.1)", borderRadius: 10}}, error)
    )
  );

  return null;
}

// ═══════════════════════════════════════════════════════════════════
// VUE : SUPPORT (Besoin d'aide — formulaire contact)
// ═══════════════════════════════════════════════════════════════════
function SupportView(props) {
  var account = props.account;
  // Pré-remplir avec les infos du compte
  var defaultFirstName = "", defaultLastName = "";
  if (account && account.name) {
    var parts = account.name.trim().split(/\s+/);
    defaultFirstName = parts[0] || "";
    defaultLastName = parts.slice(1).join(" ") || "";
  }

  var s1 = useState(defaultFirstName); var firstName = s1[0], setFirstName = s1[1];
  var s2 = useState(defaultLastName); var lastName = s2[0], setLastName = s2[1];
  var s3 = useState((account && account.email) || ""); var email = s3[0], setEmail = s3[1];
  var s4 = useState(""); var message = s4[0], setMessage = s4[1];
  var s5 = useState(false); var sending = s5[0], setSending = s5[1];
  var s6 = useState(""); var error = s6[0], setError = s6[1];
  var s7 = useState(false); var sent = s7[0], setSent = s7[1];

  var canSubmit = firstName.trim() && lastName.trim() && email.trim() && message.trim().length >= 10 && !sending;

  var submit = async function() {
    if (!canSubmit) return;
    setError(""); setSending(true);
    var r = await db.submitSupportMessage(firstName.trim(), lastName.trim(), email.trim(), message.trim());
    setSending(false);
    if (r.error) {
      var errorMap = {
        firstname_required: "Le prénom est obligatoire",
        lastname_required: "Le nom est obligatoire",
        invalid_email: "Email invalide",
        message_too_short: "Ton message est trop court (10 caractères min)",
        message_too_long: "Ton message est trop long (5000 caractères max)"
      };
      setError(errorMap[r.error.message] || ("❌ Erreur : " + (r.error.message || "Réessaie dans un instant")));
      return;
    }
    setSent(true);
    setMessage("");
  };

  var reset = function() {
    setSent(false);
    setError("");
    setMessage("");
    setFirstName(defaultFirstName);
    setLastName(defaultLastName);
    setEmail((account && account.email) || "");
  };

  if (sent) {
    return h("div", {style: {maxWidth: 640, margin: "0 auto"}},
      h(Card, {style: {textAlign: "center", padding: 40}},
        h("div", {style: {fontSize: 72, marginBottom: 16}}, "✅"),
        h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: C.deep, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}}, "Message envoyé !"),
        h("p", {style: {color: C.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 20, maxWidth: 480, margin: "0 auto 20px"}},
          "Merci ", h("strong", {style: {color: C.deep}}, firstName || ""), " ! Ton message est bien arrivé chez nous. Un membre de l'équipe CSL reviendra vers toi par email ",
          h("strong", {style: {color: C.deep}}, "dans les meilleurs délais"),
          " (généralement sous 24-48h)."
        ),
        h("div", {style: {background: C.pale, borderRadius: 12, padding: 16, fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6}},
          "💡 Tu vas aussi recevoir un email de confirmation à ", h("strong", {style: {color: C.deep}}, email), ". Pense à vérifier tes spams si tu ne le vois pas tout de suite."
        ),
        h("button", {onClick: reset, style: Object.assign({}, AB, {background: C.blue, color: "#fff"})}, "Envoyer un autre message"),
        h("div", {style: {marginTop: 30, fontFamily: "Oswald", fontSize: 14, color: C.gold, letterSpacing: 3, fontWeight: 700, textTransform: "uppercase"}}, "Allez les Lédoniens 🏉")
      )
    );
  }

  return h("div", {style: {maxWidth: 720, margin: "0 auto"}},
    // Header
    h("div", {style: {textAlign: "center", marginBottom: 24}},
      h("div", {style: {fontSize: 48, marginBottom: 8}}, "💬"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 11, color: C.gold, letterSpacing: 4, fontWeight: 700, textTransform: "uppercase", marginBottom: 6}}, "Service supporters"),
      h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: C.deep, letterSpacing: 1, textTransform: "uppercase"}}, "Une question, un souci ?"),
      h("div", {style: {fontSize: 14, color: C.muted, marginTop: 6, maxWidth: 480, margin: "6px auto 0", lineHeight: 1.6}}, "Envoie-nous un message, l'équipe CSL te répond directement par email.")
    ),

    h(Card, {style: {padding: 28}},
      // Prénom + Nom (grid 2 cols sur desktop)
      h("div", {className: "csl-support-grid", style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14}},
        h("style", null, "@media (max-width: 560px) { .csl-support-grid { grid-template-columns: 1fr !important; } }"),
        h("div", null,
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontWeight: 700}}, "Prénom *"),
          h("input", {
            value: firstName,
            onChange: function(e) { setFirstName(e.target.value); },
            placeholder: "Jean",
            disabled: sending,
            style: Object.assign({}, IS, {marginBottom: 0})
          })
        ),
        h("div", null,
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontWeight: 700}}, "Nom *"),
          h("input", {
            value: lastName,
            onChange: function(e) { setLastName(e.target.value); },
            placeholder: "Dupont",
            disabled: sending,
            style: Object.assign({}, IS, {marginBottom: 0})
          })
        )
      ),
      // Email
      h("div", {style: {marginBottom: 14}},
        h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontWeight: 700}}, "Email *"),
        h("input", {
          value: email,
          onChange: function(e) { setEmail(e.target.value); },
          placeholder: "jean.dupont@email.com",
          type: "email",
          inputMode: "email",
          disabled: sending,
          style: Object.assign({}, IS, {marginBottom: 0})
        }),
        h("div", {style: {fontSize: 11, color: C.muted, marginTop: 4, fontStyle: "italic"}}, "Notre réponse sera envoyée à cette adresse")
      ),
      // Message
      h("div", {style: {marginBottom: 18}},
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700}}, "Message *"),
          h("div", {style: {fontSize: 11, color: message.length > 4800 ? C.red : C.muted}}, message.length + "/5000")
        ),
        h("textarea", {
          value: message,
          onChange: function(e) { setMessage(e.target.value.slice(0, 5000)); },
          placeholder: "Bonjour, j'ai une question concernant... (minimum 10 caractères)",
          disabled: sending,
          rows: 7,
          style: Object.assign({}, IS, {marginBottom: 0, padding: 14, fontSize: 14, resize: "vertical", fontFamily: "inherit", lineHeight: 1.6, minHeight: 140})
        })
      ),

      error && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: C.red, fontWeight: 600, textAlign: "center"}}, error),

      // Bouton envoyer
      h("button", {
        onClick: submit,
        disabled: !canSubmit,
        style: {
          width: "100%",
          background: !canSubmit ? "#cbd5e1" : "linear-gradient(135deg, " + C.blue + ", " + C.deep + ")",
          color: "#fff",
          border: "none",
          borderRadius: 14,
          padding: "18px 24px",
          fontFamily: "Oswald",
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: 2,
          textTransform: "uppercase",
          cursor: !canSubmit ? "not-allowed" : "pointer",
          boxShadow: !canSubmit ? "none" : "0 8px 24px rgba(208,33,40,.3)",
          transition: "all .2s"
        }
      },
        sending ? "⏳ Envoi en cours..." : "📤 Envoyer mon message"
      ),

      h("div", {style: {marginTop: 16, padding: 14, background: C.pale, borderRadius: 10, fontSize: 12, color: C.muted, lineHeight: 1.6, textAlign: "center"}},
        "📧 Tu peux aussi nous écrire directement à ",
        h("a", {href: "mailto:cashlesscsl@gmail.com", style: {color: C.blue, fontWeight: 700, textDecoration: "none"}}, "cashlesscsl@gmail.com")
      )
    ),

    // Footer infos
    h("div", {style: {marginTop: 24, textAlign: "center"}},
      h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.muted, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 4}}, "🏉 Délai de réponse"),
      h("div", {style: {fontSize: 13, color: C.muted}}, "Généralement sous 24-48h en semaine")
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// VUE : ADMIN (stats, produits, rôles, comptes, export)
// ═══════════════════════════════════════════════════════════════════
function AdminView(props) {
  var products = props.products, setProducts = props.setProducts;
  var account = props.account;
  var s1 = useState("stats"); var tab = s1[0], setTab = s1[1];
  var sp1 = useState([]); var posList = sp1[0], setPosList = sp1[1];
  var sp2 = useState(null); var selectedPos = sp2[0], setSelectedPos = sp2[1];
  var sp3 = useState({name: "", emoji: "🏉"}); var newPos = sp3[0], setNewPos = sp3[1];
  var sp4 = useState(null); var qrPos = sp4[0], setQrPos = sp4[1];
  var sp5 = useState(null); var editPosId = sp5[0], setEditPosId = sp5[1];
  var sp6 = useState({name: "", emoji: ""}); var editPos = sp6[0], setEditPos = sp6[1];
  var sc1 = useState([]); var depList = sc1[0], setDepList = sc1[1];
  var sc2 = useState({name: "", emoji: "🥤", price: "1"}); var newDep = sc2[0], setNewDep = sc2[1];
  var sc3 = useState(null); var editDepId = sc3[0], setEditDepId = sc3[1];
  var sc4 = useState({name: "", emoji: "", price: ""}); var editDep = sc4[0], setEditDep = sc4[1];
  var sm1 = useState([]); var meals = sm1[0], setMeals = sm1[1];
  var sm2 = useState(null); var mealSel = sm2[0], setMealSel = sm2[1];
  var sm3 = useState([]); var mealBk = sm3[0], setMealBk = sm3[1];
  var sm4 = useState(null); var mealSheetData = sm4[0], setMealSheet = sm4[1];
  var sm5 = useState(null); var mealForm = sm5[0], setMealForm = sm5[1];
  var sm6 = useState(null); var itemForm = sm6[0], setItemForm = sm6[1];
  var sm7 = useState("carte"); var mealTab = sm7[0], setMealTab = sm7[1];
  var sm8 = useState(""); var mealErr = sm8[0], setMealErr = sm8[1];
  var ss1 = useState([]); var stageRows = ss1[0], setStageRows = ss1[1];
  var ss2 = useState([]); var stageDays = ss2[0], setStageDays = ss2[1];
  var ss3 = useState(""); var stageSlug = ss3[0], setStageSlug = ss3[1];
  var ss4 = useState("tous"); var stageFilter = ss4[0], setStageFilter = ss4[1];
  var ss5 = useState([]); var stageList = ss5[0], setStageList = ss5[1];
  var ss6 = useState("inscriptions"); var stageView = ss6[0], setStageView = ss6[1];
  var ss7 = useState(null); var stageForm = ss7[0], setStageForm = ss7[1];
  var ss8 = useState(null); var dayForm = ss8[0], setDayForm = ss8[1];
  var ss9 = useState(""); var stageErr = ss9[0], setStageErr = ss9[1];
  var sb1 = useState([]); var tkEvents = sb1[0], setTkEvents = sb1[1];
  var sb2 = useState(null); var tkSel = sb2[0], setTkSel = sb2[1];
  var sb3 = useState([]); var tkOrders = sb3[0], setTkOrders = sb3[1];
  var sb4 = useState("tarifs"); var tkTab = sb4[0], setTkTab = sb4[1];
  var sb5 = useState(null); var tkForm = sb5[0], setTkForm = sb5[1];
  var sb6 = useState(null); var tkTypeForm = sb6[0], setTkTypeForm = sb6[1];
  var sb7 = useState(""); var tkErr = sb7[0], setTkErr = sb7[1];
  var st1 = useState([]); var mTypes = st1[0], setMTypes = st1[1];
  var st2 = useState(null); var mForm = st2[0], setMForm = st2[1];
  var st3 = useState(""); var mErr = st3[0], setMErr = st3[1];
  var sk1 = useState(null); var cardFor = sk1[0], setCardFor = sk1[1];
  var sk2 = useState(""); var cardImg = sk2[0], setCardImg = sk2[1];
  var sk3 = useState(false); var cardBusy = sk3[0], setCardBusy = sk3[1];
  var sk4 = useState(true); var cardBleed = sk4[0], setCardBleed = sk4[1];
  var sk5 = useState(null); var cardData = sk5[0], setCardData = sk5[1];
  var s2 = useState([]); var transactions = s2[0], setTransactions = s2[1];
  var s3 = useState([]); var accounts = s3[0], setAccounts = s3[1];
  var s4 = useState(0); var accountsCount = s4[0], setAccountsCount = s4[1];
  var s5 = useState(0); var page = s5[0], setPage = s5[1];
  var s6 = useState(""); var search = s6[0], setSearch = s6[1];
  var s7 = useState([]); var allProducts = s7[0], setAllProducts = s7[1];
  var s8 = useState({name: "", price: "", emoji: "🍺"}); var newProd = s8[0], setNewProd = s8[1];
  var s9 = useState(null); var editingId = s9[0], setEditingId = s9[1];
  var s10 = useState({}); var editDraft = s10[0], setEditDraft = s10[1];
  var s11 = useState(""); var msg = s11[0], setMsg = s11[1];
  var s12 = useState(new Date().toISOString().slice(0, 10)); var exportDate = s12[0], setExportDate = s12[1];
  var s12b = useState(new Date().toISOString().slice(0, 10)); var exportEndDate = s12b[0], setExportEndDate = s12b[1];
  var s12c = useState("single"); var exportMode = s12c[0], setExportMode = s12c[1]; // "single" ou "range"
  var s12d = useState(false); var exporting = s12d[0], setExporting = s12d[1];
  // Modal d'ajustement de solde
  var s13 = useState(null); var adjustingAccount = s13[0], setAdjustingAccount = s13[1];
  var s14 = useState("+"); var adjustSign = s14[0], setAdjustSign = s14[1];
  var s15 = useState(""); var adjustAmount = s15[0], setAdjustAmount = s15[1];
  var s16 = useState(""); var adjustReason = s16[0], setAdjustReason = s16[1];
  var s17 = useState(false); var adjusting = s17[0], setAdjusting = s17[1];
  var s18 = useState(0); var accountsTodayCount = s18[0], setAccountsTodayCount = s18[1];
  // Dashboard enrichi
  var s19 = useState([]); var historyTx = s19[0], setHistoryTx = s19[1];
  var s20 = useState(7); var historyRange = s20[0], setHistoryRange = s20[1];
  var s21 = useState(false); var loadingHistory = s21[0], setLoadingHistory = s21[1];

  useEffect(function() {
    (async function() {
      var t = await db.listTransactions(100);
      var p = await db.listAllProducts();
      var ct = await db.countAccountsToday();
      setTransactions(t.data); setAllProducts(p.data); setAccountsTodayCount(ct.count);
    })();
    var ch = db.subscribeToTransactions(async function() {
      var r = await db.listTransactions(100); setTransactions(r.data);
    });
    // Realtime : à chaque création/maj de compte, on rafraîchit le compteur du jour
    var chAcc = db.subscribeToAccounts(async function() {
      var ct = await db.countAccountsToday();
      setAccountsTodayCount(ct.count);
    });
    return function() { supabase.removeChannel(ch); supabase.removeChannel(chAcc); };
  }, []);

  // Charge l'historique pour le dashboard enrichi (uniquement quand on est sur stats)
  useEffect(function() {
    if (tab !== "stats") return;
    (async function() {
      setLoadingHistory(true);
      var end = new Date();
      var start = new Date();
      start.setDate(start.getDate() - (historyRange - 1));
      var startStr = start.toISOString().slice(0, 10);
      var endStr = end.toISOString().slice(0, 10);
      var r = await db.listTransactionsRange(startStr, endStr);
      if (!r.error) setHistoryTx(r.data);
      setLoadingHistory(false);
    })();
  }, [tab, historyRange]);

  useEffect(function() {
    if (tab !== "accounts" && tab !== "roles") return;
    (async function() {
      var r = await db.listAccountsPage(page, 20, search);
      setAccounts(r.data); setAccountsCount(r.count);
    })();
  }, [tab, page, search]);

  var refreshProducts = async function() {
    if (selectedPos) {
      var all = await db.listAllProducts(selectedPos.id);
      setAllProducts(all.data);
    }
    var active = await db.listProducts();
    if (setProducts) setProducts(active.data);
  };

  var todayTx = transactions.filter(function(t) { return new Date(t.created_at).toDateString() === new Date().toDateString(); });
  // CA = ventes uniquement (purchases) → exclut les ajustements négatifs (remboursements)
  var todayRevCents = todayTx.filter(function(t) { return t.amount_cents < 0 && t.type !== "adjustment"; }).reduce(function(s, t) { return s + Math.abs(t.amount_cents); }, 0);

  // Mapping : crédit -> montant réellement payé (sans bonus)
  // Basé sur les SKU Shopify : RECH-10, RECH-15, RECH-23, RECH-36, RECH-48, RECH-60, RECH-123
  var RECHARGE_MAP = {
    1000: 1000,   // 10€ -> 10€ (pas de bonus)
    1500: 1500,   // 15€ -> 15€ (pas de bonus)
    2300: 2000,   // 23€ -> 20€ (+3€ bonus)
    3600: 3000,   // 36€ -> 30€ (+6€ bonus)
    4800: 4000,   // 48€ -> 40€ (+8€ bonus)
    6000: 5000,   // 60€ -> 50€ (+10€ bonus)
    12300: 10000  // 123€ -> 100€ (+23€ bonus)
  };
  function realPaidCents(creditedCents) {
    // Si le montant correspond à un palier connu, on retourne le montant payé réel
    // Sinon (recharge guichet libre, ajustement, etc.), on retourne le montant tel quel
    return RECHARGE_MAP[creditedCents] !== undefined ? RECHARGE_MAP[creditedCents] : creditedCents;
  }

  // Recharges = montant réellement payé par les supporters (sans les bonus offerts par le club)
  var todayRechCents = todayTx
    .filter(function(t) { return t.amount_cents > 0 && t.type !== "adjustment"; })
    .reduce(function(s, t) { return s + realPaidCents(t.amount_cents); }, 0);

  // Total des bonus offerts aujourd'hui (différence entre crédité et payé)
  var todayBonusCents = todayTx
    .filter(function(t) { return t.amount_cents > 0 && t.type !== "adjustment"; })
    .reduce(function(s, t) { return s + (t.amount_cents - realPaidCents(t.amount_cents)); }, 0);

  // Compteur des ajustements du jour (info séparée)
  var todayAdjustments = todayTx.filter(function(t) { return t.type === "adjustment"; }).length;

  // ═══════════════════════════════════════════════════════════════
  // DASHBOARD ENRICHI — Calculs sur historique (7 ou 30 derniers jours)
  // ═══════════════════════════════════════════════════════════════

  // CA par jour (ventes uniquement, exclut recharges + ajustements)
  var revByDay = (function() {
    var map = {}; // {YYYY-MM-DD: cents}
    // Initialiser toutes les dates de la plage à 0 (pour avoir la ligne continue)
    for (var i = 0; i < historyRange; i++) {
      var d = new Date();
      d.setDate(d.getDate() - i);
      map[d.toISOString().slice(0, 10)] = 0;
    }
    historyTx.forEach(function(t) {
      if (t.amount_cents >= 0 || t.type === "adjustment") return;
      var day = new Date(t.created_at).toISOString().slice(0, 10);
      if (map[day] === undefined) return;
      map[day] += Math.abs(t.amount_cents);
    });
    return Object.keys(map).sort().map(function(day) {
      return {day: day, cents: map[day]};
    });
  })();

  // Total CA sur la période
  var periodRevCents = revByDay.reduce(function(s, d) { return s + d.cents; }, 0);
  // Panier moyen sur la période
  var periodSales = historyTx.filter(function(t) { return t.amount_cents < 0 && t.type !== "adjustment"; });
  var periodAvgCartCents = periodSales.length > 0 ? Math.round(periodRevCents / periodSales.length) : 0;

  // Top bénévoles (par CA encaissé, ventes seulement)
  var topBenevoles = (function() {
    var map = {}; // {name: {cents, count}}
    periodSales.forEach(function(t) {
      var name = t.cashier_name || "—";
      // On exclut les AUTO Shopify (pas des vrais bénévoles)
      if (name.indexOf("AUTO") === 0 || name.indexOf("Shopify") >= 0) return;
      if (!map[name]) map[name] = {cents: 0, count: 0};
      map[name].cents += Math.abs(t.amount_cents);
      map[name].count += 1;
    });
    return Object.keys(map)
      .map(function(name) { return {name: name, cents: map[name].cents, count: map[name].count}; })
      .sort(function(a, b) { return b.cents - a.cents; })
      .slice(0, 8);
  })();

  // Supporters les plus actifs (par dépense totale)
  var topSupporters = (function() {
    var map = {}; // {account_id: {name, cents, count}}
    periodSales.forEach(function(t) {
      var id = t.account_id;
      if (!id) return;
      var name = (t.account && t.account.name) || "—";
      if (!map[id]) map[id] = {name: name, cents: 0, count: 0};
      map[id].cents += Math.abs(t.amount_cents);
      map[id].count += 1;
    });
    return Object.values(map)
      .sort(function(a, b) { return b.cents - a.cents; })
      .slice(0, 8);
  })();

  // Export CSV
  // ═══════════════════════════════════════════════════════════════
  // SYSTÈME D'EXPORT EXCEL (.xlsx) — 5 types d'exports
  // ═══════════════════════════════════════════════════════════════

  // Catégorise une transaction en fonction de son label
  // Nom du point de vente d'une transaction (null si vente hors poste)
  var posOfTx = function(tx) {
    if (tx.pos && tx.pos.name) return {id: tx.pos.id, name: tx.pos.name, emoji: tx.pos.emoji || "🏉"};
    if (tx.pos_id) return {id: tx.pos_id, name: tx.pos_id, emoji: "🏉"};
    return null;
  };

  var categorizeTx = function(tx) {
    if (tx.type === "adjustment") return "ajustement";
    if (tx.type === "recharge_counter") return "recharge_guichet";
    if (tx.amount_cents > 0) return "recharge"; // autres recharges (codes)
    // Vente : on regarde le label pour différencier
    var label = (tx.label || "").toLowerCase();
    if (label.indexOf("boutique") >= 0 || label.indexOf("article") >= 0 || label.indexOf("maillot") >= 0 || label.indexOf("polo") >= 0) return "boutique";
    if (label.indexOf("tarif") >= 0 || label.indexOf("billet") >= 0 || label.indexOf("entrée") >= 0 || label.indexOf("entree") >= 0) return "billetterie";
    return "buvette"; // par défaut, toute vente non-boutique non-billetterie est buvette
  };

  // Récupère la plage de dates effective selon le mode
  var getDateRange = function() {
    if (exportMode === "single") return {start: exportDate, end: exportDate};
    // En range, on s'assure que start <= end
    var s = exportDate, e = exportEndDate;
    if (s > e) { var tmp = s; s = e; e = tmp; }
    return {start: s, end: e};
  };

  // Helper : crée une feuille Excel avec entête formatée + lignes data + résumé
  // Retourne un worksheet SheetJS
  var buildSheet = function(opts) {
    // opts: {title, subtitle, headers, rows, summaryRows, topRows}
    var aoa = []; // array of arrays

    // Titre
    aoa.push([opts.title]);
    if (opts.subtitle) aoa.push([opts.subtitle]);
    aoa.push([]); // ligne vide

    // Résumé / Stats globales
    if (opts.summaryRows && opts.summaryRows.length > 0) {
      aoa.push(["📊 RÉSUMÉ"]);
      opts.summaryRows.forEach(function(r) { aoa.push(r); });
      aoa.push([]);
    }

    // Liste produits (top 10 OU liste complète selon opts.fullProductList)
    if (opts.topRows && opts.topRows.length > 0) {
      if (opts.fullProductList) {
        aoa.push(["📦 VENTES PAR PRODUIT (toutes références)"]);
        aoa.push(["Produit", "Quantité vendue", "Total (€)"]);
        opts.topRows.forEach(function(r) { aoa.push(r); });
        // Ligne totaux
        var totalQty = opts.topRows.reduce(function(s, r) { return s + (parseInt(r[1], 10) || 0); }, 0);
        var totalEur = opts.topRows.reduce(function(s, r) { return s + (parseFloat(r[2]) || 0); }, 0);
        aoa.push(["TOTAL", totalQty, totalEur.toFixed(2)]);
      } else {
        aoa.push(["🏆 TOP PRODUITS / ARTICLES"]);
        aoa.push(["Rang", "Produit", "Quantité", "Total (€)"]);
        opts.topRows.forEach(function(r) { aoa.push(r); });
      }
      aoa.push([]);
    }

    // Détail
    if (opts.rows && opts.rows.length > 0) {
      aoa.push(["📋 DÉTAIL DES TRANSACTIONS"]);
      aoa.push(opts.headers);
      opts.rows.forEach(function(r) { aoa.push(r); });
    } else {
      aoa.push(["Aucune transaction sur cette période."]);
    }

    var ws = XLSX.utils.aoa_to_sheet(aoa);

    // Largeurs colonnes auto-ajustées
    var colWidths = [];
    aoa.forEach(function(row) {
      row.forEach(function(cell, i) {
        var len = (cell == null ? 0 : String(cell).length);
        if (!colWidths[i] || colWidths[i] < len) colWidths[i] = len;
      });
    });
    ws["!cols"] = colWidths.map(function(w) { return {wch: Math.min(Math.max(w + 2, 10), 50)}; });

    return ws;
  };

  // Formate une date ISO en JJ/MM/AAAA HH:MM
  var formatDateTime = function(iso) {
    var d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"});
  };
  var formatDateOnly = function(iso) {
    return new Date(iso).toLocaleDateString("fr-FR");
  };

  // Calcule le résumé stats pour un set de transactions
  var computeSummary = function(txList, opts) {
    opts = opts || {};
    var total = txList.reduce(function(s, t) { return s + Math.abs(t.amount_cents); }, 0);
    var count = txList.length;
    // Par jour
    var byDay = {};
    txList.forEach(function(t) {
      var d = formatDateOnly(t.created_at);
      if (!byDay[d]) byDay[d] = {count: 0, total: 0};
      byDay[d].count++;
      byDay[d].total += Math.abs(t.amount_cents);
    });
    var byDayRows = [["Jour", "Nb transactions", "Total (€)"]];
    Object.keys(byDay).sort().forEach(function(d) {
      byDayRows.push([d, byDay[d].count, (byDay[d].total / 100).toFixed(2)]);
    });
    // Produits (depuis items JSON)
    var prodMap = {};
    txList.forEach(function(t) {
      if (!t.items) return;
      var items = t.items;
      if (typeof items === "string") { try { items = JSON.parse(items); } catch (e) { return; } }
      if (!Array.isArray(items)) return;
      items.forEach(function(it) {
        var name = it.name || "?";
        if (!prodMap[name]) prodMap[name] = {qty: 0, total: 0};
        prodMap[name].qty += (it.qty || 1);
        prodMap[name].total += (it.price_cents || 0) * (it.qty || 1);
      });
    });
    // Si fullProductList = true → on retourne TOUS les produits (pas de top 10, pas de rang)
    // Sinon → top 10 classique avec colonne "Rang"
    var sortedProducts = Object.keys(prodMap)
      .map(function(name) { return [name, prodMap[name].qty, prodMap[name].total]; })
      .sort(function(a, b) { return b[2] - a[2]; });
    var topRows;
    if (opts.fullProductList) {
      // Liste complète : Produit | Quantité vendue | Total (€) — sans rang
      topRows = sortedProducts.map(function(r) { return [r[0], r[1], (r[2] / 100).toFixed(2)]; });
    } else {
      // Top 10 classique avec rang
      topRows = sortedProducts.slice(0, 10).map(function(r, i) { return [i + 1, r[0], r[1], (r[2] / 100).toFixed(2)]; });
    }

    return {
      summaryRows: [
        ["Nombre de transactions", count],
        ["Montant total", (total / 100).toFixed(2) + " €"],
        ["Ticket moyen", count > 0 ? ((total / count) / 100).toFixed(2) + " €" : "—"],
        []
      ].concat([["─── Par jour ───"]]).concat(byDayRows),
      topRows: topRows,
      fullProductList: !!opts.fullProductList
    };
  };

  // Construit les rows détail standardisées
  var buildDetailRows = function(txList, accountsMap) {
    return txList.map(function(t) {
      var acc = (t.account && t.account.name) || (accountsMap && accountsMap[t.account_id]) || t.account_id || "?";
      var po = posOfTx(t);
      return [
        formatDateOnly(t.created_at),
        new Date(t.created_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}),
        po ? po.name : "— hors poste —",
        acc,
        t.cashier_name || "—",
        t.label || "",
        (Math.abs(t.amount_cents) / 100).toFixed(2)
      ];
    });
  };

  // Télécharge un workbook sous forme de fichier .xlsx
  var downloadWorkbook = function(wb, filename) {
    XLSX.writeFile(wb, filename);
  };

  // Construit un nom de fichier basé sur la plage de dates
  var buildFilename = function(prefix) {
    var r = getDateRange();
    var suffix = r.start === r.end ? r.start : (r.start + "_au_" + r.end);
    return "csl-" + prefix + "-" + suffix + ".xlsx";
  };

  // ─── EXPORT 1 : GLOBAL (tout : ventes + recharges en ligne + au stade) ───
  var exportGlobal = async function() {
    if (exporting) return;
    setExporting(true); setMsg("⏳ Génération de l'export global...");
    try {
      var range = getDateRange();
      var tx = await db.listTransactionsRange(range.start, range.end);
      var audit = await db.listAuditRange(range.start, range.end);
      if (tx.error) throw tx.error;

      var wb = XLSX.utils.book_new();

      var dateLabel = range.start === range.end ? "Le " + formatDateOnly(range.start + "T12:00:00") : "Du " + formatDateOnly(range.start + "T12:00:00") + " au " + formatDateOnly(range.end + "T12:00:00");

      // Feuille 1 : RÉSUMÉ GLOBAL
      var sales = tx.data.filter(function(t) { return t.amount_cents < 0 && t.type !== "adjustment"; });
      var buvetteTx = sales.filter(function(t) { return categorizeTx(t) === "buvette"; });
      var boutiqueTx = sales.filter(function(t) { return categorizeTx(t) === "boutique"; });
      var billetterieTx = sales.filter(function(t) { return categorizeTx(t) === "billetterie"; });
      // ─── CRITIQUE pour la compta ───
      // Recharges Shopify : on se base sur audit_log.shopify_order_paid qui logue TOUTES
      // les commandes payées sur Shopify (mode "auto_credit" ET mode "codes_fallback").
      // → garantit correspondance EXACTE avec le CA Shopify.
      // Recharges guichet : transactions.recharge_counter SANS cashier_name "AUTO".
      var shopifyOrders = (audit.data || []).filter(function(a) { return a.type === "shopify_order_paid"; });
      var shopifyAutoCredit = shopifyOrders.filter(function(a) { return a.details && a.details.mode === "auto_credit"; });
      var shopifyCodesFallback = shopifyOrders.filter(function(a) { return a.details && a.details.mode === "codes_fallback"; });

      var isShopifyAutoTx = function(t) {
        var c = (t.cashier_name || "");
        return c.indexOf("AUTO") === 0 || c.indexOf("Shopify") >= 0;
      };
      var allRechargeTx = tx.data.filter(function(t) { return t.type === "recharge_counter"; });
      // On garde les transactions Shopify (auto_credit) pour le détail mais on compte via audit_log
      var rechargeShopifyTx = allRechargeTx.filter(isShopifyAutoTx);
      var rechargeGuichetTx = allRechargeTx.filter(function(t) { return !isShopifyAutoTx(t); });
      var adjustmentsTx = tx.data.filter(function(t) { return t.type === "adjustment"; });

      var sum = function(list) { return list.reduce(function(s, t) { return s + Math.abs(t.amount_cents || 0); }, 0); };
      // Pour les recharges : on calcule aussi le montant RÉELLEMENT PAYÉ (sans bonus)
      // car c'est ce qui apparaît dans Shopify pour la compta.
      var sumPaid = function(list) {
        return list.reduce(function(s, t) {
          var credited = Math.abs(t.amount_cents || 0);
          return s + realPaidCents(credited);
        }, 0);
      };
      var totalBuvette = sum(buvetteTx);
      var totalBoutique = sum(boutiqueTx);
      var totalBilletterie = sum(billetterieTx);
      var totalVentes = totalBuvette + totalBoutique + totalBilletterie;
      var totalRechargeGuichetCredite = sum(rechargeGuichetTx);
      var totalRechargeGuichetPaye = sumPaid(rechargeGuichetTx);
      var totalRechargeGuichetBonus = totalRechargeGuichetCredite - totalRechargeGuichetPaye;
      // Recharges Shopify : on utilise shopifyOrders (audit_log) qui couvre TOUTES les commandes
      var totalRechargeOnlineCredite = sum(shopifyOrders);
      var totalRechargeOnlinePaye = sumPaid(shopifyOrders);
      var totalRechargeOnlineBonus = totalRechargeOnlineCredite - totalRechargeOnlinePaye;
      // Total fallback codes en attente d'utilisation (info)
      var totalFallbackPending = sumPaid(shopifyCodesFallback);
      var totalRechargesCredite = totalRechargeGuichetCredite + totalRechargeOnlineCredite;
      var totalRechargesPaye = totalRechargeGuichetPaye + totalRechargeOnlinePaye;
      // alias pour le reste du code
      var totalRechargeOnline = totalRechargeOnlinePaye;
      var totalRechargeGuichet = totalRechargeGuichetPaye;
      var totalRecharges = totalRechargesPaye;

      var summaryAoa = [
        ["CSL CASHLESS — EXPORT GLOBAL"],
        [dateLabel],
        [],
        ["📊 SYNTHÈSE GÉNÉRALE"],
        ["Catégorie", "Nombre", "Montant (€)"],
        ["Ventes Buvette", buvetteTx.length, (totalBuvette / 100).toFixed(2)],
        ["Ventes Boutique", boutiqueTx.length, (totalBoutique / 100).toFixed(2)],
        ["Ventes Billetterie", billetterieTx.length, (totalBilletterie / 100).toFixed(2)],
        ["TOTAL VENTES", sales.length, (totalVentes / 100).toFixed(2)],
        [],
        ["─── RECHARGES (montants RÉELLEMENT ENCAISSÉS, hors bonus) ───"],
        ["Catégorie", "Nombre", "Encaissé (€)", "Bonus offert (€)", "Crédité au compte (€)"],
        ["Recharges SHOPIFY (en ligne — TOTAL)", shopifyOrders.length, (totalRechargeOnlinePaye / 100).toFixed(2), (totalRechargeOnlineBonus / 100).toFixed(2), (totalRechargeOnlineCredite / 100).toFixed(2)],
        ["    dont auto-créditées immédiatement", shopifyAutoCredit.length, (sumPaid(shopifyAutoCredit) / 100).toFixed(2), ((sum(shopifyAutoCredit) - sumPaid(shopifyAutoCredit)) / 100).toFixed(2), (sum(shopifyAutoCredit) / 100).toFixed(2)],
        ["    dont codes envoyés (en attente d'utilisation)", shopifyCodesFallback.length, (sumPaid(shopifyCodesFallback) / 100).toFixed(2), ((sum(shopifyCodesFallback) - sumPaid(shopifyCodesFallback)) / 100).toFixed(2), (sum(shopifyCodesFallback) / 100).toFixed(2)],
        ["Recharges au stade (guichet bénévoles)", rechargeGuichetTx.length, (totalRechargeGuichetPaye / 100).toFixed(2), (totalRechargeGuichetBonus / 100).toFixed(2), (totalRechargeGuichetCredite / 100).toFixed(2)],
        ["TOTAL RECHARGES", shopifyOrders.length + rechargeGuichetTx.length, (totalRechargesPaye / 100).toFixed(2), ((totalRechargeOnlineBonus + totalRechargeGuichetBonus) / 100).toFixed(2), (totalRechargesCredite / 100).toFixed(2)],
        [],
        ["💡 \"Encaissé\" = montant réellement payé par le client (= correspond EXACTEMENT au CA Shopify pour les recharges en ligne)."],
        ["    \"Crédité\" = montant ajouté au compte du supporter (= encaissé + bonus offert par le club)."],
        shopifyCodesFallback.length > 0 ? ["⚠️ " + shopifyCodesFallback.length + " recharge(s) Shopify sont en mode \"codes envoyés\" (" + (totalFallbackPending / 100).toFixed(2) + " € encaissé). L'argent est bien reçu par le club, mais le supporter doit taper son code pour le créditer sur son compte cashless."] : [""],
        [],
        ["Ajustements (corrections)", adjustmentsTx.length, (sum(adjustmentsTx) / 100).toFixed(2)],
        [],
        ["📈 INDICATEURS"],
        ["Ticket moyen vente", sales.length > 0 ? ((totalVentes / sales.length) / 100).toFixed(2) + " €" : "—"],
        ["Recharge moyenne (encaissé)", (shopifyOrders.length + rechargeGuichetTx.length) > 0 ? ((totalRechargesPaye / (shopifyOrders.length + rechargeGuichetTx.length)) / 100).toFixed(2) + " €" : "—"]
      ];
      var wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa);
      wsSummary["!cols"] = [{wch: 35}, {wch: 12}, {wch: 15}];
      XLSX.utils.book_append_sheet(wb, wsSummary, "Synthèse");

      // Feuille 2 : Toutes les ventes cashless
      var allSalesSummary = computeSummary(sales);
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "VENTES CASHLESS AU STADE", subtitle: dateLabel,
        summaryRows: allSalesSummary.summaryRows,
        topRows: allSalesSummary.topRows,
        headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: buildDetailRows(sales)
      }), "Ventes stade");

      // Feuille 3 : Buvette (avec liste COMPLÈTE des produits vendus, pas un top 10)
      var buvetteSum = computeSummary(buvetteTx, {fullProductList: true});
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "BUVETTE", subtitle: dateLabel,
        summaryRows: buvetteSum.summaryRows,
        topRows: buvetteSum.topRows,
        fullProductList: true,
        headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: buildDetailRows(buvetteTx)
      }), "Buvette");

      // Feuille 4 : Boutique
      var boutiqueSum = computeSummary(boutiqueTx);
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "BOUTIQUE", subtitle: dateLabel,
        summaryRows: boutiqueSum.summaryRows,
        topRows: boutiqueSum.topRows,
        headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: buildDetailRows(boutiqueTx)
      }), "Boutique");

      // Feuille 5 : Billetterie
      var billetterieSum = computeSummary(billetterieTx);
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "BILLETTERIE", subtitle: dateLabel,
        summaryRows: billetterieSum.summaryRows,
        topRows: billetterieSum.topRows,
        headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: buildDetailRows(billetterieTx)
      }), "Billetterie");

      // Feuille 6 : Recharges Shopify — basée sur audit_log pour couvrir TOUTES les commandes
      // (auto_credit ET codes_fallback). Garantit la concordance avec le CA Shopify.
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "RECHARGES SHOPIFY (toutes commandes en ligne)", subtitle: dateLabel,
        summaryRows: [
          ["Nombre TOTAL de commandes Shopify", shopifyOrders.length],
          ["Montant ENCAISSÉ (= CA Shopify)", (totalRechargeOnlinePaye / 100).toFixed(2) + " €"],
          ["Bonus offert par le club", (totalRechargeOnlineBonus / 100).toFixed(2) + " €"],
          ["Montant TOTAL crédité aux comptes", (totalRechargeOnlineCredite / 100).toFixed(2) + " €"],
          [],
          ["─── Détail par mode ───"],
          ["Mode auto_credit (compte trouvé, crédit immédiat)", shopifyAutoCredit.length + " commandes · " + (sumPaid(shopifyAutoCredit) / 100).toFixed(2) + " € encaissé"],
          ["Mode codes_fallback (code envoyé au client)", shopifyCodesFallback.length + " commandes · " + (sumPaid(shopifyCodesFallback) / 100).toFixed(2) + " € encaissé"],
          [],
          ["💡 Le total ENCAISSÉ doit correspondre EXACTEMENT à ton CA Shopify du jour."],
          ["💡 Les recharges \"codes_fallback\" sont des clients dont l'email Shopify ne correspond à aucun compte CSL : ils ont reçu un code par mail à entrer manuellement sur la page cashless."]
        ],
        headers: ["Date", "Heure", "Client", "Commande", "Mode", "Encaissé (€)", "Bonus (€)", "Crédité (€)"],
        rows: shopifyOrders.map(function(a) {
          var credited = Math.abs(a.amount_cents || 0);
          var paid = realPaidCents(credited);
          var bonus = credited - paid;
          var mode = (a.details && a.details.mode) || "?";
          var modeLabel = mode === "auto_credit" ? "✅ Auto-crédité" : (mode === "codes_fallback" ? "📧 Code envoyé" : mode);
          var orderName = (a.details && a.details.order_name) || "—";
          return [
            formatDateOnly(a.created_at),
            new Date(a.created_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}),
            a.customer_name || "—",
            orderName,
            modeLabel,
            (paid / 100).toFixed(2),
            (bonus / 100).toFixed(2),
            (credited / 100).toFixed(2)
          ];
        })
      }), "Recharges Shopify");

      // Feuille 7 : Recharges guichet (au stade — vrais bénévoles)
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "RECHARGES AU STADE (GUICHET BÉNÉVOLES)", subtitle: dateLabel,
        summaryRows: [
          ["Nombre de recharges au stade", rechargeGuichetTx.length],
          ["Montant ENCAISSÉ en liquide/chèque", (totalRechargeGuichetPaye / 100).toFixed(2) + " €"],
          ["Bonus offert par le club", (totalRechargeGuichetBonus / 100).toFixed(2) + " €"],
          ["Montant TOTAL crédité aux comptes", (totalRechargeGuichetCredite / 100).toFixed(2) + " €"],
          ["Recharge moyenne (encaissé)", rechargeGuichetTx.length > 0 ? ((totalRechargeGuichetPaye / rechargeGuichetTx.length) / 100).toFixed(2) + " €" : "—"],
          [],
          ["✅ La colonne \"Encaissé\" correspond au cash/chèque réellement perçu par les bénévoles."]
        ],
        headers: ["Date", "Heure", "Client", "Bénévole", "Encaissé (€)", "Bonus (€)", "Crédité (€)"],
        rows: rechargeGuichetTx.map(function(t) {
          var acc = (t.account && t.account.name) || t.account_id || "?";
          var credited = Math.abs(t.amount_cents);
          var paid = realPaidCents(credited);
          var bonus = credited - paid;
          return [
            formatDateOnly(t.created_at),
            new Date(t.created_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}),
            acc,
            t.cashier_name || "—",
            (paid / 100).toFixed(2),
            (bonus / 100).toFixed(2),
            (credited / 100).toFixed(2)
          ];
        })
      }), "Recharges guichet");

      downloadWorkbook(wb, buildFilename("export-global"));
      setMsg("✅ Export global téléchargé (" + (sales.length + rechargeGuichetTx.length + shopifyOrders.length) + " mouvements)");
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur export : " + (e.message || e));
    }
    setExporting(false);
  };

  // ─── EXPORT 2 : VENTES CASHLESS GLOBALES (au stade) ───
  var exportVentesStade = async function() {
    if (exporting) return;
    setExporting(true); setMsg("⏳ Génération...");
    try {
      var range = getDateRange();
      var tx = await db.listTransactionsRange(range.start, range.end);
      if (tx.error) throw tx.error;
      var sales = tx.data.filter(function(t) { return t.amount_cents < 0 && t.type !== "adjustment"; });
      var dateLabel = range.start === range.end ? "Le " + formatDateOnly(range.start + "T12:00:00") : "Du " + formatDateOnly(range.start + "T12:00:00") + " au " + formatDateOnly(range.end + "T12:00:00");

      var buvetteTx = sales.filter(function(t) { return categorizeTx(t) === "buvette"; });
      var boutiqueTx = sales.filter(function(t) { return categorizeTx(t) === "boutique"; });
      var billetterieTx = sales.filter(function(t) { return categorizeTx(t) === "billetterie"; });
      var sum = function(list) { return list.reduce(function(s, t) { return s + Math.abs(t.amount_cents); }, 0); };

      var wb = XLSX.utils.book_new();
      var globalSum = computeSummary(sales);
      var extraSummary = [
        [],
        ["─── Répartition par catégorie ───"],
        ["Catégorie", "Nombre", "Montant (€)"],
        ["Buvette", buvetteTx.length, (sum(buvetteTx) / 100).toFixed(2)],
        ["Boutique", boutiqueTx.length, (sum(boutiqueTx) / 100).toFixed(2)],
        ["Billetterie", billetterieTx.length, (sum(billetterieTx) / 100).toFixed(2)]
      ];
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: "VENTES CASHLESS AU STADE", subtitle: dateLabel,
        summaryRows: globalSum.summaryRows.concat(extraSummary),
        topRows: globalSum.topRows,
        headers: ["Date", "Heure", "Catégorie", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: sales.map(function(t) {
          var acc = (t.account && t.account.name) || t.account_id || "?";
          return [
            formatDateOnly(t.created_at),
            new Date(t.created_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"}),
            categorizeTx(t),
            acc,
            t.cashier_name || "—",
            t.label || "",
            (Math.abs(t.amount_cents) / 100).toFixed(2)
          ];
        })
      }), "Ventes stade");

      downloadWorkbook(wb, buildFilename("ventes-stade"));
      setMsg("✅ Export téléchargé (" + sales.length + " ventes)");
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur export : " + (e.message || e));
    }
    setExporting(false);
  };

  // Helper générique pour exports d'une seule catégorie
  var exportCategory = async function(catKey, label, filePrefix) {
    if (exporting) return;
    setExporting(true); setMsg("⏳ Génération...");
    try {
      var range = getDateRange();
      var tx = await db.listTransactionsRange(range.start, range.end);
      if (tx.error) throw tx.error;
      var catTx = tx.data.filter(function(t) { return t.amount_cents < 0 && t.type !== "adjustment" && categorizeTx(t) === catKey; });
      var dateLabel = range.start === range.end ? "Le " + formatDateOnly(range.start + "T12:00:00") : "Du " + formatDateOnly(range.start + "T12:00:00") + " au " + formatDateOnly(range.end + "T12:00:00");

      var wb = XLSX.utils.book_new();
      var sum = computeSummary(catTx);
      XLSX.utils.book_append_sheet(wb, buildSheet({
        title: label.toUpperCase(), subtitle: dateLabel,
        summaryRows: sum.summaryRows,
        topRows: sum.topRows,
        headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
        rows: buildDetailRows(catTx)
      }), label);

      downloadWorkbook(wb, buildFilename(filePrefix));
      setMsg("✅ Export téléchargé (" + catTx.length + " ventes " + label.toLowerCase() + ")");
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur export : " + (e.message || e));
    }
    setExporting(false);
  };

  var exportBoutique = function() { return exportCategory("boutique", "Boutique", "boutique"); };

  // ─── EXPORT PAR POINT DE VENTE ───
  // Une feuille de synthese + une feuille par poste. C'est le Z de caisse que
  // le tresorier attend : chaque buvette est rapprochable de sa caisse.
  var exportParPoste = async function(onlyPos) {
    if (exporting) return;
    setExporting(true);
    setMsg(onlyPos ? "⏳ Génération du poste " + onlyPos.name + "..." : "⏳ Génération par point de vente...");
    try {
      var range = getDateRange();
      var tx = await db.listTransactionsRange(range.start, range.end);
      if (tx.error) throw tx.error;

      var sales = tx.data.filter(function(x) { return x.amount_cents < 0 && x.type !== "adjustment"; });
      var refunds = tx.data.filter(function(x) { return x.type === "deposit_refund"; });
      var dateLabel = range.start === range.end
        ? "Le " + formatDateOnly(range.start + "T12:00:00")
        : "Du " + formatDateOnly(range.start + "T12:00:00") + " au " + formatDateOnly(range.end + "T12:00:00");

      // Regroupement par poste (les ventes sans poste sont isolees)
      var groups = {};
      var order = [];
      sales.forEach(function(x) {
        var po = posOfTx(x);
        var key = po ? po.id : "__hors__";
        if (!groups[key]) {
          groups[key] = {name: po ? po.name : "Hors poste", emoji: po ? po.emoji : "❓", rows: []};
          order.push(key);
        }
        groups[key].rows.push(x);
      });

      if (onlyPos) {
        order = order.filter(function(k) { return k === onlyPos.id; });
        if (order.length === 0) {
          setMsg("⚠️ Aucune vente sur « " + onlyPos.name + " » pour cette période");
          setExporting(false);
          return;
        }
      }

      var wb = XLSX.utils.book_new();

      // Feuille de synthese (uniquement pour l'export complet)
      if (!onlyPos) {
        var totalAll = sales.reduce(function(s, x) { return s + Math.abs(x.amount_cents); }, 0);
        var depAll = sales.reduce(function(s, x) { return s + (x.deposit_cents || 0); }, 0);
        var synthese = order.map(function(k) {
          var g = groups[k];
          var cents = g.rows.reduce(function(s, x) { return s + Math.abs(x.amount_cents); }, 0);
          var dep = g.rows.reduce(function(s, x) { return s + (x.deposit_cents || 0); }, 0);
          return [
            g.name,
            g.rows.length,
            (cents / 100).toFixed(2),
            (dep / 100).toFixed(2),
            ((cents - dep) / 100).toFixed(2),
            g.rows.length ? (cents / g.rows.length / 100).toFixed(2) : "0.00",
            totalAll ? (cents * 100 / totalAll).toFixed(1) + " %" : "0 %"
          ];
        }).sort(function(a, b) { return parseFloat(b[2]) - parseFloat(a[2]); });

        synthese.push(["TOTAL", sales.length, (totalAll / 100).toFixed(2),
                       (depAll / 100).toFixed(2), ((totalAll - depAll) / 100).toFixed(2), "", "100 %"]);

        XLSX.utils.book_append_sheet(wb, buildSheet({
          title: "SYNTHÈSE PAR POINT DE VENTE",
          subtitle: dateLabel,
          summaryRows: [
            ["Points de vente actifs", String(order.length)],
            ["Ventes totales", String(sales.length)],
            ["Encaissé (produits + consignes)", (totalAll / 100).toFixed(2) + " €"],
            ["dont consignes encaissées", (depAll / 100).toFixed(2) + " €"],
            ["Chiffre d'affaires réel", ((totalAll - depAll) / 100).toFixed(2) + " €"]
          ],
          headers: ["Point de vente", "Ventes", "CA + consignes (€)", "dont consignes (€)", "CA produits (€)", "Ticket moyen (€)", "Part du CA"],
          rows: synthese
        }), "Synthèse");
      }

      // Une feuille par poste
      order.forEach(function(k) {
        var g = groups[k];
        var sum = computeSummary(g.rows, {fullProductList: true});
        var sheetName = g.name.substring(0, 28).replace(/[\\\/\?\*\[\]:]/g, " ");
        XLSX.utils.book_append_sheet(wb, buildSheet({
          title: g.name.toUpperCase(),
          subtitle: dateLabel,
          summaryRows: sum.summaryRows,
          topRows: sum.topRows,
          headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
          rows: buildDetailRows(g.rows)
        }), sheetName);
      });

      // Feuille des retours de consigne
      if (!onlyPos && refunds.length > 0) {
        var totRef = refunds.reduce(function(s, x) { return s + x.amount_cents; }, 0);
        XLSX.utils.book_append_sheet(wb, buildSheet({
          title: "RETOURS DE CONSIGNE", subtitle: dateLabel,
          summaryRows: [
            ["Retours", String(refunds.length)],
            ["Total remboursé", (totRef / 100).toFixed(2) + " €"]
          ],
          headers: ["Date", "Heure", "Poste", "Client", "Bénévole", "Libellé", "Montant (€)"],
          rows: buildDetailRows(refunds)
        }), "Retours consigne");
      }

      downloadWorkbook(wb, buildFilename(onlyPos ? "poste-" + onlyPos.id : "par-poste"));
      setMsg("✅ Export téléchargé (" + order.length + " poste" + (order.length > 1 ? "s" : "") + ")");
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur export : " + (e.message || e));
    }
    setExporting(false);
  };

  // Gestion produits
  var addNewProduct = async function() {
    if (!newProd.name.trim()) { setMsg("❌ Nom requis"); return; }
    var priceCents = toCents(newProd.price);
    if (!priceCents || priceCents <= 0) { setMsg("❌ Prix invalide"); return; }
    if (!selectedPos) { setMsg("❌ Sélectionne d'abord un point de vente"); return; }
    var r = await db.addProduct({name: newProd.name.trim(), priceCents: priceCents, emoji: newProd.emoji.trim(), sortOrder: allProducts.length + 1, posId: selectedPos.id});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setNewProd({name: "", price: "", emoji: "🍺"});
    setMsg("✅ Produit ajouté");
    await refreshProducts();
  };

  var startEdit = function(p) { setEditingId(p.id); setEditDraft({name: p.name, price: (p.price_cents / 100).toString(), emoji: p.emoji}); };
  var cancelEdit = function() { setEditingId(null); setEditDraft({}); };
  var saveEdit = async function(id) {
    if (!editDraft.name || !editDraft.name.trim()) { setMsg("❌ Nom requis"); return; }
    var priceCents = toCents(editDraft.price);
    if (!priceCents || priceCents <= 0) { setMsg("❌ Prix invalide"); return; }
    var r = await db.updateProduct(id, {name: editDraft.name.trim(), price_cents: priceCents, emoji: (editDraft.emoji || "🍺").trim()});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setEditingId(null); setEditDraft({}); setMsg("✅ Modifié");
    await refreshProducts();
  };
  var removeProduct = async function(p) {
    if (!confirm("Supprimer définitivement \"" + p.name + "\" ?")) return;
    var r = await db.deleteProduct(p.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg("✅ Supprimé");
    await refreshProducts();
  };

  // Les rôles d'un compte, en tenant compte des anciens comptes qui n'ont
  // que la colonne `role` renseignée.
  var rolesOf = function(a) {
    var r = (a.roles && a.roles.length) ? a.roles.slice() : [];
    if (a.role && r.indexOf(a.role) === -1) r.push(a.role);
    return r;
  };

  // Cocher ou décocher un rôle : un compte peut en cumuler plusieurs,
  // par exemple supporter et encaissement.
  var toggleRole = async function(account, role) {
    var cur = rolesOf(account);
    var next = cur.indexOf(role) !== -1
      ? cur.filter(function(x) { return x !== role; })
      : cur.concat([role]);

    if (next.length === 0) { setMsg("❌ Un compte doit garder au moins un rôle"); return; }
    if (role === "admin" && cur.indexOf("admin") === -1 &&
        !confirm("Donner les droits d'administration à " + account.name + " ?")) return;

    var r = await db.setAccountRoles(account.id, next);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (!r.data.success) {
      setMsg("❌ " + (r.data.error === "cannot_demote_ultra_admin"
        ? "Ce compte doit rester administrateur" : r.data.error));
      return;
    }
    setMsg("✅ " + account.name + " : " + next.join(", "));
    var refreshed = await db.listAccountsPage(page, 20, search);
    setAccounts(refreshed.data); setAccountsCount(refreshed.count);
  };

  // Ouvrir modal d'ajustement
  var openAdjust = function(a) {
    setAdjustingAccount(a);
    setAdjustSign("+");
    setAdjustAmount("");
    setAdjustReason("");
    setMsg("");
  };
  var closeAdjust = function() {
    if (adjusting) return;
    setAdjustingAccount(null);
    setAdjustAmount("");
    setAdjustReason("");
  };

  // Exécuter l'ajustement
  var doAdjustBalance = async function() {
    if (adjusting || !adjustingAccount) return;
    var amt = parseFloat((adjustAmount || "").replace(",", "."));
    if (!amt || amt <= 0) { setMsg("❌ Montant invalide"); return; }
    var reason = (adjustReason || "").trim();
    if (!reason) { setMsg("❌ Motif obligatoire"); return; }
    var amountCents = Math.round(amt * 100) * (adjustSign === "-" ? -1 : 1);
    // Confirmation
    var sign = adjustSign === "+" ? "+" : "−";
    var confirmMsg = "Confirmer l'ajustement " + sign + amt.toFixed(2) + " € sur le compte de " + adjustingAccount.name + " ?\n\nMotif : " + reason;
    if (!confirm(confirmMsg)) return;

    setAdjusting(true); setMsg("");
    var r = await db.adjustBalance(adjustingAccount.id, amountCents, reason, account.name);
    setAdjusting(false);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (!r.data.success) {
      if (r.data.error === "would_be_negative") {
        setMsg("❌ Solde insuffisant — l'ajustement ferait passer le solde en négatif");
      } else if (r.data.error === "not_admin") {
        setMsg("❌ Accès refusé (admin uniquement)");
      } else {
        setMsg("❌ " + r.data.error);
      }
      return;
    }
    setMsg("✅ Ajustement appliqué — Nouveau solde : " + formatEur(r.data.new_balance_cents));
    setAdjustingAccount(null);
    setAdjustAmount("");
    setAdjustReason("");
    // Refresh
    var refreshed = await db.listAccountsPage(page, 20, search);
    setAccounts(refreshed.data); setAccountsCount(refreshed.count);
    var tx = await db.listTransactions(100); setTransactions(tx.data);
  };

  // ─────────────────────────── POINTS DE VENTE
  var refreshPos = async function() {
    var r = await db.listPos(false);
    setPosList(r.data || []);
  };

  useEffect(function() {
    if (tab !== "pos" && tab !== "export") return;
    refreshPos();
  }, [tab]);

  // Carte du point de vente selectionne
  var refreshPosProducts = async function(posId) {
    var r = await db.listAllProducts(posId);
    setAllProducts(r.data || []);
  };

  useEffect(function() {
    if (tab !== "pos") return;
    if (selectedPos) refreshPosProducts(selectedPos.id);
    else setAllProducts([]);
  }, [tab, selectedPos]);

  var addNewPos = async function() {
    if (!newPos.name.trim()) { setMsg("❌ Nom requis"); return; }
    var r = await db.addPos({name: newPos.name.trim(), emoji: newPos.emoji.trim() || "🏉", sortOrder: posList.length + 1});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setNewPos({name: "", emoji: "🏉"});
    setMsg("✅ Point de vente créé");
    await refreshPos();
  };

  var startEditPos = function(p) {
    setEditPosId(p.id);
    setEditPos({name: p.name, emoji: p.emoji || "🏉"});
    setMsg("");
  };

  var saveEditPos = async function(id) {
    if (!editPos.name.trim()) { setMsg("❌ Nom requis"); return; }
    var r = await db.updatePos(id, {name: editPos.name.trim(), emoji: editPos.emoji.trim() || "🏉"});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setEditPosId(null);
    setMsg("✅ Point de vente mis à jour");
    await refreshPos();
    if (selectedPos && selectedPos.id === id) {
      setSelectedPos(Object.assign({}, selectedPos, {name: editPos.name.trim(), emoji: editPos.emoji.trim() || "🏉"}));
    }
  };

  var togglePos = async function(p) {
    var r = await db.updatePos(p.id, {active: !p.active});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(p.active ? "✅ Poste désactivé" : "✅ Poste réactivé");
    await refreshPos();
  };

  var removePos = async function(p) {
    if (!confirm("Supprimer « " + p.name + " » ?\n\nTOUS ses produits seront supprimés avec lui. Les ventes déjà enregistrées sont conservées.")) return;
    var r = await db.deletePos(p.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (selectedPos && selectedPos.id === p.id) setSelectedPos(null);
    setMsg("✅ Supprimé");
    await refreshPos();
  };

  // Genere un PNG imprimable : titre + QR + code en clair
  var downloadPosQR = function(p) {
    var holder = document.createElement("div");
    holder.style.position = "fixed";
    holder.style.left = "-9999px";
    document.body.appendChild(holder);
    try {
      new window.QRCode(holder, {
        text: p.qr_token, width: 700, height: 700,
        colorDark: "#000000", colorLight: "#ffffff",
        correctLevel: window.QRCode.CorrectLevel.H
      });
      setTimeout(function() {
        var src = holder.querySelector("canvas");
        var W = 900, H = 1160;
        var out = document.createElement("canvas");
        out.width = W; out.height = H;
        var g = out.getContext("2d");
        g.fillStyle = "#ffffff"; g.fillRect(0, 0, W, H);

        g.fillStyle = "#1A0E0F"; g.fillRect(0, 0, W, 190);
        g.fillStyle = "#ffffff";
        g.textAlign = "center";
        g.font = "bold 46px Oswald, Arial, sans-serif";
        g.fillText("CSL CASHLESS", W / 2, 78);
        g.fillStyle = "#F5C518";
        g.font = "bold 30px Oswald, Arial, sans-serif";
        g.fillText("POSTE D'ENCAISSEMENT", W / 2, 128);
        g.fillStyle = "#ffffff";
        g.font = "bold 40px Arial, sans-serif";
        g.fillText((p.emoji || "🏉") + "  " + p.name.toUpperCase(), W / 2, 175);

        if (src) g.drawImage(src, (W - 700) / 2, 240, 700, 700);

        g.fillStyle = "#1A0E0F";
        g.font = "bold 30px monospace";
        g.fillText(p.qr_token, W / 2, 1002);
        g.fillStyle = "#7A6F6C";
        g.font = "24px Arial, sans-serif";
        g.fillText("Les bénévoles scannent ce code avant d'encaisser.", W / 2, 1052);
        g.fillText("À coller sur le poste. Ne pas jeter.", W / 2, 1088);
        g.fillStyle = "#D02128";
        g.fillRect(0, H - 22, W, 22);

        var a = document.createElement("a");
        a.href = out.toDataURL("image/png");
        a.download = "CSL-poste-" + p.id + ".png";
        a.click();
        document.body.removeChild(holder);
      }, 120);
    } catch (e) {
      document.body.removeChild(holder);
      setMsg("❌ Génération du QR impossible");
    }
  };

  // ─────────────────────────── CONSIGNES
  var refreshDeposits = async function() {
    var r = await db.listDeposits(false);
    setDepList(r.data || []);
  };

  useEffect(function() {
    if (tab !== "deposits" && tab !== "pos") return;
    refreshDeposits();
  }, [tab]);

  var addNewDeposit = async function() {
    if (!newDep.name.trim()) { setMsg("❌ Nom requis"); return; }
    var cents = Math.round(parseFloat((newDep.price || "0").replace(",", ".")) * 100);
    if (isNaN(cents) || cents < 0) { setMsg("❌ Montant invalide"); return; }
    var r = await db.addDeposit({name: newDep.name.trim(), emoji: newDep.emoji.trim() || "🥤", priceCents: cents, sortOrder: depList.length + 1});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setNewDep({name: "", emoji: "🥤", price: "1"});
    setMsg("✅ Consigne créée");
    await refreshDeposits();
  };

  var startEditDep = function(d) {
    setEditDepId(d.id);
    setEditDep({name: d.name, emoji: d.emoji || "🥤", price: (d.price_cents / 100).toFixed(2)});
  };

  var saveEditDep = async function(id) {
    var cents = Math.round(parseFloat((editDep.price || "0").replace(",", ".")) * 100);
    if (!editDep.name.trim() || isNaN(cents) || cents < 0) { setMsg("❌ Valeurs invalides"); return; }
    var r = await db.updateDeposit(id, {name: editDep.name.trim(), emoji: editDep.emoji.trim() || "🥤", price_cents: cents});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setEditDepId(null);
    setMsg("✅ Consigne mise à jour");
    await refreshDeposits();
  };

  var toggleDeposit = async function(d) {
    var r = await db.updateDeposit(d.id, {active: !d.active});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(d.active ? "✅ Consigne désactivée" : "✅ Consigne réactivée");
    await refreshDeposits();
  };

  var removeDeposit = async function(d) {
    if (!confirm("Supprimer la consigne « " + d.name + " » ?\n\nLes produits qui la portaient n'auront plus de consigne. Les ventes passées sont conservées.")) return;
    var r = await db.deleteDeposit(d.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg("✅ Supprimée");
    await refreshDeposits();
    if (selectedPos) refreshPosProducts(selectedPos.id);
  };

  // Rattache / detache une consigne a un produit
  var setProductDeposit = async function(prod, depositId) {
    var r = await db.updateProduct(prod.id, {deposit_id: depositId || null});
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (selectedPos) await refreshPosProducts(selectedPos.id);
  };

  // ─────────────────────────── STAGES
  var refreshStages = async function(slug) {
    var r = await db.stageBookings(null);
    var rows = r.data || [];
    setStageRows(rows);
    // Les stages se déduisent des inscriptions : pas de liste à tenir,
    // un stage apparaît dès la première réservation.
    var target = slug || stageSlug;
    if (!target && rows.length) target = rows[0].stage_slug;
    if (target) {
      setStageSlug(target);
      var d = await db.stageDayCounts(target);
      setStageDays(d.data || []);
    } else {
      setStageDays([]);
    }
  };

  useEffect(function() {
    if (tab !== "stages") return;
    refreshStages();
    refreshStageList();
  }, [tab]);

  // ─────────────────────────── STATUTS
  var refreshMTypes = async function() {
    var r = await db.memberTypes();
    setMTypes(r.data || []);
  };

  useEffect(function() {
    if (tab !== "statuts") return;
    refreshMTypes();
  }, [tab]);

  // La liste des statuts sert aussi dans l'onglet Comptes
  useEffect(function() {
    if (tab !== "accounts") return;
    if (mTypes.length) return;
    refreshMTypes();
  }, [tab]);

  var saveMType = async function() {
    var f = mForm;
    if (!f.name) { setMErr("Indique un nom."); return; }
    var pct = function(v) {
      var n = parseInt(String(v || "0").replace(",", "."), 10);
      return isNaN(n) ? 0 : Math.min(Math.max(n, 0), 100);
    };
    setMErr("");
    var r = await db.memberTypeSave({
      id: f.id, name: f.name, slug: f.slug, emoji: f.emoji, color: f.color,
      freeEntry: !!f.freeEntry, entryPct: pct(f.entryPct), shopPct: pct(f.shopPct),
      barPct: pct(f.barPct), mealPct: pct(f.mealPct), notes: f.notes,
      active: f.active !== false, sort: f.sort ? parseInt(f.sort, 10) : 99
    });
    if (r.error) { setMErr(r.error.message); return; }
    if (r.data && r.data.success === false) {
      setMErr(r.data.error === "slug_taken" ? "Identifiant déjà pris : " + r.data.slug : r.data.error);
      return;
    }
    setMForm(null); setMErr(""); setMsg("✅ Statut enregistré");
    await refreshMTypes();
  };

  var removeMType = async function(x) {
    if (!confirm("Supprimer le statut « " + x.name + " » ?")) return;
    var r = await db.memberTypeDelete(x.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(r.data && r.data.deactivated
      ? "✅ Désactivé (" + r.data.membres + " compte(s) le portent)"
      : "✅ Supprimé");
    await refreshMTypes();
  };

  // ─────────────────────────── CARTE DE MEMBRE
  var ouvrirCarte = async function(account) {
    setCardFor(account); setCardImg(""); setCardBusy(true);
    var r = await db.memberCard(account.id);
    var d = r.data;
    setCardData(d || null);
    if (!d) { setCardBusy(false); setMsg("❌ Compte introuvable"); return; }
    var url = d.photo_path ? await db.photoUrl(d.photo_path) : null;
    var qr = await qrHorsEcran(d.qr || account.qr || "", 400);
    var png = await dessinerCarte(d, {photoUrl: url, qrCanvas: qr, fondPerdu: cardBleed});
    setCardImg(png); setCardBusy(false);
  };

  var regenererCarte = async function(avecFondPerdu) {
    if (!cardFor) return;
    setCardBleed(avecFondPerdu); setCardBusy(true);
    var r = await db.memberCard(cardFor.id);
    var d = r.data;
    setCardData(d || null);
    if (!d) { setCardBusy(false); return; }
    var url = d.photo_path ? await db.photoUrl(d.photo_path) : null;
    var qr = await qrHorsEcran(d.qr || cardFor.qr || "", 400);
    var png = await dessinerCarte(d, {photoUrl: url, qrCanvas: qr, fondPerdu: avecFondPerdu});
    setCardImg(png); setCardBusy(false);
  };

  var telechargerCarte = async function() {
    if (!cardImg || !cardFor) return;
    var a = document.createElement("a");
    a.href = cardImg;
    a.download = "carte-csl-" + String(cardFor.name || "membre")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase() + ".png";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    await db.markCardPrinted(cardFor.id);
    setMsg("✅ Carte téléchargée");
  };

  var assignMType = async function(account, typeId) {
    var r = await db.setAccountMemberType(account.id, typeId || null);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    var nom = (mTypes.filter(function(x) { return x.id === typeId; })[0] || {}).name || "aucun";
    setMsg("✅ " + account.name + " : " + nom);
    var refreshed = await db.listAccountsPage(page, 20, search);
    setAccounts(refreshed.data); setAccountsCount(refreshed.count);
  };

  // ─────────────────────────── BILLETTERIE
  var refreshTickets = async function(keepId) {
    var r = await db.ticketEvents();
    var list = r.data || [];
    setTkEvents(list);
    var id = keepId || (tkSel && tkSel.id);
    if (id) {
      var up = list.filter(function(x) { return x.id === id; })[0];
      if (up) setTkSel(up);
    }
  };

  useEffect(function() {
    if (tab !== "tickets") return;
    refreshTickets();
  }, [tab]);

  var openTicketEvent = async function(ev) {
    setTkSel(ev); setTkTab("tarifs"); setMsg(""); setTkErr("");
    var o = await db.ticketOrders(ev.id);
    setTkOrders(o.data || []);
  };

  var saveTicketEvent = async function() {
    var f = tkForm;
    if (!f.title) { setTkErr("Indique l'affiche du match."); return; }
    if (!f.starts_at) { setTkErr("Indique la date ET l'heure du coup d'envoi."); return; }
    setTkErr("");

    var r = await db.ticketEventSave({
      id: f.id, title: f.title, slug: f.slug, competition: f.competition,
      starts_at: toIso(f.starts_at),
      gates_open_at: f.gates_open_at ? toIso(f.gates_open_at) : null,
      venue: f.venue, poster_url: f.poster_url, info: f.info,
      capacity: f.capacity === "" ? null : parseInt(f.capacity, 10),
      sales_open: !!f.sales_open,
      closes_at: f.closes_at ? toIso(f.closes_at) : null,
      published: !!f.published
    });
    if (r.error) { setTkErr(r.error.message); return; }
    if (r.data && r.data.success === false) {
      setTkErr(r.data.error === "slug_taken" ? "Identifiant déjà pris : " + r.data.slug : r.data.error);
      return;
    }
    setTkForm(null); setTkErr(""); setMsg("✅ Match enregistré");
    await refreshTickets(r.data.event_id);
  };

  var removeTicketEvent = async function(ev) {
    if (!confirm("Supprimer le match « " + ev.title + " » ?")) return;
    var r = await db.ticketEventDelete(ev.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(r.data && r.data.closed ? "✅ Vente fermée (des billets ont été vendus)" : "✅ Match supprimé");
    setTkSel(null);
    await refreshTickets();
  };

  var saveTicketType = async function() {
    var f = tkTypeForm;
    if (!f.name) { setTkErr("Indique un nom de catégorie."); return; }
    var cents = Math.round(parseFloat(String(f.price || "0").replace(",", ".")) * 100);
    if (isNaN(cents) || cents < 0) { setTkErr("Prix invalide."); return; }
    setTkErr("");

    var r = await db.ticketTypeSave({
      id: f.id, eventId: f.eventId, name: f.name, priceCents: cents,
      description: f.description, capacity: f.capacity === "" ? null : parseInt(f.capacity, 10),
      maxPerOrder: f.maxPerOrder ? parseInt(f.maxPerOrder, 10) : 10,
      active: f.active !== false, sort: f.sort ? parseInt(f.sort, 10) : 0
    });
    if (r.error) { setTkErr(r.error.message); return; }
    if (r.data && r.data.success === false) { setTkErr(r.data.error); return; }
    setTkTypeForm(null); setTkErr(""); setMsg("✅ Catégorie enregistrée");
    await refreshTickets();
  };

  var removeTicketType = async function(ty) {
    if (!confirm("Retirer la catégorie « " + ty.name + " » ?")) return;
    var r = await db.ticketTypeDelete(ty.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(r.data && r.data.deactivated ? "✅ Désactivée (des billets ont été vendus)" : "✅ Retirée");
    await refreshTickets();
  };

  var copyTicketTypes = async function(fromId) {
    if (!fromId) return;
    var r = await db.ticketCopyTypes(fromId, tkSel.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg("✅ " + ((r.data && r.data.copied) || 0) + " catégorie(s) copiée(s)");
    await refreshTickets();
  };

  var ticketOrderStatus = async function(o, status) {
    if (status === "annulee" && !confirm("Annuler la commande " + o.ref + " ?\n\nLes billets non scannés seront invalidés.")) return;
    var r = await db.ticketOrderStatus(o.id, status);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(status === "payee" ? "✅ Marquée payée" : status === "annulee" ? "✅ Annulée" : "✅ Remise en attente");
    var no = await db.ticketOrders(tkSel.id);
    setTkOrders(no.data || []);
    await refreshTickets();
  };

  var exportTickets = function() {
    if (!tkSel) return;
    var wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "BILLETTERIE",
      subtitle: tkSel.title + " — " + formatDateOnly(tkSel.starts_at),
      summaryRows: [
        ["Billets vendus", String(tkSel.vendus)],
        ["Entrées scannées", String(tkSel.entres)],
        ["Encaissé", (tkSel.encaisse / 100).toFixed(2) + " €"],
        ["Reste à encaisser", (tkSel.attendu / 100).toFixed(2) + " €"]
      ],
      headers: ["Catégorie", "Prix (€)", "Vendus", "Quota"],
      rows: (tkSel.types || []).map(function(ty) {
        return [ty.name, (ty.price_cents / 100).toFixed(2), ty.vendus,
                ty.capacity == null ? "illimité" : ty.capacity];
      })
    }), "Synthèse");

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "COMMANDES",
      subtitle: tkSel.title,
      summaryRows: [["Commandes", String(tkOrders.length)]],
      headers: ["Réf.", "Acheteur", "E-mail", "Téléphone", "Billets",
                "Détail", "Total (€)", "Paiement", "Statut"],
      rows: tkOrders.map(function(o) {
        return [o.ref, o.name, o.email, o.phone || "",
                (o.tickets || []).length,
                (o.tickets || []).map(function(x) { return x.type; }).join(", "),
                (o.total_cents / 100).toFixed(2),
                o.payment_mode === "en_ligne" ? "En ligne" : o.payment_mode === "stade" ? "Au stade" :
                  o.payment_mode === "cashless" ? "Cashless" : "Offert",
                o.status === "payee" ? "Payée" : o.status === "annulee" ? "Annulée" : "En attente"];
      })
    }), "Commandes");

    var billets = [];
    tkOrders.forEach(function(o) {
      (o.tickets || []).forEach(function(x) {
        billets.push([x.code, x.type, o.name, o.ref,
          x.status === "utilise" ? "Entré" : x.status === "annule" ? "Annulé" : "Valide",
          x.used_at ? formatDateOnly(x.used_at) : ""]);
      });
    });
    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "BILLETS",
      subtitle: tkSel.title,
      summaryRows: [["Billets", String(billets.length)]],
      headers: ["Code", "Catégorie", "Porteur", "Commande", "Statut", "Entré le"],
      rows: billets
    }), "Billets");

    downloadWorkbook(wb, buildFilename("billetterie-" + String(tkSel.title).substring(0, 20)));
    setMsg("✅ Export téléchargé");
  };

  var refreshStageList = async function() {
    var r = await db.stageAdminList();
    setStageList(r.data || []);
  };

  // Un stage tout juste créé n'a aucune inscription : sans cette liste,
  // il serait invisible puisque les onglets se déduisent des réservations.
  var stageCurrent = function() {
    return stageList.filter(function(s) { return s.slug === stageSlug; })[0] || null;
  };

  var saveStage = async function() {
    var f = stageForm;
    if (!f.name) { setStageErr("Indique un nom."); return; }
    if (!f.starts_on || !f.ends_on) { setStageErr("Indique les dates de début et de fin."); return; }
    var wp = f.weekPrice === "" ? null : Math.round(parseFloat(String(f.weekPrice).replace(",", ".")) * 100);
    if (wp !== null && isNaN(wp)) { setStageErr("Prix de la semaine invalide."); return; }
    setStageErr("");

    var r = await db.stageSave({
      id: f.id, name: f.name, slug: f.slug, subtitle: f.subtitle,
      starts_on: f.starts_on, ends_on: f.ends_on,
      weekPrice: wp,
      weekTeams: (f.weekTeams || "").split(",").map(function(x) { return x.trim(); }).filter(Boolean),
      place: f.place, schedule: f.schedule, info: f.info,
      open: !!f.open, closes_at: f.closes_at ? new Date(f.closes_at).toISOString() : null
    });
    if (r.error) { setStageErr(r.error.message); return; }
    if (r.data && r.data.success === false) {
      setStageErr(r.data.error === "slug_taken" ? "Cet identifiant est déjà pris : " + r.data.slug
                : r.data.error === "end_before_start" ? "La fin est avant le début."
                : r.data.error);
      return;
    }
    setStageForm(null); setStageErr(""); setMsg("✅ Stage enregistré");
    setStageSlug(r.data.slug);
    await refreshStageList();
  };

  var removeStage = async function(s) {
    if (!confirm("Supprimer le stage « " + s.name + " » ?")) return;
    var r = await db.stageDelete(s.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(r.data && r.data.closed ? "✅ Stage fermé (des inscriptions existent)" : "✅ Stage supprimé");
    setStageSlug("");
    await refreshStageList();
  };

  var saveDay = async function() {
    var f = dayForm;
    if (!f.date) { setStageErr("Indique la date de la journée."); return; }
    if (!f.label) { setStageErr("Indique un libellé, par exemple Lundi."); return; }
    var cents = Math.round(parseFloat(String(f.price || "0").replace(",", ".")) * 100);
    if (isNaN(cents) || cents < 0) { setStageErr("Prix invalide."); return; }
    setStageErr("");

    var r = await db.stageDaySave({
      id: f.id, stageId: f.stageId, date: f.date, label: f.label,
      priceCents: cents, activity: f.activity,
      teams: (f.teams || "").split(",").map(function(x) { return x.trim(); }).filter(Boolean),
      capacity: f.capacity === "" ? null : parseInt(f.capacity, 10),
      sort: f.sort ? parseInt(f.sort, 10) : 0
    });
    if (r.error) { setStageErr(r.error.message); return; }
    if (r.data && r.data.success === false) { setStageErr(r.data.error); return; }
    setDayForm(null); setStageErr(""); setMsg("✅ Journée enregistrée");
    await refreshStageList();
  };

  var removeDay = async function(d) {
    if (!confirm("Retirer la journée « " + d.label + " » ?")) return;
    var r = await db.stageDayDelete(d.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (r.data && r.data.success === false) {
      setMsg(r.data.error === "day_booked"
        ? "❌ " + r.data.count + " inscription(s) sur cette journée : impossible de la retirer"
        : "❌ " + r.data.error);
      return;
    }
    setMsg("✅ Journée retirée");
    await refreshStageList();
  };

  var duplicateStage = async function(s) {
    var nom = prompt("Nom du nouveau stage ?", s.name.replace(/\d{4}/, function(y) { return String(parseInt(y, 10)); }));
    if (!nom) return;
    var d = prompt("Premier jour du nouveau stage (JJ/MM/AAAA) ?");
    if (!d) return;
    var m = d.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!m) { setMsg("❌ Format attendu : 20/12/2026"); return; }
    var iso = m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");

    var r = await db.stageDuplicate(s.id, nom, null, iso);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    if (r.data && r.data.success === false) {
      setMsg(r.data.error === "slug_taken" ? "❌ Identifiant déjà pris : " + r.data.slug : "❌ " + r.data.error);
      return;
    }
    setMsg("✅ Stage dupliqué avec " + (r.data.days || 0) + " journée(s)");
    setStageSlug(r.data.slug);
    await refreshStageList();
  };

  var pickStage = async function(slug) {
    setStageSlug(slug);
    var d = await db.stageDayCounts(slug);
    setStageDays(d.data || []);
  };

  var stageStatus = async function(row, status) {
    if (status === "annule" && !confirm("Annuler l'inscription de " + row.child + " ?\n\nElle sortira des effectifs. Le remboursement se fait à part.")) return;
    var r = await db.stageSetStatus(row.id, status);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(status === "paye" ? "✅ Marquée payée" : status === "annule" ? "✅ Annulée" : "✅ Remise en attente");
    await refreshStages(stageSlug);
  };

  var exportStage = function() {
    var rows = stageRows.filter(function(r) { return r.stage_slug === stageSlug; });
    if (!rows.length) { setMsg("❌ Aucune inscription à exporter"); return; }
    var name = rows[0].stage_name || stageSlug;
    var wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "EFFECTIFS PAR JOURNÉE",
      subtitle: name,
      summaryRows: [
        ["Inscriptions", String(rows.filter(function(r) { return r.status !== "annule"; }).length)],
        ["Reste à encaisser", (rows.filter(function(r) { return r.status === "en_attente"; })
          .reduce(function(s, r) { return s + r.amount_cents; }, 0) / 100).toFixed(2) + " €"]
      ],
      headers: ["Date", "Journée", "Activité", "Inscrits", "Payés"],
      rows: stageDays.map(function(d) {
        return [formatDateOnly(d.day_date), d.label, d.activity || "", d.inscrits, d.payes];
      })
    }), "Effectifs");

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "INSCRIPTIONS",
      subtitle: name,
      summaryRows: [["Total", String(rows.length)]],
      headers: ["Réf.", "Enfant", "Catégorie", "Formule", "Jours", "Responsable",
                "Téléphone", "E-mail", "Remarques", "Montant (€)", "Règlement", "Statut"],
      rows: rows.map(function(r) {
        return [r.ref, r.child, r.team,
                r.formula === "semaine" ? "Semaine" : "À la journée",
                (r.day_dates || []).slice().sort().map(function(x) {
                  var p = String(x).split("-"); return p[2] + "/" + p[1];
                }).join(", "),
                r.parent, r.phone, r.email, r.notes || "",
                (r.amount_cents / 100).toFixed(2),
                r.payment_method === "cb" ? "Carte" : r.payment_method === "cheque" ? "Chèque" :
                  r.payment_method === "especes" ? "Espèces" : "",
                r.status === "paye" ? "Payé" : r.status === "annule" ? "Annulé" : "À régler"];
      })
    }), "Inscriptions");

    downloadWorkbook(wb, buildFilename("stage-" + String(stageSlug).substring(0, 24)));
    setMsg("✅ Export téléchargé");
  };

  // ─────────────────────────── REPAS
  var refreshMeals = async function() {
    var r = await db.mealEvents();
    var list = r.data || [];
    setMeals(list);
    if (mealSel) {
      var up = list.filter(function(x) { return x.id === mealSel.id; })[0];
      if (up) setMealSel(up);
    }
  };

  useEffect(function() {
    if (tab !== "meals") return;
    refreshMeals();
  }, [tab]);

  var openMeal = async function(ev) {
    setMealSel(ev); setMealTab("carte"); setMsg("");
    var b = await db.mealBookings(ev.id);
    setMealBk(b.data || []);
    var s = await db.mealSheet(ev.id);
    setMealSheet(s.data || null);
  };

  // La date arrive au format datetime-local : il faut la repasser en ISO
  var toIso = function(v) { return v ? new Date(v).toISOString() : null; };
  var toLocal = function(iso) {
    if (!iso) return "";
    var d = new Date(iso), p = function(n) { return String(n).padStart(2, "0"); };
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) +
           "T" + p(d.getHours()) + ":" + p(d.getMinutes());
  };

  var saveMealEvent = async function() {
    var f = mealForm;
    if (!f.title) { setMealErr("Indique un titre."); return; }
    // Un champ datetime-local sans heure renvoie une chaîne vide :
    // l'erreur la plus fréquente est d'avoir saisi la date et oublié l'heure.
    if (!f.served_at) { setMealErr("Indique la date ET l'heure du repas."); return; }
    setMealErr("");
    var r = await db.mealEventSave({
      id: f.id, title: f.title, subtitle: f.subtitle,
      served_at: toIso(f.served_at), location: f.location,
      deadline: toIso(f.deadline), intro: f.intro,
      capacity: f.capacity ? parseInt(f.capacity, 10) : null,
      published: !!f.published
    });
    if (r.error) { setMealErr(r.error.message); return; }
    if (r.data && r.data.success === false) { setMealErr(r.data.error || "Enregistrement refusé"); return; }
    setMealForm(null); setMealErr(""); setMsg("✅ Repas enregistré");
    await refreshMeals();
  };

  var saveMealItem = async function() {
    var f = itemForm;
    if (!f.name) { setMealErr("Indique un nom de plat."); return; }
    var cents = Math.round(parseFloat((f.price || "0").replace(",", ".")) * 100);
    if (isNaN(cents) || cents < 0) { setMealErr("Prix invalide."); return; }
    setMealErr("");
    var r = await db.mealItemSave({
      id: f.id, eventId: mealSel.id, name: f.name, priceCents: cents,
      description: f.description, kind: f.kind || "plat",
      countsSeat: f.countsSeat !== false, active: f.active !== false,
      sort: f.sort ? parseInt(f.sort, 10) : 99
    });
    if (r.error) { setMealErr(r.error.message); return; }
    if (r.data && r.data.success === false) { setMealErr(r.data.error || "Enregistrement refusé"); return; }
    setItemForm(null); setMealErr(""); setMsg("✅ Plat enregistré");
    await refreshMeals();
  };

  var removeMealItem = async function(it) {
    if (!confirm("Retirer « " + it.name + " » de la carte ?")) return;
    var r = await db.mealItemDelete(it.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg(r.data && r.data.deactivated ? "✅ Désactivé (déjà commandé)" : "✅ Retiré");
    await refreshMeals();
  };

  var copyMenuFrom = async function(fromId) {
    if (!fromId) return;
    var r = await db.mealCopyMenu(fromId, mealSel.id);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg("✅ " + ((r.data && r.data.copied) || 0) + " plat(s) copié(s)");
    await refreshMeals();
  };

  var setBookingStatus = async function(b, status) {
    var r = await db.mealSetStatus(b.id, status);
    if (r.error) { setMsg("❌ " + r.error.message); return; }
    setMsg("✅ " + (status === "payee" ? "Marquée payée" : status === "annulee" ? "Annulée" : "Remise en attente"));
    var nb = await db.mealBookings(mealSel.id);
    setMealBk(nb.data || []);
    var s = await db.mealSheet(mealSel.id);
    setMealSheet(s.data || null);
    await refreshMeals();
  };

  var exportMealSheet = function() {
    if (!mealSel) return;
    var wb = XLSX.utils.book_new();
    var tot = (mealSheetData && mealSheetData.totaux) || [];
    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "FEUILLE DE SERVICE",
      subtitle: mealSel.title + " — " + formatDateOnly(mealSel.served_at),
      summaryRows: [
        ["Couverts", String((mealSheetData && mealSheetData.couverts) || 0)],
        ["Reste à encaisser", (((mealSheetData && mealSheetData.a_encaisser) || 0) / 100).toFixed(2) + " €"]
      ],
      headers: ["Plat", "Quantité"],
      rows: tot.map(function(x) { return [x.name, x.qty]; })
    }), "Cuisine");

    // Ce que le club imprime pour dresser les tables
    var parTable = {};
    mealBk.filter(function(b) { return b.status !== "annulee"; }).forEach(function(b) {
      var k = b.table_name || b.name;
      if (!parTable[k]) parTable[k] = {couverts: 0, nb: 0};
      parTable[k].couverts += b.seats;
      parTable[k].nb += 1;
    });
    // La liste nominative : qui est à table, et à quelle table
    var convives = [];
    mealBk.filter(function(b) { return b.status !== "annulee"; }).forEach(function(b) {
      (b.guests || []).forEach(function(g) {
        convives.push([b.table_name || b.name, g.name, g.item, b.ref,
          b.status === "payee" ? "Payée" : "À régler"]);
      });
    });
    convives.sort(function(a, z) { return String(a[0]).localeCompare(String(z[0])); });
    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "CONVIVES",
      subtitle: mealSel.title + " — " + formatDateOnly(mealSel.served_at),
      summaryRows: [["Convives", String(convives.length)]],
      headers: ["Table", "Nom", "Plat", "Réf.", "Paiement"],
      rows: convives
    }), "Convives");

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "PLAN DE SALLE",
      subtitle: mealSel.title + " — " + formatDateOnly(mealSel.served_at),
      summaryRows: [["Tables", String(Object.keys(parTable).length)]],
      headers: ["Table", "Couverts", "Réservations"],
      rows: Object.keys(parTable).sort().map(function(k) {
        return [k, parTable[k].couverts, parTable[k].nb];
      })
    }), "Plan de salle");

    XLSX.utils.book_append_sheet(wb, buildSheet({
      title: "RÉSERVATIONS",
      subtitle: mealSel.title,
      summaryRows: [["Réservations", String(mealBk.length)]],
      headers: ["Réf.", "Table", "Nom", "E-mail", "Téléphone", "Couverts", "Détail", "Total (€)", "Paiement", "Statut"],
      rows: mealBk.map(function(b) {
        return [b.ref, b.table_name || b.name, b.name, b.email, b.phone, b.seats,
                (b.lines || []).map(function(l) { return l.qty + "x " + l.name; }).join(", "),
                (b.total_cents / 100).toFixed(2),
                b.payment_mode === "en_ligne" ? "En ligne" : "Chez Buffard",
                b.status === "payee" ? "Payée" : b.status === "annulee" ? "Annulée" : "En attente"];
      })
    }), "Réservations");

    downloadWorkbook(wb, buildFilename("repas-" + mealSel.title.substring(0, 20)));
    setMsg("✅ Feuille téléchargée");
  };

  var tabs = [
    {key: "stats", icon: "📊", label: "Stats"},
    {key: "meals", icon: "🍽️", label: "Repas"},
    {key: "stages", icon: "🎒", label: "Stages"},
    {key: "tickets", icon: "🎟️", label: "Billetterie"},
    {key: "statuts", icon: "🪪", label: "Statuts"},
    {key: "pos", icon: "📍", label: "Points de vente"},
    {key: "deposits", icon: "🥤", label: "Consignes"},
    {key: "accounts", icon: "👥", label: "Comptes"},
    {key: "roles", icon: "🛡", label: "Rôles"},
    {key: "export", icon: "📥", label: "Export"}
  ];

  return h("div", null,
    // Tabs
    h("div", {style: {display: "flex", gap: 6, marginBottom: 20, overflow: "auto", paddingBottom: 4}},
      tabs.map(function(t) {
        return h("button", {key: t.key, onClick: function() { setTab(t.key); setMsg(""); }, style: {padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Oswald", fontSize: 12, letterSpacing: 1.5, background: tab === t.key ? C.deep : "#fff", color: tab === t.key ? "#fff" : C.muted, fontWeight: 700, textTransform: "uppercase", boxShadow: tab === t.key ? "0 4px 12px rgba(26,14,15,.2)" : "0 1px 3px rgba(0,0,0,.04)"}}, t.icon + " " + t.label);
      })
    ),
    // STATS
    tab === "stats" && h("div", null,
      h("div", {style: {display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20}},
        [
          [accountsCount || "—", "Comptes total", C.blue, "👥"],
          [accountsTodayCount, "Comptes créés auj.", C.gold, "✨"],
          [formatEur(todayRevCents), "CA aujourd'hui", C.green, "💰"],
          [formatEur(todayRechCents), "Recharges encaissées", C.gold, "⚡"],
          [todayTx.length, "Transactions auj.", C.deep, "📊"]
        ].map(function(s, i) {
          return h(Card, {key: i, style: {padding: 20}},
            h("div", {style: {fontSize: 24, marginBottom: 8}}, s[3]),
            h("div", {style: {fontFamily: "Oswald", fontSize: 28, fontWeight: 800, color: s[2]}}, s[0]),
            h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginTop: 4, fontFamily: "Oswald", fontWeight: 600}}, s[1])
          );
        })
      ),
      // Info-box bonus offerts du jour
      todayBonusCents > 0 && h("div", {style: {background: "rgba(208,33,40,.06)", border: "1px solid rgba(208,33,40,.2)", borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 12, color: C.muted, textAlign: "center"}},
        "🎁 ", h("strong", {style: {color: C.blue}}, formatEur(todayBonusCents)),
        " de bonus offerts aux supporters aujourd'hui (déduits des recharges encaissées)"
      ),
      todayAdjustments > 0 && h("div", {style: {background: "rgba(255,182,39,.08)", border: "1px solid rgba(255,182,39,.3)", borderRadius: 10, padding: 12, marginBottom: 18, fontSize: 12, color: C.muted, textAlign: "center"}},
        "ℹ️ " + todayAdjustments + " ajustement" + (todayAdjustments > 1 ? "s" : "") + " manuel" + (todayAdjustments > 1 ? "s" : "") + " effectué" + (todayAdjustments > 1 ? "s" : "") + " aujourd'hui (non comptabilisé" + (todayAdjustments > 1 ? "s" : "") + " dans le CA)"
      ),

      // ═══════════════════════════════════════════════════════════
      // GRAPHIQUE CA — Évolution 7/30 jours
      // ═══════════════════════════════════════════════════════════
      h(Card, {style: {marginBottom: 18}},
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16}},
          h("div", null,
            h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase"}}, "📈 Évolution du CA"),
            h("div", {style: {fontSize: 11, color: C.muted, marginTop: 3}}, "Chiffre d'affaires (ventes uniquement, hors recharges)")
          ),
          h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 8}},
            [7, 30].map(function(days) {
              var active = historyRange === days;
              return h("button", {
                key: days,
                onClick: function() { setHistoryRange(days); },
                style: {background: active ? "#fff" : "transparent", color: active ? C.deep : C.muted, border: "none", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", boxShadow: active ? "0 1px 3px rgba(0,0,0,.1)" : "none"}
              }, days + " jours");
            })
          )
        ),
        loadingHistory && h("div", {style: {textAlign: "center", padding: 30, color: C.muted, fontSize: 13}}, "⏳ Chargement..."),
        !loadingHistory && (function() {
          // Rendu SVG du graphique
          var w = 800, hSvg = 200, padL = 50, padR = 20, padT = 20, padB = 40;
          var chartW = w - padL - padR;
          var chartH = hSvg - padT - padB;
          var maxCents = Math.max.apply(null, revByDay.map(function(d) { return d.cents; }).concat([1]));
          // arrondir max supérieur pour axe joli
          var maxEur = Math.ceil(maxCents / 100 / 100) * 100 || 100;
          var pointsSpacing = revByDay.length > 1 ? chartW / (revByDay.length - 1) : 0;
          // Chemin de la courbe
          var pathPoints = revByDay.map(function(d, i) {
            var x = padL + i * pointsSpacing;
            var y = padT + chartH - (d.cents / (maxEur * 100)) * chartH;
            return {x: x, y: y, day: d.day, cents: d.cents};
          });
          var pathD = pathPoints.map(function(p, i) { return (i === 0 ? "M" : "L") + p.x + "," + p.y; }).join(" ");
          // Zone remplie sous la courbe
          var areaD = pathD + " L" + (padL + chartW) + "," + (padT + chartH) + " L" + padL + "," + (padT + chartH) + " Z";
          // Labels axe X (dates)
          var xLabels = [];
          var labelInterval = revByDay.length <= 10 ? 1 : Math.ceil(revByDay.length / 6);
          revByDay.forEach(function(d, i) {
            if (i % labelInterval !== 0 && i !== revByDay.length - 1) return;
            var x = padL + i * pointsSpacing;
            var dd = new Date(d.day + "T12:00:00");
            var short = String(dd.getDate()).padStart(2, "0") + "/" + String(dd.getMonth() + 1).padStart(2, "0");
            xLabels.push(h("text", {key: i, x: x, y: hSvg - 15, fontSize: 10, fill: C.muted, textAnchor: "middle", fontFamily: "system-ui"}, short));
          });
          // Labels axe Y (montants)
          var yLabels = [];
          for (var i = 0; i <= 4; i++) {
            var val = maxEur * (i / 4);
            var y = padT + chartH - (i / 4) * chartH;
            yLabels.push(h("g", {key: i},
              h("line", {x1: padL, y1: y, x2: padL + chartW, y2: y, stroke: C.border, strokeDasharray: "3,3"}),
              h("text", {x: padL - 8, y: y + 3, fontSize: 10, fill: C.muted, textAnchor: "end", fontFamily: "system-ui"}, val + " €")
            ));
          }
          return h("div", {style: {overflowX: "auto"}},
            h("svg", {viewBox: "0 0 " + w + " " + hSvg, style: {width: "100%", minWidth: 500, height: "auto", display: "block"}, xmlns: "http://www.w3.org/2000/svg"},
              // Gradient
              h("defs", null,
                h("linearGradient", {id: "areaGradient", x1: "0", y1: "0", x2: "0", y2: "1"},
                  h("stop", {offset: "0%", stopColor: C.blue, stopOpacity: "0.3"}),
                  h("stop", {offset: "100%", stopColor: C.blue, stopOpacity: "0"})
                )
              ),
              // Grille + Y labels
              yLabels,
              // Zone remplie
              h("path", {d: areaD, fill: "url(#areaGradient)"}),
              // Courbe
              h("path", {d: pathD, fill: "none", stroke: C.blue, strokeWidth: 2.5, strokeLinejoin: "round", strokeLinecap: "round"}),
              // Points
              pathPoints.map(function(p, i) {
                return h("g", {key: i},
                  h("circle", {cx: p.x, cy: p.y, r: 4, fill: "#fff", stroke: C.blue, strokeWidth: 2}),
                  // tooltip natif SVG
                  h("title", null, new Date(p.day + "T12:00:00").toLocaleDateString("fr-FR") + " : " + (p.cents / 100).toFixed(2) + " €")
                );
              }),
              // X labels
              xLabels
            )
          );
        })(),
        !loadingHistory && h("div", {style: {display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 16, paddingTop: 14, borderTop: "1px solid " + C.border}},
          [
            ["CA total (" + historyRange + "j)", formatEur(periodRevCents), C.green],
            ["Ventes (nb)", periodSales.length, C.deep],
            ["Panier moyen", formatEur(periodAvgCartCents), C.blue],
            ["CA moyen / jour", formatEur(Math.round(periodRevCents / historyRange)), C.blue]
          ].map(function(s, i) {
            return h("div", {key: i, style: {textAlign: "center"}},
              h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 800, color: s[2]}}, s[1]),
              h("div", {style: {fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginTop: 2, fontWeight: 600}}, s[0])
            );
          })
        )
      ),

      // ═══════════════════════════════════════════════════════════
      // TOP BÉNÉVOLES + SUPPORTERS ACTIFS (côte à côte)
      // ═══════════════════════════════════════════════════════════
      h("div", {className: "csl-top-grid", style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18}},
        h("style", null, "@media (max-width: 860px) { .csl-top-grid { grid-template-columns: 1fr !important; } }"),

        // Top bénévoles
        h(Card, null,
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 4, fontWeight: 700, textTransform: "uppercase"}}, "🏆 Top bénévoles"),
          h("div", {style: {fontSize: 11, color: C.muted, marginBottom: 14}}, "Classement par CA encaissé (" + historyRange + " derniers jours)"),
          topBenevoles.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune donnée"),
          topBenevoles.map(function(b, i) {
            var medals = ["🥇", "🥈", "🥉"];
            var maxCents = topBenevoles[0].cents || 1;
            var barPct = Math.round((b.cents / maxCents) * 100);
            return h("div", {key: b.name, style: {padding: "10px 0", borderBottom: i < topBenevoles.length - 1 ? "1px solid " + C.border : "none"}},
              h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10}},
                h("div", {style: {display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0}},
                  h("span", {style: {fontSize: 18, width: 24, textAlign: "center"}}, i < 3 ? medals[i] : (i + 1) + "."),
                  h("div", {style: {minWidth: 0, flex: 1}},
                    h("div", {style: {color: C.deep, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}, b.name),
                    h("div", {style: {fontSize: 10, color: C.muted, marginTop: 1}}, b.count + " transaction" + (b.count > 1 ? "s" : ""))
                  )
                ),
                h("div", {style: {fontFamily: "Oswald", fontSize: 15, fontWeight: 800, color: C.green, whiteSpace: "nowrap"}}, formatEur(b.cents))
              ),
              // Barre
              h("div", {style: {height: 4, background: C.pale, borderRadius: 2, overflow: "hidden"}},
                h("div", {style: {height: "100%", width: barPct + "%", background: "linear-gradient(90deg, " + C.blue + ", " + C.deep + ")", borderRadius: 2, transition: "width .3s"}})
              )
            );
          })
        ),

        // Supporters les plus actifs
        h(Card, null,
          h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 4, fontWeight: 700, textTransform: "uppercase"}}, "🎯 Supporters les plus actifs"),
          h("div", {style: {fontSize: 11, color: C.muted, marginBottom: 14}}, "Classement par dépense totale (" + historyRange + " derniers jours)"),
          topSupporters.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune donnée"),
          topSupporters.map(function(s, i) {
            var medals = ["🥇", "🥈", "🥉"];
            var maxCents = topSupporters[0].cents || 1;
            var barPct = Math.round((s.cents / maxCents) * 100);
            var avgCart = s.count > 0 ? Math.round(s.cents / s.count) : 0;
            return h("div", {key: i, style: {padding: "10px 0", borderBottom: i < topSupporters.length - 1 ? "1px solid " + C.border : "none"}},
              h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10}},
                h("div", {style: {display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0}},
                  h("span", {style: {fontSize: 18, width: 24, textAlign: "center"}}, i < 3 ? medals[i] : (i + 1) + "."),
                  h("div", {style: {minWidth: 0, flex: 1}},
                    h("div", {style: {color: C.deep, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}, s.name),
                    h("div", {style: {fontSize: 10, color: C.muted, marginTop: 1}}, s.count + " achat" + (s.count > 1 ? "s" : "") + " · panier moyen " + formatEur(avgCart))
                  )
                ),
                h("div", {style: {fontFamily: "Oswald", fontSize: 15, fontWeight: 800, color: C.gold, whiteSpace: "nowrap"}}, formatEur(s.cents))
              ),
              h("div", {style: {height: 4, background: C.pale, borderRadius: 2, overflow: "hidden"}},
                h("div", {style: {height: "100%", width: barPct + "%", background: "linear-gradient(90deg, " + C.gold + ", #C99B00)", borderRadius: 2, transition: "width .3s"}})
              )
            );
          })
        )
      ),

      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 14, fontWeight: 700, textTransform: "uppercase"}}, "Dernières transactions"),
        transactions.slice(0, 20).map(function(t) {
          var clientName = (t.account && t.account.name) || "—";
          return h("div", {key: t.id, style: {display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid " + C.border, fontSize: 13, gap: 12}},
            h("div", {style: {flex: 1, minWidth: 0}},
              h("div", {style: {color: C.deep, fontWeight: 600}}, t.label || "?"),
              h("div", {style: {fontSize: 11, color: C.muted, marginTop: 3}},
                h("span", null, "👤 " + clientName),
                t.cashier_name && h("span", {style: {color: C.blue, marginLeft: 8, fontWeight: 600}}, "· par " + t.cashier_name)
              )
            ),
            h("div", {style: {fontFamily: "Oswald", fontWeight: 700, color: t.amount_cents > 0 ? C.green : C.muted, whiteSpace: "nowrap"}}, (t.amount_cents > 0 ? "+" : "") + formatEur(t.amount_cents))
          );
        }),
        transactions.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune transaction")
      )
    ),
    // PRODUITS
    // ─────────────────────────── CARTE DE MEMBRE
    cardFor && h("div", {style: MODAL_BG, onClick: function() { setCardFor(null); setCardImg(""); }},
      h("div", {style: Object.assign({}, MODAL_BOX, {maxWidth: 560}), onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4}}, "Carte de membre"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14}}, cardFor.name),

        // Une carte non activée peut être éditée, mais le club doit le
        // savoir : la personne n'a pas fait sa première recharge.
        cardData && cardData.card_activated === false && h("div", {style: {
          background: "rgba(244,232,52,.2)", border: "1px solid rgba(200,160,0,.35)",
          borderRadius: 10, padding: "11px 13px", marginBottom: 12,
          fontSize: 13, color: "#7a5d00", lineHeight: 1.5
        }},
          h("strong", null, "Carte non activée."),
          " Cette personne n'a pas encore fait sa première recharge. Tu peux éditer la carte quand même."),

        cardBusy
          ? h("div", {style: {textAlign: "center", padding: 50, color: C.muted, fontFamily: "Oswald", letterSpacing: 1}}, "Génération…")
          : cardImg
            ? h("img", {src: cardImg, alt: "Carte", style: {width: "100%", borderRadius: 10, display: "block", boxShadow: "0 6px 24px rgba(0,0,0,.14)"}})
            : h("div", {style: {textAlign: "center", padding: 40, color: C.muted}}, "Aucun aperçu"),

        h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginTop: 14}},
          [[true, "Avec fond perdu"], [false, "Taille exacte"]].map(function(v) {
            return h("button", {key: String(v[0]), onClick: function() { regenererCarte(v[0]); },
              style: {flex: 1, border: 0, background: cardBleed === v[0] ? "#fff" : "transparent",
                      color: cardBleed === v[0] ? C.deep : C.muted, padding: 10, borderRadius: 8,
                      cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700,
                      letterSpacing: .5, textTransform: "uppercase"}}, v[1]);
          })
        ),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.5}},
          cardBleed
            ? "1087 × 721 px — 86 × 55 mm plus 3 mm de marge perdue sur chaque bord, avec les repères de coupe. C'est ce que demande un imprimeur."
            : "1016 × 650 px — 86 × 55 mm exactement, à 300 points par pouce. Pour une impression maison ou une découpe à la main."),

        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setCardFor(null); setCardImg(""); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Fermer"),
          h("button", {onClick: telechargerCarte, disabled: !cardImg || cardBusy, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13, opacity: cardImg ? 1 : .5})}, "📥 Télécharger")
        )
      )
    ),

    // ─────────────────────────── STATUTS
    tab === "statuts" && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Statuts et avantages"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5}},
          "Le statut d'une personne — supporter, licencié, bénévole, partenaire — et ce qu'il lui donne. À ne pas confondre avec les rôles, qui disent ce qu'un bénévole peut faire dans l'outil."),

        h("button", {onClick: function() {
          setMErr("");
          setMForm({name: "", slug: "", emoji: "🏉", color: "#D02128", freeEntry: false,
                    entryPct: 0, shopPct: 0, barPct: 0, mealPct: 0, notes: "",
                    active: true, sort: mTypes.length + 1});
        }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 13, fontSize: 13, marginBottom: 14})},
          "+ Nouveau statut"),

        mTypes.map(function(x) {
          var av = [];
          if (x.free_entry) av.push("Entrée gratuite");
          else if (x.entry_discount_pct) av.push("Entrée -" + x.entry_discount_pct + " %");
          if (x.shop_discount_pct) av.push("Boutique -" + x.shop_discount_pct + " %");
          if (x.bar_discount_pct) av.push("Buvette -" + x.bar_discount_pct + " %");
          if (x.meal_discount_pct) av.push("Repas -" + x.meal_discount_pct + " %");

          return h("div", {key: x.id, style: {padding: "14px 0", borderBottom: "1px solid " + C.border, opacity: x.active ? 1 : .5}},
            h("div", {style: {display: "flex", alignItems: "center", gap: 12}},
              h("span", {style: {fontSize: 28, flexShrink: 0}}, x.emoji || "🏉"),
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {display: "flex", alignItems: "center", gap: 8}},
                  h("span", {style: {width: 10, height: 10, borderRadius: 3, background: x.color || C.muted, flexShrink: 0}}),
                  h("span", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, x.name)
                ),
                h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                  x.membres + " compte" + (x.membres > 1 ? "s" : "") + (x.active ? "" : " · désactivé")),
                h("div", {style: {display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6}},
                  av.length
                    ? av.map(function(a, i) {
                        return h("span", {key: i, style: {fontSize: 10, fontFamily: "Oswald", letterSpacing: .8, textTransform: "uppercase", padding: "4px 9px", borderRadius: 999, background: "rgba(34,197,94,.12)", color: C.green, fontWeight: 700}}, a);
                      })
                    : h("span", {style: {fontSize: 12, color: C.muted, fontStyle: "italic"}}, "Aucun avantage")
                )
              )
            ),
            h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10}},
              h("button", {onClick: function() {
                setMErr("");
                setMForm({id: x.id, name: x.name, slug: x.slug, emoji: x.emoji || "🏉",
                  color: x.color || "#D02128", freeEntry: x.free_entry,
                  entryPct: x.entry_discount_pct, shopPct: x.shop_discount_pct,
                  barPct: x.bar_discount_pct, mealPct: x.meal_discount_pct,
                  notes: x.notes || "", active: x.active, sort: x.sort_order});
              }, style: {flex: "1 1 auto", background: "rgba(208,33,40,.08)", border: "none", color: C.blue, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "✏️ Modifier"),
              h("button", {onClick: function() { removeMType(x); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Supprimer")
            )
          );
        }),
        mTypes.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun statut"),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── STATUTS : formulaire
    mForm && h("div", {style: MODAL_BG, onClick: function() { setMForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          mForm.id ? "Modifier le statut" : "Nouveau statut"),

        h("div", {style: {display: "flex", gap: 8}},
          h("div", {style: {width: 70}},
            h("div", {style: LABEL}, "Emoji"),
            h("input", {value: mForm.emoji, onChange: function(e) { setMForm(Object.assign({}, mForm, {emoji: e.target.value})); }, style: Object.assign({}, IS, {textAlign: "center", fontSize: 22})})),
          h("div", {style: {flex: 1}},
            h("div", {style: LABEL}, "Nom"),
            h("input", {value: mForm.name, onChange: function(e) { setMForm(Object.assign({}, mForm, {name: e.target.value})); }, placeholder: "Licencié", style: IS}))
        ),
        h("div", {style: LABEL}, "Couleur"),
        h("input", {type: "color", value: mForm.color, onChange: function(e) { setMForm(Object.assign({}, mForm, {color: e.target.value})); }, style: Object.assign({}, IS, {height: 46, padding: 4})}),

        h("div", {style: {height: 1, background: C.border, margin: "18px 0 4px"}}),
        h("div", {style: {fontFamily: "Oswald", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, marginBottom: 6}}, "Les avantages"),

        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: !!mForm.freeEntry, onChange: function(e) { setMForm(Object.assign({}, mForm, {freeEntry: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Entrée gratuite au stade"),

        !mForm.freeEntry && h("div", null,
          h("div", {style: LABEL}, "Remise sur l'entrée (%)"),
          h("input", {type: "number", min: 0, max: 100, value: mForm.entryPct, onChange: function(e) { setMForm(Object.assign({}, mForm, {entryPct: e.target.value})); }, style: IS})
        ),

        h("div", {style: LABEL}, "Remise boutique (%)"),
        h("input", {type: "number", min: 0, max: 100, value: mForm.shopPct, onChange: function(e) { setMForm(Object.assign({}, mForm, {shopPct: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Remise buvette (%)"),
        h("input", {type: "number", min: 0, max: 100, value: mForm.barPct, onChange: function(e) { setMForm(Object.assign({}, mForm, {barPct: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Remise repas (%)"),
        h("input", {type: "number", min: 0, max: 100, value: mForm.mealPct, onChange: function(e) { setMForm(Object.assign({}, mForm, {mealPct: e.target.value})); }, style: IS}),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: -8, marginBottom: 10, lineHeight: 1.45}},
          "Les remises ne s'appliquent jamais aux consignes, qui sont remboursables."),

        h("div", {style: LABEL}, "Note interne"),
        h("input", {value: mForm.notes, onChange: function(e) { setMForm(Object.assign({}, mForm, {notes: e.target.value})); }, placeholder: "Validé en bureau du 12/10", style: IS}),

        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: mForm.active !== false, onChange: function(e) { setMForm(Object.assign({}, mForm, {active: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Statut utilisable"),

        mErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, mErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setMForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveMType, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── BILLETTERIE : liste des matchs
    tab === "tickets" && !tkSel && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Billetterie (" + tkEvents.length + ")"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5}},
          "Un match, ses catégories de billets et leurs tarifs. Chaque billet vendu porte un code unique scanné à l'entrée."),
        h("button", {onClick: function() {
          setTkErr("");
          setTkForm({title: "", slug: "", competition: "Fédérale 2", starts_at: "",
                     gates_open_at: "", venue: "Stade du Capitaine Dumas", poster_url: "",
                     info: "", capacity: "", sales_open: false, closes_at: "", published: false});
        }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 13, fontSize: 13, marginBottom: 14})},
          "+ Nouveau match"),

        tkEvents.map(function(e) {
          var d = new Date(e.starts_at);
          return h("div", {key: e.id, style: {padding: "14px 0", borderBottom: "1px solid " + C.border, opacity: e.published ? 1 : .55}},
            h("div", {onClick: function() { openTicketEvent(e); }, style: {cursor: "pointer", display: "flex", alignItems: "center", gap: 12}},
              h("div", {style: {width: 52, flexShrink: 0, background: C.deep, color: "#fff", borderRadius: 10, padding: "7px 0", textAlign: "center", fontFamily: "Oswald"}},
                h("div", {style: {fontSize: 19, fontWeight: 800, lineHeight: 1}}, String(d.getDate()).padStart(2, "0")),
                h("div", {style: {fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.gold}},
                  d.toLocaleDateString("fr-FR", {month: "short"}).replace(".", ""))
              ),
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, e.title),
                e.competition && h("div", {style: {color: C.muted, fontSize: 13}}, e.competition),
                h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                  e.vendus + " billet" + (e.vendus > 1 ? "s" : "") +
                  (e.capacity ? " / " + e.capacity : "") +
                  (e.entres ? " · " + e.entres + " entré" + (e.entres > 1 ? "s" : "") : "")),
                h("div", {style: {display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap"}},
                  !e.published && h("span", {style: {color: C.red, fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700}}, "Brouillon"),
                  e.published && !e.sales_open && h("span", {style: {color: C.muted, fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700}}, "Vente fermée"),
                  e.published && e.sales_open && h("span", {style: {color: C.green, fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700}}, "En vente")
                )
              ),
              h("div", {style: {textAlign: "right", flexShrink: 0}},
                h("div", {style: {fontFamily: "Oswald", fontSize: 17, fontWeight: 800, color: C.green}}, formatEur(e.encaisse)),
                e.attendu > 0 && h("div", {style: {fontSize: 11, color: C.muted}}, formatEur(e.attendu) + " dû")
              )
            ),
            h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10}},
              h("button", {onClick: function() { openTicketEvent(e); }, style: {flex: "1 1 auto", background: "rgba(208,33,40,.08)", border: "none", color: C.blue, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Ouvrir"),
              h("button", {onClick: function() {
                setTkErr("");
                setTkForm({id: e.id, title: e.title, slug: e.slug, competition: e.competition || "",
                  starts_at: toLocal(e.starts_at), gates_open_at: toLocal(e.gates_open_at),
                  venue: e.venue || "", poster_url: e.poster_url || "", info: e.info || "",
                  capacity: e.capacity == null ? "" : String(e.capacity),
                  sales_open: e.sales_open, closes_at: toLocal(e.closes_at), published: e.published});
              }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.deep, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "✏️ Modifier")
            )
          );
        }),
        tkEvents.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun match"),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── BILLETTERIE : un match
    tab === "tickets" && tkSel && h("div", null,
      h(Card, null,
        h("div", {style: {display: "flex", alignItems: "center", gap: 10, marginBottom: 14}},
          h("button", {onClick: function() { setTkSel(null); setMsg(""); }, style: {background: C.pale, border: "none", color: C.deep, padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 12, letterSpacing: 1, fontWeight: 700}}, "← Matchs"),
          h("div", {style: {flex: 1, minWidth: 0}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 16, color: C.deep, fontWeight: 700, textTransform: "uppercase"}}, tkSel.title),
            h("div", {style: {fontSize: 12, color: C.muted}}, formatDateOnly(tkSel.starts_at))
          )
        ),

        h("div", {style: {display: "flex", gap: 8, marginBottom: 16}},
          h("div", {style: {flex: 1, background: C.pale, borderRadius: 12, padding: 13, textAlign: "center"}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: C.deep}}, tkSel.vendus),
            h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Vendus")),
          h("div", {style: {flex: 1, background: "rgba(34,197,94,.1)", borderRadius: 12, padding: 13, textAlign: "center"}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: C.green}}, tkSel.entres),
            h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Entrés")),
          h("div", {style: {flex: 1, background: "rgba(244,232,52,.2)", borderRadius: 12, padding: 13, textAlign: "center"}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 800, color: "#8a6d00"}}, formatEur(tkSel.encaisse)),
            h("div", {style: {fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Encaissé"))
        ),

        h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 16}},
          [["tarifs", "Tarifs"], ["commandes", "Commandes"]].map(function(v) {
            return h("button", {key: v[0], onClick: function() { setTkTab(v[0]); },
              style: {flex: 1, border: 0, background: tkTab === v[0] ? "#fff" : "transparent",
                      color: tkTab === v[0] ? C.deep : C.muted, padding: 10, borderRadius: 8,
                      cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                      letterSpacing: .5, textTransform: "uppercase"}}, v[1]);
          })
        ),

        // ── Tarifs
        tkTab === "tarifs" && h("div", null,
          (tkSel.types || []).map(function(ty) {
            return h("div", {key: ty.id, style: {display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderBottom: "1px solid " + C.border, opacity: ty.active ? 1 : .5}},
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, ty.name),
                ty.description && h("div", {style: {color: C.muted, fontSize: 12}}, ty.description),
                h("div", {style: {color: C.muted, fontSize: 11, marginTop: 2}},
                  ty.vendus + " vendu" + (ty.vendus > 1 ? "s" : "") +
                  (ty.capacity ? " / " + ty.capacity : "") +
                  " · max " + ty.max_per_order + " par commande" +
                  (ty.active ? "" : " · désactivé"))
              ),
              h("div", {style: {fontFamily: "Oswald", fontSize: 17, fontWeight: 700, color: C.blue}}, formatEur(ty.price_cents)),
              h("button", {onClick: function() {
                setTkErr("");
                setTkTypeForm({id: ty.id, eventId: tkSel.id, name: ty.name,
                  description: ty.description || "", price: (ty.price_cents / 100).toFixed(2),
                  capacity: ty.capacity == null ? "" : String(ty.capacity),
                  maxPerOrder: ty.max_per_order, active: ty.active, sort: ty.sort_order});
              }, style: {background: C.pale, border: "none", color: C.blue, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, "✏️"),
              h("button", {onClick: function() { removeTicketType(ty); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
            );
          }),
          (tkSel.types || []).length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 16}}, "Aucune catégorie de billet"),

          h("button", {onClick: function() {
            setTkErr("");
            setTkTypeForm({eventId: tkSel.id, name: "", description: "", price: "",
                           capacity: "", maxPerOrder: 10, active: true,
                           sort: (tkSel.types || []).length + 1});
          }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 12, marginTop: 14})},
            "+ Ajouter une catégorie"),

          tkEvents.length > 1 && h("div", {style: {marginTop: 12}},
            h("div", {style: LABEL}, "Reprendre les tarifs d'un autre match"),
            h("select", {onChange: function(e) { if (e.target.value) { copyTicketTypes(e.target.value); e.target.value = ""; } },
                         style: Object.assign({}, IS, {marginBottom: 0})},
              h("option", {value: ""}, "— Choisir —"),
              tkEvents.filter(function(x) { return x.id !== tkSel.id && (x.types || []).length; }).map(function(x) {
                return h("option", {key: x.id, value: x.id}, x.title + " · " + formatDateOnly(x.starts_at));
              })
            )
          )
        ),

        // ── Commandes
        tkTab === "commandes" && h("div", null,
          tkOrders.map(function(o) {
            return h("div", {key: o.id, style: {padding: "13px 0", borderBottom: "1px solid " + C.border, opacity: o.status === "annulee" ? .5 : 1}},
              h("div", {style: {display: "flex", alignItems: "flex-start", gap: 10}},
                h("div", {style: {flex: 1, minWidth: 0}},
                  h("div", {style: {display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}},
                    h("span", {style: {fontFamily: "Oswald", fontSize: 13, fontWeight: 800, color: C.blue, letterSpacing: 1}}, o.ref),
                    h("span", {style: {fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999,
                      background: o.status === "payee" ? "rgba(34,197,94,.12)" : o.status === "annulee" ? C.pale : "rgba(244,232,52,.3)",
                      color: o.status === "payee" ? C.green : o.status === "annulee" ? C.muted : "#8a6d00"}},
                      o.status === "payee" ? "Payée" : o.status === "annulee" ? "Annulée" : "En attente"),
                    h("span", {style: {fontSize: 11, color: C.muted}},
                      o.payment_mode === "en_ligne" ? "en ligne" : o.payment_mode === "stade" ? "au stade" :
                      o.payment_mode === "cashless" ? "cashless" : "offert")
                  ),
                  h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700, marginTop: 3}}, o.name),
                  h("div", {style: {color: C.muted, fontSize: 12}},
                    (o.tickets || []).length + " billet" + ((o.tickets || []).length > 1 ? "s" : "") + " · " +
                    (o.tickets || []).map(function(x) { return x.type; }).join(", ")),
                  h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                    h("a", {href: "mailto:" + o.email, style: {color: C.blue}}, o.email),
                    o.phone ? [" · ", h("a", {key: "t", href: "tel:" + String(o.phone).replace(/\s/g, ""), style: {color: C.blue}}, o.phone)] : null),
                  (o.tickets || []).some(function(x) { return x.status === "utilise"; }) &&
                    h("div", {style: {color: C.green, fontSize: 11, marginTop: 3, fontWeight: 600}},
                      (o.tickets || []).filter(function(x) { return x.status === "utilise"; }).length + " entrée(s) scannée(s)")
                ),
                h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: C.deep, flexShrink: 0}}, formatEur(o.total_cents))
              ),
              h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 9}},
                o.status !== "payee" && h("button", {onClick: function() { ticketOrderStatus(o, "payee"); }, style: {flex: "1 1 auto", background: "rgba(34,197,94,.12)", border: "none", color: C.green, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Marquer payée"),
                o.status === "payee" && h("button", {onClick: function() { ticketOrderStatus(o, "en_attente"); }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.muted, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler le paiement"),
                o.status !== "annulee" && h("button", {onClick: function() { ticketOrderStatus(o, "annulee"); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, padding: "9px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler")
              )
            );
          }),
          tkOrders.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune commande"),
          h("button", {onClick: exportTickets, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 13, fontSize: 12, marginTop: 14})}, "📥 Exporter la billetterie")
        ),

        h("div", {style: {display: "flex", gap: 6, marginTop: 16, paddingTop: 14, borderTop: "1px solid " + C.border}},
          h("button", {onClick: function() { removeTicketEvent(tkSel); }, style: {flex: 1, background: "rgba(239,68,68,.08)", border: "none", color: C.red, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Supprimer le match")
        ),

        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── BILLETTERIE : formulaire du match
    tkForm && h("div", {style: MODAL_BG, onClick: function() { setTkForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          tkForm.id ? "Modifier le match" : "Nouveau match"),
        h("div", {style: LABEL}, "Affiche"),
        h("input", {value: tkForm.title, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {title: e.target.value})); }, placeholder: "CSL vs XV de la Dombes", style: IS}),
        h("div", {style: LABEL}, "Compétition"),
        h("input", {value: tkForm.competition, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {competition: e.target.value})); }, placeholder: "Fédérale 2 · J7", style: IS}),
        h("div", {style: LABEL}, "Coup d'envoi"),
        h("input", {type: "datetime-local", value: tkForm.starts_at, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {starts_at: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Ouverture des portes"),
        h("input", {type: "datetime-local", value: tkForm.gates_open_at, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {gates_open_at: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Lieu"),
        h("input", {value: tkForm.venue, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {venue: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Affiche du match (URL d'image)"),
        h("input", {value: tkForm.poster_url, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {poster_url: e.target.value})); }, placeholder: "https://…", style: IS}),
        h("div", {style: LABEL}, "Informations pratiques"),
        h("textarea", {rows: 2, value: tkForm.info, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {info: e.target.value})); }, placeholder: "Parking gratuit. Buvette dès 13h.", style: IS}),
        h("div", {style: LABEL}, "Jauge totale (vide = illimitée)"),
        h("input", {type: "number", value: tkForm.capacity, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {capacity: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Fin de la vente en ligne"),
        h("input", {type: "datetime-local", value: tkForm.closes_at, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {closes_at: e.target.value})); }, style: IS}),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: !!tkForm.published, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {published: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Visible sur le site"),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 10, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: !!tkForm.sales_open, onChange: function(e) { setTkForm(Object.assign({}, tkForm, {sales_open: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Vente ouverte"),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 4}}, "Un match peut être visible sans que la vente soit ouverte : l'affiche s'affiche, le bouton d'achat non."),
        tkErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, tkErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setTkForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveTicketEvent, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── BILLETTERIE : formulaire d'une catégorie
    tkTypeForm && h("div", {style: MODAL_BG, onClick: function() { setTkTypeForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          tkTypeForm.id ? "Modifier la catégorie" : "Nouvelle catégorie"),
        h("div", {style: LABEL}, "Nom"),
        h("input", {value: tkTypeForm.name, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {name: e.target.value})); }, placeholder: "Adulte", style: IS}),
        h("div", {style: LABEL}, "Description"),
        h("input", {value: tkTypeForm.description, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {description: e.target.value})); }, placeholder: "Entrée simple, tribune ou pourtour", style: IS}),
        h("div", {style: LABEL}, "Prix (€)"),
        h("input", {type: "number", step: "1", value: tkTypeForm.price, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {price: e.target.value})); }, placeholder: "10", style: IS}),
        h("div", {style: LABEL}, "Quota (vide = illimité)"),
        h("input", {type: "number", value: tkTypeForm.capacity, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {capacity: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Maximum par commande"),
        h("input", {type: "number", value: tkTypeForm.maxPerOrder, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {maxPerOrder: e.target.value})); }, style: IS}),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: tkTypeForm.active !== false, onChange: function(e) { setTkTypeForm(Object.assign({}, tkTypeForm, {active: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Proposée à la vente"),
        tkErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, tkErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setTkTypeForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveTicketType, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── STAGES
    tab === "stages" && h("div", null,
      h(Card, null,
        (function() {
          // Tous les stages créés, même ceux sans aucune inscription
          var slugs = stageList.map(function(s) { return {slug: s.slug, name: s.name, open: s.open}; });
          stageRows.forEach(function(r) {
            if (!slugs.filter(function(x) { return x.slug === r.stage_slug; }).length)
              slugs.push({slug: r.stage_slug, name: r.stage_name, open: true});
          });
          var cur = stageCurrent();
          var rows = stageRows.filter(function(r) { return r.stage_slug === stageSlug; });
          var visibles = rows.filter(function(r) {
            return stageFilter === "tous" ? true
                 : stageFilter === "attente" ? r.status === "en_attente"
                 : stageFilter === "payes" ? r.status === "paye"
                 : r.status === "annule";
          });
          var du = rows.filter(function(r) { return r.status === "en_attente"; })
                       .reduce(function(s, r) { return s + r.amount_cents; }, 0);

          return h("div", null,
            h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Stages"),
            h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5}},
              "Les inscriptions prises sur le site. Les effectifs par journée servent à réserver les activités et prévoir les encadrants."),

            h("button", {onClick: function() {
              setStageErr("");
              setStageForm({name: "", slug: "", subtitle: "", starts_on: "", ends_on: "",
                            weekPrice: "", weekTeams: "", place: "Stade du Capitaine Dumas",
                            schedule: "", info: "", open: false, closes_at: ""});
            }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 13, fontSize: 13, marginBottom: 14})},
              "+ Nouveau stage"),

            slugs.length > 0 && h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14}},
              slugs.map(function(s) {
                return h("button", {key: s.slug, onClick: function() { pickStage(s.slug); },
                  style: {border: "1px solid " + (stageSlug === s.slug ? C.deep : C.border),
                          background: stageSlug === s.slug ? C.deep : "#fff",
                          color: stageSlug === s.slug ? "#fff" : C.muted,
                          borderRadius: 999, padding: "8px 14px", cursor: "pointer", fontSize: 13,
                          opacity: s.open === false ? .6 : 1}},
                  s.name + (s.open === false ? " · fermé" : ""));
              })
            ),

            slugs.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 24}},
              "Aucun stage. Commence par en créer un."),

            // Vue : inscriptions ou réglages du stage
            cur && h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 16}},
              [["inscriptions", "Inscriptions"], ["reglages", "Le stage"]].map(function(v) {
                return h("button", {key: v[0], onClick: function() { setStageView(v[0]); },
                  style: {flex: 1, border: 0, background: stageView === v[0] ? "#fff" : "transparent",
                          color: stageView === v[0] ? C.deep : C.muted, padding: 10, borderRadius: 8,
                          cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                          letterSpacing: .5, textTransform: "uppercase"}}, v[1]);
              })
            ),

            // ── Réglages du stage et ses journées
            cur && stageView === "reglages" && h("div", null,
              h("div", {style: {background: C.pale, borderRadius: 12, padding: 14, marginBottom: 14}},
                h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, cur.name),
                cur.subtitle && h("div", {style: {color: C.muted, fontSize: 13}}, cur.subtitle),
                h("div", {style: {color: C.muted, fontSize: 12, marginTop: 4}},
                  "Du " + formatDateOnly(cur.starts_on) + " au " + formatDateOnly(cur.ends_on)),
                h("div", {style: {color: C.muted, fontSize: 12}},
                  cur.open ? "Inscriptions ouvertes" : "Inscriptions fermées"),
                // L'identifiant est ce qui relie la page du site au stage :
                // il est mis en avant, avec copie en un geste.
                h("div", {style: {background: "#fff", border: "1px dashed " + C.blue, borderRadius: 10,
                                  padding: "11px 13px", marginTop: 10, display: "flex",
                                  alignItems: "center", gap: 10}},
                  h("div", {style: {flex: 1, minWidth: 0}},
                    h("div", {style: {fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                                      fontFamily: "Oswald", fontWeight: 700, color: C.muted}},
                      "Identifiant à coller dans la page Stage du site"),
                    h("div", {style: {fontFamily: "monospace", fontSize: 15, color: C.deep,
                                      fontWeight: 700, wordBreak: "break-all", marginTop: 2}}, cur.slug)
                  ),
                  h("button", {onClick: function() {
                    try {
                      navigator.clipboard.writeText(cur.slug);
                      setMsg("✅ Identifiant copié");
                    } catch (e) { setMsg("❌ Copie impossible, sélectionne le texte"); }
                  }, style: {background: C.pale, border: "none", color: C.blue, padding: "9px 12px",
                             borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11,
                             fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", flexShrink: 0}},
                    "Copier")
                ),
                cur.week_price_cents && h("div", {style: {color: C.muted, fontSize: 12}},
                  "Semaine complète : " + formatEur(cur.week_price_cents) +
                  ((cur.week_teams || []).length ? " · " + cur.week_teams.join(", ") : "")),
                h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10}},
                  h("button", {onClick: function() {
                    setStageErr("");
                    setStageForm({id: cur.id, name: cur.name, slug: cur.slug,
                      subtitle: cur.subtitle || "", starts_on: cur.starts_on, ends_on: cur.ends_on,
                      weekPrice: cur.week_price_cents != null ? (cur.week_price_cents / 100).toFixed(2) : "",
                      weekTeams: (cur.week_teams || []).join(", "),
                      place: cur.place || "", schedule: cur.schedule || "", info: cur.info || "",
                      open: cur.open, closes_at: cur.closes_at ? toLocal(cur.closes_at) : ""});
                  }, style: {flex: "1 1 auto", background: "rgba(208,33,40,.08)", border: "none", color: C.blue, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "✏️ Modifier"),
                  h("button", {onClick: function() { duplicateStage(cur); }, style: {flex: "1 1 auto", background: C.pale, border: "1px solid " + C.border, color: C.deep, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Dupliquer"),
                  h("button", {onClick: function() { removeStage(cur); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Supprimer")
                )
              ),

              // Une catégorie autorisée à la semaine mais absente de toutes les
              // journées ne peut rien réserver : la page affiche alors
              // « Aucune journée n'est ouverte aux … ». On le signale ici.
              (function() {
                var jours = cur.days || [];
                var orphelines = (cur.week_teams || []).filter(function(cat) {
                  return !jours.some(function(d) {
                    return !(d.teams || []).length || d.teams.indexOf(cat) !== -1;
                  });
                });
                if (!orphelines.length) return null;
                return h("div", {style: {background: "rgba(244,232,52,.2)", border: "1px solid rgba(200,160,0,.3)",
                                         borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontSize: 13,
                                         color: "#7a5d00", lineHeight: 1.5}},
                  h("strong", null, "⚠️ " + orphelines.join(", ") + " ne peut rien réserver."),
                  h("div", {style: {marginTop: 3}},
                    "Ces catégories ont droit à la semaine, mais aucune journée ne les autorise. " +
                    "Ajoute-les dans les catégories d'au moins une journée, ou laisse le champ vide pour ouvrir à tous.")
                );
              })(),

              h("div", {style: LABEL}, "Les journées"),
              (cur.days || []).map(function(d) {
                return h("div", {key: d.id, style: {display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderBottom: "1px solid " + C.border}},
                  h("div", {style: {flex: 1, minWidth: 0}},
                    h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}},
                      d.label, h("span", {style: {color: C.muted, fontWeight: 400, fontSize: 13, marginLeft: 8}}, formatDateOnly(d.day_date))),
                    d.activity && h("div", {style: {color: C.muted, fontSize: 12}}, d.activity),
                    h("div", {style: {color: C.muted, fontSize: 11, marginTop: 2}},
                      ((d.teams || []).length ? d.teams.join(", ") : "toutes catégories") +
                      (d.capacity ? " · " + d.taken + "/" + d.capacity + " places" : " · " + d.taken + " inscrit(s)"))
                  ),
                  h("div", {style: {textAlign: "right", flexShrink: 0}},
                    h("div", {style: {fontFamily: "Oswald", fontSize: 17, fontWeight: 700, color: C.blue}}, formatEur(d.price_cents)),
                    h("div", {style: {fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase"}}, "la journée")
                  ),
                  h("button", {onClick: function() {
                    setStageErr("");
                    setDayForm({id: d.id, stageId: cur.id, date: d.day_date, label: d.label,
                      activity: d.activity || "", price: (d.price_cents / 100).toFixed(2),
                      teams: (d.teams || []).join(", "),
                      capacity: d.capacity == null ? "" : String(d.capacity), sort: d.sort_order});
                  }, style: {background: C.pale, border: "none", color: C.blue, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, "✏️"),
                  h("button", {onClick: function() { removeDay(d); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
                );
              }),
              (cur.days || []).length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 16}}, "Aucune journée"),

              h("button", {onClick: function() {
                setStageErr("");
                setDayForm({stageId: cur.id, date: cur.starts_on, label: "", activity: "",
                            price: "", teams: "", capacity: "", sort: (cur.days || []).length + 1});
              }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 12, marginTop: 14})},
                "+ Ajouter une journée")
            ),

            // ── Effectifs par journée
            stageView === "inscriptions" && stageDays.length > 0 && h("div", {style: {marginBottom: 18}},
              h("div", {style: LABEL}, "Effectifs par journée"),
              h("div", {style: {display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))"}},
                stageDays.map(function(d) {
                  return h("div", {key: d.day_date, style: {background: C.pale, borderRadius: 12, padding: 13, textAlign: "center"}},
                    h("div", {style: {fontFamily: "Oswald", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: C.muted}}, d.label),
                    h("div", {style: {fontFamily: "Oswald", fontSize: 30, fontWeight: 800, color: C.deep, lineHeight: 1.1}}, d.inscrits),
                    d.activity && h("div", {style: {fontSize: 11, color: C.muted, marginTop: 2}}, d.activity),
                    d.payes < d.inscrits && h("div", {style: {fontSize: 11, color: "#8a6d00", marginTop: 3, fontWeight: 600}},
                      (d.inscrits - d.payes) + " à régler")
                  );
                })
              )
            ),

            stageView === "inscriptions" && du > 0 && h("div", {style: {background: "rgba(244,232,52,.18)", borderRadius: 12, padding: 14, textAlign: "center", marginBottom: 16}},
              h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: "#8a6d00"}}, formatEur(du)),
              h("div", {style: {fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Reste à encaisser")),

            // ── Filtres
            stageView === "inscriptions" && slugs.length > 0 && h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 14}},
              [["tous", "Toutes"], ["attente", "À régler"], ["payes", "Payées"], ["annulees", "Annulées"]].map(function(f) {
                return h("button", {key: f[0], onClick: function() { setStageFilter(f[0]); },
                  style: {flex: 1, border: 0, background: stageFilter === f[0] ? "#fff" : "transparent",
                          color: stageFilter === f[0] ? C.deep : C.muted, padding: 9, borderRadius: 8,
                          cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700,
                          letterSpacing: .5, textTransform: "uppercase"}}, f[1]);
              })
            ),

            // ── Inscriptions
            stageView === "inscriptions" && visibles.map(function(r) {
              var jours = (r.day_dates || []).slice().sort().map(function(x) {
                var p = String(x).split("-"); return p[2] + "/" + p[1];
              }).join(", ");
              return h("div", {key: r.id, style: {padding: "13px 0", borderBottom: "1px solid " + C.border, opacity: r.status === "annule" ? .5 : 1}},
                h("div", {style: {display: "flex", alignItems: "flex-start", gap: 10}},
                  h("div", {style: {flex: 1, minWidth: 0}},
                    h("div", {style: {display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}},
                      h("span", {style: {fontFamily: "Oswald", fontSize: 13, fontWeight: 800, color: C.blue, letterSpacing: 1}}, r.ref),
                      h("span", {style: {fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999,
                        background: r.status === "paye" ? "rgba(34,197,94,.12)" : r.status === "annule" ? C.pale : "rgba(244,232,52,.3)",
                        color: r.status === "paye" ? C.green : r.status === "annule" ? C.muted : "#8a6d00"}},
                        r.status === "paye" ? "Payé" : r.status === "annule" ? "Annulé" : "À régler"),
                      r.payment_method && h("span", {style: {fontSize: 11, color: C.muted}},
                        r.payment_method === "cb" ? "carte" : r.payment_method === "cheque" ? "chèque" : "espèces")
                    ),
                    h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700, marginTop: 3}},
                      r.child, h("span", {style: {color: C.muted, fontSize: 13, fontWeight: 400, marginLeft: 8}}, r.team)),
                    h("div", {style: {color: C.muted, fontSize: 12}},
                      (r.formula === "semaine" ? "Semaine complète" : "À la journée") + (jours ? " · " + jours : "")),
                    h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                      r.parent, " · ",
                      h("a", {href: "tel:" + String(r.phone).replace(/\s/g, ""), style: {color: C.blue}}, r.phone),
                      " · ",
                      h("a", {href: "mailto:" + r.email, style: {color: C.blue}}, r.email)),
                    r.notes && h("div", {style: {color: C.red, fontSize: 12, marginTop: 4, fontWeight: 600}}, "⚠️ " + r.notes)
                  ),
                  h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: C.deep, flexShrink: 0}}, formatEur(r.amount_cents))
                ),
                h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 9}},
                  r.status !== "paye" && h("button", {onClick: function() { stageStatus(r, "paye"); }, style: {flex: "1 1 auto", background: "rgba(34,197,94,.12)", border: "none", color: C.green, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Marquer payé"),
                  r.status === "paye" && h("button", {onClick: function() { stageStatus(r, "en_attente"); }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.muted, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler le paiement"),
                  r.status !== "annule" && h("button", {onClick: function() { stageStatus(r, "annule"); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, padding: "9px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler")
                )
              );
            }),

            stageView === "inscriptions" && slugs.length > 0 && visibles.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune inscription dans ce filtre"),

            stageView === "inscriptions" && slugs.length > 0 && h("button", {onClick: exportStage, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 13, fontSize: 12, marginTop: 14})}, "📥 Exporter le stage"),

            msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
          );
        })()
      )
    ),

    // ─────────────────────────── STAGE : formulaire du stage
    stageForm && h("div", {style: MODAL_BG, onClick: function() { setStageForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          stageForm.id ? "Modifier le stage" : "Nouveau stage"),
        h("div", {style: LABEL}, "Nom"),
        h("input", {value: stageForm.name, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {name: e.target.value})); }, placeholder: "Stage de la Toussaint 2026", style: IS}),
        h("div", {style: LABEL}, "Identifiant (laisser vide pour le déduire du nom)"),
        h("input", {value: stageForm.slug, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {slug: e.target.value})); }, placeholder: "toussaint-2026", style: IS}),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: -8, marginBottom: 10}}, "C'est lui à renseigner dans les réglages de la page Stage du site."),
        h("div", {style: LABEL}, "Sous-titre"),
        h("input", {value: stageForm.subtitle, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {subtitle: e.target.value})); }, placeholder: "Rugby le matin, activités l'après-midi", style: IS}),
        h("div", {style: {display: "flex", gap: 10}},
          h("div", {style: {flex: 1}},
            h("div", {style: LABEL}, "Premier jour"),
            h("input", {type: "date", value: stageForm.starts_on, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {starts_on: e.target.value})); }, style: IS})),
          h("div", {style: {flex: 1}},
            h("div", {style: LABEL}, "Dernier jour"),
            h("input", {type: "date", value: stageForm.ends_on, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {ends_on: e.target.value})); }, style: IS}))
        ),
        h("div", {style: LABEL}, "Prix de la semaine complète (€, vide si non proposée)"),
        h("input", {type: "number", step: "1", value: stageForm.weekPrice, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {weekPrice: e.target.value})); }, placeholder: "110", style: IS}),
        h("div", {style: LABEL}, "Catégories ayant droit à la semaine"),
        h("input", {value: stageForm.weekTeams, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {weekTeams: e.target.value})); }, placeholder: "U10, U12, U14", style: IS}),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: -8, marginBottom: 10}}, "Séparées par des virgules. Vide = toutes les catégories."),
        h("div", {style: LABEL}, "Lieu"),
        h("input", {value: stageForm.place, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {place: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Horaires"),
        h("input", {value: stageForm.schedule, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {schedule: e.target.value})); }, placeholder: "9h - 17h, accueil dès 8h30", style: IS}),
        h("div", {style: LABEL}, "Informations pratiques"),
        h("textarea", {rows: 3, value: stageForm.info, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {info: e.target.value})); }, placeholder: "Repas compris. Douche possible. Prévoir une tenue de rechange.", style: IS}),
        h("div", {style: LABEL}, "Clôture des inscriptions (facultatif)"),
        h("input", {type: "datetime-local", value: stageForm.closes_at, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {closes_at: e.target.value})); }, style: IS}),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: !!stageForm.open, onChange: function(e) { setStageForm(Object.assign({}, stageForm, {open: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Inscriptions ouvertes"),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 4}}, "Décoché, la page affiche le stage sans permettre de s'inscrire."),
        stageErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, stageErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setStageForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveStage, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── STAGE : formulaire d'une journée
    dayForm && h("div", {style: MODAL_BG, onClick: function() { setDayForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          dayForm.id ? "Modifier la journée" : "Nouvelle journée"),
        h("div", {style: LABEL}, "Date"),
        h("input", {type: "date", value: dayForm.date, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {date: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Libellé"),
        h("input", {value: dayForm.label, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {label: e.target.value})); }, placeholder: "Lundi", style: IS}),
        h("div", {style: LABEL}, "Activité de l'après-midi"),
        h("input", {value: dayForm.activity, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {activity: e.target.value})); }, placeholder: "Karting", style: IS}),
        h("div", {style: LABEL}, "Prix de la journée (€)"),
        h("input", {type: "number", step: "1", value: dayForm.price, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {price: e.target.value})); }, placeholder: "30", style: IS}),
        h("div", {style: LABEL}, "Catégories autorisées ce jour-là"),
        h("input", {value: dayForm.teams, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {teams: e.target.value})); }, placeholder: "U10, U12, U14", style: IS}),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: -8, marginBottom: 10}}, "Séparées par des virgules. Vide = toutes. C'est ce qui permet d'ouvrir le mercredi aux U8 seulement."),
        h("div", {style: LABEL}, "Nombre de places (vide = illimité)"),
        h("input", {type: "number", value: dayForm.capacity, onChange: function(e) { setDayForm(Object.assign({}, dayForm, {capacity: e.target.value})); }, style: IS}),
        stageErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, stageErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setDayForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveDay, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── REPAS : liste des services
    tab === "meals" && !mealSel && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Repas (" + meals.length + ")"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5}},
          "Un repas = une date, une carte et une capacité. Les supporters réservent depuis la page Repas du site."),
        h("button", {onClick: function() {
          setMealErr("");
          setMealForm({title: "Repas d'avant-match", subtitle: "", served_at: "", deadline: "",
                       location: "Stade du Capitaine Dumas", capacity: "", intro: "", published: false});
        }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 13, fontSize: 13, marginBottom: 14})},
          "+ Nouveau repas"),

        meals.map(function(m) {
          var d = new Date(m.served_at);
          return h("div", {key: m.id, style: {padding: "14px 0", borderBottom: "1px solid " + C.border, opacity: m.published ? 1 : .55}},
            h("div", {onClick: function() { openMeal(m); }, style: {cursor: "pointer"}},
              h("div", {style: {display: "flex", alignItems: "center", gap: 12}},
                h("div", {style: {width: 52, flexShrink: 0, background: C.deep, color: "#fff", borderRadius: 10, padding: "7px 0", textAlign: "center", fontFamily: "Oswald"}},
                  h("div", {style: {fontSize: 19, fontWeight: 800, lineHeight: 1}}, String(d.getDate()).padStart(2, "0")),
                  h("div", {style: {fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.gold}},
                    d.toLocaleDateString("fr-FR", {month: "short"}).replace(".", ""))
                ),
                h("div", {style: {flex: 1, minWidth: 0}},
                  h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, m.title),
                  m.subtitle && h("div", {style: {color: C.muted, fontSize: 13}}, m.subtitle),
                  h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                    m.bookings + " réservation" + (m.bookings > 1 ? "s" : "") + " · " +
                    m.seats + " couvert" + (m.seats > 1 ? "s" : "") +
                    (m.capacity ? " / " + m.capacity : "")),
                  !m.published && h("div", {style: {color: C.red, fontSize: 11, fontFamily: "Oswald", letterSpacing: 1, marginTop: 3, textTransform: "uppercase", fontWeight: 700}}, "Brouillon")
                ),
                h("div", {style: {textAlign: "right", flexShrink: 0}},
                  h("div", {style: {fontFamily: "Oswald", fontSize: 17, fontWeight: 800, color: C.green}}, formatEur(m.encaisse)),
                  m.attendu > 0 && h("div", {style: {fontSize: 11, color: C.muted}}, formatEur(m.attendu) + " dû")
                )
              )
            ),
            h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10}},
              h("button", {onClick: function() { openMeal(m); }, style: {flex: "1 1 auto", background: "rgba(208,33,40,.08)", border: "none", color: C.blue, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Ouvrir"),
              h("button", {onClick: function() {
                setMealForm({id: m.id, title: m.title, subtitle: m.subtitle || "",
                  served_at: toLocal(m.served_at), deadline: toLocal(m.deadline),
                  location: m.location || "", capacity: m.capacity || "",
                  intro: m.intro || "", published: m.published});
              }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.deep, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "✏️ Modifier")
            )
          );
        }),
        meals.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun repas"),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── REPAS : un service
    tab === "meals" && mealSel && h("div", null,
      h(Card, null,
        h("div", {style: {display: "flex", alignItems: "center", gap: 10, marginBottom: 14}},
          h("button", {onClick: function() { setMealSel(null); setMsg(""); }, style: {background: C.pale, border: "none", color: C.deep, padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 12, letterSpacing: 1, fontWeight: 700}}, "← Repas"),
          h("div", {style: {flex: 1, minWidth: 0}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 16, color: C.deep, fontWeight: 700, textTransform: "uppercase"}}, mealSel.title),
            h("div", {style: {fontSize: 12, color: C.muted}}, formatDateOnly(mealSel.served_at))
          )
        ),

        h("div", {style: {display: "flex", gap: 6, background: C.pale, padding: 4, borderRadius: 10, marginBottom: 16}},
          ["carte", "reservations", "cuisine"].map(function(k) {
            return h("button", {key: k, onClick: function() { setMealTab(k); },
              style: {flex: 1, border: 0, background: mealTab === k ? "#fff" : "transparent",
                      color: mealTab === k ? C.deep : C.muted, padding: 10, borderRadius: 8,
                      cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700,
                      letterSpacing: .5, textTransform: "uppercase"}},
              k === "carte" ? "Carte" : k === "reservations" ? "Réservations" : "Cuisine");
          })
        ),

        // ── Carte
        mealTab === "carte" && h("div", null,
          (mealSel.items || []).map(function(it) {
            return h("div", {key: it.id, style: {display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderBottom: "1px solid " + C.border, opacity: it.active ? 1 : .5}},
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, it.name),
                it.description && h("div", {style: {color: C.muted, fontSize: 12}}, it.description),
                h("div", {style: {color: C.muted, fontSize: 11, marginTop: 2}},
                  (it.counts_seat ? "Compte un couvert" : "Ne compte pas de couvert") + (it.active ? "" : " · désactivé"))
              ),
              h("div", {style: {fontFamily: "Oswald", fontSize: 17, fontWeight: 700, color: C.blue}}, formatEur(it.price_cents)),
              h("button", {onClick: function() {
                setItemForm({id: it.id, name: it.name, description: it.description || "",
                  price: (it.price_cents / 100).toFixed(2), kind: it.kind,
                  countsSeat: it.counts_seat, active: it.active, sort: it.sort_order});
              }, style: {background: C.pale, border: "none", color: C.blue, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, "✏️"),
              h("button", {onClick: function() { removeMealItem(it); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
            );
          }),
          (mealSel.items || []).length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 16}}, "Carte vide"),

          h("button", {onClick: function() {
            setItemForm({name: "", description: "", price: "", kind: "plat", countsSeat: true, active: true, sort: ((mealSel.items || []).length + 1)});
          }, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 12, marginTop: 14})}, "+ Ajouter un plat"),

          meals.length > 1 && h("div", {style: {marginTop: 12}},
            h("div", {style: LABEL}, "Reprendre la carte d'un autre repas"),
            h("select", {onChange: function(e) { if (e.target.value) { copyMenuFrom(e.target.value); e.target.value = ""; } },
                         style: Object.assign({}, IS, {marginBottom: 0})},
              h("option", {value: ""}, "— Choisir —"),
              meals.filter(function(x) { return x.id !== mealSel.id && (x.items || []).length; }).map(function(x) {
                return h("option", {key: x.id, value: x.id}, x.title + " · " + formatDateOnly(x.served_at));
              })
            )
          )
        ),

        // ── Réservations
        mealTab === "reservations" && h("div", null,
          mealBk.map(function(b) {
            return h("div", {key: b.id, style: {padding: "13px 0", borderBottom: "1px solid " + C.border, opacity: b.status === "annulee" ? .5 : 1}},
              h("div", {style: {display: "flex", alignItems: "center", gap: 10}},
                h("div", {style: {flex: 1, minWidth: 0}},
                  h("div", {style: {display: "flex", alignItems: "center", gap: 8}},
                    h("span", {style: {fontFamily: "Oswald", fontSize: 13, fontWeight: 800, color: C.blue, letterSpacing: 1}}, b.ref),
                    h("span", {style: {fontSize: 10, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999,
                      background: b.status === "payee" ? "rgba(34,197,94,.12)" : b.status === "annulee" ? C.pale : "rgba(244,232,52,.3)",
                      color: b.status === "payee" ? C.green : b.status === "annulee" ? C.muted : "#8a6d00"}},
                      b.status === "payee" ? "Payée" : b.status === "annulee" ? "Annulée" : "En attente")
                  ),
                  h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700, marginTop: 3}},
                    b.table_name || b.name),
                  b.table_name && b.table_name !== b.name && h("div", {style: {color: C.muted, fontSize: 12}},
                    "réservé par " + b.name),
                  h("div", {style: {color: C.muted, fontSize: 12}},
                    b.seats + " couvert" + (b.seats > 1 ? "s" : "") + " · " +
                    (b.payment_mode === "en_ligne" ? "en ligne" : "chez Buffard")),
                  h("div", {style: {color: C.muted, fontSize: 12}},
                    (b.lines || []).map(function(l) { return l.qty + "x " + l.name; }).join(", ")),
                  (b.guests || []).length > 0 && h("div", {style: {marginTop: 6, paddingLeft: 10, borderLeft: "2px solid " + C.border}},
                    (b.guests || []).map(function(g, i) {
                      return h("div", {key: i, style: {color: C.deep, fontSize: 13}},
                        g.name,
                        h("span", {style: {color: C.muted, fontSize: 11, marginLeft: 6}}, g.item));
                    })
                  ),
                  h("div", {style: {color: C.muted, fontSize: 12, marginTop: 2}},
                    h("a", {href: "tel:" + String(b.phone).replace(/\s/g, ""), style: {color: C.blue}}, b.phone),
                    " · ",
                    h("a", {href: "mailto:" + b.email, style: {color: C.blue}}, b.email)),
                  b.note && h("div", {style: {color: "#8a6d00", fontSize: 12, marginTop: 3}}, "⚠️ " + b.note)
                ),
                h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: C.deep, flexShrink: 0}}, formatEur(b.total_cents))
              ),
              h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 9}},
                b.status !== "payee" && h("button", {onClick: function() { setBookingStatus(b, "payee"); }, style: {flex: "1 1 auto", background: "rgba(34,197,94,.12)", border: "none", color: C.green, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Marquer payée"),
                b.status === "payee" && h("button", {onClick: function() { setBookingStatus(b, "en_attente"); }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.muted, padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler le paiement"),
                b.status !== "annulee" && h("button", {onClick: function() { if (confirm("Annuler la réservation " + b.ref + " ?")) setBookingStatus(b, "annulee"); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, padding: "9px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "Annuler")
              )
            );
          }),
          mealBk.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune réservation")
        ),

        // ── Cuisine
        mealTab === "cuisine" && h("div", null,
          h("div", {style: {display: "flex", gap: 8, marginBottom: 14}},
            h("div", {style: {flex: 1, background: C.pale, borderRadius: 12, padding: 14, textAlign: "center"}},
              h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: C.deep}}, (mealSheetData && mealSheetData.couverts) || 0),
              h("div", {style: {fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "Couverts")),
            h("div", {style: {flex: 1, background: "rgba(244,232,52,.18)", borderRadius: 12, padding: 14, textAlign: "center"}},
              h("div", {style: {fontFamily: "Oswald", fontSize: 26, fontWeight: 800, color: "#8a6d00"}}, formatEur((mealSheetData && mealSheetData.a_encaisser) || 0)),
              h("div", {style: {fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1}}, "À encaisser"))
          ),
          ((mealSheetData && mealSheetData.totaux) || []).map(function(x, i) {
            return h("div", {key: i, style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 0", borderBottom: "1px solid " + C.border}},
              h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 600}}, x.name),
              h("div", {style: {fontFamily: "Oswald", fontSize: 24, fontWeight: 800, color: C.blue}}, x.qty)
            );
          }),
          (!mealSheetData || !(mealSheetData.totaux || []).length) && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Rien à préparer pour l'instant"),
          h("button", {onClick: exportMealSheet, style: Object.assign({}, AB, {background: C.blue, width: "100%", padding: 13, fontSize: 12, marginTop: 14})}, "📥 Télécharger la feuille de service")
        ),

        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── REPAS : formulaire du service
    mealForm && h("div", {style: MODAL_BG, onClick: function() { setMealForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          mealForm.id ? "Modifier le repas" : "Nouveau repas"),
        h("div", {style: LABEL}, "Titre"),
        h("input", {value: mealForm.title, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {title: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Sous-titre (l'adversaire)"),
        h("input", {value: mealForm.subtitle, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {subtitle: e.target.value})); }, placeholder: "CSL vs XV de la Dombes", style: IS}),
        h("div", {style: LABEL}, "Date et heure du repas"),
        h("input", {type: "datetime-local", value: mealForm.served_at, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {served_at: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Clôture des réservations"),
        h("input", {type: "datetime-local", value: mealForm.deadline, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {deadline: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Nombre de couverts (vide = illimité)"),
        h("input", {type: "number", value: mealForm.capacity, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {capacity: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Lieu"),
        h("input", {value: mealForm.location, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {location: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Mot d'accueil"),
        h("textarea", {rows: 2, value: mealForm.intro, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {intro: e.target.value})); }, placeholder: "Rendez-vous au club-house à partir de 11h30.", style: IS}),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: !!mealForm.published, onChange: function(e) { setMealForm(Object.assign({}, mealForm, {published: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Visible sur le site"),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 4}}, "Décoché, le repas reste en brouillon : personne ne peut réserver."),
        mealErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, mealErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setMealForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveMealEvent, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── REPAS : formulaire d'un plat
    itemForm && h("div", {style: MODAL_BG, onClick: function() { setItemForm(null); }},
      h("div", {style: MODAL_BOX, onClick: function(e) { e.stopPropagation(); }},
        h("div", {style: {fontFamily: "Oswald", fontSize: 19, color: C.deep, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12}},
          itemForm.id ? "Modifier le plat" : "Nouveau plat"),
        h("div", {style: LABEL}, "Nom"),
        h("input", {value: itemForm.name, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {name: e.target.value})); }, placeholder: "Repas complet", style: IS}),
        h("div", {style: LABEL}, "Description"),
        h("input", {value: itemForm.description, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {description: e.target.value})); }, placeholder: "Entrée, plat, dessert", style: IS}),
        h("div", {style: LABEL}, "Prix (€)"),
        h("input", {type: "number", step: "0.5", value: itemForm.price, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {price: e.target.value})); }, style: IS}),
        h("div", {style: LABEL}, "Type"),
        h("select", {value: itemForm.kind, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {kind: e.target.value})); }, style: IS},
          h("option", {value: "plat"}, "Plat"),
          h("option", {value: "enfant"}, "Enfant"),
          h("option", {value: "dessert"}, "Dessert"),
          h("option", {value: "boisson"}, "Boisson"),
          h("option", {value: "option"}, "Option")
        ),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 6, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: itemForm.countsSeat !== false, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {countsSeat: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Compte un couvert"),
        h("div", {style: {fontSize: 12, color: C.muted, marginTop: 4}}, "Décoché pour un café ou un supplément, qui ne prend pas de place à table."),
        h("label", {style: {display: "flex", alignItems: "center", gap: 9, marginTop: 10, fontSize: 14, cursor: "pointer"}},
          h("input", {type: "checkbox", checked: itemForm.active !== false, onChange: function(e) { setItemForm(Object.assign({}, itemForm, {active: e.target.checked})); }, style: {width: 18, height: 18, accentColor: C.red}}),
          "Proposé à la réservation"),
        mealErr && h("div", {style: {background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "11px 13px", color: C.red, fontSize: 13, marginTop: 12}}, mealErr),
        h("div", {style: {display: "flex", gap: 8, marginTop: 16}},
          h("button", {onClick: function() { setItemForm(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, flex: 1, padding: 13, fontSize: 12})}, "Annuler"),
          h("button", {onClick: saveMealItem, style: Object.assign({}, AB, {background: C.green, flex: 2, padding: 13, fontSize: 13})}, "Enregistrer")
        )
      )
    ),

    // ─────────────────────────── CONSIGNES
    tab === "deposits" && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Consignes (" + depList.length + ")"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5}},
          "Une consigne est de l'argent encaissé que le club devra rendre : elle est comptée à part du chiffre d'affaires. Le rattachement d'une consigne à un produit se règle dans la carte de chaque point de vente."),
        depList.map(function(d) {
          if (editDepId === d.id) {
            return h("div", {key: d.id, style: {background: C.pale, border: "1px solid " + C.blue, borderRadius: 10, padding: 12, marginBottom: 8}},
              h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
                h("input", {value: editDep.emoji, onChange: function(e) { setEditDep(Object.assign({}, editDep, {emoji: e.target.value})); }, style: Object.assign({}, IS, {width: 50, textAlign: "center", fontSize: 20, padding: 8, marginBottom: 0})}),
                h("input", {value: editDep.name, onChange: function(e) { setEditDep(Object.assign({}, editDep, {name: e.target.value})); }, placeholder: "Nom", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})}),
                h("input", {value: editDep.price, onChange: function(e) { setEditDep(Object.assign({}, editDep, {price: e.target.value})); }, placeholder: "€", type: "number", step: "0.5", style: Object.assign({}, IS, {width: 80, marginBottom: 0})})
              ),
              h("div", {style: {display: "flex", gap: 6}},
                h("button", {onClick: function() { saveEditDep(d.id); }, style: Object.assign({}, AB, {background: C.green, flex: 1, padding: 10, fontSize: 12})}, "💾 Enregistrer"),
                h("button", {onClick: function() { setEditDepId(null); }, style: Object.assign({}, AB, {background: C.pale, color: C.muted, padding: 10, fontSize: 12})}, "Annuler")
              )
            );
          }
          return h("div", {key: d.id, style: {display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid " + C.border, opacity: d.active ? 1 : .45}},
            h("span", {style: {fontSize: 28}}, d.emoji || "🥤"),
            h("div", {style: {flex: 1, minWidth: 0}},
              h("div", {style: {color: C.deep, fontSize: 15, fontWeight: 700}}, d.name),
              h("div", {style: {color: C.blue, fontSize: 14, fontFamily: "Oswald", fontWeight: 700}}, formatEur(d.price_cents)),
              !d.active && h("div", {style: {color: C.red, fontSize: 11, fontFamily: "Oswald", letterSpacing: 1, marginTop: 2, textTransform: "uppercase", fontWeight: 700}}, "Désactivée")
            ),
            h("div", {style: {display: "flex", gap: 6}},
              h("button", {onClick: function() { startEditDep(d); }, style: {background: C.pale, border: "none", color: C.blue, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, "✏️"),
              h("button", {onClick: function() { toggleDeposit(d); }, style: {background: C.pale, border: "none", color: C.muted, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, d.active ? "🚫" : "✅"),
              h("button", {onClick: function() { removeDeposit(d); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
            )
          );
        }),
        depList.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucune consigne"),
        h("div", {style: {background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 10, padding: 16, marginTop: 20}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.green, marginBottom: 10, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase"}}, "➕ Nouvelle consigne"),
          h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
            h("input", {value: newDep.emoji, onChange: function(e) { setNewDep(Object.assign({}, newDep, {emoji: e.target.value})); }, placeholder: "", style: Object.assign({}, IS, {width: 50, textAlign: "center", fontSize: 20, padding: 8, marginBottom: 0})}),
            h("input", {value: newDep.name, onChange: function(e) { setNewDep(Object.assign({}, newDep, {name: e.target.value})); }, placeholder: "Nom (verre, pichet…)", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})}),
            h("input", {value: newDep.price, onChange: function(e) { setNewDep(Object.assign({}, newDep, {price: e.target.value})); }, placeholder: "€", type: "number", step: "0.5", style: Object.assign({}, IS, {width: 80, marginBottom: 0})})
          ),
          h("button", {onClick: addNewDeposit, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 13})}, "Créer la consigne")
        ),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── POINTS DE VENTE : liste
    tab === "pos" && !selectedPos && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 6, fontWeight: 700, textTransform: "uppercase"}}, "Points de vente (" + posList.length + ")"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5}}, "Chaque poste a son propre QR code et sa propre carte. Les bénévoles scannent le QR du poste où ils se trouvent avant d'encaisser."),
        posList.map(function(p) {
          // Edition en ligne : plus fiable qu'une boite prompt(), et utilisable au doigt
          if (editPosId === p.id) {
            return h("div", {key: p.id, style: {background: C.pale, border: "1px solid " + C.blue, borderRadius: 10, padding: 12, marginBottom: 8}},
              h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
                h("input", {value: editPos.emoji, onChange: function(e) { setEditPos(Object.assign({}, editPos, {emoji: e.target.value})); }, style: Object.assign({}, IS, {width: 54, textAlign: "center", fontSize: 22, padding: 8, marginBottom: 0})}),
                h("input", {value: editPos.name, onChange: function(e) { setEditPos(Object.assign({}, editPos, {name: e.target.value})); }, placeholder: "Nom du poste", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})})
              ),
              h("div", {style: {display: "flex", gap: 6}},
                h("button", {onClick: function() { saveEditPos(p.id); }, style: Object.assign({}, AB, {background: C.green, flex: 1, padding: 12, fontSize: 12})}, "💾 Enregistrer"),
                h("button", {onClick: function() { setEditPosId(null); }, style: Object.assign({}, AB, {background: "#fff", color: C.muted, padding: 12, fontSize: 12})}, "Annuler")
              )
            );
          }
          return h("div", {key: p.id, style: {padding: "14px 0", borderBottom: "1px solid " + C.border, opacity: p.active ? 1 : .45}},
            h("div", {style: {display: "flex", alignItems: "center", gap: 12}},
              h("span", {style: {fontSize: 30, flexShrink: 0}}, p.emoji || "🏉"),
              h("div", {onClick: function() { setSelectedPos(p); setMsg(""); }, style: {flex: 1, cursor: "pointer", minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, p.name),
                h("div", {style: {color: C.muted, fontSize: 11, fontFamily: "monospace", marginTop: 2, wordBreak: "break-all"}}, p.qr_token),
                !p.active && h("div", {style: {color: C.red, fontSize: 11, fontFamily: "Oswald", letterSpacing: 1, marginTop: 3, textTransform: "uppercase", fontWeight: 700}}, "Désactivé")
              )
            ),
            // Les actions passent a la ligne : sur telephone, 5 boutons ne tiennent pas
            h("div", {style: {display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10}},
              h("button", {onClick: function() { startEditPos(p); }, style: {flex: "1 1 auto", background: "rgba(208,33,40,.08)", border: "none", color: C.blue, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "✏️ Renommer"),
              h("button", {onClick: function() { setQrPos(qrPos === p.id ? null : p.id); }, style: {flex: "1 1 auto", background: C.pale, border: "none", color: C.deep, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, qrPos === p.id ? "🔳 Masquer" : "🔳 QR"),
              h("button", {onClick: function() { downloadPosQR(p); }, style: {flex: "1 1 auto", background: "rgba(34,197,94,.1)", border: "none", color: C.green, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase"}}, "⬇️ Imprimer"),
              h("button", {onClick: function() { togglePos(p); }, style: {background: C.pale, border: "none", color: C.muted, width: 42, padding: "10px 0", borderRadius: 8, cursor: "pointer", fontSize: 14}}, p.active ? "🚫" : "✅"),
              h("button", {onClick: function() { removePos(p); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 42, padding: "10px 0", borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
            )
          );
        }),
        posList.map(function(p) {
          if (qrPos !== p.id) return null;
          return h("div", {key: "qr-" + p.id, style: {textAlign: "center", padding: "18px 0", borderBottom: "1px solid " + C.border}},
            h("div", {style: {fontFamily: "Oswald", fontSize: 13, color: C.deep, letterSpacing: 1, marginBottom: 10, fontWeight: 700, textTransform: "uppercase"}}, (p.emoji || "🏉") + " " + p.name),
            h(QRCodeView, {data: p.qr_token, size: 200}),
            h("div", {style: {fontFamily: "monospace", fontSize: 12, color: C.muted, marginTop: 10}}, p.qr_token),
            h("button", {onClick: function() { downloadPosQR(p); }, style: Object.assign({}, AB, {background: C.green, marginTop: 12, padding: "10px 18px", fontSize: 12})}, "⬇️ Télécharger pour impression")
          );
        }),
        posList.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun point de vente"),
        h("div", {style: {background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 10, padding: 16, marginTop: 20}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.green, marginBottom: 10, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase"}}, "➕ Nouveau point de vente"),
          h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
            h("input", {value: newPos.emoji, onChange: function(e) { setNewPos(Object.assign({}, newPos, {emoji: e.target.value})); }, placeholder: "", style: Object.assign({}, IS, {width: 50, textAlign: "center", fontSize: 20, padding: 8, marginBottom: 0})}),
            h("input", {value: newPos.name, onChange: function(e) { setNewPos(Object.assign({}, newPos, {name: e.target.value})); }, placeholder: "Nom du poste", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})})
          ),
          h("button", {onClick: addNewPos, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 13})}, "Créer le poste")
        ),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),

    // ─────────────────────────── POINTS DE VENTE : carte d'un poste
    tab === "pos" && selectedPos && h("div", null,
      h(Card, null,
        h("div", {style: {display: "flex", alignItems: "center", gap: 10, marginBottom: 16}},
          h("button", {onClick: function() { setSelectedPos(null); setMsg(""); }, style: {background: C.pale, border: "none", color: C.deep, padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 12, letterSpacing: 1, fontWeight: 700}}, "← Postes"),
          h("span", {style: {fontSize: 26}}, selectedPos.emoji || "🏉"),
          h("div", {style: {fontFamily: "Oswald", fontSize: 16, color: C.deep, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase"}}, selectedPos.name)
        ),
        h("div", {style: {fontFamily: "Oswald", fontSize: 13, color: C.muted, letterSpacing: 1, marginBottom: 14, fontWeight: 700, textTransform: "uppercase"}}, "Carte du poste (" + allProducts.length + ")"),
        allProducts.map(function(p) {
          if (editingId === p.id) {
            return h("div", {key: p.id, style: {background: C.pale, border: "1px solid " + C.blue, borderRadius: 10, padding: 12, marginBottom: 8}},
              h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
                h("input", {value: editDraft.emoji, onChange: function(e) { setEditDraft(Object.assign({}, editDraft, {emoji: e.target.value})); }, style: Object.assign({}, IS, {width: 50, textAlign: "center", fontSize: 20, padding: 8, marginBottom: 0})}),
                h("input", {value: editDraft.name, onChange: function(e) { setEditDraft(Object.assign({}, editDraft, {name: e.target.value})); }, placeholder: "Nom", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})}),
                h("input", {value: editDraft.price, onChange: function(e) { setEditDraft(Object.assign({}, editDraft, {price: e.target.value})); }, placeholder: "Prix", type: "number", step: "0.5", style: Object.assign({}, IS, {width: 80, marginBottom: 0})})
              ),
              h("div", {style: {display: "flex", gap: 6}},
                h("button", {onClick: function() { saveEdit(p.id); }, style: Object.assign({}, AB, {background: C.green, flex: 1, padding: 10, fontSize: 12})}, "💾 Enregistrer"),
                h("button", {onClick: cancelEdit, style: Object.assign({}, AB, {background: C.pale, color: C.muted, padding: 10, fontSize: 12})}, "Annuler")
              )
            );
          }
          return h("div", {key: p.id, style: {display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid " + C.border}},
            h("div", {style: {display: "flex", alignItems: "center", gap: 12, flex: 1}},
              h("span", {style: {fontSize: 28}}, p.emoji),
              h("div", null,
                h("div", {style: {color: C.deep, fontSize: 14, fontWeight: 700}}, p.name),
                h("div", {style: {color: C.blue, fontSize: 14, fontFamily: "Oswald", fontWeight: 700}}, formatEur(p.price_cents))
              )
            ),
            h("div", {style: {display: "flex", alignItems: "center", gap: 6}},
              h("select", {
                value: p.deposit_id || "",
                onChange: function(e) { setProductDeposit(p, e.target.value); },
                title: "Consigne rattachée à ce produit",
                style: {border: "1px solid " + C.border, borderRadius: 8, padding: "7px 6px", fontSize: 12, color: p.deposit_id ? C.deep : C.muted, background: p.deposit_id ? "rgba(208,33,40,.06)" : "#fff", fontFamily: "Barlow Condensed, sans-serif", maxWidth: 120}
              },
                h("option", {value: ""}, "Sans consigne"),
                depList.filter(function(d) { return d.active; }).map(function(d) {
                  return h("option", {key: d.id, value: d.id}, (d.emoji || "🥤") + " " + d.name + " " + formatEur(d.price_cents));
                })
              ),
              h("button", {onClick: function() { startEdit(p); }, style: {background: C.pale, border: "none", color: C.blue, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 14}}, "✏️"),
              h("button", {onClick: function() { removeProduct(p); }, style: {background: "rgba(239,68,68,.1)", border: "none", color: C.red, width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700}}, "×")
            )
          );
        }),
        allProducts.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun produit"),
        h("div", {style: {background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 10, padding: 16, marginTop: 20}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 12, color: C.green, marginBottom: 10, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase"}}, "➕ Ajouter un produit à " + selectedPos.name),
          h("div", {style: {display: "flex", gap: 6, marginBottom: 8}},
            h("input", {value: newProd.emoji, onChange: function(e) { setNewProd(Object.assign({}, newProd, {emoji: e.target.value})); }, placeholder: "", style: Object.assign({}, IS, {width: 50, textAlign: "center", fontSize: 20, padding: 8, marginBottom: 0})}),
            h("input", {value: newProd.name, onChange: function(e) { setNewProd(Object.assign({}, newProd, {name: e.target.value})); }, placeholder: "Nom du produit", style: Object.assign({}, IS, {flex: 1, marginBottom: 0})}),
            h("input", {value: newProd.price, onChange: function(e) { setNewProd(Object.assign({}, newProd, {price: e.target.value})); }, placeholder: "Prix €", type: "number", step: "0.5", style: Object.assign({}, IS, {width: 80, marginBottom: 0})})
          ),
          h("button", {onClick: addNewProduct, style: Object.assign({}, AB, {background: C.green, width: "100%", padding: 12, fontSize: 13})}, "Ajouter")
        ),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),
    // COMPTES
    tab === "accounts" && h("div", null,
      h(Card, null,
        h("div", {style: {display: "flex", gap: 10, marginBottom: 14}},
          h("input", {value: search, onChange: function(e) { setSearch(e.target.value); setPage(0); }, placeholder: "🔍 Rechercher par nom ou email...", style: Object.assign({}, IS, {marginBottom: 0, flex: 1})})
        ),
        h("div", {style: {fontSize: 12, color: C.muted, marginBottom: 10}},
          accountsCount + " compte(s) au total · Page " + (page + 1) + " / " + Math.max(1, Math.ceil(accountsCount / 20))
        ),
        accounts.map(function(a) {
          var ty = mTypes.filter(function(x) { return x.id === a.member_type_id; })[0];
          return h("div", {key: a.id, style: {padding: "13px 0", borderBottom: "1px solid " + C.border}},
            h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10}},
              h("div", {style: {flex: 1, minWidth: 0}},
                h("div", {style: {color: C.deep, fontSize: 14, fontWeight: 700}}, a.name),
                h("div", {style: {color: C.muted, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}, a.email),
                h("div", {style: {display: "flex", gap: 5, flexWrap: "wrap", marginTop: 5}},
                  h("span", {style: {fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: a.role === "admin" ? C.gold : a.role === "encaissement" ? C.blue : a.role === "recharge" ? C.green : C.muted, background: a.role === "admin" ? "rgba(255,182,39,.15)" : a.role === "encaissement" ? "rgba(208,33,40,.1)" : a.role === "recharge" ? "rgba(34,197,94,.1)" : C.pale, padding: "3px 8px", borderRadius: 50, fontFamily: "Oswald"}}, a.role),
                  ty && h("span", {style: {fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#fff", background: ty.color || C.muted, padding: "3px 8px", borderRadius: 50, fontFamily: "Oswald"}},
                    (ty.emoji || "") + " " + ty.name)
                )
              ),
              h("div", {style: {fontFamily: "Oswald", fontSize: 18, fontWeight: 800, color: a.balance_cents > 0 ? C.green : C.muted, whiteSpace: "nowrap"}}, formatEur(a.balance_cents)),
              h("div", {style: {display: "flex", flexDirection: "column", gap: 5, flexShrink: 0}},
                h("button", {
                  onClick: function() { openAdjust(a); },
                  title: "Ajuster le solde (correction d'erreur)",
                  style: {background: "rgba(255,182,39,.12)", border: "1px solid " + C.gold, color: C.deep, borderRadius: 8, padding: "8px 12px", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer", textTransform: "uppercase", whiteSpace: "nowrap"}
                }, "⚖️ Ajuster"),
                h("button", {
                  onClick: function() { ouvrirCarte(a); },
                  title: "Voir et télécharger la carte de membre",
                  style: {background: "rgba(208,33,40,.08)", border: "1px solid " + C.border, color: C.blue, borderRadius: 8, padding: "8px 12px", fontFamily: "Oswald", fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer", textTransform: "uppercase", whiteSpace: "nowrap"}
                }, "🪪 Carte")
              )
            ),
            // Le statut décide des avantages : entrée gratuite, remises.
            mTypes.length > 0 && h("div", {style: {display: "flex", alignItems: "center", gap: 8, marginTop: 9}},
              h("span", {style: {fontSize: 11, color: C.muted, fontFamily: "Oswald", letterSpacing: 1, textTransform: "uppercase", flexShrink: 0}}, "Statut"),
              h("select", {
                value: a.member_type_id || "",
                onChange: function(e) { assignMType(a, e.target.value || null); },
                style: Object.assign({}, IS, {marginBottom: 0, padding: "8px 10px", fontSize: 13, flex: 1})
              },
                h("option", {value: ""}, "— Aucun —"),
                mTypes.filter(function(x) { return x.active || x.id === a.member_type_id; })
                  .map(function(x) {
                    return h("option", {key: x.id, value: x.id}, (x.emoji || "") + " " + x.name);
                  })
              )
            )
          );
        }),
        accounts.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun compte"),
        // Pagination
        accountsCount > 20 && h("div", {style: {display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8}},
          h("button", {onClick: function() { setPage(Math.max(0, page - 1)); }, disabled: page === 0, style: Object.assign({}, AB, {background: page === 0 ? C.pale : C.blue, color: page === 0 ? C.muted : "#fff", padding: "10px 16px", fontSize: 12, opacity: page === 0 ? .5 : 1})}, "← Précédent"),
          h("button", {onClick: function() { if ((page + 1) * 20 < accountsCount) setPage(page + 1); }, disabled: (page + 1) * 20 >= accountsCount, style: Object.assign({}, AB, {background: (page + 1) * 20 >= accountsCount ? C.pale : C.blue, color: (page + 1) * 20 >= accountsCount ? C.muted : "#fff", padding: "10px 16px", fontSize: 12, opacity: (page + 1) * 20 >= accountsCount ? .5 : 1})}, "Suivant →")
        )
      )
    ),
    // RÔLES
    tab === "roles" && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 14, fontWeight: 700, textTransform: "uppercase"}}, "🛡 Gestion des rôles"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5}},
          "Un bénévole peut cumuler plusieurs rôles : supporter et encaissement, encaissement et boutique… Touche un rôle pour l'ajouter ou le retirer. Le changement prend effet à sa prochaine connexion."),
        h("input", {value: search, onChange: function(e) { setSearch(e.target.value); setPage(0); }, placeholder: "🔍 Rechercher la personne par nom ou email...", style: IS}),
        accounts.map(function(a) {
          return h("div", {key: a.id, style: {padding: "14px 0", borderBottom: "1px solid " + C.border}},
            h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}},
              h("div", null,
                h("div", {style: {color: C.deep, fontSize: 14, fontWeight: 700}}, a.name),
                h("div", {style: {color: C.muted, fontSize: 11}}, a.email)
              ),
              h("div", {style: {display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end"}},
                rolesOf(a).map(function(r) {
                  return h("span", {key: r, style: {fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: r === "admin" ? C.gold : C.blue, background: r === "admin" ? "rgba(255,182,39,.15)" : "rgba(208,33,40,.1)", padding: "4px 10px", borderRadius: 50, fontFamily: "Oswald"}}, r);
                })
              )
            ),
            h("div", {style: {display: "flex", gap: 6, flexWrap: "wrap"}},
              ["supporter", "encaissement", "recharge", "boutique", "billetterie", "admin"].map(function(r) {
                var on = rolesOf(a).indexOf(r) !== -1;
                return h("button", {key: r, onClick: function() { toggleRole(a, r); },
                  style: {padding: "7px 13px", borderRadius: 8,
                          border: "1px solid " + (on ? C.blue : C.border),
                          background: on ? C.blue : "#fff", color: on ? "#fff" : C.muted,
                          fontSize: 11, fontFamily: "Oswald", fontWeight: 700, letterSpacing: 1,
                          cursor: "pointer", textTransform: "uppercase"}},
                  (on ? "✓ " : "") + r);
              })
            )
          );
        }),
        accounts.length === 0 && h("div", {style: {color: C.muted, fontSize: 13, textAlign: "center", padding: 20}}, "Aucun compte"),
        accountsCount > 20 && h("div", {style: {display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8}},
          h("button", {onClick: function() { setPage(Math.max(0, page - 1)); }, disabled: page === 0, style: Object.assign({}, AB, {background: page === 0 ? C.pale : C.blue, color: page === 0 ? C.muted : "#fff", padding: "10px 16px", fontSize: 12})}, "← Précédent"),
          h("button", {onClick: function() { if ((page + 1) * 20 < accountsCount) setPage(page + 1); }, disabled: (page + 1) * 20 >= accountsCount, style: Object.assign({}, AB, {background: (page + 1) * 20 >= accountsCount ? C.pale : C.blue, color: (page + 1) * 20 >= accountsCount ? C.muted : "#fff", padding: "10px 16px", fontSize: 12})}, "Suivant →")
        ),
        msg && h("div", {style: {textAlign: "center", marginTop: 12, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg)
      )
    ),
    // EXPORT
    tab === "export" && h("div", null,
      h(Card, null,
        h("div", {style: {fontFamily: "Oswald", fontSize: 14, color: C.deep, letterSpacing: 1, marginBottom: 14, fontWeight: 700, textTransform: "uppercase"}}, "📥 Exports Excel"),
        h("div", {style: {fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.5}}, "Télécharge des fichiers Excel (.xlsx) avec mise en forme et résumés statistiques. Chaque export comprend total, par jour, par catégorie et top produits."),

        // Sélecteur Jour unique / Plage de dates
        h("div", {style: {display: "flex", gap: 8, marginBottom: 14, background: C.pale, padding: 4, borderRadius: 10}},
          h("button", {
            onClick: function() { setExportMode("single"); },
            style: {flex: 1, background: exportMode === "single" ? "#fff" : "transparent", border: "none", padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700, letterSpacing: 1, color: exportMode === "single" ? C.deep : C.muted, textTransform: "uppercase", boxShadow: exportMode === "single" ? "0 2px 6px rgba(0,0,0,.06)" : "none"}
          }, "📅 Jour unique"),
          h("button", {
            onClick: function() { setExportMode("range"); },
            style: {flex: 1, background: exportMode === "range" ? "#fff" : "transparent", border: "none", padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Oswald", fontSize: 12, fontWeight: 700, letterSpacing: 1, color: exportMode === "range" ? C.deep : C.muted, textTransform: "uppercase", boxShadow: exportMode === "range" ? "0 2px 6px rgba(0,0,0,.06)" : "none"}
          }, "🗓 Plage de dates")
        ),

        // Champs date(s)
        exportMode === "single"
          ? h(FormField, {
              label: "Date à exporter",
              inputProps: {value: exportDate, onChange: function(e) { setExportDate(e.target.value); }, type: "date", disabled: exporting}
            })
          : h("div", {style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14}},
              h(FormField, {label: "Du", inputProps: {value: exportDate, onChange: function(e) { setExportDate(e.target.value); }, type: "date", disabled: exporting}}),
              h(FormField, {label: "Au", inputProps: {value: exportEndDate, onChange: function(e) { setExportEndDate(e.target.value); }, type: "date", disabled: exporting}})
            ),

        // Grille de 5 boutons d'export
        h("div", {style: {fontFamily: "Oswald", fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginTop: 18, marginBottom: 10}}, "Choisir l'export"),

        // Bouton 1 : Export Global (toutes catégories + recharges)
        h("button", {
          onClick: exportGlobal, disabled: exporting,
          style: {width: "100%", background: exporting ? "#cbd5e1" : "linear-gradient(135deg, " + C.deep + ", " + C.blue + ")", color: "#fff", border: "none", borderRadius: 12, padding: "16px 18px", cursor: exporting ? "wait" : "pointer", marginBottom: 10, fontFamily: "Oswald", fontSize: 14, fontWeight: 800, letterSpacing: 1, textAlign: "left", textTransform: "uppercase", boxShadow: exporting ? "none" : "0 4px 14px rgba(26,14,15,.2)"}
        },
          h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
            h("div", null,
              h("div", null, "📦 Export global"),
              h("div", {style: {fontSize: 10, fontWeight: 500, opacity: .8, marginTop: 4, letterSpacing: .5, textTransform: "none"}}, "Toutes catégories + recharges en ligne et au stade")
            ),
            h("div", {style: {fontSize: 11, opacity: .7}}, "7 feuilles →")
          )
        ),

        // Bouton 2 : Ventes cashless au stade global
        h("button", {
          onClick: exportVentesStade, disabled: exporting,
          style: {width: "100%", background: exporting ? "#f7f2ed" : "#fff", color: C.deep, border: "2px solid " + C.blue, borderRadius: 12, padding: "14px 18px", cursor: exporting ? "wait" : "pointer", marginBottom: 8, fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, textAlign: "left", textTransform: "uppercase"}
        },
          h("div", null, "🏉 Ventes cashless (stade)"),
          h("div", {style: {fontSize: 10, fontWeight: 500, color: C.muted, marginTop: 4, letterSpacing: .5, textTransform: "none"}}, "Tous postes + boutique confondus")
        ),

        // Bouton 3 : tous les points de vente, une feuille par poste
        h("button", {
          onClick: function() { exportParPoste(null); }, disabled: exporting,
          style: {width: "100%", background: exporting ? "#f7f2ed" : "#fff", color: C.deep, border: "2px solid " + C.green, borderRadius: 12, padding: "14px 18px", cursor: exporting ? "wait" : "pointer", marginBottom: 8, fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, textAlign: "left", textTransform: "uppercase"}
        },
          h("div", null, "📍 Par point de vente"),
          h("div", {style: {fontSize: 10, fontWeight: 500, color: C.muted, marginTop: 4, letterSpacing: .5, textTransform: "none"}}, "Une feuille de synthèse + une feuille par poste (Z de caisse)")
        ),

        // Bouton 4 : Boutique
        h("button", {
          onClick: exportBoutique, disabled: exporting,
          style: {width: "100%", background: exporting ? "#f7f2ed" : "#fff", color: C.deep, border: "1px solid " + C.border, borderRadius: 12, padding: "12px 18px", cursor: exporting ? "wait" : "pointer", marginBottom: 8, fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, textAlign: "left", textTransform: "uppercase"}
        }, "🛍 Boutique uniquement"),

        // Un bouton par poste
        posList.length > 0 && h("div", {style: {marginTop: 14, marginBottom: 6}},
          h("div", {style: {fontFamily: "Oswald", fontSize: 11, color: C.muted, letterSpacing: 1.5, fontWeight: 700, textTransform: "uppercase", marginBottom: 8}}, "Un seul poste"),
          posList.map(function(pv) {
            return h("button", {
              key: "exp-" + pv.id,
              onClick: function() { exportParPoste(pv); }, disabled: exporting,
              style: {width: "100%", background: exporting ? "#f7f2ed" : "#fff", color: C.deep, border: "1px solid " + C.border, borderRadius: 12, padding: "11px 18px", cursor: exporting ? "wait" : "pointer", marginBottom: 6, fontFamily: "Oswald", fontSize: 12, fontWeight: 700, letterSpacing: 1, textAlign: "left", textTransform: "uppercase"}
            }, (pv.emoji || "🏉") + " " + pv.name);
          })
        ),

        msg && h("div", {style: {textAlign: "center", marginTop: 14, padding: 10, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : (msg.indexOf("⏳") === 0 ? C.blue : C.red), fontWeight: 600, background: msg.indexOf("✅") === 0 ? "rgba(34,197,94,.08)" : (msg.indexOf("⏳") === 0 ? "rgba(208,33,40,.08)" : "rgba(239,68,68,.08)"), borderRadius: 8}}, msg),

        h("div", {style: {marginTop: 20, padding: 14, background: C.pale, borderRadius: 10, fontSize: 12, color: C.muted, lineHeight: 1.6}},
          h("strong", {style: {color: C.deep}}, "💡 Contenu des exports : "),
          "tous les fichiers contiennent un résumé en haut (total, ticket moyen, par jour) + top 10 produits/articles + le détail ligne par ligne, avec la colonne Poste. L'export « Par point de vente » ouvre sur une synthèse comparant les postes, puis une feuille par poste : c'est celui à donner au trésorier."
        )
      )
    ),

    // ═══════════════════════════════════════════════════════════════
    // MODAL : Ajuster le solde d'un compte
    // ═══════════════════════════════════════════════════════════════
    adjustingAccount && h("div", {
      onClick: closeAdjust,
      style: {position: "fixed", inset: 0, background: "rgba(26,14,15,.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)"}
    },
      h("div", {
        onClick: function(e) { e.stopPropagation(); },
        style: {background: "#fff", borderRadius: 20, padding: 28, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.3)"}
      },
        // Header
        h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 20}},
          h("div", null,
            h("div", {style: {fontFamily: "Oswald", fontSize: 11, letterSpacing: 3, color: C.gold, fontWeight: 700, textTransform: "uppercase", marginBottom: 4}}, "⚖️ Correction d'erreur"),
            h("div", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.deep, textTransform: "uppercase"}}, "Ajuster le solde")
          ),
          h("button", {onClick: closeAdjust, disabled: adjusting, style: {background: "transparent", border: "none", fontSize: 24, cursor: adjusting ? "wait" : "pointer", color: C.muted, padding: 0, lineHeight: 1}}, "×")
        ),
        // Infos compte
        h("div", {style: {background: C.pale, borderRadius: 12, padding: 14, marginBottom: 18}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4, fontWeight: 700}}, "Compte"),
          h("div", {style: {color: C.deep, fontSize: 16, fontWeight: 700}}, adjustingAccount.name),
          h("div", {style: {fontSize: 12, color: C.muted, marginTop: 2}}, adjustingAccount.email),
          h("div", {style: {marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center"}},
            h("span", {style: {fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase"}}, "Solde actuel"),
            h("span", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.green}}, formatEur(adjustingAccount.balance_cents))
          )
        ),
        // Sélecteur +/-
        h("div", {style: {marginBottom: 14}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 700}}, "Type d'ajustement"),
          h("div", {style: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10}},
            h("button", {
              onClick: function() { setAdjustSign("+"); },
              disabled: adjusting,
              style: {background: adjustSign === "+" ? "linear-gradient(135deg, " + C.green + ", #16a34a)" : "#fff", color: adjustSign === "+" ? "#fff" : C.deep, border: adjustSign === "+" ? "2px solid #16a34a" : "2px solid " + C.border, borderRadius: 12, padding: "16px 12px", fontFamily: "Oswald", fontSize: 14, fontWeight: 800, letterSpacing: 1, cursor: adjusting ? "wait" : "pointer", textTransform: "uppercase", boxShadow: adjustSign === "+" ? "0 4px 14px rgba(34,197,94,.3)" : "none", transition: "all .2s"}
            }, "+ Ajouter"),
            h("button", {
              onClick: function() { setAdjustSign("-"); },
              disabled: adjusting,
              style: {background: adjustSign === "-" ? "linear-gradient(135deg, " + C.red + ", #dc2626)" : "#fff", color: adjustSign === "-" ? "#fff" : C.deep, border: adjustSign === "-" ? "2px solid #dc2626" : "2px solid " + C.border, borderRadius: 12, padding: "16px 12px", fontFamily: "Oswald", fontSize: 14, fontWeight: 800, letterSpacing: 1, cursor: adjusting ? "wait" : "pointer", textTransform: "uppercase", boxShadow: adjustSign === "-" ? "0 4px 14px rgba(239,68,68,.3)" : "none", transition: "all .2s"}
            }, "− Retirer")
          )
        ),
        // Montant
        h("div", {style: {marginBottom: 14}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 700}}, "Montant"),
          h("div", {style: {position: "relative"}},
            h("input", {
              value: adjustAmount,
              onChange: function(e) {
                var v = e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".");
                setAdjustAmount(v);
              },
              placeholder: "0.00",
              type: "text",
              inputMode: "decimal",
              autoFocus: true,
              disabled: adjusting,
              style: Object.assign({}, IS, {marginBottom: 0, paddingRight: 36, fontSize: 18, fontWeight: 700, textAlign: "right", padding: "14px 36px 14px 14px"})
            }),
            h("span", {style: {position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.muted, fontWeight: 700, pointerEvents: "none", fontSize: 17}}, "€")
          )
        ),
        // Motif
        h("div", {style: {marginBottom: 18}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 700}}, "Motif ", h("span", {style: {color: C.red}}, "*")),
          h("textarea", {
            value: adjustReason,
            onChange: function(e) { setAdjustReason(e.target.value); },
            placeholder: "Ex: Recharge en double, erreur de saisie, remboursement, etc.",
            disabled: adjusting,
            rows: 3,
            style: Object.assign({}, IS, {marginBottom: 0, padding: 12, fontSize: 13, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5})
          })
        ),
        // Aperçu
        adjustAmount && parseFloat(adjustAmount.replace(",", ".")) > 0 && h("div", {style: {background: adjustSign === "+" ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)", border: "1px solid " + (adjustSign === "+" ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"), borderRadius: 12, padding: 14, marginBottom: 18}},
          h("div", {style: {fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, fontWeight: 700}}, "Aperçu"),
          h("div", {style: {display: "flex", justifyContent: "space-between", alignItems: "center"}},
            h("span", {style: {fontSize: 13, color: C.deep}}, "Nouveau solde après ajustement :"),
            h("span", {style: {fontFamily: "Oswald", fontSize: 20, fontWeight: 800, color: adjustSign === "+" ? C.green : C.red}},
              formatEur(adjustingAccount.balance_cents + (adjustSign === "-" ? -1 : 1) * Math.round(parseFloat(adjustAmount.replace(",", ".")) * 100))
            )
          )
        ),
        // Boutons
        h("div", {style: {display: "flex", gap: 10}},
          h("button", {
            onClick: closeAdjust,
            disabled: adjusting,
            style: {flex: 1, background: C.pale, color: C.deep, border: "none", borderRadius: 12, padding: 14, fontFamily: "Oswald", fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: adjusting ? "wait" : "pointer", textTransform: "uppercase"}
          }, "Annuler"),
          h("button", {
            onClick: doAdjustBalance,
            disabled: adjusting || !adjustAmount || !adjustReason.trim(),
            style: {flex: 2, background: (adjusting || !adjustAmount || !adjustReason.trim()) ? "#cbd5e1" : (adjustSign === "+" ? "linear-gradient(135deg, " + C.green + ", #16a34a)" : "linear-gradient(135deg, " + C.red + ", #dc2626)"), color: "#fff", border: "none", borderRadius: 12, padding: 14, fontFamily: "Oswald", fontSize: 13, fontWeight: 800, letterSpacing: 1, cursor: (adjusting || !adjustAmount || !adjustReason.trim()) ? "not-allowed" : "pointer", textTransform: "uppercase", boxShadow: (adjusting || !adjustAmount || !adjustReason.trim()) ? "none" : "0 4px 14px rgba(26,14,15,.2)"}
          }, adjusting ? "⏳ En cours..." : "✅ Confirmer l'ajustement")
        ),
        msg && h("div", {style: {textAlign: "center", marginTop: 14, fontSize: 13, color: msg.indexOf("✅") === 0 ? C.green : C.red, fontWeight: 600}}, msg),
        h("div", {style: {marginTop: 14, padding: 10, background: "rgba(255,182,39,.08)", borderRadius: 8, fontSize: 11, color: C.muted, lineHeight: 1.5}},
          "💡 Toutes les corrections sont tracées dans l'historique du compte et dans le journal d'audit. Le motif sera visible par le supporter."
        )
      )
    )
  );
}

// ═══════════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════════
function App() {
  // Détection SYNCHRONE du contexte de reset dès le 1er rendu (pas de fenêtre de timing)
  var _initHash = (typeof window !== "undefined" && window.location.hash) || "";
  var _initIsExpired = _initHash.indexOf("otp_expired") >= 0 || _initHash.indexOf("access_denied") >= 0 || _initHash.indexOf("error_code=") >= 0;
  var _initIsReset = (typeof window !== "undefined") && new URL(window.location.href).searchParams.get("reset") === "1";
  var _initStep = _initIsExpired ? "auth-reset-expired" : (_initIsReset ? "auth-reset-password" : "loading");

  var s1 = useState(_initStep); var step = s1[0], setStep = s1[1];
  var s2 = useState("account"); var view = s2[0], setView = s2[1];
  var s3 = useState(null); var authSession = s3[0], setAuthSession = s3[1];
  var s4 = useState(null); var account = s4[0], setAccount = s4[1];
  var s5 = useState([]); var products = s5[0], setProducts = s5[1];
  var s6 = useState(""); var emailInput = s6[0], setEmailInput = s6[1];
  var s7 = useState(""); var passwordInput = s7[0], setPasswordInput = s7[1];
  var s8 = useState(""); var nameInput = s8[0], setNameInput = s8[1];
  var s9 = useState(""); var phoneInput = s9[0], setPhoneInput = s9[1];
  var s10 = useState(""); var error = s10[0], setError = s10[1];
  var s11 = useState(""); var info = s11[0], setInfo = s11[1];
  var s12 = useState(false); var loading = s12[0], setLoading = s12[1];
  var s13 = useState(null); var initError = s13[0], setInitError = s13[1];
  // true dès le départ si on est en contexte reset → bloque tout auto-login
  var resetModeRef = useRef(_initIsReset || _initIsExpired);

  var refreshAccount = async function() {
    if (!authSession) return;
    var r = await db.getAccountByAuthUser(authSession.user.id);
    if (r.data) setAccount(r.data);
  };

  // Init
  useEffect(function() {
    (async function() {
      try {
        var p = await db.listProducts();
        if (p.error) throw p.error;
        setProducts(p.data);

        // Détection du contexte de reset de mot de passe (PRIORITAIRE sur la session)
        var hash = window.location.hash || "";
        var url = new URL(window.location.href);
        var isResetContext = url.searchParams.get("reset") === "1";
        var isExpiredLink = hash.indexOf("otp_expired") >= 0 || hash.indexOf("access_denied") >= 0 || hash.indexOf("error_code=") >= 0;

        if (isExpiredLink) {
          // Lien expiré / déjà utilisé → message clair
          resetModeRef.current = true;
          setStep("auth-reset-expired");
        } else if (isResetContext) {
          // L'utilisateur arrive via un lien de reset valide.
          // On force le formulaire "nouveau mot de passe", MÊME si une session
          // de récupération a été créée par Supabase (sinon il serait connecté direct).
          resetModeRef.current = true;
          setStep("auth-reset-password");
        } else {
          var s = await db.getSession();
          if (s.data && s.data.session) {
            setAuthSession(s.data.session);
          } else {
            setStep("auth-login");
          }
        }
      } catch (e) {
        setInitError("Erreur de connexion : " + (e.message || e));
      }
    })();

    var sub = supabase.auth.onAuthStateChange(function(_event, session) {
      // PASSWORD_RECOVERY = l'utilisateur a cliqué un lien de reset valide.
      if (_event === "PASSWORD_RECOVERY") {
        resetModeRef.current = true;
        setStep("auth-reset-password");
        return;
      }
      // Tant qu'on est en mode reset, on NE connecte PAS automatiquement
      // (sinon la session de récupération connecte direct sans changer le mdp).
      if (resetModeRef.current && _event === "SIGNED_IN") {
        return;
      }
      setAuthSession(session);
    });
    return function() { if (sub && sub.data && sub.data.subscription) sub.data.subscription.unsubscribe(); };
  }, []);

  // Charger le compte CSL quand on a une session
  useEffect(function() {
    if (!authSession) return;
    // Si on est en plein reset de mot de passe, on ne charge PAS le compte
    // (sinon on basculerait sur l'app au lieu du formulaire de reset).
    if (resetModeRef.current) return;
    (async function() {
      var r = await db.getAccountByAuthUser(authSession.user.id);
      if (r.data) {
        setAccount(r.data);
        setStep("app");
      } else {
        // Lié par email ?
        var byEmail = await db.getAccountByEmail(authSession.user.email);
        if (byEmail.data) {
          await db.linkAccountToAuth(byEmail.data.id, authSession.user.id);
          var refreshed = await db.getAccountByAuthUser(authSession.user.id);
          setAccount(refreshed.data);
          setStep("app");
        } else {
          // Session sans compte → demander prénom + finir création
          setEmailInput(authSession.user.email);
          setStep("auth-complete-profile");
        }
      }
    })();
  }, [authSession]);

  // ── Activation de la carte : première recharge obligatoire
  var sa1 = useState(null); var cardState = sa1[0], setCardState = sa1[1];

  useEffect(function() {
    if (!account) return;
    db.myCard().then(function(r) { setCardState(r.data || null); }).catch(function() {});
  }, [account && account.id, account && account.balance_cents]);

  // ── Inscription enrichie : rôle au club, pièces justificatives, photo
  var sj1 = useState([]); var memberTypes = sj1[0], setMemberTypes = sj1[1];
  var sj2 = useState(null); var typeId = sj2[0], setTypeId = sj2[1];
  var sj3 = useState(""); var licenceInput = sj3[0], setLicenceInput = sj3[1];
  var sj4 = useState(""); var companyInput = sj4[0], setCompanyInput = sj4[1];
  var sj5 = useState(null); var photoFile = sj5[0], setPhotoFile = sj5[1];
  var sj6 = useState(""); var photoPreview = sj6[0], setPhotoPreview = sj6[1];

  useEffect(function() {
    if (step !== "auth-register" && step !== "auth-complete-profile") return;
    if (memberTypes.length) return;
    db.signupMemberTypes().then(function(r) { setMemberTypes(r.data || []); });
  }, [step]);

  var selectedType = memberTypes.filter(function(x) { return x.id === typeId; })[0] || null;

  // La photo est redimensionnée avant envoi : une photo de téléphone
  // fait 4 Mo, la carte n'a besoin que de 600 pixels de large.
  var onPhoto = function(file) {
    if (!file) return;
    var img = new Image();
    var url = URL.createObjectURL(file);
    img.onload = function() {
      var cible = 600;
      var ratio = Math.min(1, cible / Math.max(img.width, img.height));
      var cv = document.createElement("canvas");
      cv.width = Math.round(img.width * ratio);
      cv.height = Math.round(img.height * ratio);
      cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
      cv.toBlob(function(blob) {
        URL.revokeObjectURL(url);
        setPhotoFile(blob);
        setPhotoPreview(cv.toDataURL("image/jpeg", 0.85));
      }, "image/jpeg", 0.85);
    };
    img.onerror = function() { URL.revokeObjectURL(url); setError("Photo illisible"); };
    img.src = url;
  };

  // Actions auth
  var doLogin = async function() {
    var email = emailInput.trim().toLowerCase();
    if (!email) { setError("Email requis"); return; }
    if (!passwordInput) { setError("Mot de passe requis"); return; }
    setLoading(true); setError("");
    var r = await db.signIn(email, passwordInput);
    setLoading(false);
    if (r.error) {
      var msg = r.error.message || "";
      if (msg.toLowerCase().indexOf("invalid") >= 0) setError("Email ou mot de passe incorrect");
      else setError("Erreur : " + msg);
      return;
    }
    setAuthSession(r.data.session);
    setPasswordInput("");
  };

  var doRegister = async function() {
    var email = emailInput.trim().toLowerCase();
    var name = nameInput.trim();
    if (!email || !passwordInput || !name) { setError("Prénom, nom, email et mot de passe sont requis"); return; }
    if (!typeId) { setError("Choisis ton rôle au club"); return; }
    var ty = memberTypes.filter(function(x) { return x.id === typeId; })[0];
    if (ty && ty.requires_licence && !licenceInput.trim()) { setError("Le numéro de licence est requis"); return; }
    if (ty && ty.requires_company && !companyInput.trim()) { setError("Le nom de la société est requis"); return; }
    setLoading(true); setError("");
    var r = await db.signUp(email, passwordInput);
    if (r.error) {
      setLoading(false);
      var msg = r.error.message || "";
      if (msg.toLowerCase().indexOf("already") >= 0) setError("Email déjà utilisé. Connecte-toi.");
      else setError("Erreur : " + msg);
      return;
    }
    if (!r.data.session) {
      var signInRes = await db.signIn(email, passwordInput);
      if (signInRes.error || !signInRes.data.session) {
        setLoading(false);
        setError("Compte créé. Vérifie ton email pour confirmer.");
        return;
      }
      r.data.session = signInRes.data.session;
    }
    var accRes = await db.createAccount({name: name, email: email, phone: phoneInput.trim(), authUserId: r.data.session.user.id});
    if (accRes.error) {
      setLoading(false);
      if (accRes.error.code === "23505") setError("Cet email a déjà un compte");
      else setError("Erreur : " + accRes.error.message);
      return;
    }

    // Photo d'abord : si elle échoue, le compte existe quand même et la
    // personne pourra la reprendre depuis Mon compte.
    var chemin = null;
    if (photoFile) {
      var up = await db.uploadPhoto(photoFile, r.data.session.user.id);
      if (!up.error) chemin = up.data;
    }

    var prof = await db.completeMyProfile({
      typeId: typeId, licence: licenceInput.trim(), company: companyInput.trim(),
      phone: phoneInput.trim(), photoPath: chemin
    });
    setLoading(false);
    if (prof.data && prof.data.success === false) {
      setError(prof.data.error === "licence_required" ? "Le numéro de licence est requis."
             : prof.data.error === "company_required" ? "Le nom de la société est requis."
             : "Erreur : " + prof.data.error);
      return;
    }

    var complet = await db.getAccountByAuthUser(r.data.session.user.id);
    setAccount(complet.data || accRes.data);
    setAuthSession(r.data.session);
    setPasswordInput("");
  };

  var doSendReset = async function() {
    var email = emailInput.trim().toLowerCase();
    if (!email) { setError("Email requis"); return; }
    setLoading(true); setError(""); setInfo("");
    var r = await db.resetPassword(email);
    setLoading(false);
    if (r.error) { setError("Erreur : " + r.error.message); return; }
    setInfo("✅ Email envoyé. Vérifie ta boîte (et les spams).");
    setStep("auth-login");
  };

  var doResetPassword = async function() {
    if (!passwordInput) { setError("Nouveau mot de passe requis"); return; }
    setLoading(true); setError("");
    var r = await db.updatePassword(passwordInput);
    setLoading(false);
    if (r.error) { setError("Erreur : " + r.error.message); return; }
    window.history.replaceState({}, "", window.location.pathname);
    setPasswordInput("");
    // Le mot de passe est changé : on libère le mode reset et on récupère
    // la session pour connecter l'utilisateur normalement.
    resetModeRef.current = false;
    var s = await db.getSession();
    if (s.data && s.data.session) {
      setInfo("✅ Mot de passe modifié");
      setAuthSession(s.data.session);
    } else {
      // Pas de session active → on renvoie vers la connexion avec le nouveau mdp
      setInfo("✅ Mot de passe modifié, connecte-toi avec ton nouveau mot de passe.");
      setStep("auth-login");
    }
  };

  var doCompleteProfile = async function() {
    if (!nameInput.trim()) { setError("Prénom et nom requis"); return; }
    setLoading(true); setError("");
    var r = await db.createAccount({name: nameInput.trim(), email: emailInput, phone: phoneInput.trim(), authUserId: authSession.user.id});
    setLoading(false);
    if (r.error) { setError("Erreur : " + r.error.message); return; }
    setAccount(r.data);
    setStep("app");
  };

  var doLogout = async function() {
    await db.signOut();
    setAuthSession(null);
    setAccount(null);
    setEmailInput(""); setPasswordInput(""); setNameInput(""); setPhoneInput("");
    setStep("auth-login");
    setView("account");
  };

  // RENDU
  if (initError) return h("div", {style: {minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, textAlign: "center"}},
    h("div", null,
      h("div", {style: {fontSize: 48, marginBottom: 16}}, "⚠️"),
      h("div", {style: {color: C.red, fontFamily: "Oswald", fontSize: 18, marginBottom: 8}}, "Erreur de connexion"),
      h("div", {style: {color: C.muted, fontSize: 14}}, initError)
    )
  );

  if (step === "loading") return null;

  if (step.indexOf("auth-") === 0) {
    var authStep = step.replace("auth-", "");
    return h(AuthScreen, {
      step: authStep === "complete-profile" ? "register" : authStep === "reset-password" ? "reset-password" : authStep,
      setStep: function(s) { setStep("auth-" + s); setError(""); setInfo(""); },
      emailInput: emailInput, setEmailInput: setEmailInput,
      passwordInput: passwordInput, setPasswordInput: setPasswordInput,
      nameInput: nameInput, setNameInput: setNameInput,
      phoneInput: phoneInput, setPhoneInput: setPhoneInput,
      memberTypes: memberTypes, typeId: typeId, setTypeId: setTypeId,
      selectedType: selectedType,
      licence: licenceInput, setLicence: setLicenceInput,
      company: companyInput, setCompany: setCompanyInput,
      photoPreview: photoPreview, onPhoto: onPhoto,
      error: error, info: info, loading: loading,
      onLogin: doLogin,
      onRegister: authStep === "complete-profile" ? doCompleteProfile : doRegister,
      onSendReset: doSendReset,
      onResetPassword: doResetPassword
    });
  }

  if (!account) return h("div", {style: {minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center"}},
    h("div", {style: {fontFamily: "Oswald", color: C.muted}}, "Chargement...")
  );

  // Les droits suivent l'ensemble des rôles du compte, pas le seul
  // rôle principal : un bénévole peut être supporter ET encaissement.
  var myRoles = (function() {
    var r = (account.roles && account.roles.length) ? account.roles.slice() : [];
    if (account.role && r.indexOf(account.role) === -1) r.push(account.role);
    if (!r.length) r = ["supporter"];
    return r;
  })();

  // ── La première recharge est obligatoire pour utiliser le cashless.
  // Les bénévoles et l'administration ne sont JAMAIS bloqués : un
  // caissier enfermé dehors un soir de match, ce serait pire que tout.
  var estSimpleSupporter = myRoles.every(function(r) { return r === "supporter"; });

  if (account && cardState && cardState.card_activated === false && estSimpleSupporter) {
    var seuil = cardState.activation_cents || 1000;
    var deja = cardState.recharges_cents || 0;
    var manque = Math.max(seuil - deja, 0);

    return h("div", {style: {minHeight: "100vh", background: C.bg, padding: "28px 18px"}},
      h("div", {style: {maxWidth: 440, margin: "0 auto"}},

        h("div", {style: {textAlign: "center", marginBottom: 22}},
          h("img", {src: CSL_LOGO, alt: "CSL", style: {width: 66, height: 66, objectFit: "contain"}}),
          h("div", {style: {fontFamily: "Oswald", fontSize: 22, fontWeight: 800, color: C.deep, letterSpacing: 1, marginTop: 10, textTransform: "uppercase"}},
            "Activez votre carte"),
          h("div", {style: {fontSize: 14, color: C.muted, marginTop: 6}}, "Bonjour " + (account.name || "") + " 👋")
        ),

        h(Card, null,
          h("div", {style: {textAlign: "center", padding: "6px 0 2px"}},
            h("div", {style: {fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Oswald", fontWeight: 700, color: C.muted}},
              deja > 0 ? "Il vous manque" : "Première recharge"),
            h("div", {style: {fontFamily: "Oswald", fontSize: 54, fontWeight: 800, color: C.green, lineHeight: 1.05}},
              formatEur(manque)),
            deja > 0 && h("div", {style: {fontSize: 13, color: C.muted}},
              formatEur(deja) + " déjà rechargés sur " + formatEur(seuil))
          ),

          h("div", {style: {background: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.25)", borderRadius: 12, padding: "14px 16px", marginTop: 16, fontSize: 14, color: C.deep, lineHeight: 1.55}},
            h("strong", null, "Cette somme est intégralement créditée sur votre compte."),
            " Ce n'est pas un frais : vous la dépensez à la buvette, à la boutique ou au repas. Elle active simplement votre carte de membre."),

          h("a", {href: SHOPIFY_RECHARGE_URL,
            style: Object.assign({}, AB, {background: C.green, width: "100%", display: "block", textAlign: "center", padding: 16, fontSize: 15, marginTop: 16, textDecoration: "none", boxSizing: "border-box"})},
            "Recharger en ligne"),

          h("div", {style: {textAlign: "center", margin: "18px 0 14px", color: C.muted, fontSize: 12}}, "— ou au stade —"),

          h("div", {style: {textAlign: "center"}},
            h("div", {style: {fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 12}},
              "Montrez ce code à un bénévole, il encaisse vos 10 € en espèces ou par carte et votre compte est activé aussitôt."),
            h(QRCodeView, {data: account.qr, size: 170}),
            h("div", {style: {fontFamily: "monospace", fontSize: 13, color: C.muted, marginTop: 10, letterSpacing: 1}}, account.qr)
          )
        ),

        h("div", {style: {display: "flex", gap: 8, marginTop: 14}},
          h("button", {onClick: function() { refreshAccount(); db.myCard().then(function(r) { setCardState(r.data || null); }); },
            style: Object.assign({}, AB, {background: C.pale, color: C.deep, flex: 1, padding: 13, fontSize: 12})},
            "J'ai rechargé"),
          h("button", {onClick: function() { db.signOut().then(function() { window.location.reload(); }); },
            style: Object.assign({}, AB, {background: "transparent", border: "1px solid " + C.border, color: C.muted, flex: 1, padding: 13, fontSize: 12})},
            "Déconnexion")
        ),

        h("div", {style: {textAlign: "center", marginTop: 18, fontSize: 12, color: C.muted, lineHeight: 1.5}},
          "Une question ? Écrivez au club à ",
          h("a", {href: "mailto:president@csl-rugby.fr", style: {color: C.blue}}, "president@csl-rugby.fr"))
      )
    );
  }
  var can = function() {
    var args = Array.prototype.slice.call(arguments);
    return args.some(function(r) { return myRoles.indexOf(r) >= 0; });
  };
  if (view === "encaissement" && !can("encaissement", "admin")) setView("account");
  if (view === "recharge" && !can("recharge", "admin")) setView("account");
  if (view === "boutique" && !can("boutique", "admin")) setView("account");
  if (view === "scan" && !can("billetterie", "admin")) setView("account");
  if (view === "admin" && !can("admin")) setView("account");

  return h(Layout, {account: account, view: view, setView: setView, onLogout: doLogout},
    view === "account" && h(AccountView, {account: account, refreshAccount: refreshAccount}),
    view === "tickets" && h(MyTicketsView, {account: account}),
    view === "scan" && h(ScanTicketsView, {account: account}),
    view === "encaissement" && h(EncaissementView, {account: account}),
    view === "recharge" && h(RechargeView, {account: account}),
    view === "boutique" && h(BoutiqueView, {account: account}),
    view === "admin" && h(AdminView, {products: products, setProducts: setProducts, account: account}),
    view === "support" && h(SupportView, {account: account, setView: setView})
  );
}

var root = ReactDOM.createRoot(document.getElementById("csl-root"));
root.render(h(App));
